import logger from '../../lib/logger';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders';
import { OBJFileLoader } from '@babylonjs/loaders/OBJ';
import * as GUI from '@babylonjs/gui';
import { FBXLoader } from 'babylonjs-fbx-loader';
import { useApp, formatWithUnit } from '../../context/AppContext';
import { toast } from 'sonner';
import { ProjectionSystem } from './ProjectionSystem';
import { applyTextureMapping, disableTextureMapping, checkUVValidity } from '../../lib/textureMappingCoordinator';

/**
 * Universal Texture Mapping bridge — resolves an object's mapping mode into
 * two GPU toggles: (1) the `StandardMaterial` triplanar plugin on the mesh's
 * own material (primitives), and (2) the `ProjectionSystem` receiver
 * shader's `useObjectTriplanar` uniform (imported / registered meshes).
 * Both toggles are opt-in with zero cost when disabled.
 */
function applyUniversalMapping(mesh, obj, projSystem) {
  if (!mesh || !obj) return;
  const mode = obj.texture_mapping_mode || 'auto';
  const scale = obj.texture_mapping_scale ?? 1.0;

  // Path 1 — own material (StandardMaterial primitives). Safe no-op for
  // meshes rendered via ProjectionSystem (their material isn't Standard).
  applyTextureMapping(mesh, mode, { scale });

  // Path 2 — projection receiver shader. Resolve mode to a boolean.
  if (projSystem && typeof projSystem.updateReceiverTriplanar === 'function') {
    let enable = mode === 'triplanar';
    if (mode === 'auto') {
      // For primitives, metadata.uvValid was just cached by applyTextureMapping.
      // For imported/child meshes, fall back to an on-demand check against any
      // submesh that carries vertex data.
      let uvValid = mesh.metadata && typeof mesh.metadata.uvValid === 'boolean'
        ? mesh.metadata.uvValid
        : undefined;
      if (uvValid === undefined && typeof mesh.getChildMeshes === 'function') {
        const cm = mesh.getChildMeshes(false).find((c) => c && c.getTotalVertices && c.getTotalVertices() > 0);
        if (cm) {
          uvValid = checkUVValidity(cm);
          cm.metadata = { ...(cm.metadata || {}), uvValid };
        }
      }
      if (uvValid === undefined) uvValid = checkUVValidity(mesh);
      enable = !uvValid;
    }
    projSystem.updateReceiverTriplanar(mesh, enable, scale);
    // Cascade to child receivers (imported GLBs have one receiver per sub-mesh).
    if (typeof mesh.getChildMeshes === 'function') {
      mesh.getChildMeshes(false).forEach((c) => {
        if (c && projSystem.receiverMeshes && projSystem.receiverMeshes.has(c.uniqueId || c.name)) {
          projSystem.updateReceiverTriplanar(c, enable, scale);
        }
      });
    }
  }
}
import { buildScaffolding, hasScaffoldConfigChanged } from './ScaffoldingSystem';
import { buildTruss, hasTrussConfigChanged } from './TrussSystem';
import { LuxHeatmapSystem } from './LuxHeatmapSystem';
import { createEngine, getEngineType } from './engineInit';
import {
  MAX_PROJECTORS, PROJECTION_TEXTURE_SIZE, MAX_SPOTLIGHT_COUNT,
  AMBIENT_LIGHT_INTENSITY, BEAM_WIREFRAME_THRESHOLD, MAX_SIMULTANEOUS_LIGHTS,
  ASPECT_RATIOS, LENS90_MOUNT_GAP,
  configureMaterialForProjectors, parseMTLToMaterial,
  getProjectorDirectionAndUp, getProjectionOrigin
} from './sceneUtils';
import { buildHollowCylinderGeometry } from './meshBuilders';
import { computeSurfaceImageSize } from '../../lib/surfaceImageSize';

// Configure OBJ loader - DON'T skip materials but set a fallback
OBJFileLoader.SKIP_MATERIALS = false;
OBJFileLoader.MATERIAL_LOADING_FAILS_SILENTLY = true;

// Resolve texture URLs: handles both absolute (old projects) and relative (new) paths
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
function resolveAssetUrl(url) {
  if (!url) return url;
  // Data URIs (base64) — use as-is
  if (url.startsWith('data:')) return url;
  // Already relative — prepend current backend
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // Absolute URL from a different domain — extract the API path and use current backend
  const apiMatch = url.match(/(\/api\/(?:content|files)\/serve\/.+)/);
  if (apiMatch) return `${BACKEND_URL}${apiMatch[1]}`;
  return url;
}

// Register FBX loader plugin
if (BABYLON.SceneLoader) {
  try {
    BABYLON.SceneLoader.RegisterPlugin(new FBXLoader());
    logger.log('FBX loader plugin registered successfully');
  } catch (e) {
    logger.warn('FBX loader registration failed:', e);
  }
}

const BabylonScene = ({ transformMode, viewPreset, onViewPresetReset, controlMode }) => {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const [sceneReady, setSceneReady] = useState(false);
  const projectorLightsRef = useRef({});
  const shadowGeneratorsRef = useRef({});
  const meshesRef = useRef({});
  const projectorMeshesRef = useRef({});
  const gizmoManagerRef = useRef(null);
  const projectorTexturesRef = useRef({});
  const beamMeshesRef = useRef({});
  const projectorLabelsRef = useRef({});
  const beamCenterLabelsRef = useRef({});
  const guiTextureRef = useRef(null);
  const groundRef = useRef(null);
  const gridMeshRef = useRef(null);
  const axesViewerRef = useRef(null); // Center axis marker at world (0,0,0) — toggleable
  const stackGhostsRef = useRef({}); // { [projectorId]: { meshes: BABYLON.Mesh[] } } — virtual stacking display
  const highlightLayerRef = useRef(null);
  const selectionBoxRef = useRef(null); // Yellow selection bounding box
  // Bridge to call createSelectionBox from outside the selection effect (e.g.
  // from the transform-sync effect after input field changes). Set inside
  // the selection effect, called from the build/update effect.
  const refreshSelectionBoxRef = useRef(null);
  const unfrozenLEDRef = useRef(null); // LED root whose children we've unfrozen for gizmo movement
  const multiSelectBoxesRef = useRef([]); // Cyan boxes for multi-selected items
  const isDraggingRef = useRef(false); // Track if gizmo is being dragged
  const multiPivotRef = useRef(null); // Temporary pivot for multi-select gizmo transforms
  const multiInitialRef = useRef([]); // Initial positions of multi-selected meshes before drag
  const dimensionGizmoRef = useRef({ lines: [], labels: [] }); // Live dimension display
  const measurementMeshesRef = useRef({}); // Store measurement visualization meshes
  const measurementModeRef = useRef(false); // Track measurement mode for callbacks
  const measurementStartRef = useRef(null); // Track measurement start point
  const startMeasurementRef = useRef(null); // Ref to startMeasurement function
  const completeMeasurementRef = useRef(null); // Ref to completeMeasurement function
  const measurementPreviewRef = useRef(null); // Preview line and sphere during measurement
  const projectSettingsRef = useRef({ unit: 'm' }); // Keep project settings current for callbacks
  const snapIndicatorRef = useRef(null); // Visual indicator for snap points
  const ledTemplateRef = useRef(null); // Pre-loaded LED cabinet .obj template
  const measurementLocalOffsetsRef = useRef({}); // Store local offsets for object-linked measurements
  const vertexMeshesRef = useRef([]); // Vertex point meshes for edit mode
  const boxSelectRef = useRef(null); // Box selection state
  const editModeViewModeRef = useRef('wireframe'); // Current view mode in edit mode
  const sceneObjectsRef = useRef([]); // CRITICAL: Track current sceneObjects for async callbacks
  const floorPlacedIdsRef = useRef(new Set()); // Track objects already floor-placed
  const luxHeatmapRef = useRef(null); // Lux heatmap system instance
  
  // Surface projection system refs (like Mapping Matter)
  const surfaceProjectionCanvasRef = useRef(null); // Offscreen canvas for rendering
  const surfaceProjectionTexturesRef = useRef({}); // Per-mesh projection textures
  const ambientLightRef = useRef(null); // Single ambient light for scene
  const projectionSystemRef = useRef(null); // New Mapping Matter-style projection system
  
  const {
    projectors,
    sceneObjects,
    selectedObjectId,
    setSelectedObjectId,
    worldSettings,
    updateSceneObject,
    updateProjector,
    // Measurement state
    measurements,
    measurementMode,
    measurementStart,
    startMeasurement,
    completeMeasurement,
    cancelMeasurement,
    updateMeasurement,
    // Edit Mode state
    editMode,
    editingObjectId,
    selectedVertices,
    setSelectedVertices,
    multiSelectIds,
    setMultiSelectIds,
    // Project settings for units
    projectSettings,
    // Undo history save for gizmo drag
    saveToHistoryImmediate,
    // Snap-to-ground callback ref
    snapToGroundFnRef,
    // Camera system
    savedCameras,
    activeCameraId,
    setActiveCameraId,
    getCameraStateRef,
    captureScreenshotRef,
    mainCameraRef: externalMainCameraRef,
    // Lux Heatmap
    luxHeatmapEnabled,
    // Display mode ('shaded' | 'outline')
    displayMode
  } = useApp();

  // Keep measurement refs in sync with state for use in scene callbacks
  useEffect(() => {
    measurementModeRef.current = measurementMode;
    measurementStartRef.current = measurementStart;
    startMeasurementRef.current = startMeasurement;
    completeMeasurementRef.current = completeMeasurement;
    
    // Clean up preview when measurement mode is turned off or measurement is cancelled
    if (!measurementMode && measurementPreviewRef.current) {
      if (measurementPreviewRef.current.line) measurementPreviewRef.current.line.dispose();
      if (measurementPreviewRef.current.sphere) measurementPreviewRef.current.sphere.dispose();
      if (measurementPreviewRef.current.label) measurementPreviewRef.current.label.dispose();
      if (measurementPreviewRef.current.labelAnchor) measurementPreviewRef.current.labelAnchor.dispose();
      measurementPreviewRef.current = null;
    }
    // Hide snap indicator when not in measurement mode
    if (!measurementMode && snapIndicatorRef.current) {
      snapIndicatorRef.current.isVisible = false;
    }
  }, [measurementMode, measurementStart, startMeasurement, completeMeasurement]);

  // Keep projectSettings ref in sync
  useEffect(() => {
    projectSettingsRef.current = projectSettings;
    // Sync overlap brightness to projection system
    if (projectionSystemRef.current && projectSettings.overlapBrightness != null) {
      projectionSystemRef.current.setOverlapBrightness(projectSettings.overlapBrightness / 100);
    }
  }, [projectSettings]);
  
  // CRITICAL: Update refs SYNCHRONOUSLY (not in useEffect)
  // This ensures async callbacks and effects always have access to CURRENT state
  // useEffect runs AFTER render, which creates a race condition with async callbacks
  sceneObjectsRef.current = sceneObjects;
  
  // Keep a ref for projectors too, so gizmo/selection effects don't need projectors in deps
  const projectorsRef = useRef(projectors);
  projectorsRef.current = projectors;

  // Keep a ref for selection-related state so the build effect can perform a
  // self-healing gizmo re-attach when the just-created mesh is the one
  // currently selected (fixes a React effect race where a new LED couldn't be
  // gizmo-moved until the page was refreshed).
  const selectedObjectIdRef = useRef(selectedObjectId);
  selectedObjectIdRef.current = selectedObjectId;
  const transformModeRef = useRef(transformMode);
  transformModeRef.current = transformMode;

  // Register snapToGround function — uses actual Babylon bounding box
  // Formula: position.y = boundingBoxHeight / 2 (keeps pivot at center, bottom on floor)
  useEffect(() => {
    if (snapToGroundFnRef) {
      snapToGroundFnRef.current = (objectId) => {
        const mesh = meshesRef.current[objectId];
        if (!mesh) return null;
        mesh.computeWorldMatrix(true);
        mesh.refreshBoundingInfo();
        const bbox = mesh.getBoundingInfo().boundingBox;
        if (!bbox) return null;
        const height = bbox.maximumWorld.y - bbox.minimumWorld.y;
        return height / 2;
      };
    }
    return () => {
      if (snapToGroundFnRef) snapToGroundFnRef.current = null;
    };
  }, [snapToGroundFnRef]);

  // Register getCameraState function — returns current camera params
  useEffect(() => {
    if (getCameraStateRef) {
      getCameraStateRef.current = () => {
        const camera = cameraRef.current;
        if (!camera) return null;
        return {
          position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
          target: { x: camera.target.x, y: camera.target.y, z: camera.target.z },
          alpha: camera.alpha,
          beta: camera.beta,
          radius: camera.radius,
          fov: camera.fov
        };
      };
    }
    return () => {
      if (getCameraStateRef) getCameraStateRef.current = null;
    };
  }, [getCameraStateRef]);

  // Register captureScreenshot function — renders scene to canvas at given resolution
  useEffect(() => {
    if (captureScreenshotRef) {
      captureScreenshotRef.current = async (width, height) => {
        const engine = engineRef.current;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        if (!engine || !scene || !camera) return null;

        return new Promise((resolve) => {
          BABYLON.Tools.CreateScreenshotUsingRenderTarget(
            engine, camera, { width, height }, (data) => {
              resolve(data);
            }, 'image/png', 1, true
          );
        });
      };
    }
    return () => {
      if (captureScreenshotRef) captureScreenshotRef.current = null;
    };
  }, [captureScreenshotRef]);

  // React to activeCameraId changes — animate camera to saved position
  useEffect(() => {
    if (!activeCameraId || !cameraRef.current) return;
    const cam = savedCameras.find(c => c.id === activeCameraId);
    if (!cam) return;
    const camera = cameraRef.current;
    const scene = sceneRef.current;
    if (!scene) return;

    // Animate camera to saved position
    const fps = 60;
    const frames = 30; // 0.5 second animation

    const alphaAnim = new BABYLON.Animation('camAlpha', 'alpha', fps, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    alphaAnim.setKeys([{ frame: 0, value: camera.alpha }, { frame: frames, value: cam.alpha }]);

    const betaAnim = new BABYLON.Animation('camBeta', 'beta', fps, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    betaAnim.setKeys([{ frame: 0, value: camera.beta }, { frame: frames, value: cam.beta }]);

    const radiusAnim = new BABYLON.Animation('camRadius', 'radius', fps, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    radiusAnim.setKeys([{ frame: 0, value: camera.radius }, { frame: frames, value: cam.radius }]);

    const targetAnim = new BABYLON.Animation('camTarget', 'target', fps, BABYLON.Animation.ANIMATIONTYPE_VECTOR3, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    targetAnim.setKeys([
      { frame: 0, value: camera.target.clone() },
      { frame: frames, value: new BABYLON.Vector3(cam.target.x, cam.target.y, cam.target.z) }
    ]);

    const fovAnim = new BABYLON.Animation('camFov', 'fov', fps, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    fovAnim.setKeys([{ frame: 0, value: camera.fov }, { frame: frames, value: cam.fov || 0.8 }]);

    // Add easing
    const ease = new BABYLON.CubicEase();
    ease.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
    [alphaAnim, betaAnim, radiusAnim, targetAnim, fovAnim].forEach(a => a.setEasingFunction(ease));

    camera.animations = [alphaAnim, betaAnim, radiusAnim, targetAnim, fovAnim];
    scene.beginAnimation(camera, 0, frames, false);
  }, [activeCameraId, savedCameras]);

  // Sync FOV when active camera's FOV is changed via slider
  useEffect(() => {
    if (!activeCameraId || !cameraRef.current) return;
    const cam = savedCameras.find(c => c.id === activeCameraId);
    if (!cam) return;
    cameraRef.current.fov = cam.fov || 0.8;
  }, [activeCameraId, savedCameras]);

  // Lux Heatmap toggle
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !sceneReady) return;
    
    if (luxHeatmapEnabled) {
      if (!luxHeatmapRef.current) {
        luxHeatmapRef.current = new LuxHeatmapSystem(scene);
      }
      luxHeatmapRef.current.enable(sceneObjects, projectors, projectSettings);
    } else {
      if (luxHeatmapRef.current) {
        luxHeatmapRef.current.disable();
      }
    }
  }, [luxHeatmapEnabled, sceneObjects, projectors, projectSettings, sceneReady]);

  // ── Display mode (Shaded / Outline) ─────────────────────────────────────
  // Babylon's `scene.forceWireframe` is a single global toggle that renders
  // every mesh as wireframe without modifying any material, duplicating
  // meshes, or touching projection / shadow / LED logic. The scene is shared
  // across all viewports (perspective, top, bottom, left, right) so this
  // automatically applies everywhere.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.forceWireframe = displayMode === 'outline';
  }, [displayMode, sceneReady]);

  // Initialize Babylon.js scene
  useEffect(() => {
    if (!canvasRef.current) return;
    
    let isCleanedUp = false;
    let engine = null;
    
    // Async initialization with WebGPU support
    const initScene = async () => {
      // Create engine (WebGPU with WebGL2 fallback)
      engine = await createEngine(canvasRef.current);
      
      if (isCleanedUp) {
        engine.dispose();
        return;
      }
      
      engineRef.current = engine;

      // Create scene
      const scene = new BABYLON.Scene(engine);
      scene.clearColor = new BABYLON.Color4(0.035, 0.035, 0.043, 1);
      sceneRef.current = scene;
      setSceneReady(true);
      window.__babylonScene = scene;
      window.__babylonEngine = scene.getEngine();
      window.__BABYLON = BABYLON;
      // Exposed for the read-only Surface Image Size measurement in
      // PropertiesPanel — used as a hand-off for scene.pickWithRay calls.
      // PropertiesPanel never mutates these; it only reads through them.
      window.__projectorMeshes = projectorMeshesRef.current;
      window.__getObjScreenPos = (namePrefix) => {
        const mesh = scene.meshes.find(m => m.name.startsWith(namePrefix || 'obj_'));
        if (!mesh) return null;
        const engine = scene.getEngine();
        const cam = scene.activeCamera;
        const wp = mesh.getBoundingInfo().boundingBox.centerWorld;
        const sp = BABYLON.Vector3.Project(wp, BABYLON.Matrix.Identity(), scene.getTransformMatrix(), cam.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()));
        return { x: sp.x, y: sp.y, rw: engine.getRenderWidth(), rh: engine.getRenderHeight(), name: mesh.name, id: mesh.metadata?.id };
      };

      // Pre-load LED cabinet .obj template
      BABYLON.SceneLoader.ImportMeshAsync('', '/led_screen.obj', '', scene, null, '.obj')
        .then((result) => {
          const allMeshes = result.meshes.filter(m => m.getTotalVertices && m.getTotalVertices() > 0);
          let template;
          if (allMeshes.length > 1) {
            template = BABYLON.Mesh.MergeMeshes(allMeshes, true, true, undefined, false, false);
          } else if (allMeshes.length === 1) {
            template = allMeshes[0];
          }
          if (template) {
            template.name = '__ledTemplate';
            template.refreshBoundingInfo();
            const bb = template.getBoundingInfo().boundingBox;
            const center = bb.center.clone();
            const sizeRaw = bb.extendSize.scale(2);
            // Center at origin
            template.position = center.negate();
            template.bakeCurrentTransformIntoVertices();
            // Convert from mm to meters
            template.scaling = new BABYLON.Vector3(0.001, 0.001, 0.001);
            template.bakeCurrentTransformIntoVertices();
            template.position = BABYLON.Vector3.Zero();
            template.scaling = BABYLON.Vector3.One();
            template.refreshBoundingInfo();
            const bbM = template.getBoundingInfo().boundingBox;
            const sizeM = bbM.extendSize.scale(2);
            template.isVisible = false;
            template.isPickable = false;
            template.metadata = { sizeX: sizeM.x, sizeY: sizeM.y, sizeZ: sizeM.z };
            ledTemplateRef.current = template;
            logger.log('LED template loaded:', sizeM.x.toFixed(4), 'x', sizeM.y.toFixed(4), 'x', sizeM.z.toFixed(4), 'm');
          }
        })
        .catch((err) => logger.warn('LED template load failed, using box fallback:', err));

      // Create arc rotate camera
      const camera = new BABYLON.ArcRotateCamera(
        'camera',
        -Math.PI / 2,
        Math.PI / 3,
        50,
        new BABYLON.Vector3(0, 5, 0),
        scene
      );
      camera.attachControl(canvasRef.current, true);
      camera.wheelPrecision = 5;
      camera.minZ = 0.1;
      camera.maxZ = 2000;
      camera.panningSensibility = 50;
      camera.lowerRadiusLimit = 1;
      camera.upperRadiusLimit = 1000;
      cameraRef.current = camera;
      if (externalMainCameraRef) externalMainCameraRef.current = camera;

    // Create ambient light for base illumination
    // Increased intensity since we don't use spotlights (projective texturing mode)
    const ambientLight = new BABYLON.HemisphericLight('ambient', new BABYLON.Vector3(0, 1, 0), scene);
    ambientLight.intensity = AMBIENT_LIGHT_INTENSITY;
    ambientLight.groundColor = new BABYLON.Color3(0.2, 0.2, 0.2);
    ambientLight.diffuse = new BABYLON.Color3(0.9, 0.9, 0.9);
    ambientLightRef.current = ambientLight;
    
    // Initialize Mapping Matter-style Projection System (supports 100+ projectors)
    projectionSystemRef.current = new ProjectionSystem(scene);
    logger.log('✓ Projection System initialized (100+ projector support)');

      // Create transparent ground (for shadows only, fully transparent)
      const gridSize = worldSettings?.gridSize || 500;
      const ground = BABYLON.MeshBuilder.CreateGround('ground', { width: gridSize, height: gridSize }, scene);
      
      // Make ground fully transparent - it only receives shadows
      const groundMat = new BABYLON.StandardMaterial('groundMat', scene);
      groundMat.alpha = 0; // Fully transparent
      groundMat.specularColor = new BABYLON.Color3(0, 0, 0);
      groundMat.backFaceCulling = false;
      configureMaterialForProjectors(groundMat); // Support multiple projectors
      ground.material = groundMat;
      ground.receiveShadows = true;
      ground.isPickable = false; // Don't select ground when clicking
      groundRef.current = ground;

      // Add grid lines (visible) - major and minor grid for better scale perception
      const gridLines = [];
      const gridStep = worldSettings?.gridStep || 10; // 10m spacing by default
    const gridColor = worldSettings?.gridLineColor || '#4d4d4d';
    const halfGrid = gridSize / 2;
    
    // Create grid lines
    for (let i = -halfGrid; i <= halfGrid; i += gridStep) {
      gridLines.push([new BABYLON.Vector3(i, 0.01, -halfGrid), new BABYLON.Vector3(i, 0.01, halfGrid)]);
      gridLines.push([new BABYLON.Vector3(-halfGrid, 0.01, i), new BABYLON.Vector3(halfGrid, 0.01, i)]);
    }
    const gridMesh = BABYLON.MeshBuilder.CreateLineSystem('grid', { lines: gridLines }, scene);
    const gr = parseInt(gridColor.slice(1, 3), 16) / 255;
    const gg = parseInt(gridColor.slice(3, 5), 16) / 255;
    const gb = parseInt(gridColor.slice(5, 7), 16) / 255;
    gridMesh.color = new BABYLON.Color3(gr, gg, gb);
    gridMesh.isPickable = false; // Don't select grid when clicking
    gridMeshRef.current = gridMesh;

    // ── Center Axis Marker (X=red, Y=green, Z=blue) at world origin ────
    // Default ON; persisted via localStorage. Pinned at (0,0,0); never moves.
    // Constant on-screen size — scaled per frame based on camera distance so
    // the axis stays readable at any zoom level (matches gizmo behavior).
    try {
      const axes = new BABYLON.AxesViewer(scene, 1); // base size 1; scaled below
      [axes.xAxis, axes.yAxis, axes.zAxis].forEach(ax => {
        if (!ax) return;
        ax.position = BABYLON.Vector3.Zero();
        // Mark axis lines as non-pickable so they don't interfere with selection
        const walk = (n) => {
          if (n.isPickable !== undefined) n.isPickable = false;
          (n.getChildMeshes ? n.getChildMeshes(false) : []).forEach(walk);
        };
        walk(ax);
      });
      const savedAxis = localStorage.getItem('avst_showCenterAxis');
      const initialVisible = savedAxis === null ? true : savedAxis !== 'false';
      [axes.xAxis, axes.yAxis, axes.zAxis].forEach(ax => ax && ax.setEnabled(initialVisible));
      axesViewerRef.current = axes;

      // Per-frame screen-space scale: keep the axis a constant ~7% of the
      // viewport height regardless of zoom. For an ArcRotateCamera, world
      // distance corresponding to one screen unit ≈ 2 * distance * tan(fov/2).
      // Multiply by a fraction to get the desired world size.
      const SCREEN_FRACTION = 0.08;
      const updateAxisScale = () => {
        const cam = cameraRef.current;
        if (!cam || !axesViewerRef.current) return;
        const dist = BABYLON.Vector3.Distance(cam.position, BABYLON.Vector3.Zero());
        const fov = cam.fov || 0.8;
        const worldSize = 2 * dist * Math.tan(fov / 2) * SCREEN_FRACTION;
        const s = Math.max(0.001, worldSize);
        [axesViewerRef.current.xAxis, axesViewerRef.current.yAxis, axesViewerRef.current.zAxis].forEach(ax => {
          if (!ax) return;
          ax.scaling.set(s, s, s);
        });
      };
      scene.onBeforeRenderObservable.add(updateAxisScale);
    } catch (e) {
      logger.warn('AxesViewer init failed:', e);
    }

    // Gizmo manager for transform controls
    const gizmoManager = new BABYLON.GizmoManager(scene);
    gizmoManager.positionGizmoEnabled = false;
    gizmoManager.rotationGizmoEnabled = false;
    gizmoManager.scaleGizmoEnabled = false;
    gizmoManager.boundingBoxGizmoEnabled = false;
    
    // Configure gizmos to work properly with scaled objects
    gizmoManager.usePointerToAttachGizmos = false; // We handle attachment manually
    
    // Set smaller gizmo scale ratios for better usability
    // These will be applied when gizmos are enabled
    
    gizmoManagerRef.current = gizmoManager;
    // Expose gizmo manager for UV export and other tools
    scene.metadata = { ...(scene.metadata || {}), gizmoManager };
    
    // Keep the RGB transform-gizmo at a constant on-screen size — independent
    // of camera zoom / orbit / pan / pers↔ortho. Mimics Blender / Unity / UE.
    //
    // How Babylon does this internally:
    //   When `gizmo.updateScale = true` (the default), each frame Babylon sets
    //     rootMesh.scaling = (distanceToCamera / 6) * scaleRatio
    //   The gizmo's WORLD size grows linearly with camera distance, which makes
    //   its on-screen pixel size CONSTANT.
    //
    // ⚠ That means `scaleRatio` itself MUST be a constant. Multiplying it by
    //   distance (the previous bug) made screen size grow linearly with zoom.
    //
    // We just pin scaleRatio to a sensible constant on every active gizmo.
    const GIZMO_SCREEN_RATIO = 0.78; // 20% smaller than 0.98 (was 1.4 originally)
    const updateGizmoScale = () => {
      const gm = gizmoManagerRef.current;
      if (!gm) return;
      if (gm.gizmos.positionGizmo) gm.gizmos.positionGizmo.scaleRatio = GIZMO_SCREEN_RATIO;
      if (gm.gizmos.rotationGizmo) gm.gizmos.rotationGizmo.scaleRatio = GIZMO_SCREEN_RATIO;
      if (gm.gizmos.scaleGizmo)    gm.gizmos.scaleGizmo.scaleRatio    = GIZMO_SCREEN_RATIO;
    };

    // Run every frame — cheap, idempotent, and re-applies after any internal
    // Babylon resets (e.g. when a gizmo mode toggles on for the first time).
    scene.onBeforeRenderObservable.add(updateGizmoScale);
    
    // Sync orthographic bounds when zooming (radius changes in ortho mode)
    camera.onViewMatrixChangedObservable.add(() => {
      if (camera.mode === BABYLON.Camera.ORTHOGRAPHIC_CAMERA) {
        const aspect = scene.getEngine().getAspectRatio(camera) || 1.78;
        const orthoSize = camera.radius;
        camera.orthoTop = orthoSize / 2;
        camera.orthoBottom = -orthoSize / 2;
        camera.orthoLeft = -orthoSize * aspect / 2;
        camera.orthoRight = orthoSize * aspect / 2;
      }
    });
    
    // Store the update function for cleanup
    gizmoManagerRef.current._updateGizmoScale = updateGizmoScale;

    // Create fullscreen GUI for projector labels
    const guiTexture = GUI.AdvancedDynamicTexture.CreateFullscreenUI('UI', true, scene);
    guiTextureRef.current = guiTexture;

    // Create highlight layer for selection visualization
    const highlightLayer = new BABYLON.HighlightLayer('highlight', scene);
    highlightLayer.innerGlow = false;
    highlightLayer.outerGlow = true;
    highlightLayerRef.current = highlightLayer;

    // Handle mesh selection and measurement clicks
    // Uses refs to access current state from the callback
    
    // Helper function to find nearest vertex or edge point to snap to
    const findNearestVertex = (mesh, pickedPoint) => {
      if (!mesh || !mesh.getVerticesData) return pickedPoint;
      
      // Scale snap distance with camera distance for consistent feel
      const cameraDistance = BABYLON.Vector3.Distance(scene.activeCamera.position, pickedPoint);
      const snapThreshold = Math.max(0.1, Math.min(1.0, cameraDistance * 0.05));
      
      try {
        const positions = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        if (!positions || positions.length === 0) return pickedPoint;
        
        let nearestPoint = pickedPoint.clone();
        let minDistance = Infinity;
        let snapped = false;
        
        // Get world matrix for transforming local vertices to world space
        const worldMatrix = mesh.getWorldMatrix();
        
        // Collect world vertices
        const worldVertices = [];
        for (let i = 0; i < positions.length; i += 3) {
          const localVertex = new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]);
          const worldVertex = BABYLON.Vector3.TransformCoordinates(localVertex, worldMatrix);
          worldVertices.push(worldVertex);
          
          const distance = BABYLON.Vector3.Distance(worldVertex, pickedPoint);
          if (distance < minDistance) {
            minDistance = distance;
            nearestPoint = worldVertex;
          }
        }
        
        // Snap to nearest vertex if close enough
        if (minDistance < snapThreshold * 0.5) {
          snapped = true;
        } else {
          // Try edge snapping using mesh indices
          const indices = mesh.getIndices();
          if (indices && indices.length > 0) {
            for (let i = 0; i < indices.length; i += 3) {
              const triVerts = [
                worldVertices[indices[i]],
                worldVertices[indices[i + 1]],
                worldVertices[indices[i + 2]]
              ];
              if (!triVerts[0] || !triVerts[1] || !triVerts[2]) continue;
              
              // Check each edge of the triangle
              for (let e = 0; e < 3; e++) {
                const a = triVerts[e];
                const b = triVerts[(e + 1) % 3];
                const ab = b.subtract(a);
                const ap = pickedPoint.subtract(a);
                const abLenSq = ab.lengthSquared();
                if (abLenSq < 0.0001) continue;
                
                let t = BABYLON.Vector3.Dot(ap, ab) / abLenSq;
                t = Math.max(0, Math.min(1, t));
                const closestOnEdge = a.add(ab.scale(t));
                const dist = BABYLON.Vector3.Distance(closestOnEdge, pickedPoint);
                
                if (dist < minDistance && dist < snapThreshold) {
                  minDistance = dist;
                  nearestPoint = closestOnEdge;
                  snapped = true;
                }
              }
            }
          }
          
          // If still no edge snap, fall back to vertex snap with larger threshold
          if (!snapped && minDistance < snapThreshold) {
            snapped = true;
          }
        }
        
        // Show/update snap indicator
        if (snapped && measurementModeRef.current) {
          if (!snapIndicatorRef.current) {
            const indicator = BABYLON.MeshBuilder.CreateSphere('snapIndicator', { diameter: 0.15 }, scene);
            const mat = new BABYLON.StandardMaterial('snapIndicatorMat', scene);
            mat.diffuseColor = new BABYLON.Color3(1, 1, 0);
            mat.emissiveColor = new BABYLON.Color3(1, 1, 0);
            mat.alpha = 0.9;
            mat.disableLighting = true;
            indicator.material = mat;
            indicator.isPickable = false;
            indicator.renderingGroupId = 1;
            snapIndicatorRef.current = indicator;
          }
          // Scale indicator with camera distance for consistent visibility
          const indicatorScale = Math.max(0.5, cameraDistance * 0.015);
          snapIndicatorRef.current.scaling = new BABYLON.Vector3(indicatorScale, indicatorScale, indicatorScale);
          snapIndicatorRef.current.position = nearestPoint.clone();
          snapIndicatorRef.current.isVisible = true;
          return nearestPoint;
        }
        
        // Hide snap indicator if not snapping
        if (snapIndicatorRef.current) {
          snapIndicatorRef.current.isVisible = false;
        }
      } catch (e) {
        logger.log('Could not get vertex data:', e);
      }
      
      return pickedPoint;
    };
    
    // Store refs for edit mode vertex selection
    let editModeRef = { current: false };
    let selectedVerticesRef = { current: [] };
    let setSelectedVerticesRef = { current: null };
    let editingObjectIdRef = { current: null };
    let multiSelectIdsRef = { current: [] };
    
    scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN) {
        // Check for vertex click in edit mode FIRST
        if (editModeRef.current) {
          const vertexPick = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
            return mesh.name.startsWith('vertex_') && mesh.isPickable;
          });
          
          if (vertexPick.hit && vertexPick.pickedMesh) {
            const vertexIndex = vertexPick.pickedMesh.metadata?.vertexIndex;
            if (vertexIndex !== undefined && setSelectedVerticesRef.current) {
              const ctrlKey = pointerInfo.event.ctrlKey || pointerInfo.event.metaKey;
              const shiftKey = pointerInfo.event.shiftKey;
              
              if (ctrlKey || shiftKey) {
                // Multi-select: toggle vertex
                setSelectedVerticesRef.current(prev => {
                  if (prev.includes(vertexIndex)) {
                    return prev.filter(i => i !== vertexIndex);
                  }
                  return [...prev, vertexIndex];
                });
              } else {
                // Single select
                setSelectedVerticesRef.current([vertexIndex]);
              }
            }
            return; // Don't process further
          }
          
          // If not clicking on a vertex, check if clicking on a component mesh
          const componentPick = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
            // Pick any mesh that's part of the edited object (not vertex markers)
            if (mesh.name.startsWith('vertex_')) return false;
            if (mesh.name.startsWith('gizmo')) return false;
            if (mesh.name === 'ground' || mesh.name === 'grid') return false;
            
            // Check if this mesh is the edited object or a child of it
            const parentMesh = meshesRef.current[editingObjectIdRef.current];
            if (!parentMesh) return false;
            
            // Check if this IS the parent mesh itself (compare by reference or metadata id)
            if (mesh === parentMesh) {
              return true;
            }
            
            // Check if this is the picking mesh for the editing object
            if (mesh.metadata?.isPickingMesh && mesh.metadata?.id === editingObjectIdRef.current) {
              return true;
            }
            
            // Check if this mesh has the same metadata id as the editing object
            if (mesh.metadata?.id === editingObjectIdRef.current) {
              return true;
            }
            
            // Check if this mesh is a descendant of the edited object
            if (mesh.isDescendantOf && mesh.isDescendantOf(parentMesh)) {
              return true;
            }
            return false;
          });
          
          if (componentPick.hit && componentPick.pickedMesh) {
            const clickedMesh = componentPick.pickedMesh;
            logger.log('Component clicked:', clickedMesh.name, clickedMesh.id);
            
            // Notify the UI to select this component
            if (window.onComponentClicked) {
              window.onComponentClicked(clickedMesh.id, clickedMesh.name);
            }
            return; // Don't start box selection
          } else {
            // Wireframe meshes are not pickable, so try a different approach:
            // Check if the user clicked on the editing mesh by checking if the regular pick
            // hit a mesh that belongs to the editing object
            const regularPick = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
              if (!mesh.isPickable) return false;
              if (mesh.name.startsWith('vertex_')) return false;
              if (mesh.name.startsWith('gizmo')) return false;
              if (mesh.name === 'ground' || mesh.name === 'grid') return false;
              return true;
            });
            
            if (regularPick.hit && regularPick.pickedMesh) {
              const pickedMesh = regularPick.pickedMesh;
              const pickedId = pickedMesh.metadata?.id;
              
              // Check if the picked mesh belongs to the editing object
              if (pickedId === editingObjectIdRef.current) {
                logger.log('Component clicked:', pickedMesh.name, pickedMesh.id);
                
                // Notify the UI to select this component
                if (window.onComponentClicked) {
                  window.onComponentClicked(pickedMesh.metadata?.id || pickedMesh.id, pickedMesh.name);
                }
                return; // Don't start box selection
              }
              
              // Check if the picked mesh is a child of the editing object
              const parentMesh = meshesRef.current[editingObjectIdRef.current];
              if (parentMesh && pickedMesh.isDescendantOf && pickedMesh.isDescendantOf(parentMesh)) {
                logger.log('Component clicked:', pickedMesh.name, pickedMesh.id);
                
                // Notify the UI to select this component
                if (window.onComponentClicked) {
                  window.onComponentClicked(pickedMesh.metadata?.id || pickedMesh.id, pickedMesh.name);
                }
                return; // Don't start box selection
              }
            } else {
              // No mesh was picked - check if we clicked within the screen-space bounds of the editing mesh
              const parentMesh = meshesRef.current[editingObjectIdRef.current];
              if (parentMesh) {
                // Get the bounding info of the editing mesh
                const boundingInfo = parentMesh.getBoundingInfo?.();
                if (boundingInfo) {
                  // Get the 8 corners of the bounding box
                  const min = boundingInfo.boundingBox.minimumWorld;
                  const max = boundingInfo.boundingBox.maximumWorld;
                  const corners = [
                    new BABYLON.Vector3(min.x, min.y, min.z),
                    new BABYLON.Vector3(max.x, min.y, min.z),
                    new BABYLON.Vector3(min.x, max.y, min.z),
                    new BABYLON.Vector3(max.x, max.y, min.z),
                    new BABYLON.Vector3(min.x, min.y, max.z),
                    new BABYLON.Vector3(max.x, min.y, max.z),
                    new BABYLON.Vector3(min.x, max.y, max.z),
                    new BABYLON.Vector3(max.x, max.y, max.z),
                  ];
                  
                  // Project corners to screen space
                  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
                  corners.forEach(corner => {
                    const screenPos = BABYLON.Vector3.Project(
                      corner,
                      BABYLON.Matrix.Identity(),
                      scene.getTransformMatrix(),
                      camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight())
                    );
                    minX = Math.min(minX, screenPos.x);
                    maxX = Math.max(maxX, screenPos.x);
                    minY = Math.min(minY, screenPos.y);
                    maxY = Math.max(maxY, screenPos.y);
                  });
                  
                  // Check if click is within screen bounds
                  const clickX = scene.pointerX;
                  const clickY = scene.pointerY;
                  
                  if (clickX >= minX && clickX <= maxX && clickY >= minY && clickY <= maxY) {
                    logger.log('Component clicked:', parentMesh.name, parentMesh.id);
                    
                    // Notify the UI to select the root component
                    if (window.onComponentClicked) {
                      window.onComponentClicked(parentMesh.metadata?.id || parentMesh.id, parentMesh.name);
                    }
                    return; // Don't start box selection
                  }
                }
              }
            }
          }
          
          // Start box selection if clicking on empty space
          if (!vertexPick.hit) {
            boxSelectRef.current = {
              startX: scene.pointerX,
              startY: scene.pointerY,
              active: true
            };
          }
        }
        
        const pickResult = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
          // Filter: only pick actual scene objects and projectors, not gizmos, grid, ground, beams, or measurement lines
          if (!mesh.isPickable) return false;
          if (mesh.name.startsWith('gizmo')) return false;
          if (mesh.name === 'ground' || mesh.name === 'grid') return false;
          if (mesh.name.startsWith('beam_')) return false;
          if (mesh.name.startsWith('measureLine_') || mesh.name.startsWith('measureLabel_')) return false;
          if (mesh.name.startsWith('measureStart_') || mesh.name.startsWith('measureEnd_')) return false;
          if (mesh.name.startsWith('measureAnchor_') || mesh.name.startsWith('measurePreview_')) return false;
          if (mesh.name.startsWith('vertex_')) return false; // Skip edit mode vertex markers
          if (mesh.name === 'snapIndicator') return false;
          return true;
        });
        
        // Check if we're in measurement mode
        if (measurementModeRef.current && pickResult.hit && pickResult.pickedPoint) {
          // Snap to nearest vertex
          const snappedPoint = findNearestVertex(pickResult.pickedMesh, pickResult.pickedPoint);
          const point = {
            x: snappedPoint.x,
            y: snappedPoint.y,
            z: snappedPoint.z
          };
          
          // Find object ID if clicking on an object
          let meshId = pickResult.pickedMesh?.metadata?.id;
          if (!meshId && pickResult.pickedMesh) {
            let parent = pickResult.pickedMesh.parent;
            while (parent && !meshId) {
              meshId = parent.metadata?.id;
              parent = parent.parent;
            }
          }
          
          if (measurementStartRef.current) {
            // Second click - complete the measurement
            if (completeMeasurementRef.current) {
              completeMeasurementRef.current(point, meshId);
            }
            // Clean up preview
            if (measurementPreviewRef.current) {
              if (measurementPreviewRef.current.line) measurementPreviewRef.current.line.dispose();
              if (measurementPreviewRef.current.sphere) measurementPreviewRef.current.sphere.dispose();
              if (measurementPreviewRef.current.label) measurementPreviewRef.current.label.dispose();
              if (measurementPreviewRef.current.labelAnchor) measurementPreviewRef.current.labelAnchor.dispose();
              measurementPreviewRef.current = null;
            }
          } else {
            // First click - start the measurement
            if (startMeasurementRef.current) {
              startMeasurementRef.current(point, meshId);
            }
            
            // Create preview sphere at start point
            const startSphere = BABYLON.MeshBuilder.CreateSphere('measurePreview_start', { diameter: 0.08 }, scene);
            startSphere.position = snappedPoint;
            const sphereMat = new BABYLON.StandardMaterial('measurePreview_mat', scene);
            sphereMat.diffuseColor = new BABYLON.Color3(0, 1, 0);
            sphereMat.emissiveColor = new BABYLON.Color3(0, 0.5, 0);
            startSphere.material = sphereMat;
            startSphere.isPickable = false;
            
            measurementPreviewRef.current = {
              startPoint: snappedPoint.clone(),
              sphere: startSphere,
              line: null,
              label: null,
              labelAnchor: null
            };
          }
          return; // Don't select objects when measuring
        }
        
        if (pickResult.hit && pickResult.pickedMesh) {
          // Check if the picked mesh has an id, or traverse up to parent to find one
          let meshId = pickResult.pickedMesh.metadata?.id;
          let meshType = pickResult.pickedMesh.metadata?.type;
          
          // If no id on picked mesh, check parent hierarchy (for truss, imported 3D models, etc.)
          if (!meshId) {
            let parent = pickResult.pickedMesh.parent;
            while (parent && !meshId) {
              meshId = parent.metadata?.id;
              meshType = parent.metadata?.type;
              parent = parent.parent;
            }
          }
          
          if (meshId) {
            // Shift/Ctrl multi-select support in 3D viewport
            const evt = pointerInfo.event;
            if (evt && (evt.shiftKey || evt.ctrlKey || evt.metaKey)) {
              // Toggle multi-select with modifier key
              setMultiSelectIds(prev => 
                prev.includes(meshId) ? prev.filter(i => i !== meshId) : [...prev, meshId]
              );
            }
            // Always set as active object for properties panel
            setSelectedObjectId(meshId);
          }
        } else {
          // Clicked on empty space — do NOT clear selection (user must use Clear button)
        }
      }
      
      // Handle pointer move for measurement preview
      if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERMOVE) {
        // Show snap indicator when in measurement mode (even before first click)
        if (measurementModeRef.current && !measurementStartRef.current) {
          const pickResult = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
            if (!mesh.isPickable) return false;
            if (mesh.name.startsWith('gizmo')) return false;
            if (mesh.name === 'ground' || mesh.name === 'grid') return false;
            if (mesh.name.startsWith('beam_')) return false;
            if (mesh.name.startsWith('measure')) return false;
            if (mesh.name === 'snapIndicator') return false;
            return true;
          });
          if (pickResult.hit && pickResult.pickedPoint) {
            findNearestVertex(pickResult.pickedMesh, pickResult.pickedPoint);
          } else if (snapIndicatorRef.current) {
            snapIndicatorRef.current.isVisible = false;
          }
        }
        
        if (measurementModeRef.current && measurementStartRef.current && measurementPreviewRef.current) {
          const pickResult = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
            if (!mesh.isPickable) return false;
            if (mesh.name.startsWith('gizmo')) return false;
            if (mesh.name === 'ground' || mesh.name === 'grid') return false;
            if (mesh.name.startsWith('beam_')) return false;
            if (mesh.name.startsWith('measure')) return false;
            if (mesh.name === 'snapIndicator') return false;
            return true;
          });
          
          if (pickResult.hit && pickResult.pickedPoint) {
            // Snap to nearest vertex
            const snappedPoint = findNearestVertex(pickResult.pickedMesh, pickResult.pickedPoint);
            const startPt = measurementPreviewRef.current.startPoint;
            
            // Calculate distance
            const distance = BABYLON.Vector3.Distance(startPt, snappedPoint);
            
            // Update or create preview line
            if (measurementPreviewRef.current.line) {
              measurementPreviewRef.current.line.dispose();
            }
            
            const previewLine = BABYLON.MeshBuilder.CreateLines('measurePreview_line', {
              points: [startPt, snappedPoint]
            }, scene);
            previewLine.color = new BABYLON.Color3(0, 1, 0);
            previewLine.isPickable = false;
            measurementPreviewRef.current.line = previewLine;
            
            // Update or create preview label (text only, no background)
            const midPoint = startPt.add(snappedPoint).scale(0.5);
            
            if (!measurementPreviewRef.current.label && guiTextureRef.current) {
              const previewLabel = new GUI.TextBlock('measurePreview_label');
              previewLabel.text = formatWithUnit(distance, projectSettingsRef.current?.unit || 'm');
              previewLabel.color = '#00ff00';
              previewLabel.fontSize = 16;
              previewLabel.fontWeight = 'bold';
              previewLabel.outlineWidth = 2;
              previewLabel.outlineColor = 'black';
              guiTextureRef.current.addControl(previewLabel);
              
              const labelAnchor = new BABYLON.TransformNode('measurePreview_anchor', scene);
              labelAnchor.position = midPoint;
              previewLabel.linkWithMesh(labelAnchor);
              
              measurementPreviewRef.current.label = previewLabel;
              measurementPreviewRef.current.labelAnchor = labelAnchor;
            } else if (measurementPreviewRef.current.label) {
              measurementPreviewRef.current.label.text = formatWithUnit(distance, projectSettingsRef.current?.unit || 'm');
              if (measurementPreviewRef.current.labelAnchor) {
                measurementPreviewRef.current.labelAnchor.position = midPoint;
              }
            }
          }
        }
      }
    });

    // Store refs for edit mode (will be updated by useEffect)
    window._editModeRefs = {
      editMode: editModeRef,
      selectedVertices: selectedVerticesRef,
      setSelectedVertices: setSelectedVerticesRef,
      editingObjectId: editingObjectIdRef,
      multiSelectIds: multiSelectIdsRef
    };

    // ============================================
    // RENDER LOOP
    // ============================================
    engine.runRenderLoop(() => {
      scene.render();
    });

    // Handle resize
    const handleResize = () => {
      engine.resize();
    };
    window.addEventListener('resize', handleResize);
    
    // Log engine type
    logger.log(`✓ Scene initialized with ${getEngineType()} engine`);
    
    }; // End of initScene async function
    
    // Start the async initialization
    initScene().catch(logger.error);

    return () => {
      isCleanedUp = true;
      window.removeEventListener('resize', () => {});
      delete window._editModeRefs;
      
      // Dispose projection system
      if (projectionSystemRef.current) {
        projectionSystemRef.current.dispose();
        projectionSystemRef.current = null;
      }
      
      if (sceneRef.current) {
        sceneRef.current.dispose();
      }
      if (engineRef.current) {
        engineRef.current.dispose();
      }
    };
  }, [setSelectedObjectId]);

  // Keep edit mode refs in sync
  useEffect(() => {
    if (window._editModeRefs) {
      window._editModeRefs.editMode.current = editMode;
      window._editModeRefs.selectedVertices.current = selectedVertices;
      window._editModeRefs.setSelectedVertices.current = setSelectedVertices;
      window._editModeRefs.editingObjectId.current = editingObjectId;
      window._editModeRefs.multiSelectIds.current = multiSelectIds;
    }
  }, [editMode, selectedVertices, setSelectedVertices, editingObjectId, multiSelectIds]);

  // Expose focusOnObject function globally for Scene panel double-click.
  // Smooth-animated zoom that lands at a comfortable distance — not so close
  // that gizmo drags feel oversensitive (drag distance per pixel is a function
  // of camera radius; clamping radius keeps gizmo feel consistent with manual
  // wheel-zoom behavior).
  useEffect(() => {
    // Distance floor in meters — picked so that even tiny objects (projectors,
    // small props) keep the gizmo at a controllable on-screen sensitivity.
    // Wheel-zoom can still go closer (camera.lowerRadiusLimit governs that);
    // this only affects the auto-zoom landing distance.
    const FOCUS_MIN_RADIUS = 8;
    const FOCUS_RADIUS_MULTIPLIER = 5;
    const ANIM_FRAMES = 24; // ~400ms at 60fps

    const animateCameraTo = (camera, targetVec, radius) => {
      try {
        const scene = sceneRef.current;
        if (!scene) {
          camera.setTarget(targetVec);
          camera.radius = radius;
          return;
        }
        // Cancel any in-flight focus animations on this camera so successive
        // double-clicks don't fight each other.
        scene.stopAnimation(camera);

        const ease = new BABYLON.CubicEase();
        ease.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
        BABYLON.Animation.CreateAndStartAnimation(
          'focusRadius', camera, 'radius', 60, ANIM_FRAMES,
          camera.radius, radius, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT, ease
        );
        // Animate target via the camera's internal `_target` Vector3 (ArcRotate).
        const startTarget = camera.target ? camera.target.clone() : BABYLON.Vector3.Zero();
        BABYLON.Animation.CreateAndStartAnimation(
          'focusTarget', camera, 'target', 60, ANIM_FRAMES,
          startTarget, targetVec.clone(), BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT, ease
        );
      } catch (e) {
        // Fallback to instant snap if animation API throws
        camera.setTarget(targetVec);
        camera.radius = radius;
      }
    };

    window.focusOnObject = (objectId) => {
      if (!sceneRef.current || !cameraRef.current) return;
      const camera = cameraRef.current;
      const targetMesh = meshesRef.current[objectId] || projectorMeshesRef.current[objectId];
      if (!targetMesh) return;

      // Get bounding info (TransformNode → use first child)
      let boundingInfo;
      if (targetMesh.getBoundingInfo) {
        boundingInfo = targetMesh.getBoundingInfo();
      } else if (targetMesh.getChildMeshes) {
        const children = targetMesh.getChildMeshes();
        if (children.length > 0 && children[0].getBoundingInfo) {
          boundingInfo = children[0].getBoundingInfo();
        }
      }

      if (boundingInfo) {
        const center = boundingInfo.boundingBox.centerWorld;
        const radius = boundingInfo.boundingSphere.radiusWorld;
        // Comfortable distance — never closer than FOCUS_MIN_RADIUS.
        const newRadius = Math.max(radius * FOCUS_RADIUS_MULTIPLIER, FOCUS_MIN_RADIUS);
        animateCameraTo(camera, center, newRadius);
      } else {
        const pos = targetMesh.position || targetMesh.getAbsolutePosition?.();
        if (pos) animateCameraTo(camera, pos.clone ? pos.clone() : new BABYLON.Vector3(pos.x, pos.y, pos.z), Math.max(10, FOCUS_MIN_RADIUS));
      }
    };

    return () => {
      delete window.focusOnObject;
    };
  }, []);

  // ============ PROJECTOR POV (FLOATING PREVIEW WINDOWS) ============
  // Each open POV window has:
  //   • a dedicated FreeCamera positioned at the projector's lens origin with
  //     the projector's exact FOV / direction / lens shift
  //   • a RenderTargetTexture rendered by the main scene every frame
  //   • a per-frame updater that re-reads the projector data so the preview
  //     reflects live changes (move / rotate / lens / zoom)
  //
  // The React layer (ProjectorPOVWindow.jsx) exposes a draggable floating
  // window with a 2D canvas, and pulls pixel data from the RTT each animation
  // frame via window.readProjectorPOVPixels(id).
  //
  // Pure addition: does NOT touch projection / shadow / LED / truss /
  // scaffolding systems, NOR the main camera.
  useEffect(() => {
    const REGISTRY_KEY = '__projectorPOVRegistry';
    if (!window[REGISTRY_KEY]) window[REGISTRY_KEY] = new Map();
    const registry = window[REGISTRY_KEY];

    const computeProjectorFovY = (proj) => {
      const aspect = (ASPECT_RATIOS[proj.aspect_ratio || '16:9']?.value) || 16 / 9;
      const tr = proj.throw_ratio || 1.5;
      const zoom = (proj.zoom || 100) / 100;
      const eff = tr * zoom;
      return 2 * Math.atan(1 / (2 * eff * aspect));
    };

    const updateCameraFromProjector = (cam, proj) => {
      const origin = getProjectionOrigin(proj, meshesRef.current);
      const { direction, up } = getProjectorDirectionAndUp(proj);
      cam.position.copyFrom(origin);
      cam.upVector.copyFrom(up);
      cam.setTarget(origin.add(direction.scale(50)));
      cam.fov = computeProjectorFovY(proj);
    };

    // Performance caps: RTT max resolution (so fullscreen on 4K monitors
    // doesn't blow up readPixels cost) and a refresh-rate divider so the
    // POV preview only re-renders every Nth main-engine frame.
    const RTT_MAX_W = 1280;
    const RTT_MAX_H = 720;
    const POV_REFRESH_DIV = 2; // ~30fps when main engine runs at 60

    const clampRTTSize = (w, h) => {
      const maxW = RTT_MAX_W, maxH = RTT_MAX_H;
      let nw = Math.max(160, Math.round(w));
      let nh = Math.max(90, Math.round(h));
      if (nw > maxW) { nh = Math.round(nh * (maxW / nw)); nw = maxW; }
      if (nh > maxH) { nw = Math.round(nw * (maxH / nh)); nh = maxH; }
      return { w: nw, h: nh };
    };

    const openWindow = (projectorId, opts = {}) => {
      const scene = sceneRef.current;
      if (!scene) return null;
      // Toggle: if already open, close it
      if (registry.has(projectorId)) {
        closeWindow(projectorId);
        return null;
      }
      const proj = (projectorsRef.current || []).find(p => p.id === projectorId);
      if (!proj) {
        console.warn('[Projector POV] projector not found:', projectorId);
        return null;
      }

      const { w: width, h: height } = clampRTTSize(opts.width || 480, opts.height || 300);

      const cam = new BABYLON.FreeCamera(`povCam_${projectorId}`, new BABYLON.Vector3(0, 0, 0), scene);
      cam.minZ = 0.05;
      cam.maxZ = 5000;
      cam.inputs.clear(); // no user controls — strictly slaved to projector
      updateCameraFromProjector(cam, proj);

      const rtt = new BABYLON.RenderTargetTexture(
        `povRTT_${projectorId}`,
        { width, height },
        scene,
        false,    // no mipmaps
        true      // generate depth buffer
      );
      rtt.activeCamera = cam;
      rtt.renderList = null; // null = render all visible meshes (mirrors main view)
      // Refresh every Nth frame so POV runs at ~30fps not 60fps. Big perf win
      // when multiple windows are open.
      rtt.refreshRate = POV_REFRESH_DIV;
      // Match scene clear color so the preview blends naturally
      rtt.clearColor = scene.clearColor.clone();
      scene.customRenderTargets.push(rtt);

      // Per-frame updater — keeps POV in lock-step with projector edits.
      // Pause when entry.paused is true (used by visibility/minimize).
      const obs = scene.onBeforeRenderObservable.add(() => {
        const entryNow = registry.get(projectorId);
        if (entryNow?.paused) return;
        const liveProj = (projectorsRef.current || []).find(p => p.id === projectorId);
        if (liveProj) updateCameraFromProjector(cam, liveProj);
      });

      registry.set(projectorId, { cam, rtt, obs, width, height, projectorId });
      window.dispatchEvent(new CustomEvent('projector-pov-windows-changed'));
      return { width, height };
    };

    const closeWindow = (projectorId) => {
      const scene = sceneRef.current;
      const entry = registry.get(projectorId);
      if (!entry) return;
      try {
        if (scene) {
          const idx = scene.customRenderTargets.indexOf(entry.rtt);
          if (idx >= 0) scene.customRenderTargets.splice(idx, 1);
          if (entry.obs) scene.onBeforeRenderObservable.remove(entry.obs);
        }
        try { entry.rtt.dispose(); } catch (e) {}
        try { entry.cam.dispose(); } catch (e) {}
      } finally {
        registry.delete(projectorId);
        window.dispatchEvent(new CustomEvent('projector-pov-windows-changed'));
      }
    };

    const resizeWindow = (projectorId, width, height) => {
      const scene = sceneRef.current;
      const entry = registry.get(projectorId);
      if (!entry || !scene) return;
      const { w, h } = clampRTTSize(width, height);
      if (w === entry.width && h === entry.height) return;
      // Recreate RTT at new size (Babylon RTTs aren't resizable in place)
      const oldIdx = scene.customRenderTargets.indexOf(entry.rtt);
      if (oldIdx >= 0) scene.customRenderTargets.splice(oldIdx, 1);
      try { entry.rtt.dispose(); } catch (e) {}
      const rtt = new BABYLON.RenderTargetTexture(
        `povRTT_${projectorId}`,
        { width: w, height: h },
        scene, false, true
      );
      rtt.activeCamera = entry.cam;
      rtt.renderList = null;
      rtt.refreshRate = POV_REFRESH_DIV;
      rtt.clearColor = scene.clearColor.clone();
      scene.customRenderTargets.push(rtt);
      entry.rtt = rtt;
      entry.width = w;
      entry.height = h;
    };

    // Pause/resume per-window rendering (called when popup hidden / minimized).
    // When paused: the RTT is removed from customRenderTargets so the engine
    // skips rendering it entirely → zero cost.
    const setPaused = (projectorId, paused) => {
      const scene = sceneRef.current;
      const entry = registry.get(projectorId);
      if (!entry || !scene) return;
      if (paused === !!entry.paused) return;
      entry.paused = !!paused;
      const idx = scene.customRenderTargets.indexOf(entry.rtt);
      if (paused) {
        if (idx >= 0) scene.customRenderTargets.splice(idx, 1);
      } else {
        if (idx < 0) scene.customRenderTargets.push(entry.rtt);
      }
    };

    // React polls this each animation frame to draw the preview to its 2D
    // canvas. Returns { pixels, width, height } or null if window not open.
    const readPixels = async (projectorId) => {
      const entry = registry.get(projectorId);
      if (!entry) return null;
      try {
        const buf = new Uint8Array(entry.width * entry.height * 4);
        await entry.rtt.readPixels(0, 0, buf);
        return { pixels: buf, width: entry.width, height: entry.height };
      } catch (e) { return null; }
    };

    const listOpenWindows = () => {
      const out = [];
      registry.forEach((entry, id) => out.push({ id, width: entry.width, height: entry.height }));
      return out;
    };

    window.openProjectorPOVWindow = openWindow;
    window.closeProjectorPOVWindow = closeWindow;
    window.resizeProjectorPOVWindow = resizeWindow;
    window.pauseProjectorPOVWindow = setPaused;
    window.readProjectorPOVPixels = readPixels;
    window.listProjectorPOVWindows = listOpenWindows;

    return () => {
      // Close all windows on unmount
      const ids = Array.from(registry.keys());
      ids.forEach(id => closeWindow(id));
      delete window.openProjectorPOVWindow;
      delete window.closeProjectorPOVWindow;
      delete window.resizeProjectorPOVWindow;
      delete window.pauseProjectorPOVWindow;
      delete window.readProjectorPOVPixels;
      delete window.listProjectorPOVWindows;
    };
  }, []);

  // ============ VERTEX EDIT MODE (Lightweight - List Based) ============
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;
    
    // Clean up any existing vertex meshes
    vertexMeshesRef.current.forEach(mesh => {
      if (mesh) mesh.dispose();
    });
    vertexMeshesRef.current = [];
    
    // Clean up old materials
    const oldMat = scene.getMaterialByName('selectedVertexMat');
    const oldVertMat = scene.getMaterialByName('vertexMat');
    if (oldMat) oldMat.dispose();
    if (oldVertMat) oldVertMat.dispose();
    
    if (!editMode || !editingObjectId) {
      delete window.getEditModeVertexData;
      delete window.setEditModeViewMode;
      delete window.deleteVerticesFromMesh;
      return;
    }
    
    // Find the mesh to edit
    let targetMesh = meshesRef.current[editingObjectId];
    
    if (!targetMesh) {
      const allMeshes = scene.meshes;
      targetMesh = allMeshes.find(m => m.metadata?.id === editingObjectId);
    }
    
    if (!targetMesh) return;
    
    // Get the mesh with vertex data
    let meshWithVertices = targetMesh;
    if (!targetMesh.getVerticesData || !targetMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind)) {
      if (targetMesh.getChildMeshes) {
        const children = targetMesh.getChildMeshes();
        for (const child of children) {
          if (child.getVerticesData && child.getVerticesData(BABYLON.VertexBuffer.PositionKind)) {
            meshWithVertices = child;
            break;
          }
        }
      }
    }
    
    // Get vertex data for the list panel
    const positions = meshWithVertices.getVerticesData?.(BABYLON.VertexBuffer.PositionKind);
    if (!positions) return;
    
    const worldMatrix = meshWithVertices.getWorldMatrix();
    
    // EDIT MODE: Hide ALL projector beams and lights
    scene.meshes.forEach(mesh => {
      if (mesh.name.startsWith('beam_') || 
          mesh.name.startsWith('projectorBeam_') || 
          mesh.name.startsWith('projector_light_') ||
          mesh.name.includes('spotlight') ||
          mesh.name.includes('Spotlight')) {
        mesh.isVisible = false;
        mesh._wasHiddenByEditMode = true;
      }
    });
    
    // Hide all spot lights
    scene.lights.forEach(light => {
      if (light.name.includes('projector') || light.name.includes('spot')) {
        light.setEnabled(false);
        light._wasDisabledByEditMode = true;
      }
    });
    
    // EDIT MODE: Hide ALL other objects (only show editing object)
    let hiddenCount = 0;
    scene.meshes.forEach(mesh => {
      if (mesh.name === 'ground' || mesh.name === 'grid') return;
      if (mesh.name.startsWith('vertex_')) return;
      if (mesh.metadata?.id === editingObjectId) return;
      // Check if mesh is child of editing object
      let parent = mesh.parent;
      while (parent) {
        if (parent.metadata?.id === editingObjectId) return;
        parent = parent.parent;
      }
      
      // Hide other objects and make them unpickable
      if (mesh.isVisible !== false) {
        mesh._wasVisibleBeforeEditMode = mesh.isVisible;
        mesh._wasPickableBeforeEditMode = mesh.isPickable;
        mesh.isVisible = false;
        mesh.isPickable = false;
        hiddenCount++;
      }
    });
    
    // Store original material
    if (meshWithVertices.material) {
      meshWithVertices._originalMaterial = meshWithVertices.material;
    }
    
    // Create wireframe material
    const wireframeMaterial = new BABYLON.StandardMaterial('wireframeMat', scene);
    wireframeMaterial.wireframe = true;
    wireframeMaterial.emissiveColor = new BABYLON.Color3(0.5, 0.5, 0.5);
    wireframeMaterial.disableLighting = true;
    
    // Create solid material - Gray like Blender with edge visibility
    const solidMaterial = new BABYLON.StandardMaterial('solidEditMat', scene);
    solidMaterial.diffuseColor = new BABYLON.Color3(0.6, 0.6, 0.6); // Light gray
    solidMaterial.emissiveColor = new BABYLON.Color3(0.15, 0.15, 0.15);
    solidMaterial.specularColor = new BABYLON.Color3(0.2, 0.2, 0.2);
    solidMaterial.backFaceCulling = true;
    
    // Apply based on current view mode
    const viewMode = editModeViewModeRef.current || 'wireframe';
    meshWithVertices.material = viewMode === 'wireframe' ? wireframeMaterial : solidMaterial;
    
    // Ensure mesh is pickable during edit mode (even in wireframe mode)
    meshWithVertices.isPickable = true;
    
    // Create an invisible picking mesh for wireframe mode
    // This allows clicking on the mesh even when it's in wireframe mode
    let pickingMesh = null;
    if (viewMode === 'wireframe') {
      pickingMesh = meshWithVertices.clone('pickingMesh_' + editingObjectId);
      // Use visibility = 0 instead of isVisible = false to keep it pickable
      pickingMesh.visibility = 0; // Invisible but still pickable
      pickingMesh.isPickable = true;
      pickingMesh.metadata = { ...meshWithVertices.metadata, isPickingMesh: true };
      window._editModePickingMesh = pickingMesh;
    }
    
    // Create materials - simple dots
    const vertexMaterial = new BABYLON.StandardMaterial('vertexMat', scene);
    vertexMaterial.diffuseColor = new BABYLON.Color3(0.3, 0.3, 0.3); // Medium gray - visible
    vertexMaterial.emissiveColor = new BABYLON.Color3(0.4, 0.4, 0.4);
    vertexMaterial.specularColor = new BABYLON.Color3(0, 0, 0);
    vertexMaterial.disableLighting = true;
    
    // BLACK vertex material for solid mode (like Blender)
    const blackVertexMaterial = new BABYLON.StandardMaterial('blackVertexMat', scene);
    blackVertexMaterial.diffuseColor = new BABYLON.Color3(0, 0, 0);
    blackVertexMaterial.emissiveColor = new BABYLON.Color3(0.1, 0.1, 0.1);
    blackVertexMaterial.specularColor = new BABYLON.Color3(0, 0, 0);
    blackVertexMaterial.disableLighting = true;
    
    // YELLOW for selected vertices - NO PINK GLOW
    const selectedMaterial = new BABYLON.StandardMaterial('selectedVertexMat', scene);
    selectedMaterial.diffuseColor = new BABYLON.Color3(1, 0.8, 0); // Orange-Yellow for selected
    selectedMaterial.emissiveColor = new BABYLON.Color3(1, 0.8, 0);
    selectedMaterial.specularColor = new BABYLON.Color3(0, 0, 0);
    selectedMaterial.disableLighting = true;
    
    // Store wireframe overlay mesh for solid mode
    let wireframeOverlay = null;
    
    // Function to toggle view mode - AFTER materials are created
    window.setEditModeViewMode = (mode) => {
      editModeViewModeRef.current = mode;
      if (meshWithVertices) {
        if (mode === 'wireframe') {
          // Wireframe mode
          meshWithVertices.material = wireframeMaterial;
          // Hide wireframe overlay
          if (wireframeOverlay) {
            wireframeOverlay.isVisible = false;
          }
        } else {
          // Solid mode
          meshWithVertices.material = solidMaterial;
          // Create or show wireframe overlay for edges
          if (!wireframeOverlay) {
            wireframeOverlay = meshWithVertices.clone('wireframeOverlay');
            const overlayMat = new BABYLON.StandardMaterial('overlayWireframeMat', scene);
            overlayMat.wireframe = true;
            overlayMat.emissiveColor = new BABYLON.Color3(0.15, 0.15, 0.15); // Dark edges
            overlayMat.disableLighting = true;
            wireframeOverlay.material = overlayMat;
            wireframeOverlay.renderingGroupId = 2;
            // Store reference for cleanup
            window._editModeWireframeOverlay = wireframeOverlay;
          }
          wireframeOverlay.isVisible = true;
        }
      }
      
      // Update vertex colors based on mode
      vertexMeshesRef.current.forEach(sphere => {
        if (sphere.metadata && sphere.metadata.type === 'vertex') {
          const isSelected = selectedVertices && selectedVertices.includes(sphere.metadata.vertexIndex);
          if (!isSelected) {
            sphere.material = mode === 'solid' ? blackVertexMaterial : vertexMaterial;
          }
        }
      });
    };
    
    // Apply initial wireframe mode
    meshWithVertices.material = wireframeMaterial;
    
    // DISABLE HIGHLIGHT LAYER during edit mode to remove pink glow
    if (highlightLayerRef.current) {
      highlightLayerRef.current.removeAllMeshes();
    }
    
    // Build unique vertex map
    const uniqueVertices = new Map();
    for (let i = 0; i < positions.length / 3; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      const key = `${x.toFixed(3)}_${y.toFixed(3)}_${z.toFixed(3)}`;
      
      if (!uniqueVertices.has(key)) {
        const localPos = new BABYLON.Vector3(x, y, z);
        const worldPos = BABYLON.Vector3.TransformCoordinates(localPos, worldMatrix);
        uniqueVertices.set(key, { index: i, worldPos, localPos });
      }
    }
    
    // Create small spheres at ALL vertices - MAKE THEM PICKABLE
    uniqueVertices.forEach(({ index, worldPos }) => {
      const isSelected = selectedVertices && selectedVertices.includes(index);
      const sphere = BABYLON.MeshBuilder.CreateSphere(`vertex_${index}`, { diameter: 0.08 }, scene);
      sphere.position = worldPos;
      sphere.material = isSelected ? selectedMaterial : vertexMaterial;
      sphere.isPickable = true; // PICKABLE for 3D selection
      sphere.metadata = { type: 'vertex', vertexIndex: index };
      sphere.renderingGroupId = 3; // Render on top
      vertexMeshesRef.current.push(sphere);
    });
    
    // Store reference to restore later
    window._editModeOriginalMaterial = meshWithVertices._originalMaterial;
    window._editModeMesh = meshWithVertices;
    window._editModeUniqueVertices = uniqueVertices;
    window._editModeWireframeOverlay = wireframeOverlay;
    window._vertexMaterial = vertexMaterial;
    window._blackVertexMaterial = blackVertexMaterial;
    window._selectedMaterial = selectedMaterial;
    
    // Expose vertex data for PropertiesPanel
    window.getEditModeVertexData = () => ({
      positions: [...positions],
      vertexCount: positions.length / 3,
      mesh: meshWithVertices,
      objectId: editingObjectId
    });
    
    // Delete vertices function - returns data for undo
    window.deleteVerticesFromMesh = (indicesToDelete) => {
      if (!meshWithVertices || indicesToDelete.length === 0) return null;
      
      // Save current state BEFORE deletion for undo
      const beforePositions = [...meshWithVertices.getVerticesData(BABYLON.VertexBuffer.PositionKind)];
      const beforeIndices = [...meshWithVertices.getIndices()];
      const beforeNormals = meshWithVertices.getVerticesData(BABYLON.VertexBuffer.NormalKind);
      
      const currentPositions = meshWithVertices.getVerticesData(BABYLON.VertexBuffer.PositionKind);
      const indices = meshWithVertices.getIndices();
      
      if (!currentPositions || !indices) return null;
      
      // Build set of vertices to delete
      const deleteSet = new Set(indicesToDelete);
      
      // Create new positions array without deleted vertices
      const newPositions = [];
      const newNormals = [];
      const vertexMap = new Map(); // oldIndex -> newIndex
      let newIndex = 0;
      
      for (let i = 0; i < currentPositions.length / 3; i++) {
        if (!deleteSet.has(i)) {
          newPositions.push(
            currentPositions[i * 3],
            currentPositions[i * 3 + 1],
            currentPositions[i * 3 + 2]
          );
          if (beforeNormals) {
            newNormals.push(
              beforeNormals[i * 3],
              beforeNormals[i * 3 + 1],
              beforeNormals[i * 3 + 2]
            );
          }
          vertexMap.set(i, newIndex);
          newIndex++;
        }
      }
      
      // Update indices to reference new vertex positions
      const newIndices = [];
      for (let i = 0; i < indices.length; i += 3) {
        const i0 = indices[i];
        const i1 = indices[i + 1];
        const i2 = indices[i + 2];
        
        // Only keep faces where all 3 vertices still exist
        if (vertexMap.has(i0) && vertexMap.has(i1) && vertexMap.has(i2)) {
          newIndices.push(vertexMap.get(i0), vertexMap.get(i1), vertexMap.get(i2));
        }
      }
      
      // Update mesh with new data
      meshWithVertices.updateVerticesData(BABYLON.VertexBuffer.PositionKind, new Float32Array(newPositions));
      if (newNormals.length > 0) {
        meshWithVertices.updateVerticesData(BABYLON.VertexBuffer.NormalKind, new Float32Array(newNormals));
      }
      meshWithVertices.setIndices(newIndices);
      meshWithVertices.refreshBoundingInfo();
      
      logger.log(`Deleted ${indicesToDelete.length} vertices. Remaining: ${newPositions.length / 3}`);
      
      // Return data needed for undo
      return {
        beforePositions,
        beforeIndices,
        beforeNormals: beforeNormals ? [...beforeNormals] : null,
        deletedIndices: indicesToDelete
      };
    };
    
    // Restore mesh to a specific state (for undo delete)
    window.restoreMeshToState = (savedState) => {
      if (!meshWithVertices || !savedState) return;
      
      meshWithVertices.updateVerticesData(
        BABYLON.VertexBuffer.PositionKind, 
        new Float32Array(savedState.beforePositions)
      );
      if (savedState.beforeNormals) {
        meshWithVertices.updateVerticesData(
          BABYLON.VertexBuffer.NormalKind, 
          new Float32Array(savedState.beforeNormals)
        );
      }
      meshWithVertices.setIndices(savedState.beforeIndices);
      meshWithVertices.refreshBoundingInfo();
      logger.log('Mesh restored to previous state');
    };
    
    // Update hidden vertices visualization
    window.updateHiddenVertices = (hiddenIndices) => {
      if (!sceneRef.current) return;
      const hiddenSet = new Set(hiddenIndices);
      
      // Update vertex spheres visibility
      vertexMeshesRef.current.forEach(sphere => {
        if (sphere.metadata && sphere.metadata.type === 'vertex') {
          sphere.isVisible = !hiddenSet.has(sphere.metadata.vertexIndex);
        }
      });
    };
    
    // Update selected vertices visualization
    window.updateSelectedVertices = (selectedIndices) => {
      if (!sceneRef.current) return;
      const selectedSet = new Set(selectedIndices);
      const selectedMat = window._selectedMaterial;
      const viewMode = editModeViewModeRef.current || 'wireframe';
      const normalMat = viewMode === 'solid' ? window._blackVertexMaterial : window._vertexMaterial;
      
      vertexMeshesRef.current.forEach(sphere => {
        if (sphere.metadata && sphere.metadata.type === 'vertex') {
          const isSelected = selectedSet.has(sphere.metadata.vertexIndex);
          sphere.material = isSelected ? selectedMat : normalMat;
        }
      });
    };
    
    // Store original mesh data for cancel
    window._originalMeshPositions = [...meshWithVertices.getVerticesData(BABYLON.VertexBuffer.PositionKind)];
    window._originalMeshIndices = [...meshWithVertices.getIndices()];
    const originalNormals = meshWithVertices.getVerticesData(BABYLON.VertexBuffer.NormalKind);
    window._originalMeshNormals = originalNormals ? [...originalNormals] : null;
    
    // Restore original mesh data (for Cancel)
    window.restoreOriginalMesh = () => {
      if (!meshWithVertices || !window._originalMeshPositions || !window._originalMeshIndices) return;
      
      meshWithVertices.updateVerticesData(
        BABYLON.VertexBuffer.PositionKind, 
        new Float32Array(window._originalMeshPositions)
      );
      if (window._originalMeshNormals) {
        meshWithVertices.updateVerticesData(
          BABYLON.VertexBuffer.NormalKind, 
          new Float32Array(window._originalMeshNormals)
        );
      }
      meshWithVertices.setIndices(window._originalMeshIndices);
      meshWithVertices.refreshBoundingInfo();
      
      // Also restore component visibility
      if (window.restoreComponentVisibility) {
        window.restoreComponentVisibility();
      }
      
      logger.log('Restored original mesh data and component visibility');
    };
    
    // Export edited mesh data for saving
    window.exportEditedMesh = () => {
      if (!meshWithVertices) return null;
      
      // Use string constants for consistency (same as exitEditMode)
      const positions = meshWithVertices.getVerticesData('position');
      const indices = meshWithVertices.getIndices();
      const normals = meshWithVertices.getVerticesData('normal');
      const uvs = meshWithVertices.getVerticesData('uv');
      
      return {
        positions: positions ? Array.from(positions) : [],
        indices: indices ? Array.from(indices) : [],
        normals: normals ? Array.from(normals) : [],
        uvs: uvs ? Array.from(uvs) : [],
        name: meshWithVertices.name
      };
    };
    
    // Track deleted and hidden components for persistence
    // Store both ID and name since IDs change on reload but names are preserved
    const deletedComponentNames = [];
    const hiddenComponentNames = [];
    
    // IMPORTANT: Initialize with previously saved hidden components
    // This prevents losing hidden state when re-entering edit mode
    const editingSceneObject = sceneObjects?.find(o => o.id === editingObjectId);
    if (editingSceneObject?.hiddenComponents) {
      hiddenComponentNames.push(...editingSceneObject.hiddenComponents);
      logger.log('Initialized hiddenComponentNames from saved state:', hiddenComponentNames);
    }
    if (editingSceneObject?.deletedComponents) {
      deletedComponentNames.push(...editingSceneObject.deletedComponents);
      logger.log('Initialized deletedComponentNames from saved state:', deletedComponentNames);
    }
    
    // Store original component visibility state for Cancel
    const originalComponentVisibility = new Map();
    
    // Initialize original visibility state from all child meshes
    const parentMeshForVisibility = meshesRef.current[editingObjectId];
    if (parentMeshForVisibility && parentMeshForVisibility.getChildMeshes) {
      const allMeshes = parentMeshForVisibility.getDescendants(false, (node) => node instanceof BABYLON.Mesh);
      allMeshes.forEach(mesh => {
        originalComponentVisibility.set(mesh.id, mesh.isVisible);
      });
      logger.log('Stored original visibility for', originalComponentVisibility.size, 'components');
    }
    
    // Restore original component visibility (for Cancel)
    window.restoreComponentVisibility = () => {
      const scene = sceneRef.current;
      if (!scene) return;
      
      originalComponentVisibility.forEach((wasVisible, meshId) => {
        const mesh = scene.getMeshById(meshId);
        if (mesh) {
          mesh.isVisible = wasVisible;
        }
      });
      // Clear the tracking arrays
      deletedComponentNames.length = 0;
      hiddenComponentNames.length = 0;
      logger.log('Restored original component visibility');
    };
    
    // Get component state for saving
    window.getComponentState = () => {
      return {
        deletedComponents: [...deletedComponentNames],
        hiddenComponents: [...hiddenComponentNames]
      };
    };
    
    // Get mesh components (sub-meshes, child meshes, groups)
    window.getMeshComponents = () => {
      const components = [];
      const scene = sceneRef.current;
      if (!scene) return components;
      
      // Find the parent mesh being edited
      const parentMesh = meshesRef.current[editingObjectId];
      if (!parentMesh) return components;
      
      // Get all child meshes and sub-meshes
      const addChildComponents = (mesh, depth = 0) => {
        const children = mesh.getChildMeshes ? mesh.getChildMeshes(false) : [];
        children.forEach((child, idx) => {
          if (child instanceof BABYLON.Mesh && child.getTotalVertices && child.getTotalVertices() > 0) {
            components.push({
              id: child.id || `${mesh.id}_child_${idx}`,
              name: child.name || `Mesh ${idx + 1}`,
              vertexCount: child.getTotalVertices(),
              type: 'mesh',
              depth,
              mesh: child,
              isVisible: child.isVisible !== false // Track visibility state
            });
          }
          // Recursively get children
          addChildComponents(child, depth + 1);
        });
      };
      
      // If the parent has geometry, add it first
      if (parentMesh instanceof BABYLON.Mesh && parentMesh.getTotalVertices && parentMesh.getTotalVertices() > 0) {
        components.push({
          id: parentMesh.id || 'root',
          name: parentMesh.name || 'Root Mesh',
          vertexCount: parentMesh.getTotalVertices(),
          type: 'root',
          depth: 0,
          mesh: parentMesh,
          isVisible: parentMesh.isVisible !== false
        });
      }
      
      // Add all children
      addChildComponents(parentMesh);
      
      // Also check if this is a loaded mesh with multiple parts stored in the parent group
      if (parentMesh.getChildMeshes) {
        const allMeshes = parentMesh.getDescendants(false, (node) => node instanceof BABYLON.Mesh);
        allMeshes.forEach((mesh, idx) => {
          // Check if already added
          if (!components.find(c => c.id === mesh.id)) {
            if (mesh.getTotalVertices && mesh.getTotalVertices() > 0) {
              components.push({
                id: mesh.id || `desc_${idx}`,
                name: mesh.name || `Part ${idx + 1}`,
                vertexCount: mesh.getTotalVertices(),
                type: 'part',
                depth: 1,
                mesh,
                isVisible: mesh.isVisible !== false
              });
            }
          }
        });
      }
      
      return components;
    };
    
    // Hide a component
    window.hideComponent = (componentId) => {
      const scene = sceneRef.current;
      if (!scene) return;
      
      scene.meshes.forEach(mesh => {
        if (mesh.id === componentId) {
          mesh.isVisible = false;
          // Track by NAME (not ID) since IDs change on reload
          const meshName = mesh.name;
          if (meshName && !hiddenComponentNames.includes(meshName)) {
            hiddenComponentNames.push(meshName);
            logger.log('Tracked hidden component:', meshName);
          }
        }
      });
    };
    
    // Show a component
    window.showComponent = (componentId) => {
      const scene = sceneRef.current;
      if (!scene) return;
      
      scene.meshes.forEach(mesh => {
        if (mesh.id === componentId) {
          mesh.isVisible = true;
          // Remove from hidden list by name
          const meshName = mesh.name;
          const idx = hiddenComponentNames.indexOf(meshName);
          if (idx > -1) {
            hiddenComponentNames.splice(idx, 1);
            logger.log('Unhidden component:', meshName);
          }
        }
      });
    };
    
    // Delete a component
    window.deleteComponent = (componentId) => {
      const scene = sceneRef.current;
      if (!scene) return;
      
      scene.meshes.forEach(mesh => {
        if (mesh.id === componentId) {
          // Track by NAME (not ID) since IDs change on reload
          const meshName = mesh.name;
          if (meshName && !deletedComponentNames.includes(meshName)) {
            deletedComponentNames.push(meshName);
            logger.log('Tracked deleted component:', meshName);
          }
          mesh.dispose();
        }
      });
    };
    
    // Restore deleted components (remove from deleted list so they appear on next reload)
    window.restoreDeletedComponents = (componentIds) => {
      // Since meshes are disposed, we can only remove them from the deleted list
      // They will reappear when the model is reloaded (on next edit mode entry)
      componentIds.forEach(componentId => {
        // Find and remove the component name from deleted list
        // Component might have been stored by ID or name
        const idx = deletedComponentNames.indexOf(componentId);
        if (idx > -1) {
          deletedComponentNames.splice(idx, 1);
          logger.log('Restored deleted component from list:', componentId);
        }
      });
    };
    
    // Highlight selected components with a colored outline/material
    window.highlightComponents = (componentIds) => {
      const scene = sceneRef.current;
      if (!scene) return;
      
      // Create highlight material if not exists
      if (!scene._componentHighlightMat) {
        scene._componentHighlightMat = new BABYLON.StandardMaterial('componentHighlight', scene);
        scene._componentHighlightMat.diffuseColor = new BABYLON.Color3(0, 1, 1); // Cyan
        scene._componentHighlightMat.emissiveColor = new BABYLON.Color3(0, 0.5, 0.5);
        scene._componentHighlightMat.alpha = 0.8;
        scene._componentHighlightMat.backFaceCulling = false;
      }
      
      // Get the mesh being edited
      const parentMesh = meshesRef.current[editingObjectId];
      if (!parentMesh) return;
      
      // Get all descendant meshes
      const allMeshes = parentMesh.getDescendants ? 
        parentMesh.getDescendants(false, (node) => node instanceof BABYLON.Mesh) : 
        [];
      if (parentMesh instanceof BABYLON.Mesh) {
        allMeshes.unshift(parentMesh);
      }
      
      // Reset all materials and apply highlight to selected
      allMeshes.forEach(mesh => {
        if (componentIds.includes(mesh.id)) {
          // Store original material if not stored
          if (!mesh._originalMaterial) {
            mesh._originalMaterial = mesh.material;
          }
          // Apply highlight
          mesh.material = scene._componentHighlightMat;
          mesh.renderOutline = true;
          mesh.outlineColor = new BABYLON.Color3(0, 1, 1);
          mesh.outlineWidth = 0.02;
        } else {
          // Restore original material
          if (mesh._originalMaterial) {
            mesh.material = mesh._originalMaterial;
          }
          mesh.renderOutline = false;
        }
      });
    };
    
    // Clear component highlights
    window.clearComponentHighlights = () => {
      const scene = sceneRef.current;
      if (!scene) return;
      
      const parentMesh = meshesRef.current[editingObjectId];
      if (!parentMesh) return;
      
      const allMeshes = parentMesh.getDescendants ? 
        parentMesh.getDescendants(false, (node) => node instanceof BABYLON.Mesh) : 
        [];
      if (parentMesh instanceof BABYLON.Mesh) {
        allMeshes.unshift(parentMesh);
      }
      
      allMeshes.forEach(mesh => {
        if (mesh._originalMaterial) {
          mesh.material = mesh._originalMaterial;
        }
        mesh.renderOutline = false;
      });
    };
    
    return () => {
      // Restore all hidden meshes
      if (sceneRef.current) {
        sceneRef.current.meshes.forEach(mesh => {
          if (mesh._wasHiddenByEditMode) {
            mesh.isVisible = true;
            delete mesh._wasHiddenByEditMode;
          }
          if (mesh._wasVisibleBeforeEditMode !== undefined) {
            mesh.isVisible = mesh._wasVisibleBeforeEditMode;
            delete mesh._wasVisibleBeforeEditMode;
          }
          if (mesh._wasPickableBeforeEditMode !== undefined) {
            mesh.isPickable = mesh._wasPickableBeforeEditMode;
            delete mesh._wasPickableBeforeEditMode;
          }
        });
        sceneRef.current.lights.forEach(light => {
          if (light._wasDisabledByEditMode) {
            light.setEnabled(true);
            delete light._wasDisabledByEditMode;
          }
        });
      }
      // Restore original material
      if (window._editModeMesh && window._editModeOriginalMaterial) {
        window._editModeMesh.material = window._editModeOriginalMaterial;
      }
      // Dispose wireframe overlay
      if (window._editModeWireframeOverlay) {
        window._editModeWireframeOverlay.dispose();
      }
      // Dispose picking mesh
      if (window._editModePickingMesh) {
        window._editModePickingMesh.dispose();
        delete window._editModePickingMesh;
      }
      // Dispose materials
      const wireMat = scene?.getMaterialByName('wireframeMat');
      const solidMat = scene?.getMaterialByName('solidEditMat');
      const blackVertMat = scene?.getMaterialByName('blackVertexMat');
      if (wireMat) wireMat.dispose();
      if (solidMat) solidMat.dispose();
      if (blackVertMat) blackVertMat.dispose();
      
      delete window.getEditModeVertexData;
      delete window.setEditModeViewMode;
      delete window.deleteVerticesFromMesh;
      delete window.restoreMeshToState;
      delete window.updateHiddenVertices;
      delete window.updateSelectedVertices;
      delete window.restoreOriginalMesh;
      delete window.exportEditedMesh;
      delete window.getMeshComponents;
      delete window.hideComponent;
      delete window.showComponent;
      delete window.deleteComponent;
      delete window.restoreDeletedComponents;
      delete window.getComponentState;
      delete window.highlightComponents;
      delete window.restoreComponentVisibility;
      delete window._editModeOriginalMaterial;
      delete window._editModeMesh;
      delete window._editModeUniqueVertices;
      delete window._editModeWireframeOverlay;
      delete window._vertexMaterial;
      delete window._blackVertexMaterial;
      delete window._selectedMaterial;
      delete window._originalMeshPositions;
      delete window._originalMeshIndices;
      delete window._originalMeshNormals;
    };
  }, [editMode, editingObjectId, selectedVertices]);

  // Update world settings
  useEffect(() => {
    if (!sceneRef.current) return;
    const bgColor = worldSettings?.backgroundColor || '#09090b';
    const r = parseInt(bgColor.slice(1, 3), 16) / 255;
    const g = parseInt(bgColor.slice(3, 5), 16) / 255;
    const b = parseInt(bgColor.slice(5, 7), 16) / 255;
    sceneRef.current.clearColor = new BABYLON.Color4(r, g, b, 1);
  }, [worldSettings?.backgroundColor]);

  // ── Center axis visibility toggle (driven by 'avst-center-axis-toggle'
  // window event from PropertiesPanel; persisted via localStorage). Keeps
  // the axes pinned at world origin and just enables/disables the nodes. ──
  useEffect(() => {
    const applyAxisVisibility = () => {
      const axes = axesViewerRef.current;
      if (!axes) return;
      const saved = localStorage.getItem('avst_showCenterAxis');
      const visible = saved === null ? true : saved !== 'false';
      [axes.xAxis, axes.yAxis, axes.zAxis].forEach(ax => {
        if (!ax) return;
        ax.position = BABYLON.Vector3.Zero();
        ax.setEnabled(visible);
      });
    };
    window.addEventListener('avst-center-axis-toggle', applyAxisVisibility);
    return () => window.removeEventListener('avst-center-axis-toggle', applyAxisVisibility);
  }, []);

  // Update grid color, size, and visibility
  useEffect(() => {
    if (!gridMeshRef.current || !sceneRef.current) return;
    
    const scene = sceneRef.current;
    const showGrid = worldSettings?.showGrid !== false;
    
    // Update grid visibility
    gridMeshRef.current.isVisible = showGrid;
    if (groundRef.current) {
      groundRef.current.isVisible = true; // Keep ground for shadows but invisible (alpha=0)
    }
    
    // Update grid color
    const gridColor = worldSettings?.gridLineColor || '#4d4d4d';
    const gr = parseInt(gridColor.slice(1, 3), 16) / 255;
    const gg = parseInt(gridColor.slice(3, 5), 16) / 255;
    const gb = parseInt(gridColor.slice(5, 7), 16) / 255;
    gridMeshRef.current.color = new BABYLON.Color3(gr, gg, gb);
    
    // Rebuild grid if size changed (only when necessary)
    const gridSize = worldSettings?.gridSize || 20;
    const gridStep = worldSettings?.gridStep || 1;
    
    // Rebuild grid if size, step, OR color changed. (LinesMesh's `.color`
    // setter is supposed to update the shader uniform live, but in practice
    // it sometimes doesn't propagate when set outside of an active render
    // tick — which manifests as "Grid Line Color is not working". Forcing a
    // rebuild on color change is cheap and 100 % reliable.)
    if (
      gridMeshRef.current._gridSize !== gridSize ||
      gridMeshRef.current._gridStep !== gridStep ||
      gridMeshRef.current._gridColorHex !== gridColor
    ) {
      gridMeshRef.current.dispose();
      
      const gridLines = [];
      for (let i = -gridSize/2; i <= gridSize/2; i += gridStep) {
        gridLines.push([new BABYLON.Vector3(i, 0.01, -gridSize/2), new BABYLON.Vector3(i, 0.01, gridSize/2)]);
        gridLines.push([new BABYLON.Vector3(-gridSize/2, 0.01, i), new BABYLON.Vector3(gridSize/2, 0.01, i)]);
      }
      const newGridMesh = BABYLON.MeshBuilder.CreateLineSystem('grid', { lines: gridLines }, scene);
      newGridMesh.color = new BABYLON.Color3(gr, gg, gb);
      newGridMesh.isPickable = false;
      newGridMesh.isVisible = showGrid;
      newGridMesh._gridSize = gridSize;
      newGridMesh._gridStep = gridStep;
      newGridMesh._gridColorHex = gridColor;
      gridMeshRef.current = newGridMesh;
      
      // Update ground size too
      if (groundRef.current) {
        groundRef.current.dispose();
        const ground = BABYLON.MeshBuilder.CreateGround('ground', { width: gridSize * 2, height: gridSize * 2 }, scene);
        const groundMat = new BABYLON.StandardMaterial('groundMat', scene);
        groundMat.alpha = 0;
        groundMat.specularColor = new BABYLON.Color3(0, 0, 0);
        configureMaterialForProjectors(groundMat); // Support multiple projectors
        ground.material = groundMat;
        ground.receiveShadows = true;
        ground.isPickable = false;
        groundRef.current = ground;
      }
    }
  }, [worldSettings?.gridLineColor, worldSettings?.showGrid, worldSettings?.gridSize, worldSettings?.gridStep]);

  // Create projection texture - rectangle must match spotlight cone geometry exactly
  const createProjectionTexture = useCallback((projector) => {
    if (!sceneRef.current) return null;
    
    const ar = ASPECT_RATIOS[projector.aspect_ratio] || ASPECT_RATIOS['16:10'];
    
    // Use larger texture for better quality
    const size = 1024;
    
    const texture = new BABYLON.DynamicTexture('projTexture_' + projector.id, { width: size, height: size }, sceneRef.current, true);
    const ctx = texture.getContext();
    
    // Get the projector's Z rotation (roll) for rotating the projection content
    const rotZ = BABYLON.Tools.ToRadians(projector.rotation?.z || 0);
    
    // Fill ENTIRE texture with pure black - this masks the circular spotlight
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, size, size);
    
    // Apply rotation transform around center of texture
    // This rotates the entire projection content with the projector body
    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.rotate(-rotZ); // Negative because canvas Y is inverted
    ctx.translate(-size / 2, -size / 2);
    
    // The spotlight cone covers a circle. The rectangle must fit INSIDE this circle.
    // For a rectangle with aspect ratio AR inscribed in a circle:
    // diagonal = diameter of circle
    // width^2 + height^2 = diagonal^2
    // width = AR * height
    // So: AR^2 * h^2 + h^2 = d^2
    // h = d / sqrt(AR^2 + 1)
    // w = AR * h
    
    // Use slightly smaller rectangle (95%) to ensure clean edges
    const diagonal = size * 0.95; // Slightly smaller than full texture
    const rectHeight = diagonal / Math.sqrt(ar.value * ar.value + 1);
    const rectWidth = rectHeight * ar.value;
    const rectX = (size - rectWidth) / 2;
    const rectY = (size - rectHeight) / 2;
    
    // Check if custom image is provided and not using test pattern
    if (projector.projector_image && !projector.test_pattern_on) {
      // Store reference to this texture for the async callback
      const currentTexture = texture;
      
      // Load custom image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Get fresh context from the texture (important for async callback)
        const freshCtx = currentTexture.getContext();
        
        // Clear the canvas properly (including alpha channel)
        freshCtx.clearRect(0, 0, size, size);
        freshCtx.fillStyle = '#000000';
        freshCtx.fillRect(0, 0, size, size);
        
        // Apply rotation transform for custom images
        freshCtx.save();
        freshCtx.translate(size / 2, size / 2);
        freshCtx.rotate(-rotZ);
        freshCtx.translate(-size / 2, -size / 2);
        
        // Draw the image STRETCHED to fill the entire rectangular projection area (all 4 corners)
        // No aspect ratio preservation - just stretch to fit
        freshCtx.drawImage(img, rectX, rectY, rectWidth, rectHeight);
        
        // Add white border if enabled (thin)
        // Use show_border for both normal and reverse projectors
        if (projector.show_border) {
          freshCtx.strokeStyle = '#ffffff';
          freshCtx.lineWidth = 3;
          freshCtx.strokeRect(rectX + 1, rectY + 1, rectWidth - 2, rectHeight - 2);
        }
        
        freshCtx.restore();
        currentTexture.update();
      };
      img.onerror = (err) => {
        logger.error('[createProjectionTexture] Failed to load custom image:', err);
      };
      img.src = resolveAssetUrl(projector.projector_image);
      
      // Draw placeholder while image is loading
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, size, size);
      // Use show_border for both normal and reverse projectors
      if (projector.show_border) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3;
        ctx.strokeRect(rectX + 1, rectY + 1, rectWidth - 2, rectHeight - 2);
      }
      ctx.restore();
      texture.update();
      
      return texture;
    }
    
    // Draw the projection content ONLY in the rectangular area
    const patternId = projector.test_pattern_on ? (projector.test_pattern_id || 'grid') : null;
    
    if (patternId === 'grid' || patternId === 'crosshatch') {
      // Grid pattern with black background (blocks light, only grid lines emit)
      ctx.fillStyle = '#000000';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
      
      // Thin green grid lines
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1;
      const gridDivisions = patternId === 'crosshatch' ? 16 : 8;
      for (let i = 0; i <= gridDivisions; i++) {
        const x = rectX + i * rectWidth / gridDivisions;
        const y = rectY + i * rectHeight / gridDivisions;
        ctx.beginPath();
        ctx.moveTo(x, rectY);
        ctx.lineTo(x, rectY + rectHeight);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(rectX, y);
        ctx.lineTo(rectX + rectWidth, y);
        ctx.stroke();
      }
      // Center crosshairs in green (slightly brighter)
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(rectX + rectWidth / 2, rectY);
      ctx.lineTo(rectX + rectWidth / 2, rectY + rectHeight);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(rectX, rectY + rectHeight / 2);
      ctx.lineTo(rectX + rectWidth, rectY + rectHeight / 2);
      ctx.stroke();
      
      // White border (thin)
      if (projector.show_border) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
      }
    } else if (patternId === 'color_bars') {
      const colors = ['#ffffff', '#ffff00', '#00ffff', '#00ff00', '#ff00ff', '#ff0000', '#0000ff', '#111111'];
      colors.forEach((c, i) => {
        ctx.fillStyle = c;
        ctx.fillRect(rectX + i * rectWidth / colors.length, rectY, rectWidth / colors.length + 1, rectHeight);
      });
    } else if (patternId === 'white') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'red') {
      ctx.fillStyle = '#ff0000';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'green') {
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'blue') {
      ctx.fillStyle = '#0000ff';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'cyan') {
      ctx.fillStyle = '#00ffff';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'magenta') {
      ctx.fillStyle = '#ff00ff';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'yellow') {
      ctx.fillStyle = '#ffff00';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'black') {
      ctx.fillStyle = '#111111';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'checkerboard') {
      const sz = 64;
      for (let y = rectY; y < rectY + rectHeight; y += sz) {
        for (let x = rectX; x < rectX + rectWidth; x += sz) {
          ctx.fillStyle = ((Math.floor((x - rectX) / sz) + Math.floor((y - rectY) / sz)) % 2 === 0) ? '#ffffff' : '#111111';
          ctx.fillRect(x, y, Math.min(sz, rectX + rectWidth - x), Math.min(sz, rectY + rectHeight - y));
        }
      }
    } else if (patternId === 'gradient-h') {
      // Horizontal gradient
      const gradient = ctx.createLinearGradient(rectX, rectY, rectX + rectWidth, rectY);
      gradient.addColorStop(0, '#000000');
      gradient.addColorStop(1, '#ffffff');
      ctx.fillStyle = gradient;
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'gradient-v') {
      // Vertical gradient
      const gradient = ctx.createLinearGradient(rectX, rectY, rectX, rectY + rectHeight);
      gradient.addColorStop(0, '#000000');
      gradient.addColorStop(1, '#ffffff');
      ctx.fillStyle = gradient;
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
    } else if (patternId === 'focus') {
      // Focus pattern - black background with green elements
      ctx.fillStyle = '#000000';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
      
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1;
      const centerX = rectX + rectWidth / 2;
      const centerY = rectY + rectHeight / 2;
      const maxRadius = Math.min(rectWidth, rectHeight) / 2;
      
      // Concentric circles
      for (let r = maxRadius / 5; r <= maxRadius; r += maxRadius / 5) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      
      // Crosshairs
      ctx.beginPath();
      ctx.moveTo(rectX, centerY);
      ctx.lineTo(rectX + rectWidth, centerY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(centerX, rectY);
      ctx.lineTo(centerX, rectY + rectHeight);
      ctx.stroke();
      
      // Diagonal lines
      ctx.beginPath();
      ctx.moveTo(rectX, rectY);
      ctx.lineTo(rectX + rectWidth, rectY + rectHeight);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(rectX + rectWidth, rectY);
      ctx.lineTo(rectX, rectY + rectHeight);
      ctx.stroke();
      
      // White border
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
    } else {
      // Default - black background with green grid (technical calibration style)
      // Black background blocks light, only grid lines emit light
      ctx.fillStyle = '#000000';
      ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
      
      // Thin green grid lines
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1;
      const gridDivisions = 8;
      for (let i = 0; i <= gridDivisions; i++) {
        const x = rectX + i * rectWidth / gridDivisions;
        const y = rectY + i * rectHeight / gridDivisions;
        ctx.beginPath();
        ctx.moveTo(x, rectY);
        ctx.lineTo(x, rectY + rectHeight);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(rectX, y);
        ctx.lineTo(rectX + rectWidth, y);
        ctx.stroke();
      }
      // Center crosshairs
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(rectX + rectWidth / 2, rectY);
      ctx.lineTo(rectX + rectWidth / 2, rectY + rectHeight);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(rectX, rectY + rectHeight / 2);
      ctx.lineTo(rectX + rectWidth, rectY + rectHeight / 2);
      ctx.stroke();
      
      // White border
      if (projector.show_border) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
      }
    }
    
    // Add white border if enabled (thin line)
    // Use show_border for both normal and reverse projectors
    if (projector.show_border) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.strokeRect(rectX + 1, rectY + 1, rectWidth - 2, rectHeight - 2);
    }
    
    // Restore canvas transform state after drawing
    ctx.restore();
    
    texture.update();
    return texture;
  }, []);

  // ============================================
  // PROJECTIVE TEXTURING SYSTEM (Like Mapping Matter)
  // ============================================
  // Renders ALL projector patterns onto receiving surfaces
  // Uses simple projective math - no complex UV calculations
  // Supports 100+ projectors without WebGL limits!
  
  // Create a projection pattern texture for a projector
  const createProjectorPattern = useCallback((projector, width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Determine if we need a transparent/grid background or solid
    const projColor = projector.projection_color || '#ffffff';
    const patternId = projector.test_pattern_on ? projector.test_pattern_id : null;
    const useTransparentBackground = patternId === 'grid' || patternId === 'focus' || !patternId;
    
    if (useTransparentBackground) {
      // Clear to transparent for grid-based patterns
      ctx.clearRect(0, 0, width, height);
    } else {
      // Solid background for other patterns
      ctx.fillStyle = projColor;
      ctx.fillRect(0, 0, width, height);
    }
    
    // Draw test pattern if enabled
    if (projector.test_pattern_on && projector.test_pattern_id) {
      
      if (patternId === 'grid') {
        // Black background with green grid (only grid lines emit light)
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, width, height);
        
        // Thin green grid lines
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 1;
        const gridLines = 10;
        for (let i = 0; i <= gridLines; i++) {
          ctx.beginPath();
          ctx.moveTo((width * i / gridLines), 0);
          ctx.lineTo((width * i / gridLines), height);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(0, (height * i / gridLines));
          ctx.lineTo(width, (height * i / gridLines));
          ctx.stroke();
        }
        // Center cross in green (slightly thicker)
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(width / 2, 0);
        ctx.lineTo(width / 2, height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.stroke();
        
        // White border (thin)
        if (projector.show_border) {
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.strokeRect(0, 0, width, height);
        }
      } else if (patternId === 'color_bars') {
        // Standard SMPTE color bars
        const colors = ['#ffffff', '#ffff00', '#00ffff', '#00ff00', '#ff00ff', '#ff0000', '#0000ff', '#000000'];
        const barW = width / colors.length;
        colors.forEach((color, i) => {
          ctx.fillStyle = color;
          ctx.fillRect(Math.floor(i * barW), 0, Math.ceil(barW) + 1, height);
        });
      } else if (patternId === 'checkerboard') {
        const checkSize = 8;
        const cellW = width / checkSize;
        const cellH = height / checkSize;
        for (let i = 0; i < checkSize; i++) {
          for (let j = 0; j < checkSize; j++) {
            ctx.fillStyle = (i + j) % 2 === 0 ? '#ffffff' : '#000000';
            ctx.fillRect(Math.floor(i * cellW), Math.floor(j * cellH), Math.ceil(cellW) + 1, Math.ceil(cellH) + 1);
          }
        }
      } else if (patternId === 'rgbw') {
        // RGBW quadrant pattern
        const halfW = width / 2;
        const halfH = height / 2;
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(0, 0, halfW, halfH);
        ctx.fillStyle = '#00ff00';
        ctx.fillRect(halfW, 0, halfW, halfH);
        ctx.fillStyle = '#0000ff';
        ctx.fillRect(0, halfH, halfW, halfH);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(halfW, halfH, halfW, halfH);
      } else if (patternId === 'focus') {
        // Focus pattern with fine green lines on black background
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, width, height);
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 1;
        const lines = 20;
        for (let i = 0; i <= lines; i++) {
          ctx.beginPath();
          ctx.moveTo((width * i / lines), 0);
          ctx.lineTo((width * i / lines), height);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(0, (height * i / lines));
          ctx.lineTo(width, (height * i / lines));
          ctx.stroke();
        }
        // White border
        if (projector.show_border) {
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.strokeRect(0, 0, width, height);
        }
      }
      // 'white' or default stays white/projection color
    } else {
      // No test pattern selected - draw default green grid on black background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1;
      const gridLines = 8;
      for (let i = 0; i <= gridLines; i++) {
        ctx.beginPath();
        ctx.moveTo((width * i / gridLines), 0);
        ctx.lineTo((width * i / gridLines), height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, (height * i / gridLines));
        ctx.lineTo(width, (height * i / gridLines));
        ctx.stroke();
      }
      // Center cross
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();
      // White border
      if (projector.show_border) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.strokeRect(0, 0, width, height);
      }
    }
    
    // Draw white border if enabled (thin line)
    // Use show_border for both normal and reverse projectors
    if (projector.show_border) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, width - 2, height - 2);
    }
    
    return canvas;
  }, []);

  // ============================================
  // SHADER-BASED PROJECTIVE TEXTURING (Like Mapping Matter)
  // ============================================
  // Uses per-pixel shader calculation for accurate projection
  // Supports 100+ projectors, single-face projection, shadows/occlusion
  
  // Store original materials for restoration
  const originalMaterialsRef = useRef({});
  // Store shader materials we create
  const projectionMaterialsRef = useRef({});
  
  /**
   * Create projection vertex shader
   */
  const projectionVertexShader = `
    precision highp float;
    
    // Attributes
    attribute vec3 position;
    attribute vec3 normal;
    attribute vec2 uv;
    
    // Uniforms
    uniform mat4 worldViewProjection;
    uniform mat4 world;
    uniform mat4 projectorVP;
    
    // Varyings
    varying vec3 vWorldPosition;
    varying vec3 vWorldNormal;
    varying vec4 vProjCoord;
    varying vec2 vUV;
    
    void main() {
      vec4 worldPos = world * vec4(position, 1.0);
      vWorldPosition = worldPos.xyz;
      vWorldNormal = normalize((world * vec4(normal, 0.0)).xyz);
      vUV = uv;
      
      // Calculate projector texture coordinates
      vProjCoord = projectorVP * worldPos;
      
      gl_Position = worldViewProjection * vec4(position, 1.0);
    }
  `;
  
  /**
   * Create projection fragment shader
   */
  const projectionFragmentShader = `
    precision highp float;
    
    // Varyings
    varying vec3 vWorldPosition;
    varying vec3 vWorldNormal;
    varying vec4 vProjCoord;
    varying vec2 vUV;
    
    // Uniforms
    uniform vec3 baseColor;
    uniform vec3 projectorPosition;
    uniform float brightness;
    uniform sampler2D projectorTexture;
    uniform float ambientLight;
    
    void main() {
      // Start with base color
      vec3 finalColor = baseColor * ambientLight;
      
      // Normalize the world normal
      vec3 normal = normalize(vWorldNormal);
      
      // Projector direction check
      vec3 toProjector = normalize(projectorPosition - vWorldPosition);
      float facingFactor = dot(normal, toProjector);
      
      // Only project on surfaces facing the projector
      if (facingFactor > 0.0) {
        // Perspective divide for projection coordinates
        vec3 projCoord = vProjCoord.xyz / vProjCoord.w;
        
        // Check if within projector frustum [-1, 1] -> [0, 1]
        vec2 projUV = projCoord.xy * 0.5 + 0.5;
        
        // Only apply projection if within bounds and in front of projector
        if (projUV.x >= 0.0 && projUV.x <= 1.0 && 
            projUV.y >= 0.0 && projUV.y <= 1.0 &&
            vProjCoord.w > 0.0) {
          
          // Flip Y for texture sampling
          projUV.y = 1.0 - projUV.y;
          
          // Sample projection texture
          vec4 projColor = texture2D(projectorTexture, projUV);
          
          // Apply projection with brightness and falloff based on facing angle
          float projIntensity = brightness * facingFactor;
          finalColor += projColor.rgb * projIntensity;
        }
      }
      
      gl_FragColor = vec4(clamp(finalColor, 0.0, 1.0), 1.0);
    }
  `;
  
  /**
   * Create or get projection texture for a projector
   */
  const getProjectorTexture = useCallback((projector, scene) => {
    const texKey = `projTex_${projector.id}`;
    let texture = surfaceProjectionTexturesRef.current[texKey];
    
    if (!texture) {
      const texSize = 512;
      texture = new BABYLON.DynamicTexture(texKey, { width: texSize, height: texSize }, scene, false);
      texture.hasAlpha = true;
      texture.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
      texture.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;
      surfaceProjectionTexturesRef.current[texKey] = texture;
    }
    
    // Update texture content
    const ctx = texture.getContext();
    const size = 512;
    
    // Clear with black background (blocks light except where grid lines are drawn)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, size, size);
    
    // Draw thin green grid pattern for visibility
    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 1;
    const gridLines = 8;
    for (let i = 0; i <= gridLines; i++) {
      ctx.beginPath();
      ctx.moveTo((size * i / gridLines), 0);
      ctx.lineTo((size * i / gridLines), size);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, (size * i / gridLines));
      ctx.lineTo(size, (size * i / gridLines));
      ctx.stroke();
    }
    
    // Draw test pattern if enabled
    if (projector.test_pattern_on && projector.test_pattern_id) {
      drawTestPatternOnContext(ctx, projector.test_pattern_id, size);
    }
    
    // Always draw thin white border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, size - 2, size - 2);
    
    texture.update();
    return texture;
  }, []);
  
  /**
   * Draw test pattern on canvas context
   */
  const drawTestPatternOnContext = useCallback((ctx, patternId, size) => {
    if (patternId === 'grid') {
      // Black background with green grid
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, size, size);
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1;
      const gridLines = 10;
      for (let i = 0; i <= gridLines; i++) {
        ctx.beginPath();
        ctx.moveTo((size * i / gridLines), 0);
        ctx.lineTo((size * i / gridLines), size);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, (size * i / gridLines));
        ctx.lineTo(size, (size * i / gridLines));
        ctx.stroke();
      }
      // Green center cross (slightly thicker)
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(size / 2, 0);
      ctx.lineTo(size / 2, size);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, size / 2);
      ctx.lineTo(size, size / 2);
      ctx.stroke();
      // White border
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, size, size);
    } else if (patternId === 'color_bars') {
      const colors = ['#ffffff', '#ffff00', '#00ffff', '#00ff00', '#ff00ff', '#ff0000', '#0000ff', '#000000'];
      const barW = size / colors.length;
      colors.forEach((color, i) => {
        ctx.fillStyle = color;
        ctx.fillRect(Math.floor(i * barW), 0, Math.ceil(barW) + 1, size);
      });
    } else if (patternId === 'checkerboard') {
      const checkSize = 8;
      const cellW = size / checkSize;
      const cellH = size / checkSize;
      for (let i = 0; i < checkSize; i++) {
        for (let j = 0; j < checkSize; j++) {
          ctx.fillStyle = (i + j) % 2 === 0 ? '#ffffff' : '#000000';
          ctx.fillRect(Math.floor(i * cellW), Math.floor(j * cellH), 
                      Math.ceil(cellW) + 1, Math.ceil(cellH) + 1);
        }
      }
    } else if (patternId === 'rgbw') {
      const halfW = size / 2;
      const halfH = size / 2;
      ctx.fillStyle = '#ff0000';
      ctx.fillRect(0, 0, halfW, halfH);
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(halfW, 0, halfW, halfH);
      ctx.fillStyle = '#0000ff';
      ctx.fillRect(0, halfH, halfW, halfH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(halfW, halfH, halfW, halfH);
    }
  }, []);
  
  /**
   * Calculate projector view-projection matrix
   */
  const calculateProjectorMatrix = useCallback((projector) => {
    const position = getProjectionOrigin(projector, projectorMeshesRef.current);
    
    const { direction, up } = getProjectorDirectionAndUp(projector);
    
    // Target point
    const throwDistance = projector.throw_distance || 10;
    const target = position.add(direction.scale(throwDistance));
    
    // Create view matrix
    const viewMatrix = BABYLON.Matrix.LookAtLH(position, target, up);
    
    // Calculate FOV from throw ratio
    const throwRatio = projector.throw_ratio || 1.5;
    const aspectRatio = getAspectRatioValue(projector.aspect_ratio || '16:9');
    
    // FOV calculation
    const fovY = 2 * Math.atan(1 / (2 * throwRatio * aspectRatio));
    
    // Create projection matrix
    const near = 0.1;
    const far = throwDistance * 3;
    const projectionMatrix = BABYLON.Matrix.PerspectiveFovLH(fovY, aspectRatio, near, far);
    
    return viewMatrix.multiply(projectionMatrix);
  }, []);
  
  /**
   * Get aspect ratio value from string
   */
  const getAspectRatioValue = (aspectStr) => {
    const ratios = { '16:10': 16/10, '16:9': 16/9, '4:3': 4/3, '21:9': 21/9, '1:1': 1 };
    return ratios[aspectStr] || 16/9;
  };
  
  /**
   * Create or update shader material for projective texturing
   * Uses custom shader that calculates projective UV coordinates PER-PIXEL
   * This ensures projection ONLY appears where the projector light hits
   */
  const getProjectionMaterial = useCallback((mesh, projector, texture, vpMatrix, scene) => {
    const meshId = mesh.uniqueId || mesh.name;
    
    // Store original material if not already
    if (!originalMaterialsRef.current[meshId] && mesh.material) {
      originalMaterialsRef.current[meshId] = mesh.material;
    }
    
    // Get base color from original material
    const origMat = originalMaterialsRef.current[meshId];
    let baseColor = new BABYLON.Color3(0.3, 0.3, 0.3);
    if (origMat && origMat.diffuseColor) {
      baseColor = origMat.diffuseColor.clone();
    }
    
    // Define custom shaders for projective texturing
    const vertexShader = `
      precision highp float;
      
      attribute vec3 position;
      attribute vec3 normal;
      attribute vec2 uv;
      
      uniform mat4 worldViewProjection;
      uniform mat4 world;
      uniform mat4 projectorVP;
      
      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;
      varying vec4 vProjCoord;
      
      void main() {
        vec4 worldPos = world * vec4(position, 1.0);
        vWorldPosition = worldPos.xyz;
        vWorldNormal = normalize((world * vec4(normal, 0.0)).xyz);
        
        // Calculate projector texture coordinates
        vProjCoord = projectorVP * worldPos;
        
        gl_Position = worldViewProjection * vec4(position, 1.0);
      }
    `;
    
    const fragmentShader = `
      precision highp float;
      
      varying vec3 vWorldPosition;
      varying vec3 vWorldNormal;
      varying vec4 vProjCoord;
      
      uniform vec3 baseColor;
      uniform vec3 projectorPosition;
      uniform float brightness;
      uniform float ambientLight;
      uniform sampler2D projectorTexture;
      
      void main() {
        // Start with ambient lit base color (higher ambient for visibility)
        vec3 finalColor = baseColor * 0.5;
        
        vec3 normal = normalize(vWorldNormal);
        
        // Check if surface is facing the projector
        vec3 toProjector = normalize(projectorPosition - vWorldPosition);
        float facingFactor = dot(normal, toProjector);
        
        // Only apply projection on surfaces facing the projector (facingFactor > 0)
        if (facingFactor > 0.0 && vProjCoord.w > 0.0) {
          // Perspective divide for projection coordinates
          vec3 projCoord = vProjCoord.xyz / vProjCoord.w;
          
          // Convert from [-1, 1] to [0, 1] for texture sampling
          vec2 projUV = projCoord.xy * 0.5 + 0.5;
          
          // Only apply projection if within projector frustum [0, 1]
          if (projUV.x >= 0.0 && projUV.x <= 1.0 && 
              projUV.y >= 0.0 && projUV.y <= 1.0) {
            
            // Flip Y for correct texture orientation
            projUV.y = 1.0 - projUV.y;
            
            // Sample projection texture
            vec4 projColor = texture2D(projectorTexture, projUV);
            
            // Apply projection with brightness - ADDITIVE blending
            float projIntensity = brightness * facingFactor;
            finalColor = finalColor + projColor.rgb * projIntensity;
          }
        }
        
        // Output final color (clamped)
        gl_FragColor = vec4(clamp(finalColor, 0.0, 1.0), 1.0);
      }
    `;
    
    // Register shaders
    const shaderName = "projective_" + meshId;
    if (!BABYLON.Effect.ShadersStore[shaderName + "VertexShader"]) {
      BABYLON.Effect.ShadersStore[shaderName + "VertexShader"] = vertexShader;
      BABYLON.Effect.ShadersStore[shaderName + "FragmentShader"] = fragmentShader;
    }
    
    // Get or create shader material
    let projMat = projectionMaterialsRef.current[meshId];
    
    if (!projMat || !(projMat instanceof BABYLON.ShaderMaterial)) {
      projMat = new BABYLON.ShaderMaterial(
        "projMat_" + meshId,
        scene,
        { vertex: shaderName, fragment: shaderName },
        {
          attributes: ["position", "normal", "uv"],
          uniforms: ["worldViewProjection", "world", "projectorVP",
                    "baseColor", "projectorPosition", "brightness", "ambientLight"],
          samplers: ["projectorTexture"]
        }
      );
      
      projMat.backFaceCulling = true;
      projectionMaterialsRef.current[meshId] = projMat;
    }
    
    // Get projection origin (accounts for lens90 offset)
    const projPos = getProjectionOrigin(projector, projectorMeshesRef.current);
    projMat.setMatrix("projectorVP", vpMatrix);
    projMat.setVector3("projectorPosition", projPos);
    projMat.setVector3("baseColor", baseColor);
    // Stacking multiplier — virtual brightness boost (no extra projections)
    {
      const _s = projector.stacking;
      const _stkCnt = (_s && _s.enabled)
        ? 1 + (parseInt(_s.top) || 0) + (parseInt(_s.bottom) || 0) + (parseInt(_s.left) || 0) + (parseInt(_s.right) || 0)
        : 1;
      projMat.setFloat("brightness", Math.max(0.1, Math.min(1, (projector.brightness || 100) / 100)) * _stkCnt);
    }
    projMat.setFloat("ambientLight", AMBIENT_LIGHT_INTENSITY);
    projMat.setTexture("projectorTexture", texture);
    
    return projMat;
  }, []);
  
  /**
   * Render projections using shader-based approach
   */
  const renderShaderProjections = useCallback((targetMesh, activeProjectors, scene) => {
    if (!targetMesh || !activeProjectors.length || !scene) return 0;
    
    const meshId = targetMesh.uniqueId || targetMesh.name;
    const meshCenter = targetMesh.getAbsolutePosition();
    
    // Find projectors that could illuminate this mesh
    let projectionsApplied = 0;
    let primaryProjector = null;
    
    activeProjectors.forEach((projector) => {
      if (!projector.power_on) return;
      
      const projPos = getProjectionOrigin(projector, projectorMeshesRef.current);
      const { direction: projDir } = getProjectorDirectionAndUp(projector);
      
      const toMesh = meshCenter.subtract(projPos).normalize();
      const distToMesh = meshCenter.subtract(projPos).length();
      
      // Check if projector is roughly pointing toward mesh (relaxed threshold)
      const dotDir = BABYLON.Vector3.Dot(projDir, toMesh);
      if (dotDir < -0.3) return; // Only skip if pointing completely away
      
      // Check throw distance (generous margin)
      const throwDist = projector.throw_distance || 10;
      if (distToMesh > throwDist * 5) return;
      
      // OCCLUSION CHECK - exclude projector meshes and non-projection surfaces
      const rayToMesh = new BABYLON.Ray(projPos, toMesh, distToMesh - 0.1);
      const occlusionPick = scene.pickWithRay(rayToMesh, (mesh) => {
        // Always exclude the target mesh itself
        if (mesh === targetMesh) return false;
        if (mesh.parent === targetMesh) return false;
        
        // Exclude projector-related meshes
        const meshName = mesh.name?.toLowerCase() || '';
        if (meshName.includes('beam')) return false;
        if (meshName.includes('projector')) return false;
        if (meshName.includes('frame')) return false;
        if (meshName.includes('clamp')) return false;
        if (meshName.includes('proj_')) return false;
        
        // Exclude scene infrastructure
        if (meshName.includes('grid')) return false;
        if (meshName.includes('ground')) return false;
        if (meshName.includes('gizmo')) return false;
        if (meshName.includes('screen')) return false; // Screens shouldn't block
        
        // Exclude non-pickable/invisible
        if (!mesh.isPickable || !mesh.isVisible) return false;
        
        return true;
      });
      
      if (occlusionPick && occlusionPick.hit) {
        // Only block if it's a solid object (not another projection surface)
        const blockerName = occlusionPick.pickedMesh?.name?.toLowerCase() || '';
        if (!blockerName.includes('screen') && !blockerName.includes('plane')) {
          logger.log(`Projector ${projector.name} blocked by ${occlusionPick.pickedMesh?.name}`);
          return;
        }
      }
      
      projectionsApplied++;
      if (!primaryProjector) primaryProjector = projector;
    });
    
    // Apply shader material if projection applies
    if (projectionsApplied > 0 && primaryProjector) {
      const projTexture = getProjectorTexture(primaryProjector, scene);
      const vpMatrix = calculateProjectorMatrix(primaryProjector);
      const shaderMat = getProjectionMaterial(targetMesh, primaryProjector, projTexture, vpMatrix, scene);
      
      targetMesh.material = shaderMat;
    } else {
      // Restore original material
      const origMat = originalMaterialsRef.current[meshId];
      if (origMat && targetMesh.material !== origMat) {
        targetMesh.material = origMat;
      }
    }
    
    return projectionsApplied;
  }, [getProjectorTexture, calculateProjectorMatrix, getProjectionMaterial]);

  // Keep the old function for backwards compatibility but it now uses shader approach
  const renderSurfaceProjections = renderShaderProjections;

  // Get all meshes that can receive projections
  const getProjectionReceivers = useCallback((scene, sceneObjectsList) => {
    const receivers = [];
    
    sceneObjectsList.forEach(obj => {
      // Skip objects with MTL material applied - they retain their own look, no projection
      if (obj.mtlData) return;

      // Per-object Projection Mode (Normal / Reverse / None) — control-layer gate.
      // Backward compat: objects without `projectionMode` but with `uvMappingEnabled === true`
      // are treated as Reverse mode receivers (UV on). Otherwise default is Normal.
      const mode = obj.projectionMode
        || (obj.uvMappingEnabled === true ? 'reverse' : 'normal');
      if (mode === 'none') return;
      if (mode === 'reverse' && obj.uvMappingEnabled !== true) return;

      // Include all 3D objects that can receive projections:
      // screens, planes, cubes, spheres, cylinders, domes, trusses, and imported 3D models
      const receiverTypes = ['screen', 'scrim_screen', 'plane', 'cube', 'sphere', 'cylinder', 'dome', 'hemisphere', 'object', 'mesh', 'truss', 'truss_system', 'scaffolding'];
      
      if (receiverTypes.includes(obj.type)) {
        // For imported 3D models (type 'mesh'), get child meshes from the TransformNode
        if (obj.type === 'mesh') {
          const meshParent = scene.getTransformNodeByName('mesh_' + obj.id);
          if (meshParent) {
            const childMeshes = meshParent.getChildMeshes();
            childMeshes.forEach(childMesh => {
              if (childMesh && childMesh.isVisible && childMesh.getTotalVertices && childMesh.getTotalVertices() > 0) {
                receivers.push({ mesh: childMesh, obj });
              }
            });
          }
        } else if (obj.type === 'scaffolding') {
          // Scaffolding uses a bounding box mesh for projection receiving
          const boundMesh = scene.getMeshByName('scaffBound_' + obj.id);
          if (boundMesh) {
            receivers.push({ mesh: boundMesh, obj });
          }
        } else if (obj.type === 'truss') {
          // Trusses use 'truss_' prefix instead of 'obj_'
          const mesh = scene.getMeshByName('truss_' + obj.id);
          if (mesh && mesh.isVisible) {
            receivers.push({ mesh, obj });
          }
        } else if (obj.type === 'truss_system') {
          // Truss system uses bounding box for projection receiving
          const boundMesh = scene.getMeshByName('trussBound_' + obj.id);
          if (boundMesh) {
            receivers.push({ mesh: boundMesh, obj });
          }
        } else {
          // For primitives and standard objects, use the direct mesh
          const mesh = scene.getMeshByName('obj_' + obj.id);
          if (mesh && mesh.isVisible) {
            receivers.push({ mesh, obj });
          }
        }
      }
    });
    
    return receivers;
  }, []);

  // Manage projectors
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    // IMPORTANT: Update ALL existing materials to support the current number of projectors
    // This ensures meshes don't disappear when projector count changes
    const totalLights = Math.min(projectors.length, MAX_SIMULTANEOUS_LIGHTS);
    if (totalLights > 0) {
      scene.materials.forEach(material => {
        if (material && typeof material.maxSimultaneousLights !== 'undefined') {
          material.maxSimultaneousLights = MAX_SIMULTANEOUS_LIGHTS;
        }
      });
    }
    
    // CRITICAL: If no projectors, clean up ALL projector-related resources
    if (projectors.length === 0) {
      logger.log('No projectors - cleaning up ALL projector resources');
      
      // Clean all projector lights
      Object.keys(projectorLightsRef.current).forEach(id => {
        try { projectorLightsRef.current[id]?.dispose(); } catch(e) { /* ignore */ }
      });
      projectorLightsRef.current = {};
      
      // Clean all shadow generators
      Object.keys(shadowGeneratorsRef.current).forEach(id => {
        try { shadowGeneratorsRef.current[id]?.dispose(); } catch(e) { /* ignore */ }
      });
      shadowGeneratorsRef.current = {};
      
      // Clean all projector meshes
      Object.keys(projectorMeshesRef.current).forEach(id => {
        try { projectorMeshesRef.current[id]?.dispose(); } catch(e) { /* ignore */ }
      });
      projectorMeshesRef.current = {};
      
      // Clean all projector textures
      Object.keys(projectorTexturesRef.current).forEach(id => {
        try { projectorTexturesRef.current[id]?.dispose(); } catch(e) { /* ignore */ }
      });
      projectorTexturesRef.current = {};
      
      // Clean all beam meshes
      Object.keys(beamMeshesRef.current).forEach(id => {
        try { beamMeshesRef.current[id]?.dispose(); } catch(e) { /* ignore */ }
      });
      beamMeshesRef.current = {};
      
      // Clean all projector labels
      Object.keys(projectorLabelsRef.current).forEach(id => {
        try { projectorLabelsRef.current[id]?.dispose(); } catch(e) { /* ignore */ }
      });
      projectorLabelsRef.current = {};

      // Clean all beam-center distance labels
      Object.keys(beamCenterLabelsRef.current).forEach(id => {
        try { beamCenterLabelsRef.current[id]?.dispose(); } catch(e) { /* ignore */ }
      });
      beamCenterLabelsRef.current = {};
      
      return; // Exit early - nothing more to do
    }
    
    // Remove old projector lights, meshes, and related resources
    // Only collect ACTUAL projector IDs (filter out sub-component keys like lens_xxx, frame_xxx)
    const projectorIdSet = new Set(projectors.map(p => p.id));
    const allTrackedIds = new Set([
      ...Object.keys(projectorLightsRef.current),
      ...Object.keys(projectorMeshesRef.current),
      ...Object.keys(beamMeshesRef.current),
      ...Object.keys(projectorLabelsRef.current)
    ]);
    
    allTrackedIds.forEach(id => {
      // Skip sub-component keys (lens_xxx, frame_xxx, mount_xxx, clamp_xxx) - they're cleaned up with their parent
      if (id.startsWith('lens_') || id.startsWith('frame_') || id.startsWith('mount_') || id.startsWith('clamp_')) return;
      
      if (!projectorIdSet.has(id)) {
        logger.log(`Cleaning up deleted projector: ${id}`);
        try {
          projectorLightsRef.current[id]?.dispose();
          delete projectorLightsRef.current[id];
        } catch (e) { logger.warn('Error disposing light:', e); }
        try {
          shadowGeneratorsRef.current[id]?.dispose();
          delete shadowGeneratorsRef.current[id];
        } catch (e) { logger.warn('Error disposing shadow generator:', e); }
        try {
          const mesh = projectorMeshesRef.current[id];
          if (mesh && typeof mesh.dispose === 'function') {
            // Unregister from projection system occluders before disposing
            if (projectionSystemRef.current) {
              projectionSystemRef.current.unregisterOccluder(mesh);
            }
            mesh.dispose();
          }
          delete projectorMeshesRef.current[id];
        } catch (e) { logger.warn('Error disposing mesh:', e); }
        // Dispose lens components
        try {
          const lensKey = `lens_${id}`;
          const lens = projectorMeshesRef.current[lensKey];
          if (lens) {
            if (typeof lens.housing?.dispose === 'function') lens.housing.dispose();
            if (typeof lens.glass?.dispose === 'function') lens.glass.dispose();
            delete projectorMeshesRef.current[lensKey];
          }
        } catch (e) { logger.warn('Error disposing lens:', e); }
        // Dispose 90-degree lens mesh
        try {
          ['lens90pivot_', 'lens90pipe1_', 'lens90pipe2_', 'lens90joint_', 'lens90glass_',
           'lens90pipe1v2_', 'lens90pipe2v2_', 'lens90jointv2_', 'lens90glassv2_',
           'lens90glassv3_',
           'lens90mat_', 'lens90glassmat_',
           'lens90pipe_', 'lens90elbow_', 'lens90barrel_',
           'lens90mount_', 'lens90body_',
           'ustHousing_', 'ustBarrel_', 'ustGlass_',
           'ustNeckv2_', 'ustBarrelv2_', 'ustGlassv2_',
           'ustNeckv3_', 'ustBarrelv3_', 'ustGlassv3_',
           'ustNeckv4_', 'ustBarrelv4_', 'ustGlassv4_',
           'ustMat_', 'ustGlassMat_'].forEach(prefix => {
            const key = prefix + id;
            if (projectorMeshesRef.current[key]) {
              if (projectorMeshesRef.current[key].dispose) projectorMeshesRef.current[key].dispose();
              delete projectorMeshesRef.current[key];
            }
          });
        } catch (e) { logger.warn('Error disposing lens90:', e); }
        // Dispose frame components
        try {
          const frameKey = `frame_${id}`;
          const frame = projectorMeshesRef.current[frameKey];
          if (frame && typeof frame.dispose === 'function') {
            frame.dispose();
          } else if (frame) {
            // Frame might be a parent mesh with children
            if (frame.getChildMeshes) {
              frame.getChildMeshes().forEach(child => child.dispose());
            }
            if (typeof frame.dispose === 'function') frame.dispose();
          }
          delete projectorMeshesRef.current[frameKey];
        } catch (e) { logger.warn('Error disposing frame:', e); }
        // Dispose mount + clamp components (parented to projMesh, so already
        // gone after projMesh.dispose() above — just clear the ref entries).
        try {
          [`mount_${id}`, `clamp_${id}`].forEach((subKey) => {
            const sub = projectorMeshesRef.current[subKey];
            if (sub && typeof sub.dispose === 'function' && !sub.isDisposed?.()) {
              sub.dispose();
            }
            delete projectorMeshesRef.current[subKey];
          });
        } catch (e) { logger.warn('Error disposing mount/clamp:', e); }
        try {
          projectorTexturesRef.current[id]?.dispose();
          delete projectorTexturesRef.current[id];
        } catch (e) { logger.warn('Error disposing texture:', e); }
        try {
          beamMeshesRef.current[id]?.dispose();
          delete beamMeshesRef.current[id];
        } catch (e) { logger.warn('Error disposing beam:', e); }
        // Dispose stack ghost meshes (virtual stacking display)
        try {
          const ghostState = stackGhostsRef.current[id];
          if (ghostState?.meshes) ghostState.meshes.forEach(m => { try { m.dispose(); } catch (e) {} });
          if (ghostState?.mat) { try { ghostState.mat.dispose(); } catch (e) {} }
          delete stackGhostsRef.current[id];
        } catch (e) { logger.warn('Error disposing stack ghosts:', e); }
        // Remove projector label
        if (projectorLabelsRef.current[id]) {
          try {
            projectorLabelsRef.current[id].dispose();
            delete projectorLabelsRef.current[id];
          } catch (e) { logger.warn('Error disposing label:', e); }
        }
        // Remove orphaned beam-center distance label
        if (beamCenterLabelsRef.current[id]) {
          try {
            beamCenterLabelsRef.current[id].dispose();
            delete beamCenterLabelsRef.current[id];
          } catch (e) { logger.warn('Error disposing center label:', e); }
        }
      }
    });

    // Create/update projector lights and meshes
    // NOTE: SpotLights are LIMITED to MAX_SPOTLIGHT_COUNT to avoid WebGL uniform buffer overflow
    // Surface projections handle ALL projector patterns (unlimited) - spotlights are for illumination only
    projectors.forEach((projector, projectorIndex) => {
      const ar = ASPECT_RATIOS[projector.aspect_ratio] || ASPECT_RATIOS['16:10'];
      const throwDistance = projector.throw_distance || 5;
      // Apply zoom (effective throw ratio) so beam wireframe matches the
      // projection matrix in ProjectionSystem.js (which uses throw_ratio * zoom).
      // Without this, any non-100% zoom causes beam ≠ projected image size.
      const effectiveThrowRatio = (projector.throw_ratio || 1.5) * ((projector.zoom || 100) / 100);
      const projWidth = throwDistance / effectiveThrowRatio;
      const projHeight = projWidth / ar.value;
      
      // Calculate projector direction with lens shift (or 90-degree lens override)
      const { direction: transformedDir, rotX, rotY, rotZ, rotMatrix: rotationMatrix } = getProjectorDirectionAndUp(projector);
      
      // Calculate beam/spotlight origin position
      // For lens90: use the projection origin (lens exit point)
      // For standard: use projector front face
      let lensPosition;
      if (projector.lensType === 'lens90') {
        // lens90 apex (incl. +100mm X, +20mm Z body-local nudge) baked into
        // getProjectionOrigin — single source of truth shared with the
        // projection matrix (ProjectionSystem.js) and surface overlay
        // (surfaceImageSize.js). No additional offset here.
        lensPosition = getProjectionOrigin(projector, projectorMeshesRef.current);
      } else if (projector.lensType === 'topBottom') {
        // Periscope apex is now baked into getProjectionOrigin — beam,
        // projection matrix, and surface overlay all share that one source.
        lensPosition = getProjectionOrigin(projector, projectorMeshesRef.current);
      } else if (projector.lensType === 'ust') {
        // UST: prefer the ACTUAL mirror mesh world position so beam apex
        // always tracks the visible green mirror exactly (no math/render
        // drift). Plus a body-local fine-tune nudge of +110mm Y, +360mm Z.
        // The cyan surface overlay uses the SAME logic — see surfaceImageSize.js.
        const mirrorMesh = projectorMeshesRef.current['ustGlassv4_' + projector.id];
        if (mirrorMesh) {
          mirrorMesh.computeWorldMatrix(true);
          lensPosition = mirrorMesh.getAbsolutePosition().clone();
        } else {
          lensPosition = getProjectionOrigin(projector, projectorMeshesRef.current);
        }
        const ustNudgeLocal = new BABYLON.Vector3(0, 0.11, 0.36);
        const ustNudgeWorld = BABYLON.Vector3.TransformNormal(ustNudgeLocal, rotationMatrix);
        lensPosition.addInPlace(ustNudgeWorld);
      } else {
        const bodyDepthMm = projector.body_depth || projector.spec?.depth || 600;
        const bodyDepth = bodyDepthMm / 1000;
        const lensOffset = bodyDepth / 2 + 0.02;
        lensPosition = new BABYLON.Vector3(
          projector.position.x + transformedDir.x * lensOffset,
          projector.position.y + transformedDir.y * lensOffset,
          projector.position.z + transformedDir.z * lensOffset
        );
      }

      // Calculate spotlight angles based on throw ratio and aspect ratio
      const diagonal = Math.sqrt(projWidth * projWidth + projHeight * projHeight);
      let outerAngle = 2 * Math.atan((diagonal / 2) / throwDistance);
      outerAngle = Math.min(outerAngle, Math.PI * 0.94); // Clamp to 170 degrees max
      const innerAngle = outerAngle * 0.98; // Slight falloff at edges

      // ============================================
      // SPOTLIGHT WITH PROJECTION TEXTURE
      // ============================================
      // Create spotlight for first MAX_SPOTLIGHT_COUNT projectors
      // This gives accurate projection with proper throw calculations
      const shouldHaveSpotlight = projectorIndex < MAX_SPOTLIGHT_COUNT;
      let spotLight = projectorLightsRef.current[projector.id];

      // Clean up spotlight if this projector shouldn't have one
      if (!shouldHaveSpotlight && spotLight) {
        spotLight.dispose();
        delete projectorLightsRef.current[projector.id];
        spotLight = null;
      }

      if (shouldHaveSpotlight && !spotLight) {
        // Create spotlight with projection texture
        spotLight = new BABYLON.SpotLight(
          'projector_' + projector.id,
          lensPosition,
          transformedDir,
          outerAngle,
          2, // Exponent for falloff
          scene
        );
        projectorLightsRef.current[projector.id] = spotLight;
        logger.log(`Created spotlight for projector: ${projector.name || projector.id} (${projectorIndex + 1}/${MAX_SPOTLIGHT_COUNT} max)`);
      }

      // Update spotlight properties
      if (spotLight) {
        spotLight.position = lensPosition;
        spotLight.direction = transformedDir;
        spotLight.angle = outerAngle;
        spotLight.innerAngle = innerAngle;
        
        // Stacking multiplier — virtual brightness boost without extra projections
        const _stk = projector.stacking || {};
        const _stackCount = _stk.enabled
          ? 1 + (parseInt(_stk.top) || 0) + (parseInt(_stk.bottom) || 0) + (parseInt(_stk.left) || 0) + (parseInt(_stk.right) || 0)
          : 1;

        // Intensity based on brightness × stack count
        const brightnessValue = projector.brightness || 100;
        spotLight.intensity = projector.power_on ? (brightnessValue / 100) * 2 * _stackCount : 0;
        
        // Range based on throw distance
        spotLight.range = Math.max(throwDistance * 3, 50);
        spotLight.exponent = 2;
        spotLight.falloffType = BABYLON.Light.FALLOFF_GLTF;
        
        // White light color
        spotLight.diffuse = new BABYLON.Color3(1, 1, 1);
        spotLight.specular = new BABYLON.Color3(0.2, 0.2, 0.2);
        
        // Create or update projection texture
        if (projector.power_on) {
          // Always recreate texture to ensure latest settings are applied
          // (test pattern, brightness, custom image, border, etc.)
          let projTexture = projectorTexturesRef.current[projector.id];
          if (projTexture) {
            projTexture.dispose();
          }
          projTexture = createProjectionTexture(projector);
          projectorTexturesRef.current[projector.id] = projTexture;
          
          if (projTexture) {
            spotLight.projectionTexture = projTexture;
          }
        } else {
          spotLight.projectionTexture = null;
        }
        
        // Store metadata
        spotLight.metadata = {
          brightness: brightnessValue / 100,
          rotation: rotZ,
          projectorIndex: projectorIndex,
          throwDistance: throwDistance,
          throwRatio: projector.throw_ratio
        };
      }
      
      // Log status for first projector only
      if (projectorIndex === 0 && projectors.length > 0) {
        const spotlightCount = Math.min(projectors.length, MAX_SPOTLIGHT_COUNT);
        const perfNote = projectors.length >= BEAM_WIREFRAME_THRESHOLD 
          ? ' (beams/labels hidden for performance)' 
          : '';
        
        if (projectors.length > MAX_SPOTLIGHT_COUNT) {
          logger.warn(`⚠️ ${projectors.length} projectors: Only first ${MAX_SPOTLIGHT_COUNT} have spotlight projection (WebGL limit). Projectors ${MAX_SPOTLIGHT_COUNT + 1}+ show beam only.`);
        } else {
          logger.log(`✓ ${projectors.length} projectors: ${spotlightCount} with spotlight projection${perfNote}`);
        }
      }

      // Create projector body mesh
      let projMesh = projectorMeshesRef.current[projector.id];
      
      // Get body dimensions from projector properties or spec (in mm, convert to meters)
      const bodyW = (projector.body_width || projector.spec?.width || 500) / 1000;
      const bodyH = (projector.body_height || projector.spec?.height || 200) / 1000;
      const bodyD = (projector.body_depth || projector.spec?.depth || 600) / 1000;
      
      // Check if mesh needs recreation due to size change
      const currentDims = projMesh?.metadata?.bodyDimensions;
      const dimsChanged = currentDims && (
        currentDims.w !== bodyW || currentDims.h !== bodyH || currentDims.d !== bodyD
      );
      
      if (projMesh && dimsChanged) {
        projMesh.dispose();
        delete projectorMeshesRef.current[projector.id];
        // Also dispose lens if it exists
        const lensKey = `lens_${projector.id}`;
        const lens = projectorMeshesRef.current[lensKey];
        if (lens) {
          lens.housing?.dispose();
          lens.glass?.dispose();
          delete projectorMeshesRef.current[lensKey];
        }
        projMesh = null;
      }
      
      if (!projMesh) {
        projMesh = BABYLON.MeshBuilder.CreateBox('projectorBody_' + projector.id, {
          width: bodyW,
          height: bodyH,
          depth: bodyD
        }, scene);
        
        const bodyMat = new BABYLON.StandardMaterial('projBodyMat_' + projector.id, scene);
        const bodyColor = BABYLON.Color3.FromHexString(projector.body_color || '#4a4a4a');
        bodyMat.diffuseColor = bodyColor;
        bodyMat.specularColor = new BABYLON.Color3(0.5, 0.5, 0.5);
        // Add stronger emissive to make the projector body more visible/brighter
        bodyMat.emissiveColor = bodyColor.scale(0.4);
        bodyMat.ambientColor = bodyColor.scale(0.7);
        projMesh.material = bodyMat;
        projMesh.isPickable = true; // Ensure projector body is pickable
        projMesh.metadata = { 
          id: projector.id, 
          type: 'projector',
          bodyDimensions: { w: bodyW, h: bodyH, d: bodyD }
        };
        projectorMeshesRef.current[projector.id] = projMesh;
        
        // White bounding box outline — always visible, purely visual
        projMesh.enableEdgesRendering();
        projMesh.edgesWidth = 3.0;
        projMesh.edgesColor = new BABYLON.Color4(1, 1, 1, 1);
        projMesh.alwaysSelectAsActiveMesh = true;
        
        // Create lens at the front of the projector
        const lensKey = `lens_${projector.id}`;
        const lensRadius = Math.min(bodyW, bodyH) * 0.35; // Lens diameter ~70% of smaller dimension
        const lensDepth = 0.03; // 30mm lens depth
        
        // Lens housing (black ring around the lens)
        const lensHousing = BABYLON.MeshBuilder.CreateCylinder('lensHousing_' + projector.id, {
          diameter: lensRadius * 2.4,
          height: lensDepth * 1.5,
          tessellation: 32
        }, scene);
        lensHousing.rotation.x = Math.PI / 2; // Rotate to face forward (Z axis)
        lensHousing.position = new BABYLON.Vector3(0, 0, bodyD / 2 + lensDepth * 0.5);
        lensHousing.parent = projMesh;
        
        const housingMat = new BABYLON.StandardMaterial('lensHousingMat_' + projector.id, scene);
        housingMat.diffuseColor = new BABYLON.Color3(0.1, 0.1, 0.1);
        housingMat.specularColor = new BABYLON.Color3(0.3, 0.3, 0.3);
        lensHousing.material = housingMat;
        lensHousing.metadata = { id: projector.id, type: 'projector' };
        
        // Glass lens element (slightly reflective/glossy)
        const lensGlass = BABYLON.MeshBuilder.CreateCylinder('lensGlass_' + projector.id, {
          diameter: lensRadius * 2,
          height: lensDepth,
          tessellation: 32
        }, scene);
        lensGlass.rotation.x = Math.PI / 2;
        lensGlass.position = new BABYLON.Vector3(0, 0, bodyD / 2 + lensDepth * 0.8);
        lensGlass.parent = projMesh;
        
        const glassMat = new BABYLON.StandardMaterial('lensGlassMat_' + projector.id, scene);
        glassMat.diffuseColor = new BABYLON.Color3(0.2, 0.25, 0.35); // Slight blue tint like real lens
        glassMat.specularColor = new BABYLON.Color3(1, 1, 1);
        glassMat.specularPower = 64;
        glassMat.emissiveColor = new BABYLON.Color3(0.05, 0.08, 0.12); // Subtle glow
        glassMat.alpha = 0.85; // Slightly transparent
        lensGlass.material = glassMat;
        lensGlass.metadata = { id: projector.id, type: 'projector' };
        
        // Store lens references for cleanup
        projectorMeshesRef.current[lensKey] = { housing: lensHousing, glass: lensGlass };
      } else if (projMesh) {
        // Update body color on existing mesh with brighter emissive
        if (projMesh.material) {
          const bodyColor = BABYLON.Color3.FromHexString(projector.body_color || '#4a4a4a');
          projMesh.material.diffuseColor = bodyColor;
          projMesh.material.emissiveColor = bodyColor.scale(0.4);
          projMesh.material.ambientColor = bodyColor.scale(0.7);
        }
      }
      
      // Create/update frame if show_frame is true
      const frameKey = `frame_${projector.id}`;
      if (projector.show_frame) {
        if (!projectorMeshesRef.current[frameKey]) {
          // Create tube cage frame like professional rigging
          const tubeRadius = 0.015; // 15mm tube diameter
          
          // Use custom frame dimensions if set, otherwise auto-calculate from body + margin
          const fw = (projector.frame_width || projector.spec?.frame_width) ? 
            (projector.frame_width || projector.spec?.frame_width) / 1000 : bodyW + 0.1;
          const fh = (projector.frame_height || projector.spec?.frame_height) ? 
            (projector.frame_height || projector.spec?.frame_height) / 1000 : bodyH + 0.1;
          const fd = (projector.frame_depth || projector.spec?.frame_depth) ? 
            (projector.frame_depth || projector.spec?.frame_depth) / 1000 : bodyD + 0.1;
          
          const frameMat = new BABYLON.StandardMaterial('frameMat_' + projector.id, scene);
          frameMat.diffuseColor = new BABYLON.Color3(0.2, 0.2, 0.2);
          frameMat.specularColor = new BABYLON.Color3(0.3, 0.3, 0.3);
          
          // Create frame tubes - 12 edges of a rectangular box
          const frameParts = [];
          
          // Bottom 4 horizontal tubes
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft1', { height: fw, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.z = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(0, -fh/2, -fd/2);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft2', { height: fw, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.z = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(0, -fh/2, fd/2);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft3', { height: fd, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.x = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(-fw/2, -fh/2, 0);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft4', { height: fd, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.x = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(fw/2, -fh/2, 0);
          
          // Top 4 horizontal tubes
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft5', { height: fw, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.z = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(0, fh/2, -fd/2);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft6', { height: fw, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.z = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(0, fh/2, fd/2);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft7', { height: fd, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.x = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(-fw/2, fh/2, 0);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft8', { height: fd, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].rotation.x = Math.PI / 2;
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(fw/2, fh/2, 0);
          
          // 4 vertical tubes (corners)
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft9', { height: fh, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(-fw/2, 0, -fd/2);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft10', { height: fh, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(fw/2, 0, -fd/2);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft11', { height: fh, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(-fw/2, 0, fd/2);
          
          frameParts.push(BABYLON.MeshBuilder.CreateCylinder('ft12', { height: fh, diameter: tubeRadius * 2 }, scene));
          frameParts[frameParts.length - 1].position = new BABYLON.Vector3(fw/2, 0, fd/2);
          
          // Add corner spheres (joint connectors)
          const corners = [
            [-fw/2, -fh/2, -fd/2], [fw/2, -fh/2, -fd/2], [-fw/2, -fh/2, fd/2], [fw/2, -fh/2, fd/2],
            [-fw/2, fh/2, -fd/2], [fw/2, fh/2, -fd/2], [-fw/2, fh/2, fd/2], [fw/2, fh/2, fd/2]
          ];
          corners.forEach((pos, i) => {
            const sphere = BABYLON.MeshBuilder.CreateSphere('fc' + i, { diameter: tubeRadius * 3 }, scene);
            sphere.position = new BABYLON.Vector3(pos[0], pos[1], pos[2]);
            sphere.material = frameMat;
            frameParts.push(sphere);
          });
          
          // Apply material to all frame parts
          frameParts.forEach(p => { if (!p.material) p.material = frameMat; });
          
          // Merge into single mesh and parent to projector
          const frameMesh = BABYLON.Mesh.MergeMeshes(frameParts, true, true, undefined, false, true);
          frameMesh.name = 'frame_' + projector.id;
          frameMesh.parent = projMesh;
          frameMesh.position = BABYLON.Vector3.Zero();
          // Set metadata so clicking frame also selects the projector
          frameMesh.metadata = { id: projector.id, type: 'projector' };
          projectorMeshesRef.current[frameKey] = frameMesh;
        }
      } else {
        // Remove frame if exists but not needed
        if (projectorMeshesRef.current[frameKey]) {
          projectorMeshesRef.current[frameKey].dispose();
          delete projectorMeshesRef.current[frameKey];
        }
      }
      
      // Create/update clamp if show_clamp is true
      const clampKey = `clamp_${projector.id}`;
      if (projector.show_clamp) {
        if (!projectorMeshesRef.current[clampKey]) {
          const clampMat = new BABYLON.StandardMaterial('clampMat_' + projector.id, scene);
          clampMat.diffuseColor = new BABYLON.Color3(0.15, 0.15, 0.15);
          clampMat.specularColor = new BABYLON.Color3(0.2, 0.2, 0.2);
          
          // Create C-clamp shape
          const clampHeight = 0.1; // 100mm
          const clampWidth = 0.08; // 80mm
          const clampDepth = 0.05; // 50mm
          
          // Main clamp body (C-shape using boxes)
          const clampParts = [];
          
          // Top horizontal bar
          const topBar = BABYLON.MeshBuilder.CreateBox('clampTop', { width: clampWidth, height: 0.02, depth: clampDepth }, scene);
          topBar.position.y = bodyH / 2 + clampHeight;
          topBar.material = clampMat;
          clampParts.push(topBar);
          
          // Vertical stem
          const stem = BABYLON.MeshBuilder.CreateBox('clampStem', { width: 0.02, height: clampHeight, depth: clampDepth }, scene);
          stem.position.y = bodyH / 2 + clampHeight / 2;
          stem.material = clampMat;
          clampParts.push(stem);
          
          // Hook part (for truss)
          const hook = BABYLON.MeshBuilder.CreateTorus('clampHook', { diameter: 0.06, thickness: 0.015, tessellation: 16 }, scene);
          hook.rotation.x = Math.PI / 2;
          hook.position.y = bodyH / 2 + clampHeight + 0.03;
          hook.material = clampMat;
          clampParts.push(hook);
          
          const clampMesh = BABYLON.Mesh.MergeMeshes(clampParts, true, true, undefined, false, true);
          clampMesh.name = 'clamp_' + projector.id;
          clampMesh.parent = projMesh;
          clampMesh.position = BABYLON.Vector3.Zero();
          // Set metadata so clicking clamp also selects the projector
          clampMesh.metadata = { id: projector.id, type: 'projector' };
          projectorMeshesRef.current[clampKey] = clampMesh;
        }
      } else {
        // Remove clamp if exists but not needed
        if (projectorMeshesRef.current[clampKey]) {
          projectorMeshesRef.current[clampKey].dispose();
          delete projectorMeshesRef.current[clampKey];
        }
      }

      // ── PROJECTOR MOUNT (low-poly multi-side stand) ──
      // Lightweight rig: 4-arm cross base + vertical pipe + optional L-fold
      // horizontal arm + mounting plate. The whole rig can be attached to any
      // face of the projector body (top / front / back / left / right) so it
      // works for ceiling, floor-stand, side-wall and back-wall installations.
      // ALL parts merged into a single mesh for cheap 1000+ instance scaling.
      const mountKey = `mount_${projector.id}`;
      const mountHeight = Math.max(0, Math.min(5, parseFloat(projector.mount_height ?? 0.5)));
      const mountLFoldOn = !!projector.mount_lfold_enabled;
      const mountLFoldLen = Math.max(0, Math.min(3, parseFloat(projector.mount_lfold_length ?? 0.5)));
      const mountLFoldDir = projector.mount_lfold_direction || 'forward';
      const mountSide = projector.mount_side || 'top';
      // Signature includes side so changing face triggers a rebuild.
      const mountSig = `${mountSide}|${mountHeight.toFixed(3)}|${mountLFoldOn ? 1 : 0}|${mountLFoldLen.toFixed(3)}|${mountLFoldDir}`;
      const existingMount = projectorMeshesRef.current[mountKey];
      const existingSig = existingMount?.metadata?.mountSig;
      const mountNeedsRebuild = projector.show_mount
        && (!existingMount || existingSig !== mountSig);
      if (existingMount && (mountNeedsRebuild || !projector.show_mount)) {
        try { existingMount.dispose(); } catch (_) { /* noop */ }
        delete projectorMeshesRef.current[mountKey];
      }
      if (projector.show_mount && !projectorMeshesRef.current[mountKey]) {
        const mountMat = new BABYLON.StandardMaterial('mountMat_' + projector.id, scene);
        mountMat.diffuseColor = new BABYLON.Color3(0.85, 0.85, 0.88); // satin white
        mountMat.specularColor = new BABYLON.Color3(0.25, 0.25, 0.25);
        mountMat.emissiveColor = new BABYLON.Color3(0.05, 0.05, 0.05);

        // Build the rig in MOUNT-LOCAL canonical space: cross sits at y=0,
        // pipe extends +Y, L-fold in mount-local ±X / ±Z. After merging we
        // rotate + translate the whole mesh so the cross lands on the chosen
        // body face with the pipe extending OUTWARD from that face.
        const parts = [];
        const armLen = 0.15;
        const armThick = 0.025;
        const armWidth = 0.05;
        const hubY = armThick / 2;
        for (let i = 0; i < 4; i++) {
          const arm = BABYLON.MeshBuilder.CreateBox('mountArm' + i, {
            width: armLen, height: armThick, depth: armWidth,
          }, scene);
          arm.rotation.y = (Math.PI / 2) * i; // 0°, 90°, 180°, 270°
          arm.position = new BABYLON.Vector3(
            Math.cos(arm.rotation.y) * (armLen / 2),
            hubY,
            -Math.sin(arm.rotation.y) * (armLen / 2)
          );
          arm.material = mountMat;
          parts.push(arm);
        }
        const hub = BABYLON.MeshBuilder.CreateBox('mountHub', {
          width: 0.06, height: 0.04, depth: 0.06,
        }, scene);
        hub.position = new BABYLON.Vector3(0, hubY + 0.005, 0);
        hub.material = mountMat;
        parts.push(hub);
        const swivel = BABYLON.MeshBuilder.CreateBox('mountSwivel', {
          width: 0.05, height: 0.04, depth: 0.05,
        }, scene);
        swivel.position = new BABYLON.Vector3(0, hubY + 0.045, 0);
        swivel.material = mountMat;
        parts.push(swivel);
        const pipeSide = 0.04;
        const pipeBaseY = hubY + 0.07;
        let topJointPos = new BABYLON.Vector3(0, pipeBaseY, 0);
        if (mountHeight > 0.001) {
          const pipe = BABYLON.MeshBuilder.CreateBox('mountPipe', {
            width: pipeSide, height: mountHeight, depth: pipeSide,
          }, scene);
          pipe.position = new BABYLON.Vector3(0, pipeBaseY + mountHeight / 2, 0);
          pipe.material = mountMat;
          parts.push(pipe);
          topJointPos = new BABYLON.Vector3(0, pipeBaseY + mountHeight, 0);
        }
        let plateCentre = topJointPos.clone();
        if (mountLFoldOn && mountLFoldLen > 0.001) {
          const dirMap = {
            forward:  new BABYLON.Vector3(0, 0,  1),
            backward: new BABYLON.Vector3(0, 0, -1),
            left:     new BABYLON.Vector3(-1, 0, 0),
            right:    new BABYLON.Vector3( 1, 0, 0),
          };
          const dir = (dirMap[mountLFoldDir] || dirMap.forward).clone();
          const elbow = BABYLON.MeshBuilder.CreateBox('mountElbow', {
            width: pipeSide + 0.01, height: pipeSide + 0.01, depth: pipeSide + 0.01,
          }, scene);
          elbow.position = topJointPos.clone();
          elbow.material = mountMat;
          parts.push(elbow);
          const arm = BABYLON.MeshBuilder.CreateBox('mountLFoldArm', {
            width: pipeSide, height: pipeSide, depth: mountLFoldLen,
          }, scene);
          if (mountLFoldDir === 'left' || mountLFoldDir === 'right') {
            arm.rotation.y = Math.PI / 2;
          }
          arm.position = topJointPos.add(dir.scale(mountLFoldLen / 2));
          arm.material = mountMat;
          parts.push(arm);
          plateCentre = topJointPos.add(dir.scale(mountLFoldLen));
        }
        const plateW = 0.18, plateH = 0.14, plateThk = 0.012;
        let plate;
        if (mountLFoldOn && mountLFoldLen > 0.001) {
          if (mountLFoldDir === 'left' || mountLFoldDir === 'right') {
            plate = BABYLON.MeshBuilder.CreateBox('mountPlate', {
              width: plateThk, height: plateW, depth: plateH,
            }, scene);
          } else {
            plate = BABYLON.MeshBuilder.CreateBox('mountPlate', {
              width: plateW, height: plateH, depth: plateThk,
            }, scene);
          }
          const offsetMap = {
            forward:  new BABYLON.Vector3(0, 0,  plateThk / 2),
            backward: new BABYLON.Vector3(0, 0, -plateThk / 2),
            left:     new BABYLON.Vector3(-plateThk / 2, 0, 0),
            right:    new BABYLON.Vector3( plateThk / 2, 0, 0),
          };
          const off = offsetMap[mountLFoldDir] || offsetMap.forward;
          plate.position = plateCentre.add(off);
        } else {
          plate = BABYLON.MeshBuilder.CreateBox('mountPlate', {
            width: plateW, height: plateThk, depth: plateH,
          }, scene);
          plate.position = new BABYLON.Vector3(plateCentre.x, plateCentre.y + plateThk / 2, plateCentre.z);
        }
        plate.material = mountMat;
        parts.push(plate);

        const mountMesh = BABYLON.Mesh.MergeMeshes(parts, true, true, undefined, false, true);
        if (mountMesh) {
          mountMesh.name = 'mount_' + projector.id;
          mountMesh.parent = projMesh;
          // Place the cross on the chosen face of the projector body and
          // rotate the whole rig so the pipe extends outward. The mount
          // builds canonically with cross at local-y=0 and pipe along +Y,
          // so each side is just a translate + rotate of that frame.
          const sideTransform = {
            top:    { pos: new BABYLON.Vector3(0,  bodyH / 2, 0),  rot: new BABYLON.Vector3(0, 0, 0) },
            bottom: { pos: new BABYLON.Vector3(0, -bodyH / 2, 0),  rot: new BABYLON.Vector3(Math.PI, 0, 0) },
            front:  { pos: new BABYLON.Vector3(0, 0,  bodyD / 2),  rot: new BABYLON.Vector3(-Math.PI / 2, 0, 0) },
            back:   { pos: new BABYLON.Vector3(0, 0, -bodyD / 2),  rot: new BABYLON.Vector3( Math.PI / 2, 0, 0) },
            left:   { pos: new BABYLON.Vector3(-bodyW / 2, 0, 0),  rot: new BABYLON.Vector3(0, 0,  Math.PI / 2) },
            right:  { pos: new BABYLON.Vector3( bodyW / 2, 0, 0),  rot: new BABYLON.Vector3(0, 0, -Math.PI / 2) },
          };
          const xform = sideTransform[mountSide] || sideTransform.top;
          mountMesh.position = xform.pos;
          mountMesh.rotation = xform.rot;
          mountMesh.isPickable = true;
          // Selecting the mount selects the projector — same UX as frame/clamp.
          mountMesh.metadata = { id: projector.id, type: 'projector', mountSig };
          projectorMeshesRef.current[mountKey] = mountMesh;
        }
      }

      // Update projector mesh position and rotation (only if not dragging)
      if (!isDraggingRef.current) {
        projMesh.position = new BABYLON.Vector3(
          projector.position.x,
          projector.position.y,
          projector.position.z
        );
        // Clear rotationQuaternion to use Euler angles
        projMesh.rotationQuaternion = null;
        projMesh.rotation = new BABYLON.Vector3(rotX, rotY, rotZ);

        // Real-time selection-outline sync for projector input edits.
        if (
          selectedObjectIdRef.current === projector.id &&
          refreshSelectionBoxRef.current
        ) {
          refreshSelectionBoxRef.current(projMesh);
        }
      }
      
      // Update body color if changed
      if (projMesh.material) {
        projMesh.material.diffuseColor = BABYLON.Color3.FromHexString(projector.body_color || '#2a2a2a');
      }

      // ── PROJECTOR STACKING (virtual brightness boost — visual ghosts only) ──
      // Renders CLONES of the main projector body (with lens, frame, clamp,
      // and same material/color) at top/bottom/left/right offsets in the
      // projector's LOCAL frame. NO beams, NO spotlights, NO projection
      // textures — projection logic is untouched. Brightness/lux multiply
      // by stack count via separate code paths.
      {
        const stk = projector.stacking || {};
        const stkEnabled = !!stk.enabled;
        const stkTop = Math.max(0, Math.min(10, parseInt(stk.top) || 0));
        const stkBot = Math.max(0, Math.min(10, parseInt(stk.bottom) || 0));
        const stkLeft = Math.max(0, Math.min(10, parseInt(stk.left) || 0));
        const stkRight = Math.max(0, Math.min(10, parseInt(stk.right) || 0));
        const stkSpacing = Math.max(0, Math.min(2, parseFloat(stk.spacing ?? 0.5)));

        // Always rebuild stack ghosts on every projector update so visual
        // changes (body color, frame, clamp, lens) propagate immediately.
        const prev = stackGhostsRef.current[projector.id];
        if (prev?.meshes) prev.meshes.forEach(m => { try { m.dispose(); } catch (e) {} });
        delete stackGhostsRef.current[projector.id];

        if (stkEnabled && (stkTop + stkBot + stkLeft + stkRight) > 0) {
          const ghosts = [];
          // Optional ghost-only body color override (stack_color). When unset,
          // ghosts inherit the original projector's material so they match.
          const overrideColor = stk.stack_color || null;
          let ghostBodyMat = null;
          if (overrideColor) {
            const ghostMatKey = `stackGhostMat_${projector.id}`;
            ghostBodyMat = projectorMeshesRef.current[ghostMatKey];
            const c = BABYLON.Color3.FromHexString(overrideColor);
            if (!ghostBodyMat) {
              ghostBodyMat = new BABYLON.PBRMaterial(ghostMatKey, scene);
              ghostBodyMat.metallic = 0.4;
              ghostBodyMat.roughness = 0.6;
              ghostBodyMat.environmentIntensity = 1.0;
              projectorMeshesRef.current[ghostMatKey] = ghostBodyMat;
            }
            ghostBodyMat.albedoColor = c;
          }

          const buildOne = (localOffset, idx, dirLabel) => {
            // Clone the entire projector mesh hierarchy (body + lens housing +
            // lens glass + frame + clamp). Cloned meshes share materials with
            // the main projector so any color/style change propagates.
            const ghost = projMesh.clone(`stackGhost_${projector.id}_${dirLabel}_${idx}`, null, false);
            if (!ghost) return;
            ghost.parent = projMesh;
            ghost.position = localOffset;
            ghost.rotationQuaternion = null;
            ghost.rotation = BABYLON.Vector3.Zero(); // Inherit parent rotation; no extra rotation
            ghost.scaling = new BABYLON.Vector3(1, 1, 1);

            // Edge handling for the ghost stack:
            //   • Body (the cloned `projMesh` box) gets the SAME white box
            //     outline as the original projector — that's the only line
            //     the user wants on stack ghosts.
            //   • Every descendant (lens housing, lens glass, frame pipes,
            //     clamps, …) MUST NOT have edges — Babylon's mesh.clone()
            //     happens to copy the parent's `edgesRenderer` flag onto
            //     children when the source had one, so we explicitly
            //     disable edges for every child node.
            try {
              if (typeof ghost.enableEdgesRendering === 'function') {
                ghost.enableEdgesRendering();
                ghost.edgesWidth = 3.0;
                ghost.edgesColor = new BABYLON.Color4(1, 1, 1, 1);
              }
            } catch (_e) { /* not all node types support edges */ }

            // Walk descendants: tag as ghost / non-pickable, strip any edge
            // renderer they may have inherited, and (optionally) override
            // body color via stack_color. We deliberately use
            // `getDescendants(false)` so we get the FULL subtree, not just
            // direct children — frame pipes are nested under intermediate
            // helper nodes.
            const tagGhost = (n) => {
              if (n === ghost) return; // root already handled above
              if (n.isPickable !== undefined) n.isPickable = false;
              n.metadata = { id: projector.id, type: 'projector_stack_ghost', isStackGhost: true };
              try {
                if (n.edgesRenderer && typeof n.disableEdgesRendering === 'function') {
                  n.disableEdgesRendering();
                }
              } catch (_e) { /* ignore */ }
              if (overrideColor && ghostBodyMat && n.material !== undefined) {
                // We already coloured the body root above; do NOT recolour
                // child meshes (lens / frame / clamp keep their materials).
                // Intentionally no-op here.
              }
            };
            // Tag root metadata + colour swap (root only)
            ghost.isPickable = false;
            ghost.metadata = { id: projector.id, type: 'projector_stack_ghost', isStackGhost: true };
            if (overrideColor && ghostBodyMat && ghost.material !== undefined) {
              ghost.material = ghostBodyMat;
            }
            const allKids = ghost.getDescendants ? ghost.getDescendants(false) : [];
            allKids.forEach(tagGhost);

            ghosts.push(ghost);
          };

          // Local frame offsets: +Y top, -Y bottom, -X left, +X right.
          // Spacing = center-to-center distance per stack level.
          for (let i = 0; i < stkTop; i++)   buildOne(new BABYLON.Vector3(0,  (i + 1) * stkSpacing, 0), i, 'top');
          for (let i = 0; i < stkBot; i++)   buildOne(new BABYLON.Vector3(0, -(i + 1) * stkSpacing, 0), i, 'bot');
          for (let i = 0; i < stkLeft; i++)  buildOne(new BABYLON.Vector3(-(i + 1) * stkSpacing, 0, 0), i, 'left');
          for (let i = 0; i < stkRight; i++) buildOne(new BABYLON.Vector3( (i + 1) * stkSpacing, 0, 0), i, 'right');

          stackGhostsRef.current[projector.id] = { meshes: ghosts };
        }
      }

      // ============================================
      // 90-DEGREE LENS — L-shape (pipe1 → joint → pipe2 → glass dome)
      // ============================================
      const lens90PivotKey = 'lens90pivot_' + projector.id;
      const lens90PipeKey = 'lens90pipe1v2_' + projector.id;       // v2 = 50% thicker
      const lens90BarrelKey = 'lens90pipe2v2_' + projector.id;
      const lens90JointKey = 'lens90jointv2_' + projector.id;
      const lens90GlassKey = 'lens90glassv3_' + projector.id;
      
      if (projector.lensType === 'lens90') {
        // Hide standard lens
        const stdLensKey = `lens_${projector.id}`;
        if (projectorMeshesRef.current[stdLensKey]) {
          if (projectorMeshesRef.current[stdLensKey].housing) projectorMeshesRef.current[stdLensKey].housing.setEnabled(false);
          if (projectorMeshesRef.current[stdLensKey].glass) projectorMeshesRef.current[stdLensKey].glass.setEnabled(false);
        }
        
        // PBR pipe material (metallic blue)
        const lens90MatKey = 'lens90mat_' + projector.id;
        let pipeMat = projectorMeshesRef.current[lens90MatKey];
        if (!pipeMat || !(pipeMat instanceof BABYLON.PBRMaterial)) {
          if (pipeMat && pipeMat.dispose) pipeMat.dispose();
          pipeMat = new BABYLON.PBRMaterial('lens90PipeMat_' + projector.id, scene);
          pipeMat.albedoColor = new BABYLON.Color3(0.1, 0.4, 1.0);
          pipeMat.metallic = 0.6;
          pipeMat.roughness = 0.4;
          projectorMeshesRef.current[lens90MatKey] = pipeMat;
        }
        
        // PBR glass material (translucent lens)
        const lens90GlassMatKey = 'lens90glassmat_' + projector.id;
        let glassMat = projectorMeshesRef.current[lens90GlassMatKey];
        if (!glassMat || !(glassMat instanceof BABYLON.PBRMaterial)) {
          if (glassMat && glassMat.dispose) glassMat.dispose();
          glassMat = new BABYLON.PBRMaterial('lens90GlassMat_' + projector.id, scene);
          glassMat.albedoColor = new BABYLON.Color3(0.1, 0.3, 0.8);
          glassMat.emissiveColor = new BABYLON.Color3(0.05, 0.1, 0.3);
          glassMat.metallic = 0;
          glassMat.roughness = 0.1;
          glassMat.alpha = 0.6;
          projectorMeshesRef.current[lens90GlassMatKey] = glassMat;
        }
        
        // --- lensPivot: FIXED at projector front center ---
        let lensPivot = projectorMeshesRef.current[lens90PivotKey];
        if (!lensPivot) {
          lensPivot = new BABYLON.TransformNode('lensPivot_' + projector.id, scene);
          lensPivot.parent = projMesh;
          projectorMeshesRef.current[lens90PivotKey] = lensPivot;
        }
        lensPivot.position = new BABYLON.Vector3(0, 0, bodyD / 2 + 0.01);
        
        // --- pipe1: 300mm forward (along lensPivot local Z), 100mm diameter ---
        let pipe1 = projectorMeshesRef.current[lens90PipeKey];
        if (!pipe1) {
          pipe1 = BABYLON.MeshBuilder.CreateCylinder('lens90Pipe1v2_' + projector.id, {
            height: 0.30,
            diameter: 0.15,
            tessellation: 24
          }, scene);
          pipe1.material = pipeMat;
          pipe1.isPickable = true;
          pipe1.metadata = { id: projector.id, type: 'projector' };
          pipe1.parent = lensPivot;
          pipe1.rotation.x = Math.PI / 2; // align cylinder along Z
          pipe1.position.z = 0.15; // half of 300mm
          projectorMeshesRef.current[lens90PipeKey] = pipe1;
        } else {
          pipe1.material = pipeMat;
        }
        
        // --- joint: connector collar at pipe1/pipe2 junction ---
        let joint = projectorMeshesRef.current[lens90JointKey];
        if (!joint) {
          joint = BABYLON.MeshBuilder.CreateCylinder('lens90Jointv2_' + projector.id, {
            height: 0.12,
            diameter: 0.21,
            tessellation: 24
          }, scene);
          joint.material = pipeMat;
          joint.isPickable = true;
          joint.metadata = { id: projector.id, type: 'projector' };
          joint.parent = pipe1;
          // Place at end of pipe1 (y=0.15 in pipe1 local = tip)
          joint.position.y = 0.15;
          projectorMeshesRef.current[lens90JointKey] = joint;
        } else {
          joint.material = pipeMat;
        }
        
        // --- pipe2: 200mm (direction depends on rotation), 100mm diameter ---
        let pipe2 = projectorMeshesRef.current[lens90BarrelKey];
        if (!pipe2) {
          pipe2 = BABYLON.MeshBuilder.CreateCylinder('lens90Pipe2v2_' + projector.id, {
            height: 0.20,
            diameter: 0.15,
            tessellation: 24
          }, scene);
          pipe2.material = pipeMat;
          pipe2.isPickable = true;
          pipe2.metadata = { id: projector.id, type: 'projector' };
          pipe2.parent = pipe1;
          projectorMeshesRef.current[lens90BarrelKey] = pipe2;
        } else {
          pipe2.material = pipeMat;
        }
        
        // --- lensGlass: half-sphere dome at pipe2 tip ---
        let lensGlass = projectorMeshesRef.current[lens90GlassKey];
        if (!lensGlass) {
          lensGlass = BABYLON.MeshBuilder.CreateSphere('lens90Glassv3_' + projector.id, {
            diameter: 0.24,
            slice: 0.5,
            segments: 20
          }, scene);
          lensGlass.material = glassMat;
          lensGlass.isPickable = true;
          lensGlass.metadata = { id: projector.id, type: 'projector' };
          lensGlass.parent = pipe2;
          // Position at end of pipe2 (y=0.10 = half of 0.20 height)
          lensGlass.position.y = 0.10;
          // Face outward (flip the dome)
          lensGlass.rotation.x = Math.PI;
          projectorMeshesRef.current[lens90GlassKey] = lensGlass;
        } else {
          lensGlass.material = glassMat;
        }
        
        // Position pipe2 at end of pipe1, rotated per direction
        // pipe1 is a cylinder along Y (default), rotated X=PI/2 to align Z.
        // Positions are in pipe1's child space (pre-rotation).
        // pipe1 Rx(PI/2): local Y→parent Z, local Z→parent -Y, local X→parent X
        // So to reach pipe1 tip (y=0.15 local) and offset in exit direction:
        const rot90 = projector.lens90Rotation || 'right';
        pipe2.rotationQuaternion = null;
        switch (rot90) {
          case 'top':
            pipe2.position = new BABYLON.Vector3(0, 0.15, -0.10);
            pipe2.rotation = new BABYLON.Vector3(-Math.PI / 2, 0, 0);
            break;
          case 'bottom':
            pipe2.position = new BABYLON.Vector3(0, 0.15, 0.10);
            pipe2.rotation = new BABYLON.Vector3(Math.PI / 2, 0, 0);
            break;
          case 'right':
            pipe2.position = new BABYLON.Vector3(0.10, 0.15, 0);
            pipe2.rotation = new BABYLON.Vector3(0, 0, -Math.PI / 2);
            break;
          case 'left':
            pipe2.position = new BABYLON.Vector3(-0.10, 0.15, 0);
            pipe2.rotation = new BABYLON.Vector3(0, 0, Math.PI / 2);
            break;
          default:
            pipe2.position = new BABYLON.Vector3(0.10, 0.15, 0);
            pipe2.rotation = new BABYLON.Vector3(0, 0, -Math.PI / 2);
            break;
        }
        
        // No rotation on lensPivot — pipe2 direction handles it
        lensPivot.rotationQuaternion = null;
        lensPivot.rotation = BABYLON.Vector3.Zero();
        
      } else if (projector.lensType === 'topBottom') {
        // ===========================================================
        // TOP/BOTTOM PERISCOPE LENS — U-shape (90°-lens style pipes)
        // Path: lensPivot at body FRONT face → pipe1 forward → joint
        //       → pipe2 vertical (UP for top mount, DOWN for bottom)
        //       → joint → pipe3 backward over the body → lens cube
        //       at the back end facing BACKWARD.
        // ===========================================================
        const tbPivotKey  = 'tbPivot_' + projector.id;
        const tbPipe1Key  = 'tbPipe1v2_' + projector.id;  // forward (v2 = thicker)
        const tbJoint1Key = 'tbJoint1v2_'+ projector.id;
        const tbPipe2Key  = 'tbPipe2v2_' + projector.id;
        const tbJoint2Key = 'tbJoint2v2_'+ projector.id;
        const tbPipe3Key  = 'tbPipe3v2_' + projector.id;
        const tbCubeKey   = 'tbCubev2_'  + projector.id;
        const tbGlassKey  = 'tbGlassv3_' + projector.id;
        const tbMatKey    = 'tbMat_'   + projector.id;
        const tbGlassMatKey = 'tbGlassMat_' + projector.id;

        // Dispose old (v1, v2) meshes — pre-50%-thicker geometry & half-dome glass
        ['tbPipe1_' + projector.id, 'tbJoint1_' + projector.id,
         'tbPipe2_' + projector.id, 'tbJoint2_' + projector.id,
         'tbPipe3_' + projector.id, 'tbCube_' + projector.id, 'tbGlass_' + projector.id,
         'tbGlassv2_' + projector.id]
        .forEach(k => {
          const m = projectorMeshesRef.current[k];
          if (m) {
            try { m.dispose(); } catch (_) { /* noop */ }
            delete projectorMeshesRef.current[k];
          }
        });

        // Hide standard front lens — periscope replaces the optical path
        const stdLensKey = `lens_${projector.id}`;
        if (projectorMeshesRef.current[stdLensKey]) {
          if (projectorMeshesRef.current[stdLensKey].housing) projectorMeshesRef.current[stdLensKey].housing.setEnabled(false);
          if (projectorMeshesRef.current[stdLensKey].glass)   projectorMeshesRef.current[stdLensKey].glass.setEnabled(false);
        }

        // Remove leftover lens90 parts (v1 + v2 + v3)
        [lens90PivotKey, lens90PipeKey, lens90BarrelKey, lens90JointKey, lens90GlassKey,
         'lens90pipe1_' + projector.id, 'lens90pipe2_' + projector.id,
         'lens90joint_' + projector.id, 'lens90glass_' + projector.id,
         'lens90glassv2_' + projector.id,
         'lens90mat_' + projector.id, 'lens90glassmat_' + projector.id].forEach(key => {
          if (projectorMeshesRef.current[key]) {
            if (projectorMeshesRef.current[key].dispose) projectorMeshesRef.current[key].dispose();
            delete projectorMeshesRef.current[key];
          }
        });
        // Remove leftover old box-style topBottom parts (tbArm/tbHoriz) + UST parts
        ['tbArm_' + projector.id, 'tbHoriz_' + projector.id,
         'ustHousing_' + projector.id, 'ustBarrel_' + projector.id, 'ustGlass_' + projector.id,
         'ustNeckv2_' + projector.id, 'ustBarrelv2_' + projector.id, 'ustGlassv2_' + projector.id,
         'ustNeckv3_' + projector.id, 'ustBarrelv3_' + projector.id, 'ustGlassv3_' + projector.id,
         'ustNeckv4_' + projector.id, 'ustBarrelv4_' + projector.id, 'ustGlassv4_' + projector.id,
         'ustMat_' + projector.id, 'ustGlassMat_' + projector.id].forEach(key => {
          if (projectorMeshesRef.current[key]) {
            if (projectorMeshesRef.current[key].dispose) projectorMeshesRef.current[key].dispose();
            delete projectorMeshesRef.current[key];
          }
        });

        // PBR pipe material — metallic blue (matches 90° lens aesthetic)
        let tbMat = projectorMeshesRef.current[tbMatKey];
        if (!tbMat || !(tbMat instanceof BABYLON.PBRMaterial)) {
          if (tbMat && tbMat.dispose) tbMat.dispose();
          tbMat = new BABYLON.PBRMaterial('tbMat_' + projector.id, scene);
          tbMat.albedoColor = new BABYLON.Color3(0.1, 0.4, 1.0);
          tbMat.metallic = 0.6;
          tbMat.roughness = 0.4;
          projectorMeshesRef.current[tbMatKey] = tbMat;
        }
        // Glass material — translucent blue lens
        let tbGlassMat = projectorMeshesRef.current[tbGlassMatKey];
        if (!tbGlassMat || !(tbGlassMat instanceof BABYLON.PBRMaterial)) {
          if (tbGlassMat && tbGlassMat.dispose) tbGlassMat.dispose();
          tbGlassMat = new BABYLON.PBRMaterial('tbGlassMat_' + projector.id, scene);
          tbGlassMat.albedoColor = new BABYLON.Color3(0.1, 0.3, 0.8);
          tbGlassMat.emissiveColor = new BABYLON.Color3(0.05, 0.1, 0.3);
          tbGlassMat.metallic = 0;
          tbGlassMat.roughness = 0.1;
          tbGlassMat.alpha = 0.6;
          projectorMeshesRef.current[tbGlassMatKey] = tbGlassMat;
        }

        const dir = projector.topBottomDirection || 'top';
        const verticalSign = (dir === 'bottom') ? -1 : 1;
        const halfD = (bodyD || 0.60) / 2;

        const pipeDia   = 0.15;            // pipe cross-section (50% thicker)
        const pipe1L    = 0.20;            // forward pipe length (out from front face)
        const pipe2L    = 0.30;            // vertical pipe length (riser)
        // Horizontal back-pipe: just long enough so the lens cube ends up
        // over the BACK half of the body (compact periscope, like Epson ELPLX02S)
        const pipe3L    = ((bodyD || 0.60) * 0.55 + pipe1L) * 0.49;  // trim 30% more (0.7 * 0.7)
        const cubeSize  = 0.27;            // lens cube (50% bigger)
        const jointDia  = 0.21;            // joint collar (50% thicker)

        // Dispose any obsolete pivot from earlier code paths
        const obsoletePivot = projectorMeshesRef.current[tbPivotKey];
        if (obsoletePivot) {
          try { obsoletePivot.dispose(); } catch (_) { /* noop */ }
          delete projectorMeshesRef.current[tbPivotKey];
        }

        // --- pipe1: forward cylinder (out the front lens, like 90° lens) ---
        const pivotZ = halfD + 0.01;  // anchor point at front face of body
        let pipe1 = projectorMeshesRef.current[tbPipe1Key];
        if (!pipe1) {
          pipe1 = BABYLON.MeshBuilder.CreateCylinder('tbPipe1_' + projector.id, {
            height: pipe1L, diameter: pipeDia, tessellation: 24
          }, scene);
          pipe1.material = tbMat;
          pipe1.isPickable = true;
          pipe1.metadata = { id: projector.id, type: 'projector' };
          pipe1.parent = projMesh;
          projectorMeshesRef.current[tbPipe1Key] = pipe1;
        } else {
          pipe1.material = tbMat;
        }
        pipe1.rotation = new BABYLON.Vector3(Math.PI / 2, 0, 0);
        pipe1.position = new BABYLON.Vector3(0, 0, pivotZ + pipe1L / 2);

        // --- joint1: collar at bend 1 (forward → vertical) ---
        let joint1 = projectorMeshesRef.current[tbJoint1Key];
        if (!joint1) {
          joint1 = BABYLON.MeshBuilder.CreateCylinder('tbJoint1v2_' + projector.id, {
            height: 0.12, diameter: jointDia, tessellation: 24
          }, scene);
          joint1.material = tbMat;
          joint1.isPickable = true;
          joint1.metadata = { id: projector.id, type: 'projector' };
          joint1.parent = projMesh;
          projectorMeshesRef.current[tbJoint1Key] = joint1;
        } else {
          joint1.material = tbMat;
        }
        joint1.rotation = BABYLON.Vector3.Zero();
        joint1.position = new BABYLON.Vector3(0, 0, pivotZ + pipe1L);

        // --- pipe2: vertical cylinder (UP for top mount, DOWN for bottom) ---
        let pipe2 = projectorMeshesRef.current[tbPipe2Key];
        if (!pipe2) {
          pipe2 = BABYLON.MeshBuilder.CreateCylinder('tbPipe2_' + projector.id, {
            height: pipe2L, diameter: pipeDia, tessellation: 24
          }, scene);
          pipe2.material = tbMat;
          pipe2.isPickable = true;
          pipe2.metadata = { id: projector.id, type: 'projector' };
          pipe2.parent = projMesh;
          projectorMeshesRef.current[tbPipe2Key] = pipe2;
        } else {
          pipe2.material = tbMat;
        }
        pipe2.rotation = BABYLON.Vector3.Zero();
        pipe2.position = new BABYLON.Vector3(0, verticalSign * (pipe2L / 2), pivotZ + pipe1L);

        // --- joint2: collar at bend 2 (vertical → backward) ---
        let joint2 = projectorMeshesRef.current[tbJoint2Key];
        if (!joint2) {
          joint2 = BABYLON.MeshBuilder.CreateCylinder('tbJoint2v2_' + projector.id, {
            height: 0.12, diameter: jointDia, tessellation: 24
          }, scene);
          joint2.material = tbMat;
          joint2.isPickable = true;
          joint2.metadata = { id: projector.id, type: 'projector' };
          joint2.parent = projMesh;
          projectorMeshesRef.current[tbJoint2Key] = joint2;
        } else {
          joint2.material = tbMat;
        }
        joint2.rotation = new BABYLON.Vector3(Math.PI / 2, 0, 0);
        joint2.position = new BABYLON.Vector3(0, verticalSign * pipe2L, pivotZ + pipe1L);

        // --- pipe3: backward cylinder (over the body) ---
        // Track baked height in metadata; rebuild if formula changed.
        let pipe3 = projectorMeshesRef.current[tbPipe3Key];
        if (pipe3 && pipe3.metadata?.bakedHeight !== pipe3L) {
          try { pipe3.dispose(); } catch (_) { /* noop */ }
          delete projectorMeshesRef.current[tbPipe3Key];
          pipe3 = null;
        }
        if (!pipe3) {
          pipe3 = BABYLON.MeshBuilder.CreateCylinder('tbPipe3_' + projector.id, {
            height: pipe3L, diameter: pipeDia, tessellation: 24
          }, scene);
          pipe3.material = tbMat;
          pipe3.isPickable = true;
          pipe3.metadata = { id: projector.id, type: 'projector', bakedHeight: pipe3L };
          pipe3.parent = projMesh;
          projectorMeshesRef.current[tbPipe3Key] = pipe3;
        } else {
          pipe3.material = tbMat;
        }
        pipe3.rotation = new BABYLON.Vector3(Math.PI / 2, 0, 0);
        pipe3.position = new BABYLON.Vector3(
          0,
          verticalSign * pipe2L,
          pivotZ + pipe1L - pipe3L / 2
        );

        // --- Lens cube at end of pipe3, facing BACKWARD ---
        let cube = projectorMeshesRef.current[tbCubeKey];
        if (!cube) {
          cube = BABYLON.MeshBuilder.CreateBox('tbCube_' + projector.id, {
            width: cubeSize, height: cubeSize, depth: cubeSize
          }, scene);
          cube.material = tbMat;
          cube.isPickable = true;
          cube.metadata = { id: projector.id, type: 'projector' };
          cube.parent = projMesh;
          projectorMeshesRef.current[tbCubeKey] = cube;
        } else {
          cube.material = tbMat;
        }
        cube.rotation = BABYLON.Vector3.Zero();
        cube.position = new BABYLON.Vector3(
          0,
          verticalSign * pipe2L,
          pivotZ + pipe1L - pipe3L - cubeSize / 2
        );

        // --- Glass half-sphere on the BACK face of the lens cube ---
        let glass = projectorMeshesRef.current[tbGlassKey];
        if (!glass) {
          glass = BABYLON.MeshBuilder.CreateSphere('tbGlassv3_' + projector.id, {
            diameter: cubeSize * 0.85,
            slice: 0.5,        // half hemisphere
            segments: 24
          }, scene);
          glass.material = tbGlassMat;
          glass.isPickable = false;
          glass.metadata = { id: projector.id, type: 'projector' };
          glass.parent = cube;
          projectorMeshesRef.current[tbGlassKey] = glass;
        } else {
          glass.material = tbGlassMat;
        }
        // Rotate 90° around X so the dome bulges BACKWARD (-Z) instead of UP.
        // Flat face sits flush against the back face of the lens cube.
        // +20mm body-local Z nudge (cumulative 10+10).
        glass.rotation = new BABYLON.Vector3(-Math.PI / 2, 0, 0);
        glass.position = new BABYLON.Vector3(0, 0, -cubeSize / 2 + 0.02);

      } else if (projector.lensType === 'ust') {
        // ===========================================================
        // UST LENS — straight rear-throw lens (Panasonic ET-DLE055 style).
        // Cylindrical lens barrel mounted on the BACK face of the projector;
        // beam exits from the lens tip going BACKWARD. No folds, no periscope.
        // ===========================================================
        const ustNeckKey   = 'ustNeckv4_'   + projector.id;   // square neck (body-side)
        const ustBarrelKey = 'ustBarrelv4_' + projector.id;   // TRIANGULAR-PRISM barrel (diagonal cut)
        const ustGlassKey  = 'ustGlassv4_'  + projector.id;   // 45° tilted mirror plate
        const ustMatKey    = 'ustMat_'      + projector.id;
        const ustGlassMatKey = 'ustGlassMat_' + projector.id;

        // Hide standard front lens — UST replaces it entirely
        const stdLensKey = `lens_${projector.id}`;
        if (projectorMeshesRef.current[stdLensKey]) {
          if (projectorMeshesRef.current[stdLensKey].housing) projectorMeshesRef.current[stdLensKey].housing.setEnabled(false);
          if (projectorMeshesRef.current[stdLensKey].glass)   projectorMeshesRef.current[stdLensKey].glass.setEnabled(false);
        }

        // Dispose v1 + v2 + v3 UST parts (old shapes) + leftover lens90/topBottom parts
        ['ustHousing_' + projector.id, 'ustBarrel_' + projector.id, 'ustGlass_' + projector.id,
         'ustNeckv2_' + projector.id, 'ustBarrelv2_' + projector.id, 'ustGlassv2_' + projector.id,
         'ustNeckv3_' + projector.id, 'ustBarrelv3_' + projector.id, 'ustGlassv3_' + projector.id,
         lens90PivotKey, lens90PipeKey, lens90BarrelKey, lens90JointKey, lens90GlassKey,
         'lens90pipe1_' + projector.id, 'lens90pipe2_' + projector.id,
         'lens90joint_' + projector.id, 'lens90glass_' + projector.id, 'lens90glassv2_' + projector.id,
         'lens90mat_' + projector.id, 'lens90glassmat_' + projector.id,
         'tbPivot_' + projector.id, 'tbArm_' + projector.id, 'tbHoriz_' + projector.id,
         'tbPipe1_' + projector.id, 'tbJoint1_' + projector.id, 'tbPipe2_' + projector.id,
         'tbJoint2_' + projector.id, 'tbPipe3_' + projector.id, 'tbCube_' + projector.id, 'tbGlass_' + projector.id,
         'tbPipe1v2_' + projector.id, 'tbJoint1v2_' + projector.id, 'tbPipe2v2_' + projector.id,
         'tbJoint2v2_' + projector.id, 'tbPipe3v2_' + projector.id, 'tbCubev2_' + projector.id, 'tbGlassv2_' + projector.id,
         'tbMat_' + projector.id, 'tbGlassMat_' + projector.id]
        .forEach(k => {
          const m = projectorMeshesRef.current[k];
          if (m) {
            try { m.dispose(); } catch (_) { /* noop */ }
            delete projectorMeshesRef.current[k];
          }
        });

        // Materials — matte black housing + green-tinted glass
        let ustMat = projectorMeshesRef.current[ustMatKey];
        if (!ustMat || !(ustMat instanceof BABYLON.PBRMaterial)) {
          if (ustMat && ustMat.dispose) ustMat.dispose();
          ustMat = new BABYLON.PBRMaterial('ustMat_' + projector.id, scene);
          ustMat.albedoColor = new BABYLON.Color3(0.06, 0.06, 0.08);
          ustMat.metallic = 0.3;
          ustMat.roughness = 0.55;
          projectorMeshesRef.current[ustMatKey] = ustMat;
        }
        let ustGlassMat = projectorMeshesRef.current[ustGlassMatKey];
        if (!ustGlassMat || !(ustGlassMat instanceof BABYLON.PBRMaterial)) {
          if (ustGlassMat && ustGlassMat.dispose) ustGlassMat.dispose();
          ustGlassMat = new BABYLON.PBRMaterial('ustGlassMat_' + projector.id, scene);
          ustGlassMat.albedoColor = new BABYLON.Color3(0.12, 0.45, 0.30);
          ustGlassMat.emissiveColor = new BABYLON.Color3(0.05, 0.18, 0.10);
          ustGlassMat.metallic = 0;
          ustGlassMat.roughness = 0.05;
          ustGlassMat.alpha = 0.7;
          projectorMeshesRef.current[ustGlassMatKey] = ustGlassMat;
        }

        const halfD = (bodyD || 0.60) / 2;
        // SQUARE neck + TRIANGULAR-PRISM barrel (diagonal cut on top-back)
        const neckL = 0.18;        // neck depth (Z)
        const neckW = 0.20;        // neck width (X)
        const neckH = 0.18;        // neck height (Y)
        const barrelL = 0.30;      // barrel depth (Z)
        const barrelW = 0.34;      // barrel width (X)
        const barrelH = 0.32;      // barrel height (Y)
        // Mirror plate sits flush along the hypotenuse: back-bottom → front-top
        const mirrorTilt = -Math.PI / 4;  // -45°: plate +Z aligns with diagonal up-and-forward
        const mirrorTh   = 0.025;
        const mirrorLength = Math.sqrt(barrelL * barrelL + barrelH * barrelH);

        // --- Neck: square box attached to FRONT face of body (unchanged) ---
        let neck = projectorMeshesRef.current[ustNeckKey];
        if (!neck) {
          neck = BABYLON.MeshBuilder.CreateBox('ustNeckv4_' + projector.id, {
            width: neckW, height: neckH, depth: neckL
          }, scene);
          neck.material = ustMat;
          neck.isPickable = true;
          neck.metadata = { id: projector.id, type: 'projector' };
          neck.parent = projMesh;
          projectorMeshesRef.current[ustNeckKey] = neck;
        } else {
          neck.material = ustMat;
        }
        neck.rotation = new BABYLON.Vector3(0, 0, 0);
        neck.position = new BABYLON.Vector3(0, 0, halfD + neckL / 2 + 0.005);

        // --- Barrel: TRIANGULAR PRISM (cut at -45°, removes upper-back) ---
        // Cross-section in YZ (looking along +X) is a right triangle with corners:
        //   back-bottom (-h/2, -d/2),  front-bottom (-h/2, +d/2),  front-top (+h/2, +d/2)
        // Right angle at front-bottom; hypotenuse runs back-bottom → front-top.
        let barrel = projectorMeshesRef.current[ustBarrelKey];
        if (!barrel) {
          const w = barrelW, h = barrelH, d = barrelL;
          const positions = [
            // Left face (X=-w/2)
            -w/2, -h/2, -d/2,   // 0: back-bottom-left
            -w/2, -h/2,  d/2,   // 1: front-bottom-left
            -w/2,  h/2,  d/2,   // 2: front-top-left
            // Right face (X=+w/2)
             w/2, -h/2, -d/2,   // 3: back-bottom-right
             w/2, -h/2,  d/2,   // 4: front-bottom-right
             w/2,  h/2,  d/2,   // 5: front-top-right
          ];
          const indices = [
            // Left triangle (outward -X)
            0, 2, 1,
            // Right triangle (outward +X)
            3, 4, 5,
            // Bottom rect (outward -Y): 0-1-4-3
            0, 1, 4,   0, 4, 3,
            // Front rect (outward +Z): 1-2-5-4
            1, 2, 5,   1, 5, 4,
            // Diagonal hypotenuse (outward up-and-back): 0-3-5-2
            0, 3, 5,   0, 5, 2
          ];
          const normals = [];
          BABYLON.VertexData.ComputeNormals(positions, indices, normals);
          barrel = new BABYLON.Mesh('ustBarrelv4_' + projector.id, scene);
          const vd = new BABYLON.VertexData();
          vd.positions = positions;
          vd.indices = indices;
          vd.normals = normals;
          vd.applyToMesh(barrel, true);
          barrel.material = ustMat;
          barrel.isPickable = true;
          barrel.metadata = { id: projector.id, type: 'projector' };
          barrel.parent = projMesh;
          projectorMeshesRef.current[ustBarrelKey] = barrel;
        } else {
          barrel.material = ustMat;
        }
        barrel.rotation = new BABYLON.Vector3(0, 0, 0);
        // Shift the entire barrel + mirror UP by 15% of barrel height
        barrel.position = new BABYLON.Vector3(0, barrelH * 0.15, halfD + neckL + barrelL / 2 + 0.005);

        // --- Mirror plate FLUSH along the diagonal hypotenuse ---
        // Centered at barrel center (0,0,0) and tilted -45°, the plate's
        // long edges land on the hypotenuse endpoints (back-bottom & front-top).
        let glass = projectorMeshesRef.current[ustGlassKey];
        if (!glass) {
          glass = BABYLON.MeshBuilder.CreateBox('ustGlassv4_' + projector.id, {
            width: barrelW + 0.005,
            height: mirrorTh,
            depth: mirrorLength
          }, scene);
          glass.material = ustGlassMat;
          glass.isPickable = false;
          glass.metadata = { id: projector.id, type: 'projector' };
          glass.parent = barrel;
          projectorMeshesRef.current[ustGlassKey] = glass;
        } else {
          glass.material = ustGlassMat;
        }
        glass.rotation = new BABYLON.Vector3(mirrorTilt, 0, 0);
        glass.position = new BABYLON.Vector3(0, 0, 0);

      } else {
        // Standard: remove all lens90 parts (v1 + v2 + v3)
        [lens90PivotKey, lens90PipeKey, lens90BarrelKey, lens90JointKey, lens90GlassKey,
         'lens90pipe1_' + projector.id, 'lens90pipe2_' + projector.id,
         'lens90joint_' + projector.id, 'lens90glass_' + projector.id,
         'lens90glassv2_' + projector.id,
         'lens90mat_' + projector.id, 'lens90glassmat_' + projector.id,
         'lens90pivot_' + projector.id, 'lens90pipe_' + projector.id,
         'lens90elbow_' + projector.id, 'lens90barrel_' + projector.id,
         'lens90mount_' + projector.id, 'lens90body_' + projector.id].forEach(key => {
          if (projectorMeshesRef.current[key]) {
            if (projectorMeshesRef.current[key].dispose) projectorMeshesRef.current[key].dispose();
            delete projectorMeshesRef.current[key];
          }
        });
        // Also remove any topBottom periscope parts (pipes, joints, cube, glass, pivot — v1 + v2)
        // and UST lens parts.
        ['tbPivot_' + projector.id,
         'tbPipe1_' + projector.id, 'tbJoint1_' + projector.id,
         'tbPipe2_' + projector.id, 'tbJoint2_' + projector.id, 'tbPipe3_' + projector.id,
         'tbPipe1v2_' + projector.id, 'tbJoint1v2_' + projector.id,
         'tbPipe2v2_' + projector.id, 'tbJoint2v2_' + projector.id, 'tbPipe3v2_' + projector.id,
         'tbArm_' + projector.id, 'tbHoriz_' + projector.id,
         'tbCube_' + projector.id, 'tbCubev2_' + projector.id,
         'tbGlass_' + projector.id, 'tbGlassv2_' + projector.id,
         'tbMat_' + projector.id, 'tbGlassMat_' + projector.id,
         'ustHousing_' + projector.id, 'ustBarrel_' + projector.id, 'ustGlass_' + projector.id,
         'ustNeckv2_' + projector.id, 'ustBarrelv2_' + projector.id, 'ustGlassv2_' + projector.id,
         'ustNeckv3_' + projector.id, 'ustBarrelv3_' + projector.id, 'ustGlassv3_' + projector.id,
         'ustNeckv4_' + projector.id, 'ustBarrelv4_' + projector.id, 'ustGlassv4_' + projector.id,
         'ustMat_' + projector.id, 'ustGlassMat_' + projector.id].forEach(key => {
          if (projectorMeshesRef.current[key]) {
            if (projectorMeshesRef.current[key].dispose) projectorMeshesRef.current[key].dispose();
            delete projectorMeshesRef.current[key];
          }
        });
        const stdLensKey = `lens_${projector.id}`;
        if (projectorMeshesRef.current[stdLensKey]) {
          if (projectorMeshesRef.current[stdLensKey].housing) projectorMeshesRef.current[stdLensKey].housing.setEnabled(true);
          if (projectorMeshesRef.current[stdLensKey].glass) projectorMeshesRef.current[stdLensKey].glass.setEnabled(true);
        }
      }

      // Create beam visualization - DISABLED when too many projectors for performance
      if (beamMeshesRef.current[projector.id]) {
        beamMeshesRef.current[projector.id].dispose();
        delete beamMeshesRef.current[projector.id];
      }
      // Dispose stale beam-center distance label so a new one can be created
      if (beamCenterLabelsRef.current[projector.id]) {
        try { beamCenterLabelsRef.current[projector.id].dispose(); } catch (_) { /* ignore */ }
        delete beamCenterLabelsRef.current[projector.id];
      }

      // Only create beam wireframes if under threshold (performance optimization)
      const shouldShowBeam = projector.show_beam && projector.power_on && projectors.length < BEAM_WIREFRAME_THRESHOLD;
      
      if (shouldShowBeam) {
        // For UST lenses, pull the near plane very close to the apex so the
        // visible wireframe small-end sits right at the mirror surface.
        const nearDist = (projector.lensType === 'ust') ? 0.005 : 0.1;
        const farDist = throwDistance;
        
        // Calculate near plane dimensions (small rectangle near lens)
        // Use effective throw ratio (zoom-adjusted) so the wireframe shape
        // matches the projection matrix exactly.
        const nearW = (nearDist / effectiveThrowRatio) / 2;
        const nearH = nearW / ar.value;
        
        // Calculate far plane dimensions (large rectangle at throw distance)
        const farW = projWidth / 2;
        const farH = projHeight / 2;
        
        // Lens shift offsets — UST auto-adds +70%, Periscope auto-adds +45%
        // vertical bias so the projection shifts UP on the wall (matches real
        // projector behavior). User's lens_shift_v stays untouched in panel.
        let lensVBias = 0;
        if (projector.lensType === 'ust') lensVBias = 70;
        else if (projector.lensType === 'topBottom') {
          // Periscope: +45% UP for top mount, -45% DOWN for bottom mount
          // (beam direction flips with the mount).
          lensVBias = (projector.topBottomDirection === 'bottom') ? -45 : 45;
        }
        const lensShiftH = (projector.lens_shift_h || 0) / 100;
        const lensShiftV = ((projector.lens_shift_v || 0) + lensVBias) / 100;
        const nearShiftX = lensShiftH * nearW * 2;
        const nearShiftY = lensShiftV * nearH * 2;
        const farShiftX = lensShiftH * farW * 2;
        const farShiftY = lensShiftV * farH * 2;

        // --- Rectangular frustum beam (8 vertices) ---
        const positions = [
          // Near rectangle (lens side) - vertices 0-3
          -nearW + nearShiftX,  nearH + nearShiftY, nearDist,  // 0: top-left
           nearW + nearShiftX,  nearH + nearShiftY, nearDist,  // 1: top-right
           nearW + nearShiftX, -nearH + nearShiftY, nearDist,  // 2: bottom-right
          -nearW + nearShiftX, -nearH + nearShiftY, nearDist,  // 3: bottom-left
          // Far rectangle (screen side) - vertices 4-7
          -farW + farShiftX,  farH + farShiftY, farDist,       // 4: top-left
           farW + farShiftX,  farH + farShiftY, farDist,       // 5: top-right
           farW + farShiftX, -farH + farShiftY, farDist,       // 6: bottom-right
          -farW + farShiftX, -farH + farShiftY, farDist        // 7: bottom-left
        ];

        const indices = [
          // 4 side faces
          0, 1, 5,  0, 5, 4,  // top
          1, 2, 6,  1, 6, 5,  // right
          2, 3, 7,  2, 7, 6,  // bottom
          3, 0, 4,  3, 4, 7,  // left
          // near cap
          0, 2, 1,  0, 3, 2,
          // far cap
          4, 5, 6,  4, 6, 7
        ];

        // Compute normals for Fresnel
        const normals = [];
        BABYLON.VertexData.ComputeNormals(positions, indices, normals);

        // Per-vertex RGBA colors for a length-based fade.
        // Near vertices (0-3, at the projector) keep full alpha so the beam
        // is strongest at its source. Far vertices (4-7, at the wall) drop
        // to alpha = 0 so the beam vanishes into the projection — strong
        // near projector, soft / invisible at the screen.
        const NEAR_ALPHA = 1.0;
        const FAR_ALPHA = 0.0;
        const colors = [
          1, 1, 1, NEAR_ALPHA,  // 0 near
          1, 1, 1, NEAR_ALPHA,  // 1 near
          1, 1, 1, NEAR_ALPHA,  // 2 near
          1, 1, 1, NEAR_ALPHA,  // 3 near
          1, 1, 1, FAR_ALPHA,   // 4 far
          1, 1, 1, FAR_ALPHA,   // 5 far
          1, 1, 1, FAR_ALPHA,   // 6 far
          1, 1, 1, FAR_ALPHA,   // 7 far
        ];

        const beamMesh = new BABYLON.Mesh('beam_' + projector.id, scene);
        const vertexData = new BABYLON.VertexData();
        vertexData.positions = positions;
        vertexData.indices = indices;
        vertexData.normals = normals;
        vertexData.colors = colors;
        vertexData.applyToMesh(beamMesh, true); // updatable=true
        beamMesh.hasVertexAlpha = true;
        beamMesh.isPickable = false;

        // Gradient material — uses projector beamOpacity and color
        const beamOpacity = Math.max(0.001, Math.min(0.5, projector.beamOpacity ?? 0.2));
        const beamColor = BABYLON.Color3.FromHexString(projector.color || '#878787');
        
        const beamMat = new BABYLON.StandardMaterial('beamMat_' + projector.id, scene);
        beamMat.diffuseColor = beamColor;
        beamMat.emissiveColor = beamColor.scale(0.8);
        beamMat.alpha = beamOpacity;
        beamMat.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND;
        beamMat.backFaceCulling = false;
        beamMat.disableLighting = true;

        beamMesh.material = beamMat;
        // No Fresnel — it overrides vertex alpha and creates uniform opacity.
        // The length-based vertex alpha gradient alone gives a clean "strong
        // near projector, soft near screen" feel.

        // ── Optional edge wireframe (default ON) ──
        // Re-introduces the white frustum outline so users have a clear
        // "where the projection lands" guide. Toggleable per-projector via
        // the "Beam Edges" switch in the Properties panel. Uses subtle alpha
        // so it doesn't dominate the projection content on the wall.
        if (projector.show_beam_edges !== false) {
          const n1 = new BABYLON.Vector3(positions[0], positions[1], positions[2]);
          const n2 = new BABYLON.Vector3(positions[3], positions[4], positions[5]);
          const n3 = new BABYLON.Vector3(positions[6], positions[7], positions[8]);
          const n4 = new BABYLON.Vector3(positions[9], positions[10], positions[11]);
          const f1 = new BABYLON.Vector3(positions[12], positions[13], positions[14]);
          const f2 = new BABYLON.Vector3(positions[15], positions[16], positions[17]);
          const f3 = new BABYLON.Vector3(positions[18], positions[19], positions[20]);
          const f4 = new BABYLON.Vector3(positions[21], positions[22], positions[23]);

          const edgeLines = BABYLON.MeshBuilder.CreateLineSystem('beamEdges_' + projector.id, {
            lines: [
              // near rectangle (at the projector lens)
              [n1, n2], [n2, n3], [n3, n4], [n4, n1],
              // far rectangle (at the projection target)
              [f1, f2], [f2, f3], [f3, f4], [f4, f1],
              // longitudinal edges (lens corners → target corners)
              [n1, f1], [n2, f2], [n3, f3], [n4, f4]
            ]
          }, scene);
          edgeLines.color = new BABYLON.Color3(1, 1, 1);
          edgeLines.alpha = Math.min(0.6, beamOpacity * 1.5);
          edgeLines.isPickable = false;
          edgeLines.parent = beamMesh;
        }

        // ── Optical center line + distance label (default OFF) ──
        // Single bright cyan line from the lens to the projection center,
        // with a floating "lens → screen" distance label at the midpoint.
        // Toggleable per-projector via "Beam Center Line" in Properties.
        if (projector.show_center_line === true) {
          // Centers in beam-local space (matches the frustum positions above)
          const nearCenter = new BABYLON.Vector3(nearShiftX, nearShiftY, nearDist);
          const farCenter = new BABYLON.Vector3(farShiftX, farShiftY, farDist);

          const centerLine = BABYLON.MeshBuilder.CreateLines('beamCenter_' + projector.id, {
            points: [nearCenter, farCenter]
          }, scene);
          centerLine.color = new BABYLON.Color3(0.2, 1.0, 0.4);
          centerLine.alpha = 0.95;
          centerLine.isPickable = false;
          centerLine.parent = beamMesh;
          // Slight depth-bias so the line stays visible through the beam mesh
          centerLine.renderingGroupId = 1;

          // Distance label (lens → screen) — uses the user's project unit
          if (guiTextureRef.current && projector.show_name !== false && projectors.length < BEAM_WIREFRAME_THRESHOLD) {
            // Tiny invisible anchor mesh at the line midpoint so the GUI label
            // can track the projector's transform.
            const midLocal = BABYLON.Vector3.Lerp(nearCenter, farCenter, 0.5);
            const anchor = new BABYLON.TransformNode('beamCenterAnchor_' + projector.id, scene);
            anchor.position = midLocal;
            anchor.parent = beamMesh;

            const distLabel = new GUI.TextBlock('beamCenterLabel_' + projector.id);
            distLabel.text = formatWithUnit(throwDistance, projectSettings.unit || 'm');
            distLabel.color = '#86efac';
            distLabel.fontSize = 11;
            distLabel.fontWeight = 'bold';
            distLabel.outlineWidth = 2;
            distLabel.outlineColor = '#000000';
            guiTextureRef.current.addControl(distLabel);
            distLabel.linkWithMesh(anchor);
            distLabel.linkOffsetY = -8;

            // Stash on the parent so we can dispose on next rebuild
            beamMesh._centerLabel = distLabel;
            beamMesh._centerAnchor = anchor;
            beamCenterLabelsRef.current[projector.id] = distLabel;
          }
        }
        
        // Create parent for proper transform
        const beamParent = new BABYLON.TransformNode('beamParent_' + projector.id, scene);
        beamParent.position = lensPosition;
        if (projector.lensType === 'lens90') {
          // Orient beam in the combined lens90 + projector rotation direction
          const rot90 = projector.lens90Rotation || 'right';
          const projQuat = BABYLON.Quaternion.RotationYawPitchRoll(rotY, rotX, rotZ);
          let lensOffsetQuat;
          switch (rot90) {
            case 'left':   lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(-Math.PI / 2, 0, 0); break;
            case 'right':  lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI / 2, 0, 0); break;
            case 'top':    lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(0, -Math.PI / 2, 0); break;
            case 'bottom': lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(0, Math.PI / 2, 0); break;
            default:       lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI / 2, 0, 0); break;
          }
          // Apply lens offset in local space, then projector rotation to world
          beamParent.rotationQuaternion = projQuat.multiply(lensOffsetQuat);
        } else if (projector.lensType === 'topBottom') {
          // Periscope: beam goes straight back along -Z. Yaw 180° flip.
          const projQuat = BABYLON.Quaternion.RotationYawPitchRoll(rotY, rotX, rotZ);
          const flipQuat = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI, 0, 0);
          beamParent.rotationQuaternion = projQuat.multiply(flipQuat);
        } else if (projector.lensType === 'ust') {
          // UST: lens at front, beam fires STRAIGHT BACK along -Z (horizontal).
          // Just yaw 180° to flip the +Z frustum to -Z. No pitch.
          const projQuat = BABYLON.Quaternion.RotationYawPitchRoll(rotY, rotX, rotZ);
          const yawQuat   = BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Y, Math.PI);
          beamParent.rotationQuaternion = projQuat.multiply(yawQuat);
        } else {
          beamParent.rotation = new BABYLON.Vector3(rotX, rotY, rotZ);
        }
        beamMesh.parent = beamParent;
        
        beamMeshesRef.current[projector.id] = beamParent;
      }

      // Create/update projector name label
      if (guiTextureRef.current) {
        // Remove existing label if any
        if (projectorLabelsRef.current[projector.id]) {
          projectorLabelsRef.current[projector.id].dispose();
          delete projectorLabelsRef.current[projector.id];
        }
        
        // Only create labels when show_name is not explicitly false and fewer projectors (perf)
        if (projector.show_name !== false && projectors.length < BEAM_WIREFRAME_THRESHOLD) {
          // Create text block for projector name (small, no background)
          const label = new GUI.TextBlock('label_' + projector.id);
          label.text = projector.name || 'Projector';
          label.color = '#ffffff';
          label.fontSize = 11;
          label.fontWeight = 'normal';
          label.outlineWidth = 1;
          label.outlineColor = '#000000';
          
          guiTextureRef.current.addControl(label);
          
          // Link to projector mesh position (closer to the projector)
          label.linkWithMesh(projMesh);
          label.linkOffsetY = -25; // Position just above the mesh (was -50)
          
          projectorLabelsRef.current[projector.id] = label;
        }
      }
    });
    
    // Second pass: Update shadow casters AFTER all projector meshes are created
    // This ensures all projector meshes exist when adding them as shadow casters
    projectors.forEach(projector => {
      const shadowGenerator = shadowGeneratorsRef.current[projector.id];
      
      // Update shadow casters - only add meshes with actual geometry
      if (shadowGenerator) {
        try {
          const renderList = shadowGenerator.getShadowMap().renderList;
          renderList.length = 0; // Clear existing list
          
          // Add scene objects as shadow casters
          Object.values(meshesRef.current).forEach(mesh => {
            if (!mesh) return;
            
            // For TransformNodes (like imported 3D models), add child meshes
            if (mesh instanceof BABYLON.TransformNode && !(mesh instanceof BABYLON.Mesh)) {
              const children = mesh.getChildMeshes(false);
              children.forEach(child => {
                if (child.getTotalVertices && child.getTotalVertices() > 0) {
                  // Only add meshes that aren't already in the list
                  if (!renderList.includes(child)) {
                    renderList.push(child);
                  }
                }
              });
            } else if (mesh instanceof BABYLON.Mesh && mesh.getTotalVertices && mesh.getTotalVertices() > 0) {
              // Direct mesh with geometry
              if (!renderList.includes(mesh)) {
                renderList.push(mesh);
              }
            }
          });
          
          // Add OTHER projector meshes as shadow casters (not the projector that owns this shadow generator)
          // This makes projectors cast shadows on the projection when they block each other's beams
          Object.entries(projectorMeshesRef.current).forEach(([key, projMesh]) => {
            if (!projMesh) return;
            // Skip the projector's own mesh and its accessories - don't cast shadow from self
            // Keys are: projector.id, frame_${projector.id}, clamp_${projector.id}
            if (key === projector.id || key.endsWith('_' + projector.id)) return;
            
            if (projMesh instanceof BABYLON.Mesh && projMesh.getTotalVertices && projMesh.getTotalVertices() > 0) {
              if (!renderList.includes(projMesh)) {
                renderList.push(projMesh);
              }
            }
          });
        } catch (e) {
          logger.warn('Error updating shadow casters:', e);
        }
      }
    });
  }, [projectors, createProjectionTexture]);

  // ============================================
  // IMAGE SIZE ON SURFACE — visual overlay (pure decoration)
  // ============================================
  // Draws a LineSystem (4-corner quad + diagonals) showing the actual
  // projected image footprint on whatever surface the beam intersects.
  // Read-only: uses scene.pickWithRay; does not modify the projection
  // shader, materials, lighting, or any projector state.
  const imageSizeOverlaysRef = useRef({}); // { [projectorId]: BABYLON.LinesMesh }
  useEffect(() => {
    if (!sceneRef.current) return undefined;
    const scene = sceneRef.current;

    const enabledIds = new Set(
      (projectors || [])
        .filter((p) => p && p.imageSizeOverlayEnabled === true && !p.is_reverse_projector)
        .map((p) => p.id)
    );

    // Dispose overlays whose projector is gone or has the toggle off.
    Object.keys(imageSizeOverlaysRef.current).forEach((id) => {
      if (!enabledIds.has(id)) {
        try { imageSizeOverlaysRef.current[id]?.dispose(); } catch (_) { /* noop */ }
        delete imageSizeOverlaysRef.current[id];
      }
    });

    if (enabledIds.size === 0) return undefined;

    // Babylon render observer at ~4 Hz — visual overlay only, no perf hit.
    let frame = 0;
    const obs = scene.onBeforeRenderObservable.add(() => {
      frame = (frame + 1) % 15;
      if (frame !== 0) return;
      enabledIds.forEach((id) => {
        const proj = (projectors || []).find((p) => p.id === id);
        if (!proj) return;
        const result = computeSurfaceImageSize(proj, scene, {
          projectorMeshes: projectorMeshesRef.current,
        });
        if (!result || result.status === 'no-surface' || result.status === 'reverse') {
          if (imageSizeOverlaysRef.current[id]) {
            imageSizeOverlaysRef.current[id].setEnabled(false);
          }
          return;
        }
        const { tl, tr, bl, br } = result.cornerHits;
        if (!tl || !tr || !bl || !br) {
          if (imageSizeOverlaysRef.current[id]) {
            imageSizeOverlaysRef.current[id].setEnabled(false);
          }
          return;
        }
        const lines = [
          [tl, tr], [tr, br], [br, bl], [bl, tl], // outline
          [tl, br], [tr, bl],                       // diagonals
        ];
        const colour = new BABYLON.Color4(0.10, 0.95, 1.00, 1.0); // cyan
        const colours = lines.map(() => [colour, colour]);

        const existing = imageSizeOverlaysRef.current[id];
        const updated = BABYLON.MeshBuilder.CreateLineSystem(
          'imageSizeOverlay_' + id,
          { lines, colors: colours, instance: existing && !existing.isDisposed() ? existing : null, updatable: true },
          scene
        );
        if (!existing) {
          updated.isPickable = false;
          updated.alwaysSelectAsActiveMesh = true;
          updated.renderingGroupId = 1; // draw above ground grid
          updated.metadata = { isHelper: true, isImageSizeOverlay: true };
        }
        updated.setEnabled(true);
        imageSizeOverlaysRef.current[id] = updated;
      });
    });

    return () => {
      try { scene.onBeforeRenderObservable.remove(obs); } catch (_) { /* noop */ }
    };
  }, [projectors]);



  // ============================================
  // REVERSE MAPPING PROJECTION UPDATE
  // ============================================
  // Updates projections on all receiving surfaces when projectors or objects change
  // This is the Mapping Matter-style approach - supports 100+ projectors!
  const surfaceProjectionTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (!sceneRef.current) return;
    
    // Clear any pending update
    if (surfaceProjectionTimeoutRef.current) {
      clearTimeout(surfaceProjectionTimeoutRef.current);
    }
    
    // Debounce projection updates (150ms delay for responsiveness)
    surfaceProjectionTimeoutRef.current = setTimeout(() => {
      const scene = sceneRef.current;
      const projSystem = projectionSystemRef.current;
      if (!scene || !projSystem) return;
      
      const activeProjectors = projectors.filter(p => p.power_on);
      const receivers = getProjectionReceivers(scene, sceneObjects);
      
      // Unregister meshes that now have MTL applied OR whose Projection Mode
      // disqualifies them as receivers (None, or Reverse with UV off).
      sceneObjects.forEach(obj => {
        const mode = obj.projectionMode
          || (obj.uvMappingEnabled === true ? 'reverse' : 'normal');
        const isReceiver = !obj.mtlData && (
          mode === 'normal' || (mode === 'reverse' && obj.uvMappingEnabled === true)
        );
        if (!isReceiver) {
          // Resolve the mesh by the same naming scheme used in getProjectionReceivers
          let mesh = null;
          if (obj.type === 'mesh') {
            const parent = scene.getTransformNodeByName('mesh_' + obj.id);
            if (parent) {
              parent.getChildMeshes().forEach(child => {
                if (child && projSystem.receiverMeshes.has(child.uniqueId || child.name)) {
                  projSystem.unregisterReceiver(child);
                }
              });
            }
          } else if (obj.type === 'scaffolding') {
            mesh = scene.getMeshByName('scaffBound_' + obj.id);
          } else if (obj.type === 'truss') {
            mesh = scene.getMeshByName('truss_' + obj.id);
          } else if (obj.type === 'truss_system') {
            mesh = scene.getMeshByName('trussBound_' + obj.id);
          } else {
            mesh = scene.getMeshByName('obj_' + obj.id);
          }
          if (mesh && projSystem.receiverMeshes.has(mesh.uniqueId || mesh.name)) {
            projSystem.unregisterReceiver(mesh);
          }
        }
      });

      // Register all receiver meshes with the projection system
      receivers.forEach(({ mesh }) => {
        if (mesh && !projSystem.receiverMeshes.has(mesh.uniqueId || mesh.name)) {
          projSystem.registerReceiver(mesh, mesh.material);
        }
      });
      
      // Register projector body meshes as occluders (they block light but don't receive projections)
      // Each projector body should cast a shadow for OTHER projectors' beams
      // Pass owner projector ID so self-shadowing is excluded
      Object.entries(projectorMeshesRef.current).forEach(([key, mesh]) => {
        // Only register actual body meshes (skip lens objects, frames, etc.)
        if (mesh && mesh.dispose && mesh.isEnabled && !mesh.isDisposed() && mesh.isEnabled()
            && key.indexOf('lens_') !== 0 && key.indexOf('frame_') !== 0
            && key.indexOf('lens90') !== 0) {
          const meshId = mesh.uniqueId || mesh.name;
          if (!projSystem.occluderMeshes.has(meshId)) {
            projSystem.registerOccluderOnly(mesh, key);
          }
        }
      });
      
      // Register truss and scaffolding child meshes as occluders (they block projection light)
      // These use thin instances, so the depth shader needs instancesDeclaration/instancesVertex support
      sceneObjects.forEach(obj => {
        if (obj.type === 'truss_system' || obj.type === 'truss' || obj.type === 'scaffolding') {
          let rootName;
          if (obj.type === 'truss_system') rootName = 'trussRoot_' + obj.id;
          else if (obj.type === 'truss') rootName = 'truss_' + obj.id;
          else rootName = 'scaffolding_' + obj.id;
          
          const rootNode = scene.getTransformNodeByName(rootName) || scene.getMeshByName(rootName);
          if (rootNode && rootNode.getChildMeshes) {
            rootNode.getChildMeshes(false).forEach(child => {
              if (child && !child.isDisposed() && child.isEnabled() && child.visibility > 0) {
                const childId = child.uniqueId || child.name;
                if (!projSystem.occluderMeshes.has(childId)) {
                  projSystem.registerOccluderOnly(child);
                }
              }
            });
          }
        }
      });
      
      // Re-apply edge rendering to all receiver meshes AFTER registration
      // This ensures edges persist after ProjectionSystem modifies materials
      // BUT respect the show_edges property — don't re-enable if user turned them off
      receivers.forEach(({ mesh, obj }) => {
        if (obj?.show_edges === false) {
          // Explicitly disable edges for objects with show_edges OFF
          if (mesh && mesh.edgesRenderer) {
            mesh.disableEdgesRendering();
          }
        } else if (mesh && mesh.enableEdgesRendering) {
          const isTruss = obj?.type === 'truss' || obj?.type === 'truss_system' || mesh.name?.includes('truss');
          const isLoadedMesh = obj?.type === 'mesh';
          
          // Use silver for trusses and loaded meshes, white for primitives
          if (isTruss || isLoadedMesh) {
            mesh.enableEdgesRendering(0.5);
            mesh.edgesWidth = 2.0;
            mesh.edgesColor = new BABYLON.Color4(0.75, 0.75, 0.75, 1.0); // Silver
          } else {
            mesh.enableEdgesRendering(0.5);
            mesh.edgesWidth = 2.0;
            mesh.edgesColor = new BABYLON.Color4(1.0, 1.0, 1.0, 1.0); // White
          }
        }
      });
      
      // Update projectors in the projection system
      projSystem.updateProjectors(projectors);
      
      // Log status
      if (activeProjectors.length > 0) {
        logger.log(`✓ Projection System: ${activeProjectors.length} projectors → ${receivers.length} surfaces`);
      } else if (projectors.length === 0) {
        logger.log('✓ All projectors removed - projections cleared');
      }
    }, 150);
    
    return () => {
      if (surfaceProjectionTimeoutRef.current) {
        clearTimeout(surfaceProjectionTimeoutRef.current);
      }
    };
  }, [projectors, sceneObjects, getProjectionReceivers]);

  // Manage scene objects
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    // Remove old meshes - properly clean up shadow casters first
    Object.keys(meshesRef.current).forEach(id => {
      if (!sceneObjects.find(obj => obj.id === id)) {
        const mesh = meshesRef.current[id];
        if (mesh) {
          try {
            // Remove from all shadow generators before disposing
            Object.values(shadowGeneratorsRef.current).forEach(sg => {
              if (sg) {
                const renderList = sg.getShadowMap()?.renderList;
                if (renderList) {
                  // Remove the mesh and its children from shadow casters
                  if (mesh instanceof BABYLON.TransformNode) {
                    const children = mesh.getChildMeshes(false);
                    children.forEach(child => {
                      const idx = renderList.indexOf(child);
                      if (idx > -1) renderList.splice(idx, 1);
                    });
                  }
                  const idx = renderList.indexOf(mesh);
                  if (idx > -1) renderList.splice(idx, 1);
                }
              }
            });
            // Clean up mirror resources
            if (mesh.metadata?.isMirror) {
              if (mesh.metadata.mirrorTexture) mesh.metadata.mirrorTexture.dispose();
              if (scene.metadata?.mirrorTextures) {
                scene.metadata.mirrorTextures = scene.metadata.mirrorTextures.filter(m => m.id !== obj.id);
              }
            }
            mesh.dispose();
          } catch (e) {
            logger.warn('Error disposing mesh:', e);
          }
        }
        delete meshesRef.current[id];
        floorPlacedIdsRef.current.delete(id);
      }
    });

    // ─── ORPHAN SWEEP (safety net, project-switch race fix) ───
    // Belt-and-suspenders: dispose any scene-root node whose `metadata.id`
    // points to a scene object OR projector that is no longer present —
    // typically left behind by an async model loader that resolved AFTER
    // the user switched projects, parented its meshes onto a stale parent,
    // and slipped past the per-id cleanup above.
    //
    // Tightly scoped: only nodes tagged `type: 'object'` or `type: 'projector'`
    // (i.e. ours) are considered, and only when their id isn't current.
    // This never touches system meshes (gizmo, ground, grid, helpers,
    // pickers, projection receivers, lights, cameras).
    {
      const validIds = new Set();
      sceneObjects.forEach(o => { if (o?.id) validIds.add(o.id); });
      (projectorsRef.current || []).forEach(p => { if (p?.id) validIds.add(p.id); });

      const considerOrphan = (node) => {
        const meta = node?.metadata;
        if (!meta || typeof meta !== 'object') return false;
        if (meta.isStackGhost || meta.isHelper || meta.isPickingMesh
            || meta.scaffoldHelper || meta.isImageSizeOverlay) return false;
        if (!meta.id) return false;
        if (meta.type !== 'object' && meta.type !== 'projector') return false;
        return !validIds.has(meta.id);
      };

      // TransformNodes first (parents) — disposing the parent recurses to
      // its child meshes which avoids double-dispose warnings.
      scene.transformNodes.slice().forEach(node => {
        if (!considerOrphan(node)) return;
        try { node.dispose(); } catch (_e) { /* best effort */ }
      });
      // Then any straggler meshes with no parent.
      scene.meshes.slice().forEach(m => {
        if (!m || m.isDisposed?.()) return;
        if (m.parent) return; // skip — parent path will own it
        if (!considerOrphan(m)) return;
        try { m.dispose(false, true); } catch (_e) { /* best effort */ }
      });
    }

    // Create/update scene objects
    sceneObjects.forEach(obj => {
      let mesh = meshesRef.current[obj.id];
      
      // Check if mesh needs to be recreated due to dimension changes
      // SKIP for mesh type - async loaded meshes handle dimensions internally
      const currentDims = mesh?.metadata?.dimensions;
      const newDims = obj.dimensions;
      const dimsChanged = currentDims && newDims && (
        currentDims.width !== newDims.width ||
        currentDims.height !== newDims.height ||
        currentDims.depth !== newDims.depth
      );
      
      // Cylinder/Pipe: check if wallThickness or arcAngle changed
      let cylinderConfigChanged = false;
      if ((obj.type === 'cylinder' || obj.type === 'pipe') && mesh) {
        cylinderConfigChanged = 
          mesh.metadata?.wallThickness !== (obj.wallThickness || 0) ||
          mesh.metadata?.arcAngle !== (obj.arcAngle || 360) ||
          mesh.metadata?.topCap !== (obj.topCap !== false) ||
          mesh.metadata?.bottomCap !== (obj.bottomCap !== false);
      }
      
      // LED Screen: check if ledConfig changed
      let ledConfigChanged = false;
      if (obj.type === 'led_screen' && mesh) {
        const prevLed = mesh.metadata?.ledConfig;
        const newLed = obj.ledConfig;
        if (prevLed && newLed) {
          ledConfigChanged = prevLed.columns !== newLed.columns ||
            prevLed.rows !== newLed.rows ||
            prevLed.tileWidth !== newLed.tileWidth ||
            prevLed.tileHeight !== newLed.tileHeight ||
            prevLed.tileDepth !== newLed.tileDepth ||
            prevLed.contentUrl !== newLed.contentUrl ||
            prevLed.curveAngle !== newLed.curveAngle ||
            prevLed.cabinetStep !== newLed.cabinetStep ||
            prevLed.ledMode !== newLed.ledMode ||
            prevLed.cornerAngle !== newLed.cornerAngle ||
            prevLed.leftColumns !== newLed.leftColumns ||
            prevLed.rightColumns !== newLed.rightColumns ||
            prevLed.smoothBend !== newLed.smoothBend ||
            prevLed.softCornerLeft !== newLed.softCornerLeft ||
            prevLed.softCornerRight !== newLed.softCornerRight ||
            JSON.stringify(prevLed.removedTiles || []) !== JSON.stringify(newLed.removedTiles || []);
        }
      }

      // Scaffolding: check if scaffoldConfig changed
      let scaffoldConfigChanged = false;
      if (obj.type === 'scaffolding' && mesh) {
        scaffoldConfigChanged = hasScaffoldConfigChanged(
          mesh.metadata?.scaffoldConfig,
          obj.scaffoldConfig
        );
      }

      // Truss: check if trussConfig changed
      let trussConfigChanged = false;
      if (obj.type === 'truss_system' && mesh) {
        trussConfigChanged = hasTrussConfigChanged(
          mesh.metadata?.trussConfig,
          obj.trussConfig
        );
      }

      // Mirror: check if mirrorConfig changed
      let mirrorConfigChanged = false;
      if (obj.type === 'mirror' && mesh) {
        const prevMir = mesh.metadata?.mirrorConfig;
        const newMir = obj.mirrorConfig;
        if (prevMir && newMir) {
          mirrorConfigChanged = prevMir.reflectionEnabled !== newMir.reflectionEnabled ||
            prevMir.resolution !== newMir.resolution ||
            prevMir.reflectionLevel !== newMir.reflectionLevel ||
            prevMir.bounces !== newMir.bounces ||
            prevMir.recursionEnabled !== newMir.recursionEnabled ||
            prevMir.blur !== newMir.blur ||
            prevMir.maxDistance !== newMir.maxDistance;
        }
      }
      
      // MTL: check if mtlData changed (applied or removed)
      let mtlConfigChanged = false;
      if (mesh) {
        const prevMtl = mesh.metadata?.mtlData || null;
        const newMtl = obj.mtlData || null;
        mtlConfigChanged = prevMtl !== newMtl;
      }

      // Scrim: check if curveDegrees changed
      let scrimCurveChanged = false;
      if (obj.type === 'scrim_screen' && mesh) {
        scrimCurveChanged = (mesh.metadata?.scrimCurveDegrees || 0) !== (obj.scrimCurveDegrees || 0);
      }

      // Plane/Screen: check if curveDegrees changed
      let planeCurveChanged = false;
      if ((obj.type === 'plane' || obj.type === 'projection_screen') && mesh) {
        planeCurveChanged = (mesh.metadata?.curveDegrees || 0) !== (obj.curveDegrees || 0);
      }

      // Only recreate PRIMITIVES when dimensions change
      // Mesh types (async loaded 3D files) should NOT be recreated - they update internally
      const shouldRecreate = (dimsChanged || cylinderConfigChanged || ledConfigChanged || scaffoldConfigChanged || trussConfigChanged || mirrorConfigChanged || mtlConfigChanged || scrimCurveChanged || planeCurveChanged) && obj.type !== 'mesh' && !isDraggingRef.current;
      
      if (mesh && shouldRecreate) {
        // Clean up mirror resources before disposing
        if (mesh.metadata?.isMirror) {
          if (mesh._mirrorUpdateFn) scene.unregisterBeforeRender(mesh._mirrorUpdateFn);
          if (mesh.metadata.mirrorTexture) mesh.metadata.mirrorTexture.dispose();
          // Remove from recursive mirror tracking
          if (scene.metadata?.mirrorTextures) {
            scene.metadata.mirrorTextures = scene.metadata.mirrorTextures.filter(m => m.id !== obj.id);
          }
        }
        // Dispose all descendants (meshes AND TransformNodes) for clean recreation
        const allChildren = mesh.getChildren(undefined, false); // all descendants, recursive
        allChildren.forEach(child => { if (child.dispose) child.dispose(); });
        mesh.dispose();
        delete meshesRef.current[obj.id];
        mesh = null;
      }

      if (!mesh) {
        const geoType = obj.type === 'primitive' ? obj.primitive_type : obj.type;
        const normalizedType = geoType === 'box' ? 'cube' : geoType;
        
        // Get dimensions in meters - default to 1m if not specified
        const dimX = obj.dimensions?.width || 1;
        const dimY = obj.dimensions?.height || 1;
        const dimZ = obj.dimensions?.depth || 1;

        switch (normalizedType) {
          case 'cube':
            mesh = BABYLON.MeshBuilder.CreateBox('obj_' + obj.id, { 
              width: dimX, 
              height: dimY, 
              depth: dimZ,
              sideOrientation: BABYLON.Mesh.DOUBLESIDE 
            }, scene);
            break;
          case 'sphere':
            mesh = BABYLON.MeshBuilder.CreateSphere('obj_' + obj.id, { 
              diameterX: dimX, 
              diameterY: dimY, 
              diameterZ: dimZ, 
              segments: 32, 
              sideOrientation: BABYLON.Mesh.DOUBLESIDE 
            }, scene);
            break;
          case 'cylinder': {
            const wallThickness = obj.wallThickness || 0;
            const arcAngle = obj.arcAngle || 360;
            const topCap = obj.topCap !== false;
            const bottomCap = obj.bottomCap !== false;
            const outerR = dimX / 2;
            const innerR = wallThickness > 0 ? Math.max(0.001, outerR - wallThickness) : 0;
            const halfH = dimY / 2;
            const segments = 48;
            const isHollow = wallThickness > 0 && innerR > 0;
            const isPartial = arcAngle < 360;
            
            if (isHollow || isPartial || !topCap || !bottomCap) {
              // Build custom hollow/partial cylinder geometry using extracted builder
              const { positions, indices, normals, uvs } = buildHollowCylinderGeometry(
                outerR, innerR, halfH, segments, arcAngle, topCap, bottomCap
              );
              
              // Build mesh from vertex data
              mesh = new BABYLON.Mesh('obj_' + obj.id, scene);
              const vertexData = new BABYLON.VertexData();
              vertexData.positions = positions;
              vertexData.indices = indices;
              vertexData.normals = normals;
              vertexData.uvs = uvs;
              vertexData.applyToMesh(mesh);
              mesh.sideOrientation = BABYLON.Mesh.DOUBLESIDE;
            } else {
              // Standard solid cylinder with both caps
              mesh = BABYLON.MeshBuilder.CreateCylinder('obj_' + obj.id, { 
                height: dimY, 
                diameter: dimX, 
                tessellation: segments,
                sideOrientation: BABYLON.Mesh.DOUBLESIDE 
              }, scene);
            }
            break;
          }
          case 'plane':
          case 'projection_screen': {
            const planeCurve = obj.curveDegrees || 0;
            if (planeCurve !== 0) {
              const absDeg = Math.abs(planeCurve);
              const arcRad = (absDeg * Math.PI) / 180;
              const radius = dimX / arcRad;
              const segments = Math.max(16, Math.round(absDeg / 2));
              const pathTop = [];
              const pathBot = [];
              const halfAngle = arcRad / 2;
              for (let i = 0; i <= segments; i++) {
                const t = i / segments;
                const angle = -halfAngle + t * arcRad;
                const dir = planeCurve > 0 ? 1 : -1;
                const x = Math.sin(angle) * radius * dir;
                const z = (Math.cos(angle) - Math.cos(halfAngle)) * radius * dir;
                pathTop.push(new BABYLON.Vector3(x, dimY / 2, z));
                pathBot.push(new BABYLON.Vector3(x, -dimY / 2, z));
              }
              mesh = BABYLON.MeshBuilder.CreateRibbon('obj_' + obj.id, {
                pathArray: [pathBot, pathTop],
                sideOrientation: BABYLON.Mesh.DOUBLESIDE,
                updatable: false
              }, scene);
            } else {
              mesh = BABYLON.MeshBuilder.CreatePlane('obj_' + obj.id, {
                width: dimX, height: dimY,
                sideOrientation: BABYLON.Mesh.DOUBLESIDE
              }, scene);
            }
            if (mesh) mesh.metadata = { ...mesh.metadata, curveDegrees: planeCurve };
            break;
          }
          case 'scrim_screen': {
            // Scrim screen - semi-transparent mesh that partially blocks light
            const scrimCurve = obj.scrimCurveDegrees || 0;

            if (scrimCurve !== 0) {
              // Curved scrim: create a cylinder arc segment
              const absDeg = Math.abs(scrimCurve);
              const arcRad = (absDeg * Math.PI) / 180;
              const radius = dimX / arcRad; // radius so arc length = dimX
              const segments = Math.max(16, Math.round(absDeg / 2));

              // Build curved plane from scratch (ribbon)
              const pathTop = [];
              const pathBot = [];
              const halfAngle = arcRad / 2;
              for (let i = 0; i <= segments; i++) {
                const t = i / segments;
                const angle = -halfAngle + t * arcRad;
                const dir = scrimCurve > 0 ? 1 : -1;
                const x = Math.sin(angle) * radius * dir;
                const z = (Math.cos(angle) - Math.cos(halfAngle)) * radius * dir;
                pathTop.push(new BABYLON.Vector3(x, dimY / 2, z));
                pathBot.push(new BABYLON.Vector3(x, -dimY / 2, z));
              }

              mesh = BABYLON.MeshBuilder.CreateRibbon('obj_' + obj.id, {
                pathArray: [pathBot, pathTop],
                sideOrientation: BABYLON.Mesh.DOUBLESIDE,
                updatable: false
              }, scene);
            } else {
              mesh = BABYLON.MeshBuilder.CreatePlane('obj_' + obj.id, {
                width: dimX, height: dimY,
                sideOrientation: BABYLON.Mesh.DOUBLESIDE
              }, scene);
            }

            // Create special scrim material
            const scrimMat = new BABYLON.StandardMaterial('scrimMat_' + obj.id, scene);
            scrimMat.diffuseColor = BABYLON.Color3.FromHexString(obj.color || '#ffffff');
            scrimMat.alpha = obj.scrimOpacity || 0.15;
            scrimMat.backFaceCulling = false;
            scrimMat.specularColor = new BABYLON.Color3(0, 0, 0);
            scrimMat.emissiveColor = new BABYLON.Color3(0.05, 0.05, 0.05);
            configureMaterialForProjectors(scrimMat);
            mesh.material = scrimMat;
            mesh.metadata = { ...mesh.metadata, isScrim: true, scrimOpacity: obj.scrimOpacity || 0.15, scrimCurveDegrees: scrimCurve };
            mesh.receiveShadows = true;
            mesh.hasVertexAlpha = false;
            mesh.useVertexColors = false;

            mesh.enableEdgesRendering(0.5);
            mesh.edgesWidth = 2.0;
            mesh.edgesColor = new BABYLON.Color4(1.0, 1.0, 1.0, 1.0);
            break;
          }
          case 'dome':
            // Create hemisphere for dome projection (planetarium style)
            mesh = BABYLON.MeshBuilder.CreateSphere('obj_' + obj.id, { 
              diameterX: dimX, 
              diameterY: dimY, 
              diameterZ: dimZ, 
              segments: 64,
              arc: 1,
              slice: 0.5,
              sideOrientation: BABYLON.Mesh.DOUBLESIDE
            }, scene);
            break;
          case 'pipe': {
            // Pipe uses same hollow cylinder logic as cylinder
            const pipeWT = obj.wallThickness || 0;
            const pipeArc = obj.arcAngle || 360;
            const pipeTopCap = obj.topCap !== false;
            const pipeBotCap = obj.bottomCap !== false;
            const pipeOuterR = dimX / 2;
            const pipeInnerR = pipeWT > 0 ? Math.max(0.001, pipeOuterR - pipeWT) : 0;
            const pipeHalfH = dimY / 2;
            const pipeSegs = 48;
            const pipeIsHollow = pipeWT > 0 && pipeInnerR > 0;
            const pipeIsPartial = pipeArc < 360;
            
            if (pipeIsHollow || pipeIsPartial || !pipeTopCap || !pipeBotCap) {
              // Reuse same geometry builder as cylinder
              const { positions, indices, normals, uvs } = buildHollowCylinderGeometry(
                pipeOuterR, pipeInnerR, pipeHalfH, pipeSegs, pipeArc, pipeTopCap, pipeBotCap
              );
              
              mesh = new BABYLON.Mesh('obj_' + obj.id, scene);
              mesh = new BABYLON.Mesh('obj_' + obj.id, scene);
              const vertexData = new BABYLON.VertexData();
              vertexData.positions = positions;
              vertexData.indices = indices;
              vertexData.normals = normals;
              vertexData.uvs = uvs;
              vertexData.applyToMesh(mesh);
              mesh.sideOrientation = BABYLON.Mesh.DOUBLESIDE;
            } else {
              mesh = BABYLON.MeshBuilder.CreateCylinder('obj_' + obj.id, { 
                height: dimY, diameter: dimX, tessellation: 32,
                sideOrientation: BABYLON.Mesh.DOUBLESIDE
              }, scene);
            }
            break;
          }
          case 'truss':
            // Create a realistic aluminum box truss structure
            // Collect all meshes first, then merge for performance
            const trussLength = obj.trussLength || 3; // Length in meters
            const trussSize = 0.3; // Cross-section size (300mm standard)
            const mainPipeRadius = 0.025; // Main corner tubes (50mm diameter)
            const bracePipeRadius = 0.015; // Diagonal braces (30mm diameter)
            const trussColor = new BABYLON.Color3(0.85, 0.85, 0.85); // Aluminum silver
            
            // Create material for truss
            const trussMat = new BABYLON.StandardMaterial('trussMat_' + obj.id, scene);
            trussMat.diffuseColor = trussColor;
            trussMat.specularColor = new BABYLON.Color3(0.5, 0.5, 0.5);
            trussMat.specularPower = 32;
            trussMat.backFaceCulling = false;
            
            // Array to collect all parts for merging
            const trussParts = [];
            
            // Corner positions for the 4 main tubes (square cross-section)
            const corners = [
              { x: -trussSize/2, y: -trussSize/2 },
              { x: trussSize/2, y: -trussSize/2 },
              { x: trussSize/2, y: trussSize/2 },
              { x: -trussSize/2, y: trussSize/2 }
            ];
            
            // Create the 4 main corner tubes (running along Z axis)
            corners.forEach((corner, i) => {
              const mainTube = BABYLON.MeshBuilder.CreateCylinder('mainTube_' + i + '_' + obj.id, {
                height: trussLength,
                diameter: mainPipeRadius * 2,
                tessellation: 12
              }, scene);
              mainTube.position = new BABYLON.Vector3(corner.x, corner.y, 0);
              mainTube.rotation.x = Math.PI / 2; // Align along Z axis
              mainTube.bakeCurrentTransformIntoVertices();
              trussParts.push(mainTube);
            });
            
            // Create horizontal frames and diagonal braces
            // Number of bays (sections) - typically every 0.5m for standard truss
            const bayLength = 0.5;
            const numBays = Math.max(2, Math.round(trussLength / bayLength));
            const actualBayLength = trussLength / numBays;
            
            for (let bay = 0; bay <= numBays; bay++) {
              const zPos = -trussLength/2 + bay * actualBayLength;
              
              // Create horizontal ring at each bay end
              // Connect adjacent corners with horizontal braces
              for (let side = 0; side < 4; side++) {
                const c1 = corners[side];
                const c2 = corners[(side + 1) % 4];
                
                const horizBrace = BABYLON.MeshBuilder.CreateCylinder('horizBrace_' + bay + '_' + side + '_' + obj.id, {
                  height: trussSize,
                  diameter: bracePipeRadius * 2,
                  tessellation: 8
                }, scene);
                
                // Position at midpoint between corners
                horizBrace.position = new BABYLON.Vector3(
                  (c1.x + c2.x) / 2,
                  (c1.y + c2.y) / 2,
                  zPos
                );
                
                // Rotate to connect corners (either horizontal or vertical)
                if (side === 0 || side === 2) {
                  // Horizontal brace (along X)
                  horizBrace.rotation.z = Math.PI / 2;
                }
                
                horizBrace.bakeCurrentTransformIntoVertices();
                trussParts.push(horizBrace);
              }
              
              // Create X-shaped diagonal braces between bays (on each face)
              if (bay < numBays) {
                const nextZ = zPos + actualBayLength;
                
                // Create diagonals on all 4 faces
                // Each face has 2 diagonals forming an X
                for (let face = 0; face < 4; face++) {
                  const c1 = corners[face];
                  const c2 = corners[(face + 1) % 4];
                  
                  // Diagonal 1: from corner1 at zPos to corner2 at nextZ
                  const start1 = new BABYLON.Vector3(c1.x, c1.y, zPos);
                  const end1 = new BABYLON.Vector3(c2.x, c2.y, nextZ);
                  const mid1 = start1.add(end1).scale(0.5);
                  const dir1 = end1.subtract(start1);
                  const len1 = dir1.length();
                  
                  const diag1 = BABYLON.MeshBuilder.CreateCylinder('diag1_' + bay + '_' + face + '_' + obj.id, {
                    height: len1,
                    diameter: bracePipeRadius * 2,
                    tessellation: 6
                  }, scene);
                  
                  diag1.position = mid1;
                  
                  // Calculate rotation to align cylinder with diagonal direction
                  const up = new BABYLON.Vector3(0, 1, 0);
                  const axis = BABYLON.Vector3.Cross(up, dir1.normalize());
                  const angle = Math.acos(BABYLON.Vector3.Dot(up, dir1.normalize()));
                  if (axis.length() > 0.001) {
                    diag1.rotationQuaternion = BABYLON.Quaternion.RotationAxis(axis.normalize(), angle);
                  }
                  
                  diag1.bakeCurrentTransformIntoVertices();
                  trussParts.push(diag1);
                  
                  // Diagonal 2: from corner2 at zPos to corner1 at nextZ (crossing the first)
                  const start2 = new BABYLON.Vector3(c2.x, c2.y, zPos);
                  const end2 = new BABYLON.Vector3(c1.x, c1.y, nextZ);
                  const mid2 = start2.add(end2).scale(0.5);
                  const dir2 = end2.subtract(start2);
                  const len2 = dir2.length();
                  
                  const diag2 = BABYLON.MeshBuilder.CreateCylinder('diag2_' + bay + '_' + face + '_' + obj.id, {
                    height: len2,
                    diameter: bracePipeRadius * 2,
                    tessellation: 6
                  }, scene);
                  
                  diag2.position = mid2;
                  
                  // Calculate rotation for second diagonal
                  const axis2 = BABYLON.Vector3.Cross(up, dir2.normalize());
                  const angle2 = Math.acos(BABYLON.Vector3.Dot(up, dir2.normalize()));
                  if (axis2.length() > 0.001) {
                    diag2.rotationQuaternion = BABYLON.Quaternion.RotationAxis(axis2.normalize(), angle2);
                  }
                  
                  diag2.bakeCurrentTransformIntoVertices();
                  trussParts.push(diag2);
                }
              }
            }
            
            // Merge all parts into a single mesh for better performance
            if (trussParts.length > 0) {
              mesh = BABYLON.Mesh.MergeMeshes(
                trussParts,
                true, // Dispose source meshes
                true, // Allow 32 bit indices
                undefined, // Use default mesh for merged
                false, // Don't subdivide
                true // Keep multi-materials (false since we use one material)
              );
              if (mesh) {
                mesh.name = 'truss_' + obj.id;
                mesh.material = trussMat;
                mesh.receiveShadows = true;
                mesh.metadata = { id: obj.id, type: 'object' };
                
                // Enable silver edge rendering for truss visibility
                mesh.enableEdgesRendering(0.5);
                mesh.edgesWidth = 2.0;
                mesh.edgesColor = new BABYLON.Color4(0.75, 0.75, 0.75, 1.0); // Silver edges for truss
              }
            }
            
            // If merge failed, create a simple fallback
            if (!mesh) {
              mesh = BABYLON.MeshBuilder.CreateBox('truss_' + obj.id, { width: trussSize, height: trussSize, depth: trussLength }, scene);
              mesh.material = trussMat;
              mesh.metadata = { id: obj.id, type: 'object' };
              
              // Enable silver edge rendering for fallback truss
              mesh.enableEdgesRendering(0.5);
              mesh.edgesWidth = 2.0;
              mesh.edgesColor = new BABYLON.Color4(0.75, 0.75, 0.75, 1.0); // Silver edges for truss
            }
            break;
          case 'mirror': {
            // ═══ Advanced Mirror System ═══
            const mirCfg = obj.mirrorConfig || {};
            const mirW = dimX;
            const mirH = dimY;

            // DOUBLESIDE so mirror is always visible from both directions
            mesh = BABYLON.MeshBuilder.CreatePlane('obj_' + obj.id, {
              width: mirW, height: mirH,
              sideOrientation: BABYLON.Mesh.DOUBLESIDE
            }, scene);

            const mirMat = new BABYLON.StandardMaterial('mirrorMat_' + obj.id, scene);
            mirMat.backFaceCulling = false;

            if (mirCfg.reflectionEnabled) {
              const mirRes = mirCfg.resolution || 512;
              const bounces = Math.max(1, Math.min(10, mirCfg.bounces || 3));
              const recursionEnabled = mirCfg.recursionEnabled !== false;
              const blurKernel = mirCfg.blur || 0;
              const maxDistance = mirCfg.maxDistance || 100;

              const mirrorTexture = new BABYLON.MirrorTexture('mirrorTex_' + obj.id, mirRes, scene, true);
              mirrorTexture.mirrorPlane = new BABYLON.Plane(0, 0, -1, 0);
              mirrorTexture.level = mirCfg.reflectionLevel ?? 1.0;
              mirrorTexture.adaptiveBlurKernel = blurKernel;
              mirrorTexture.clearColor = new BABYLON.Color4(0, 0, 0, 1);

              // ShaderMaterial support for projection system
              mirrorTexture.onBeforeRenderObservable.add(() => {
                scene.clipPlane = null;
                const ps = projectionSystemRef.current;
                if (ps && ps.receiverMeshes) {
                  ps.receiverMeshes.forEach((entry) => {
                    if (entry.mesh && entry.material && !entry.mesh.isDisposed()) {
                      mirrorTexture.setMaterialForRendering(entry.mesh, entry.material);
                    }
                  });
                }
              });

              mirMat.reflectionTexture = mirrorTexture;
              mirMat.diffuseColor = new BABYLON.Color3(0.02, 0.02, 0.02);
              mirMat.specularColor = new BABYLON.Color3(0, 0, 0);

              mesh.metadata = {
                id: obj.id, type: 'object', isMirror: true,
                mirrorConfig: { ...mirCfg }, mirrorTexture
              };

              // Register in global mirror tracking
              if (!scene.metadata.mirrorTextures) scene.metadata.mirrorTextures = [];
              scene.metadata.mirrorTextures = scene.metadata.mirrorTextures.filter(m => m.id !== obj.id);
              scene.metadata.mirrorTextures.push({
                id: obj.id, texture: mirrorTexture, mesh,
                bounces, recursionEnabled, maxDistance, blurKernel
              });

              // Single global mirror system: updates ALL planes + does recursive passes
              if (!scene.metadata._mirrorSystemRegistered) {
                scene.metadata._mirrorSystemRegistered = true;
                scene.registerBeforeRender(() => {
                  const mirrors = scene.metadata.mirrorTextures;
                  if (!mirrors || mirrors.length === 0) return;

                  // Clean disposed mirrors and mirrors with disposed textures
                  for (let i = mirrors.length - 1; i >= 0; i--) {
                    if (!mirrors[i].mesh || mirrors[i].mesh.isDisposed() || !mirrors[i].texture || mirrors[i].texture._disposed) {
                      mirrors.splice(i, 1);
                    }
                  }
                  if (mirrors.length === 0) return;

                  // Step 1: Update ALL mirror planes and render lists
                  for (const m of mirrors) {
                    m.mesh.computeWorldMatrix(true);
                    const wMat = m.mesh.getWorldMatrix();
                    const wNorm = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 0, -1), wMat).normalize();
                    const wPos = m.mesh.getAbsolutePosition();
                    const d = -BABYLON.Vector3.Dot(wNorm, wPos);
                    m.texture.mirrorPlane = new BABYLON.Plane(wNorm.x, wNorm.y, wNorm.z, d);

                    // Rebuild render list
                    if (!m.texture.renderList) m.texture.renderList = [];
                    m.texture.renderList.length = 0;
                    const maxDist = m.maxDistance || 100;
                    for (let j = 0; j < scene.meshes.length; j++) {
                      const sm = scene.meshes[j];
                      if (sm === m.mesh) continue;
                      if (!sm.isEnabled() || !sm.isVisible) continue;
                      if (maxDist > 0 && maxDist < 9999) {
                        const dist = BABYLON.Vector3.Distance(wPos, sm.getAbsolutePosition());
                        if (dist > maxDist) continue;
                      }
                      m.texture.renderList.push(sm);
                    }
                  }

                  // Step 2: Recursive reflection passes (only if 2+ recursive mirrors)
                  if (mirrors.length >= 2) {
                    const active = mirrors.filter(m2 => m2.recursionEnabled);
                    if (active.length >= 2) {
                      const maxB = Math.max(...active.map(m2 => m2.bounces || 1));
                      for (let pass = 0; pass < maxB - 1; pass++) {
                        for (const mirror of active) {
                          try { mirror.texture.render(); } catch { /* disposed */ }
                        }
                      }
                    }
                  }
                });
              }
            } else {
              // Reflection off — dark mirror surface
              mirMat.diffuseColor = new BABYLON.Color3(0.12, 0.12, 0.14);
              mirMat.specularColor = new BABYLON.Color3(0.6, 0.6, 0.6);
              mirMat.specularPower = 128;
              mesh.metadata = { id: obj.id, type: 'object', isMirror: true, mirrorConfig: { ...mirCfg } };
            }

            mesh.material = mirMat;
            mesh.receiveShadows = true;

            mesh.enableEdgesRendering(0.5);
            mesh.edgesWidth = 2.0;
            mesh.edgesColor = new BABYLON.Color4(0.5, 0.5, 0.6, 1.0);
            break;
          }
          case 'scaffolding': {
            // Modular ringlock scaffolding using thin instances
            const scaffConfig = obj.scaffoldConfig || { width: 3, depth: 2, height: 3 };
            const scaffRoot = buildScaffolding(scaffConfig, obj.id, scene);
            // Store the TransformNode as the mesh reference
            mesh = scaffRoot;
            mesh.metadata = { id: obj.id, type: 'object', isScaffolding: true, scaffoldConfig: { ...scaffConfig } };
            // Per UX request: scaffolding edges stay OFF by default (the
            // thin-instance ringlock geometry already reads cleanly without
            // outlines). The Show Edges toggle is hidden for scaffolding,
            // so we never auto-enable edges here.
            break;
          }
          case 'truss_system': {
            // Modular truss system using thin instances
            const trussConfig = obj.trussConfig || { structureType: 'straight', trussProfile: 'box', length: 3 };
            const trussRoot = buildTruss(trussConfig, obj.id, scene);
            mesh = trussRoot;
            mesh.metadata = { id: obj.id, type: 'object', isTruss: true, trussConfig: { ...trussConfig } };
            break;
          }
          case 'led_screen': {
            // LED Screen - modular LED wall with curvature support
            const led = obj.ledConfig || {};
            const tileW = (led.tileWidth || 500) / 1000;
            const tileH = (led.tileHeight || 500) / 1000;
            const tileD = (led.tileDepth || 80) / 1000;
            const cols = led.columns || 4;
            const rows = led.rows || 3;
            const totalW = tileW * cols;
            const totalH = tileH * rows;
            const curveAngle = led.curveAngle || 0;
            const ledMode = led.ledMode || 'standard';
            const isCorner = ledMode === 'corner';
            const isSoftCorner = ledMode === 'soft_corner';
            const isFlexible = ledMode === 'flexible';
            const cabinetStepMm = led.cabinetStep || (led.tileWidth || 500);

            // Root mesh for LED (bounding box, always invisible - children are the visible cabinets)
            mesh = BABYLON.MeshBuilder.CreateBox('obj_' + obj.id, {
              width: totalW, height: totalH, depth: tileD
            }, scene);
            mesh.isVisible = false;
            mesh.isPickable = true;

            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

            // Light gray cabinet material for visibility
            const cabinetMat = new BABYLON.StandardMaterial('ledCabinetMat_' + obj.id, scene);
            cabinetMat.diffuseColor = new BABYLON.Color3(0.45, 0.45, 0.45);
            cabinetMat.specularColor = new BABYLON.Color3(0.3, 0.3, 0.3);

            // Display material (light-grey LED surface by default so the
            // panel is visible in the dark scene even before content is
            // loaded; becomes emissive-white when an image / video
            // texture is assigned a few lines below — at which point the
            // texture fully overrides this default color).
            const displayMat = new BABYLON.StandardMaterial('ledDisplayMat_' + obj.id, scene);
            displayMat.diffuseColor = new BABYLON.Color3(0.643, 0.635, 0.635);   // #a4a2a2
            displayMat.specularColor = new BABYLON.Color3(0, 0, 0);
            displayMat.emissiveColor = new BABYLON.Color3(0.45, 0.45, 0.45);     // self-lit so unlit scenes still show it
            displayMat.backFaceCulling = false;

            if (led.contentUrl) {
              const texUrl = resolveAssetUrl(led.contentUrl);
              const tex = new BABYLON.Texture(texUrl, scene);
              displayMat.diffuseTexture = tex;
              displayMat.emissiveTexture = tex;
              displayMat.emissiveColor = new BABYLON.Color3(1, 1, 1);
            }

            // Helper: make a cabinet - uses .obj template if available, falls back to box
            const makeCabinet = (name, w, h, d) => {
              let cab;
              const template = ledTemplateRef.current;
              if (template) {
                cab = template.clone(name);
                cab.isVisible = true;
                const tmeta = template.metadata;
                cab.scaling = new BABYLON.Vector3(
                  w / tmeta.sizeX,
                  h / tmeta.sizeY,
                  d / tmeta.sizeZ
                );
              } else {
                cab = BABYLON.MeshBuilder.CreateBox(name, { width: w, height: h, depth: d }, scene);
              }
              cab.material = cabinetMat;
              cab.isPickable = true;
              cab.metadata = { id: obj.id, type: 'object' };
              cab.parent = mesh;
              return cab;
            };

            // Helper: make a display plane on a cabinet
            const makeDisplay = (name, w, h, parent) => {
              const disp = BABYLON.MeshBuilder.CreatePlane(name, { width: w, height: h, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
              disp.material = displayMat;
              disp.position.z = tileD / 2 + 0.001;
              disp.parent = parent;
              disp.isPickable = true;
              disp.metadata = { id: obj.id, type: 'object' };
              return disp;
            };

            // ─── SHARED HELPER: build merged cabinet + display meshes from per-tile poses ───
            // tiles = [{ cx, cy, cz, rotY, tileW, tileH, tileD, uvU0, uvU1, uvV0, uvV1 }]
            // One merged mesh for cabinets + (optionally) one for display. Scales to 10k+ tiles
            // with 2 draw calls total. Projection/shadow/beam systems are untouched.
            const buildMergedLEDMeshes = (tiles, hasContent, suffix) => {
              if (!tiles || tiles.length === 0) return;
              const n = tiles.length;
              const V_PER_BOX = 24, I_PER_BOX = 36;
              const cabPositions = new Float32Array(n * V_PER_BOX * 3);
              const cabNormals   = new Float32Array(n * V_PER_BOX * 3);
              const cabIndices   = new Uint32Array(n * I_PER_BOX);

              // Unit-box face definitions (corners in half-size coords; scaled per-tile below).
              const faces = [
                { nx: 0, ny: 0, nz: 1,  corners: [[-0.5,-0.5, 0.5],[ 0.5,-0.5, 0.5],[ 0.5, 0.5, 0.5],[-0.5, 0.5, 0.5]] }, // +Z
                { nx: 0, ny: 0, nz:-1,  corners: [[ 0.5,-0.5,-0.5],[-0.5,-0.5,-0.5],[-0.5, 0.5,-0.5],[ 0.5, 0.5,-0.5]] }, // -Z
                { nx: 1, ny: 0, nz: 0,  corners: [[ 0.5,-0.5, 0.5],[ 0.5,-0.5,-0.5],[ 0.5, 0.5,-0.5],[ 0.5, 0.5, 0.5]] }, // +X
                { nx:-1, ny: 0, nz: 0,  corners: [[-0.5,-0.5,-0.5],[-0.5,-0.5, 0.5],[-0.5, 0.5, 0.5],[-0.5, 0.5,-0.5]] }, // -X
                { nx: 0, ny: 1, nz: 0,  corners: [[-0.5, 0.5, 0.5],[ 0.5, 0.5, 0.5],[ 0.5, 0.5,-0.5],[-0.5, 0.5,-0.5]] }, // +Y
                { nx: 0, ny:-1, nz: 0,  corners: [[-0.5,-0.5,-0.5],[ 0.5,-0.5,-0.5],[ 0.5,-0.5, 0.5],[-0.5,-0.5, 0.5]] }, // -Y
              ];

              for (let i = 0; i < n; i++) {
                const t = tiles[i];
                const cosR = Math.cos(t.rotY || 0), sinR = Math.sin(t.rotY || 0);
                const vOff = i * V_PER_BOX;
                const iOff = i * I_PER_BOX;
                let v = vOff;
                for (let f = 0; f < 6; f++) {
                  const face = faces[f];
                  for (let k = 0; k < 4; k++) {
                    const c = face.corners[k];
                    const dx = c[0] * t.tileW, dy = c[1] * t.tileH, dz = c[2] * t.tileD;
                    // Rotate Y around tile origin
                    const rx = dx * cosR + dz * sinR;
                    const rz = -dx * sinR + dz * cosR;
                    const p3 = v * 3;
                    cabPositions[p3]     = t.cx + rx;
                    cabPositions[p3 + 1] = t.cy + dy;
                    cabPositions[p3 + 2] = t.cz + rz;
                    const nx = face.nx * cosR + face.nz * sinR;
                    const nz = -face.nx * sinR + face.nz * cosR;
                    cabNormals[p3]     = nx;
                    cabNormals[p3 + 1] = face.ny;
                    cabNormals[p3 + 2] = nz;
                    v++;
                  }
                  const base = vOff + f * 4;
                  const iStart = iOff + f * 6;
                  cabIndices[iStart]     = base;
                  cabIndices[iStart + 1] = base + 1;
                  cabIndices[iStart + 2] = base + 2;
                  cabIndices[iStart + 3] = base;
                  cabIndices[iStart + 4] = base + 2;
                  cabIndices[iStart + 5] = base + 3;
                }
              }

              const cabRoot = new BABYLON.Mesh('ledCabRoot_' + obj.id + suffix, scene);
              const cabVD = new BABYLON.VertexData();
              cabVD.positions = cabPositions;
              cabVD.indices = cabIndices;
              cabVD.normals = cabNormals;
              cabVD.applyToMesh(cabRoot);
              cabRoot.material = cabinetMat;
              cabRoot.isPickable = true;
              cabRoot.metadata = { id: obj.id, type: 'object' };
              cabRoot.parent = mesh;
              cabRoot.freezeWorldMatrix();

              if (hasContent) {
                const dispPositions = new Float32Array(n * 4 * 3);
                const dispUVs       = new Float32Array(n * 4 * 2);
                const dispNormals   = new Float32Array(n * 4 * 3);
                const dispIndices   = new Uint32Array(n * 6);

                for (let i = 0; i < n; i++) {
                  const t = tiles[i];
                  const hw = t.tileW / 2, hh = t.tileH / 2;
                  // Display on the -Z side of the tile so it faces the default front
                  // view when the LED sits at rotation.y = 0.
                  const zOff = -(t.tileD / 2 + 0.001);
                  const cosR = Math.cos(t.rotY || 0), sinR = Math.sin(t.rotY || 0);
                  // Tile-local BL, BR, TR, TL
                  const corners = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]];
                  const uvsPerCorner = [
                    // Flip U so content reads non-mirrored from the front view.
                    [1 - t.uvU0, t.uvV0],
                    [1 - t.uvU1, t.uvV0],
                    [1 - t.uvU1, t.uvV1],
                    [1 - t.uvU0, t.uvV1],
                  ];
                  const base = i * 4;
                  const p = i * 12, u = i * 8, idx = i * 6;
                  // Normal points -Z in tile-local space (rotated by rotY about Y)
                  const nrx = -sinR, nrz = -cosR;
                  for (let k = 0; k < 4; k++) {
                    const dx = corners[k][0], dy = corners[k][1];
                    const rx = dx * cosR + zOff * sinR;
                    const rz = -dx * sinR + zOff * cosR;
                    const p3 = p + k * 3;
                    dispPositions[p3]     = t.cx + rx;
                    dispPositions[p3 + 1] = t.cy + dy;
                    dispPositions[p3 + 2] = t.cz + rz;
                    dispNormals[p3]     = nrx;
                    dispNormals[p3 + 1] = 0;
                    dispNormals[p3 + 2] = nrz;
                    const u2 = u + k * 2;
                    dispUVs[u2]     = uvsPerCorner[k][0];
                    dispUVs[u2 + 1] = uvsPerCorner[k][1];
                  }
                  // Reverse winding so the front face points -Z (visible from front view).
                  dispIndices[idx]     = base;
                  dispIndices[idx + 1] = base + 2;
                  dispIndices[idx + 2] = base + 1;
                  dispIndices[idx + 3] = base;
                  dispIndices[idx + 4] = base + 3;
                  dispIndices[idx + 5] = base + 2;
                }

                const dispWall = new BABYLON.Mesh('ledDisp_' + obj.id + suffix + '_wall', scene);
                const dispVD = new BABYLON.VertexData();
                dispVD.positions = dispPositions;
                dispVD.indices = dispIndices;
                dispVD.uvs = dispUVs;
                dispVD.normals = dispNormals;
                dispVD.applyToMesh(dispWall);
                dispWall.material = displayMat; // displayMat has backFaceCulling=false for two-sided view
                dispWall.parent = mesh;
                dispWall.isPickable = true;
                dispWall.metadata = { id: obj.id, type: 'object' };
                dispWall.freezeWorldMatrix();
              }
            };

            if (isCorner) {
              // CORNER SYSTEM — merged-geometry build (scales to 10k+ tiles).
              // Two flat panels pivoting from a shared inner edge, both rotated around corner joint.
              const cornerAngle = (led.cornerAngle || 90) * (Math.PI / 180);
              const halfAngle = cornerAngle / 2;
              const leftCols = Math.max(1, led.leftColumns || Math.ceil(cols / 2));
              const rightCols = Math.max(1, led.rightColumns || Math.floor(cols / 2));
              const leftW = leftCols * tileW;
              const rightW = rightCols * tileW;
              const autoGapMm = Math.abs(led.cornerAngle || 90) / 90 * 0.2;
              const gapHalf = (autoGapMm / 1000) / 2;
              // side=0 is viewer's LEFT (sideRotY = +halfAngle → sidePosX < 0 → world -X),
              // side=1 is viewer's RIGHT. Match panel widths & UI column ranges so that
              // UI left columns (0..leftCols-1) render on viewer's LEFT — mirrors the
              // Standard LED WYSIWYG convention.
              const panelWidths = [leftW, rightW];

              const cornerTiles = [];
              const removedSet = new Set(led.removedTiles || []);
              const cornerTotalCols = leftCols + rightCols;

              for (let side = 0; side < 2; side++) {
                const pw = panelWidths[side];
                const sideRotY = side === 0 ? halfAngle : -halfAngle;
                const displayZ = tileD / 2 + 0.001;
                // Position each panel so that its INNER display edge (panel-local x=0,
                // z=-displayZ after the -Z-face winding fix) lands exactly at world
                // origin (0, y, 0) for both panels. Inner edge world-pos after rotation:
                //   x = sidePosX + (-displayZ)*sin(sideRotY)
                //   z = sidePosZ + (-displayZ)*cos(sideRotY)
                // Setting sidePosX = displayZ*sin(sideRotY) and
                //         sidePosZ = displayZ*cos(halfAngle) makes both sides meet at
                // (0, y, 0) regardless of the sign of cornerAngle — fixes the overlap
                // that appeared for negative angles (-90°, -30°, -10°).
                let sidePosX = displayZ * Math.sin(sideRotY);
                const sidePosZ = displayZ * Math.cos(halfAngle);
                if (gapHalf > 0) sidePosX += (side === 0 ? -1 : 1) * gapHalf;

                const cosS = Math.cos(sideRotY), sinS = Math.sin(sideRotY);
                const tilesInPanel = Math.max(1, Math.round(pw / tileW));
                const globalColStart = side === 0 ? 0 : leftCols;

                for (let t = 0; t < tilesInPanel; t++) {
                  for (let r = 0; r < rows; r++) {
                    // t=0 on side=0 is physical far-LEFT (localX = -pw+tileW/2, worldX
                    // most negative). Map it to UI col 0. t=0 on side=1 is near the
                    // corner vertex → UI col leftCols. No visualT flip here — UI col
                    // index increases left→right just like the viewer sees.
                    const globalCol = globalColStart + t;
                    const tileKey = `${r}-${globalCol}`;
                    const flippedR = rows - 1 - r;
                    if (removedSet.has(tileKey)) continue;

                    // Tile position in side-local space (side Y-rotation only affects X/Z)
                    const localX = side === 0
                      ? -pw + tileW / 2 + t * tileW
                      : tileW / 2 + t * tileW;
                    const localY = -totalH / 2 + tileH / 2 + flippedR * tileH;
                    // Rotate (localX, 0) around Y by sideRotY, translate by sidePos
                    const cx = sidePosX + localX * cosS;
                    const cy = localY;
                    const cz = sidePosZ + -localX * sinS;

                    // UV: use the same c/N pattern as Standard + Curved LEDs. The
                    // U-flip inside buildMergedLEDMeshes (1 - u) compensates for the
                    // -Z face winding so content reads non-mirrored from the front.
                    const u0 = globalCol / cornerTotalCols;
                    const u1 = (globalCol + 1) / cornerTotalCols;
                    const v0 = flippedR / rows, v1 = (flippedR + 1) / rows;

                    cornerTiles.push({
                      cx, cy, cz, rotY: sideRotY,
                      tileW, tileH, tileD,
                      uvU0: u0, uvU1: u1, uvV0: v0, uvV1: v1,
                    });
                  }
                }
              }

              buildMergedLEDMeshes(cornerTiles, !!led.contentUrl, '');
            } else if (isSoftCorner) {
              // SOFT CORNER: Two flat panels tangent to a 125mm curved corner piece (90° sweep).
              // Geometry convention: viewer looks from world -Z toward +Z. ALL display faces
              // (flat panels + ribbon) are on the -Z side of the structure so the viewer
              // always sees the FRONT (content) side. The arc is centered at (0,y,R) so the
              // corner tip passes through world origin (0,y,0) at theta=0 — this is the point
              // closest to the viewer. Flat panels are tangent to the arc at its endpoints
              // and extend BACKWARD (+Z) and sideways (∓X), matching a real-world soft-corner
              // LED module where the corner piece bows TOWARD the audience.
              const cornerArcLen = 0.125; // 125mm module arc length
              const scAngle = Math.PI / 2; // 90° sweep
              const R = cornerArcLen / scAngle; // arc radius ~0.0796m
              const scHalf = scAngle / 2; // 45°
              const numSegs = 24;
              const displayZ = tileD / 2 + 0.001;
              const sin45 = Math.sin(scHalf);
              const cos45 = Math.cos(scHalf);

              const leftCols = Math.max(1, led.leftColumns || Math.ceil(cols / 2));
              const rightCols = Math.max(1, led.rightColumns || Math.floor(cols / 2));
              const leftW = leftCols * tileW;
              const rightW = rightCols * tileW;

              // === 1. DISPLAY RIBBON (corner piece, always present, 125mm arc) ===
              // Arc point at theta: (R·sin θ, y, R − R·cos θ). theta=0 → (0,y,0) (tip/front),
              // theta=±π/4 → tangent endpoints at (±R·sin45, y, R·(1−cos45)).
              const frontPaths = [];
              for (let row = 0; row <= 1; row++) {
                const path = [];
                const y = row === 0 ? -totalH / 2 : totalH / 2;
                for (let i = 0; i <= numSegs; i++) {
                  const theta = -scHalf + (scAngle / numSegs) * i;
                  const x = R * Math.sin(theta);
                  const z = R - R * Math.cos(theta);
                  path.push(new BABYLON.Vector3(x, y, z));
                }
                frontPaths.push(path);
              }
              const displayRibbon = BABYLON.MeshBuilder.CreateRibbon(
                'ledSoftCornerRibbon_' + obj.id,
                { pathArray: frontPaths, sideOrientation: BABYLON.Mesh.DOUBLESIDE, closeArray: false },
                scene
              );
              displayRibbon.material = displayMat;
              displayRibbon.parent = mesh;
              displayRibbon.isPickable = true;
              displayRibbon.metadata = { id: obj.id, type: 'object' };

              // Back cabinet ribbon sits BEHIND the display ribbon (same arc, pushed +Z by tileD).
              const backR = R; // keep same arc shape
              const backPaths = [];
              for (let row = 0; row <= 1; row++) {
                const path = [];
                const y = row === 0 ? -totalH / 2 : totalH / 2;
                for (let i = 0; i <= numSegs; i++) {
                  const theta = -scHalf + (scAngle / numSegs) * i;
                  const x = backR * Math.sin(theta);
                  const z = tileD + (R - backR * Math.cos(theta));
                  path.push(new BABYLON.Vector3(x, y, z));
                }
                backPaths.push(path);
              }
              try {
                const backRibbon = BABYLON.MeshBuilder.CreateRibbon(
                  'ledSoftCornerBack_' + obj.id,
                  { pathArray: backPaths, sideOrientation: BABYLON.Mesh.DOUBLESIDE, closeArray: false },
                  scene
                );
                backRibbon.material = cabinetMat;
                backRibbon.parent = mesh;
                backRibbon.isPickable = false;
              } catch (e) {
                logger.warn('Failed to create soft corner back ribbon:', e);
              }

              // === 2. FLAT PANELS — tangent to arc at its endpoints ===
              // sideRotY = +scHalf for side=0 (viewer's LEFT, panel extends -X/+Z);
              //            -scHalf for side=1 (viewer's RIGHT, panel extends +X/+Z).
              // Inner edge (localX=0, tile-local z=-displayZ) lands on the arc endpoint:
              //   side=0 endpoint: (-R·sin45, y, R·(1−cos45))
              //   side=1 endpoint: (+R·sin45, y, R·(1−cos45))
              // Solving sidePosX + (-displayZ)·sin(sideRotY) = ∓R·sin45 and
              //         sidePosZ + (-displayZ)·cos(sideRotY) = R·(1−cos45):
              const panelWidths = [leftW, rightW];
              const scTiles = [];
              const removedSetSC = new Set(led.removedTiles || []);
              const scTotalCols = leftCols + rightCols;

              for (let side = 0; side < 2; side++) {
                const pw = panelWidths[side];
                const sideRotY = side === 0 ? scHalf : -scHalf;
                const sinS = Math.sin(sideRotY), cosS = Math.cos(sideRotY);
                const sidePosX = side === 0 ? -(R - displayZ) * sin45 : +(R - displayZ) * sin45;
                const sidePosZ = R - (R - displayZ) * cos45;

                const tilesInPanel = Math.max(1, Math.round(pw / tileW));
                const globalColStart = side === 0 ? 0 : leftCols;

                for (let t = 0; t < tilesInPanel; t++) {
                  for (let r = 0; r < rows; r++) {
                    // t=0 side=0: localX = -pw+tileW/2 → far-LEFT physical → UI col 0.
                    // t=0 side=1: localX = +tileW/2   → near-corner right → UI col leftCols.
                    const globalCol = globalColStart + t;
                    const tileKey = `${r}-${globalCol}`;
                    const flippedR = rows - 1 - r;
                    if (removedSetSC.has(tileKey)) continue;

                    const localX = side === 0
                      ? -pw + tileW / 2 + t * tileW
                      : tileW / 2 + t * tileW;
                    const localY = -totalH / 2 + tileH / 2 + flippedR * tileH;
                    const cx = sidePosX + localX * cosS;
                    const cy = localY;
                    const cz = sidePosZ + -localX * sinS;

                    const u0 = globalCol / scTotalCols;
                    const u1 = (globalCol + 1) / scTotalCols;
                    const v0 = flippedR / rows, v1 = (flippedR + 1) / rows;

                    scTiles.push({
                      cx, cy, cz, rotY: sideRotY,
                      tileW, tileH, tileD,
                      uvU0: u0, uvU1: u1, uvV0: v0, uvV1: v1,
                    });
                  }
                }
              }

              buildMergedLEDMeshes(scTiles, !!led.contentUrl, '_sc');

              // === 3. UNIFIED UV MAPPING across flat panels + curved ribbon ===
              const scDisplayMeshes = [];
              mesh.getChildMeshes(false).forEach(child => {
                if (child.name.includes('ledDisp_') || child.name.includes('ledSoftCornerRibbon_')) {
                  scDisplayMeshes.push(child);
                }
              });

              if (scDisplayMeshes.length > 0) {
                let minX = Infinity, maxX = -Infinity;
                let minY = Infinity, maxY = -Infinity;
                for (const dm of scDisplayMeshes) {
                  dm.computeWorldMatrix(true);
                  const positions = dm.getVerticesData(BABYLON.VertexBuffer.PositionKind);
                  if (!positions) continue;
                  const wm = dm.getWorldMatrix();
                  for (let i = 0; i < positions.length; i += 3) {
                    const wp = BABYLON.Vector3.TransformCoordinates(
                      new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]), wm
                    );
                    minX = Math.min(minX, wp.x); maxX = Math.max(maxX, wp.x);
                    minY = Math.min(minY, wp.y); maxY = Math.max(maxY, wp.y);
                  }
                }
                const tw = maxX - minX || 1;
                const th = maxY - minY || 1;
                for (const dm of scDisplayMeshes) {
                  const positions = dm.getVerticesData(BABYLON.VertexBuffer.PositionKind);
                  if (!positions) continue;
                  dm.computeWorldMatrix(true);
                  const wm = dm.getWorldMatrix();
                  const newUVs = [];
                  for (let i = 0; i < positions.length; i += 3) {
                    const wp = BABYLON.Vector3.TransformCoordinates(
                      new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]), wm
                    );
                    // Flip U so the texture's LEFT (u=0) maps to viewer's LEFT (world -X).
                    // The display mesh has reversed winding (from the -Z face-forward fix
                    // inside buildMergedLEDMeshes), which otherwise reads the texture
                    // backwards; (maxX - wp.x)/tw un-mirrors numbers/text across the surface.
                    newUVs.push((maxX - wp.x) / tw, (wp.y - minY) / th);
                  }
                  dm.setVerticesData(BABYLON.VertexBuffer.UVKind, newUVs);
                  dm.material = displayMat;
                }
              }
            } else if (curveAngle !== 0 && !isFlexible) {
              // SEGMENTED CURVED WALL — merged-geometry build (scales to 10k+ tiles)
              const safeStepMm = Math.max(10, Math.min(5000, cabinetStepMm || (led.tileWidth || 500)));
              const cabinetStepM = safeStepMm / 1000;
              const numCabinets = Math.max(1, Math.round(totalW / cabinetStepM));
              const cabWidth = totalW / numCabinets;
              const angleStep = curveAngle * (Math.PI / 180);
              const safeAngle = Math.abs(angleStep) < 0.0001 ? Math.sign(angleStep || 1) * 0.0001 : angleStep;
              const radius = cabWidth / safeAngle;
              const offsetIndex = (numCabinets - 1) / 2;

              const curvedTiles = [];
              const removedSetCurved = new Set(led.removedTiles || []);

              for (let c = 0; c < numCabinets; c++) {
                let colX, colZ, colRotY;
                if (Math.abs(radius) > 0.001) {
                  const theta = (c - offsetIndex) * angleStep;
                  colX = Math.sin(theta) * radius;
                  colZ = radius - Math.cos(theta) * radius;
                  colRotY = -theta;
                } else {
                  colX = -totalW / 2 + cabWidth / 2 + c * cabWidth;
                  colZ = 0;
                  colRotY = 0;
                }

                for (let r = 0; r < rows; r++) {
                  // UI grid's column 0 = viewer's LEFT. Here c=0 is the first cabinet
                  // placed at world -X (viewer's LEFT after the -Z / U-flip display
                  // convention used by buildMergedLEDMeshes). So match removedTiles
                  // against raw `c` to keep the UI and 3D mesh WYSIWYG, mirroring the
                  // Standard LED fix.
                  const tileKey = `${r}-${c}`;
                  const flippedR = rows - 1 - r;
                  if (removedSetCurved.has(tileKey)) continue;

                  // Tile at (0, ty, 0) in column-local space → rotation doesn't move center
                  const cy = -totalH / 2 + tileH / 2 + flippedR * tileH;
                  const u0 = c / numCabinets, u1 = (c + 1) / numCabinets;
                  const v0 = flippedR / rows, v1 = (flippedR + 1) / rows;

                  curvedTiles.push({
                    cx: colX, cy, cz: colZ, rotY: colRotY,
                    tileW: cabWidth, tileH, tileD,
                    uvU0: u0, uvU1: u1, uvV0: v0, uvV1: v1,
                  });
                }
              }

              buildMergedLEDMeshes(curvedTiles, !!led.contentUrl, '_curved');

            } else if (isFlexible && curveAngle !== 0) {
              // FLEXIBLE LED: smooth continuous curved surface using Ribbon
              // Uses signed radius — no dirSign needed, handles +/- angles naturally
              const numSegments = Math.max(cols * 2, 20);
              const totalAngleRad = curveAngle * (Math.PI / 180) * cols; // signed
              const radius = Math.abs(totalAngleRad) > 0.0001 ? totalW / totalAngleRad : 0;

              if (Math.abs(radius) > 0.001) {
                const startAngle = -totalAngleRad / 2;

                // Back surface: on the opposite side of display from viewer
                let backRadius = radius - Math.sign(radius) * tileD;
                if (Math.sign(backRadius) !== Math.sign(radius)) backRadius = Math.sign(radius) * 0.01;

                // Display surface paths
                const frontPaths = [];
                for (let row = 0; row <= 1; row++) {
                  const path = [];
                  const y = row === 0 ? -totalH / 2 : totalH / 2;
                  for (let i = 0; i <= numSegments; i++) {
                    const a = startAngle + (totalAngleRad / numSegments) * i;
                    path.push(new BABYLON.Vector3(
                      radius * Math.sin(a), y,
                      radius - radius * Math.cos(a)
                    ));
                  }
                  frontPaths.push(path);
                }

                // Display ribbon - DOUBLESIDE
                const ribbon = BABYLON.MeshBuilder.CreateRibbon('ledFlex_' + obj.id, {
                  pathArray: frontPaths, sideOrientation: BABYLON.Mesh.DOUBLESIDE
                }, scene);
                ribbon.material = displayMat;
                ribbon.parent = mesh;
                ribbon.isPickable = true;
                ribbon.metadata = { id: obj.id, type: 'object' };

                // Flip U so content reads left-to-right from the viewer's front. The
                // ribbon's default UV runs 0..1 along the path (viewer's left → right),
                // but the visible DOUBLESIDE face reads it mirrored; inverting U here
                // un-mirrors numbers/text on the curved surface. Same fix pattern as
                // the Soft Corner unified-UV block.
                const flexUVs = ribbon.getVerticesData(BABYLON.VertexBuffer.UVKind);
                if (flexUVs) {
                  for (let i = 0; i < flexUVs.length; i += 2) flexUVs[i] = 1 - flexUVs[i];
                  ribbon.setVerticesData(BABYLON.VertexBuffer.UVKind, flexUVs);
                }

                // Back surface paths (cabinet body)
                const backPathsArr = [];
                for (let row = 0; row <= 1; row++) {
                  const path = [];
                  const y = row === 0 ? -totalH / 2 : totalH / 2;
                  for (let i = 0; i <= numSegments; i++) {
                    const a = startAngle + (totalAngleRad / numSegments) * i;
                    path.push(new BABYLON.Vector3(
                      backRadius * Math.sin(a), y,
                      radius - backRadius * Math.cos(a)
                    ));
                  }
                  backPathsArr.push(path);
                }

                // Back ribbon - gray cabinet body
                try {
                  const backRibbon = BABYLON.MeshBuilder.CreateRibbon('ledFlexBack_' + obj.id, {
                    pathArray: backPathsArr, sideOrientation: BABYLON.Mesh.DOUBLESIDE
                  }, scene);
                  backRibbon.material = cabinetMat;
                  backRibbon.parent = mesh;
                  backRibbon.isPickable = false;
                } catch (e) {
                  logger.warn('Failed to create back ribbon:', e);
                }

              } else {
                // Flat flexible fallback
                const cab = makeCabinet('ledFlex_' + obj.id + '_flat', totalW, totalH, tileD);
                makeDisplay('ledDisp_' + obj.id, totalW, totalH, cab);
              }
            } else {
              // STANDARD FLAT WALL — SCALABLE BUILD (thin-instanced cabinets + merged display geometry)
              // Designed to handle 10,000+ tiles with ~2 draw calls instead of N×2.
              // Preserves: remove-tile-from-middle (no geometry/instance for removed tiles).
              // Does NOT touch: projectors, shadows, projection shader, beam/border toggles.
              mesh.isVisible = false;
              const removedTiles = new Set(led.removedTiles || []);

              // 1) Compute active tile list (skipping removed tiles)
              //    UI grid's column 0 = viewer's LEFT. The display face is rendered on
              //    the -Z side with flipped U, so viewer's LEFT maps to iteration
              //    index `c = cols - 1` (i.e. flippedC = 0). Match removedTiles against
              //    flippedC so the UI and 3D mesh stay WYSIWYG.
              const activeInstances = [];
              for (let c = 0; c < cols; c++) {
                for (let r = 0; r < rows; r++) {
                  const flippedR = rows - 1 - r; // row 0 in grid = top of wall
                  const flippedC = cols - 1 - c; // col 0 in grid = viewer's left
                  const tileKey = `${r}-${flippedC}`;
                  if (removedTiles.has(tileKey)) continue;
                  activeInstances.push({
                    x: -totalW / 2 + tileW / 2 + flippedC * tileW,
                    y: -totalH / 2 + tileH / 2 + flippedR * tileH,
                    r, c,
                  });
                }
              }

              // 2) CABINETS — single merged-geometry mesh for ALL tiles (1 draw call, zero per-instance overhead)
              //    Builds one mesh with simple-box geometry per tile. Scales linearly to 10k+ tiles
              //    without triggering per-mesh scene observers, shadow atlas updates, or React reconciliation.
              if (activeInstances.length > 0) {
                const n = activeInstances.length;
                const V_PER_BOX = 24; // 6 faces × 4 verts (separate normals per face)
                const I_PER_BOX = 36; // 6 faces × 6 indices
                const positions = new Float32Array(n * V_PER_BOX * 3);
                const normals = new Float32Array(n * V_PER_BOX * 3);
                const indices = new Uint32Array(n * I_PER_BOX);

                const hw = tileW / 2, hh = tileH / 2, hd = tileD / 2;
                // Face definitions: [normal, 4 corner offsets (dx, dy, dz)]
                const faces = [
                  { nx: 0, ny: 0, nz: 1,  corners: [[-hw,-hh, hd],[ hw,-hh, hd],[ hw, hh, hd],[-hw, hh, hd]] }, // +Z (front)
                  { nx: 0, ny: 0, nz:-1,  corners: [[ hw,-hh,-hd],[-hw,-hh,-hd],[-hw, hh,-hd],[ hw, hh,-hd]] }, // -Z (back)
                  { nx: 1, ny: 0, nz: 0,  corners: [[ hw,-hh, hd],[ hw,-hh,-hd],[ hw, hh,-hd],[ hw, hh, hd]] }, // +X (right)
                  { nx:-1, ny: 0, nz: 0,  corners: [[-hw,-hh,-hd],[-hw,-hh, hd],[-hw, hh, hd],[-hw, hh,-hd]] }, // -X (left)
                  { nx: 0, ny: 1, nz: 0,  corners: [[-hw, hh, hd],[ hw, hh, hd],[ hw, hh,-hd],[-hw, hh,-hd]] }, // +Y (top)
                  { nx: 0, ny:-1, nz: 0,  corners: [[-hw,-hh,-hd],[ hw,-hh,-hd],[ hw,-hh, hd],[-hw,-hh, hd]] }, // -Y (bottom)
                ];

                for (let i = 0; i < n; i++) {
                  const inst = activeInstances[i];
                  const cx = inst.x, cy = inst.y;
                  const vOff = i * V_PER_BOX;
                  const iOff = i * I_PER_BOX;
                  let v = vOff;
                  for (let f = 0; f < 6; f++) {
                    const face = faces[f];
                    for (let k = 0; k < 4; k++) {
                      const c = face.corners[k];
                      const p3 = v * 3;
                      positions[p3]     = cx + c[0];
                      positions[p3 + 1] = cy + c[1];
                      positions[p3 + 2] = c[2];
                      normals[p3]       = face.nx;
                      normals[p3 + 1]   = face.ny;
                      normals[p3 + 2]   = face.nz;
                      v++;
                    }
                    const base = vOff + f * 4;
                    const iStart = iOff + f * 6;
                    indices[iStart]     = base;
                    indices[iStart + 1] = base + 1;
                    indices[iStart + 2] = base + 2;
                    indices[iStart + 3] = base;
                    indices[iStart + 4] = base + 2;
                    indices[iStart + 5] = base + 3;
                  }
                }

                const cabRoot = new BABYLON.Mesh('ledCabRoot_' + obj.id, scene);
                const cabVertexData = new BABYLON.VertexData();
                cabVertexData.positions = positions;
                cabVertexData.indices = indices;
                cabVertexData.normals = normals;
                cabVertexData.applyToMesh(cabRoot);
                cabRoot.material = cabinetMat;
                cabRoot.isPickable = true;
                cabRoot.metadata = { id: obj.id, type: 'object' };
                cabRoot.parent = mesh;
                cabRoot.freezeWorldMatrix();
              }

              // 3) DISPLAY — single custom-geometry mesh covering all active tiles
              //    (removed tiles produce real geometry holes, so projection passes through).
              if (led.contentUrl && activeInstances.length > 0) {
                const n = activeInstances.length;
                const positions = new Float32Array(n * 4 * 3);
                const uvs = new Float32Array(n * 4 * 2);
                const normals = new Float32Array(n * 4 * 3);
                const indices = new Uint32Array(n * 6);
                // Display on the -Z side of the cabinets so content faces the default
                // front view when LED sits at rotation.y = 0.
                const zOff = -(tileD / 2 + 0.001);
                const halfW = tileW / 2;
                const halfH = tileH / 2;
                const invTotalW = 1 / totalW;
                const invTotalH = 1 / totalH;

                for (let i = 0; i < n; i++) {
                  const inst = activeInstances[i];
                  const x0 = inst.x - halfW, x1 = inst.x + halfW;
                  const y0 = inst.y - halfH, y1 = inst.y + halfH;
                  // U=0 on viewer's LEFT (world +X) so text reads correctly from front view.
                  const u0 = 1 - (x0 + totalW / 2) * invTotalW;
                  const u1 = 1 - (x1 + totalW / 2) * invTotalW;
                  const v0 = (y0 + totalH / 2) * invTotalH;
                  const v1 = (y1 + totalH / 2) * invTotalH;

                  const p = i * 12, u = i * 8, idx = i * 6, base = i * 4;
                  // BL
                  positions[p]     = x0; positions[p + 1]  = y0; positions[p + 2]  = zOff;
                  // BR
                  positions[p + 3] = x1; positions[p + 4]  = y0; positions[p + 5]  = zOff;
                  // TR
                  positions[p + 6] = x1; positions[p + 7]  = y1; positions[p + 8]  = zOff;
                  // TL
                  positions[p + 9] = x0; positions[p + 10] = y1; positions[p + 11] = zOff;

                  uvs[u]     = u0; uvs[u + 1] = v0;
                  uvs[u + 2] = u1; uvs[u + 3] = v0;
                  uvs[u + 4] = u1; uvs[u + 5] = v1;
                  uvs[u + 6] = u0; uvs[u + 7] = v1;

                  // Normal points -Z so the quad is front-facing from -Z camera
                  normals[p]     = 0; normals[p + 1]  = 0; normals[p + 2]  = -1;
                  normals[p + 3] = 0; normals[p + 4]  = 0; normals[p + 5]  = -1;
                  normals[p + 6] = 0; normals[p + 7]  = 0; normals[p + 8]  = -1;
                  normals[p + 9] = 0; normals[p + 10] = 0; normals[p + 11] = -1;

                  // Reverse winding so the front face points -Z (visible from front view).
                  indices[idx]     = base;     indices[idx + 1] = base + 2; indices[idx + 2] = base + 1;
                  indices[idx + 3] = base;     indices[idx + 4] = base + 3; indices[idx + 5] = base + 2;
                }

                const dispWall = new BABYLON.Mesh('ledDisp_' + obj.id + '_wall', scene);
                const vertexData = new BABYLON.VertexData();
                vertexData.positions = positions;
                vertexData.indices = indices;
                vertexData.uvs = uvs;
                vertexData.normals = normals;
                vertexData.applyToMesh(dispWall);
                dispWall.material = displayMat; // displayMat has backFaceCulling=false for two-sided view
                dispWall.parent = mesh;
                dispWall.isPickable = true;
                dispWall.metadata = { id: obj.id, type: 'object' };
                dispWall.freezeWorldMatrix();
              }

              // Display plane for walls WITHOUT removed tiles (backward compat for no content)
              if (!led.contentUrl) {
                const dispParent = new BABYLON.TransformNode('ledDispParent_' + obj.id, scene);
                dispParent.parent = mesh;
              }

              // 90° SOFT CORNER MODULES — smooth quarter-cylinder ribbon
              const addSoftCorner = (side) => {
                const cornerArcLen = 0.125; // 125mm module arc length
                const cornerAngle = Math.PI / 2; // 90°
                const R = cornerArcLen / cornerAngle; // arc radius ~0.0796m
                const numSegs = 24; // smooth segments
                const frontZ = tileD / 2 + 0.001; // display face Z (flush with flat wall display)
                const backR = Math.max(R - tileD, 0.005);

                // Arc center Z (behind the display surface)
                const arcCenterZ = frontZ - R;

                // Build front (display) ribbon paths — two horizontal paths (bottom & top)
                const frontPaths = [];
                for (let row = 0; row <= 1; row++) {
                  const path = [];
                  const y = row === 0 ? -totalH / 2 : totalH / 2;
                  for (let i = 0; i <= numSegs; i++) {
                    const theta = (cornerAngle / numSegs) * i;
                    const sinT = Math.sin(theta);
                    const cosT = Math.cos(theta);
                    const x = side === 'left'
                      ? -totalW / 2 - R * sinT
                      : totalW / 2 + R * sinT;
                    const z = arcCenterZ + R * cosT;
                    path.push(new BABYLON.Vector3(x, y, z));
                  }
                  frontPaths.push(path);
                }

                const displayRibbon = BABYLON.MeshBuilder.CreateRibbon(
                  'ledSoftCorner_' + side + '_' + obj.id,
                  { pathArray: frontPaths, sideOrientation: BABYLON.Mesh.DOUBLESIDE },
                  scene
                );
                displayRibbon.material = displayMat;
                displayRibbon.parent = mesh;
                displayRibbon.isPickable = true;
                displayRibbon.metadata = { id: obj.id, type: 'object' };

                // Build back (cabinet body) ribbon — occludes content from behind
                const backPaths = [];
                for (let row = 0; row <= 1; row++) {
                  const path = [];
                  const y = row === 0 ? -totalH / 2 : totalH / 2;
                  for (let i = 0; i <= numSegs; i++) {
                    const theta = (cornerAngle / numSegs) * i;
                    const sinT = Math.sin(theta);
                    const cosT = Math.cos(theta);
                    const x = side === 'left'
                      ? -totalW / 2 - backR * sinT
                      : totalW / 2 + backR * sinT;
                    const z = arcCenterZ + backR * cosT;
                    path.push(new BABYLON.Vector3(x, y, z));
                  }
                  backPaths.push(path);
                }

                try {
                  const backRibbon = BABYLON.MeshBuilder.CreateRibbon(
                    'ledSoftCornerBack_' + side + '_' + obj.id,
                    { pathArray: backPaths, sideOrientation: BABYLON.Mesh.DOUBLESIDE },
                    scene
                  );
                  backRibbon.material = cabinetMat;
                  backRibbon.parent = mesh;
                  backRibbon.isPickable = false;
                } catch (e) {
                  logger.warn('Failed to create soft corner back ribbon:', e);
                }
              };

              if (led.softCornerLeft) addSoftCorner('left');
              if (led.softCornerRight) addSoftCorner('right');

              // ─── AUTO-MERGE: Unified UV when corners are enabled ───
              if (led.softCornerLeft || led.softCornerRight) {
                // Collect all display meshes (main wall columns + corner ribbons)
                const displayMeshes = [];
                mesh.getChildMeshes(false).forEach(child => {
                  if (child.name.includes('ledDisp_') ||
                      child.name.includes('ledSoftCorner_') ||
                      child.name.includes('ledFlex_')) {
                    displayMeshes.push(child);
                  }
                });

                if (displayMeshes.length > 0) {
                  // Compute combined local bounding box from all display surfaces
                  let minX = Infinity, maxX = -Infinity;
                  let minY = Infinity, maxY = -Infinity;

                  for (const dm of displayMeshes) {
                    const positions = dm.getVerticesData(BABYLON.VertexBuffer.PositionKind);
                    if (!positions) continue;
                    // Positions are in parent-local space (child of mesh)
                    // For children parented to colNode → need to get absolute local pos
                    dm.computeWorldMatrix(true);
                    const wm = dm.getWorldMatrix();
                    for (let i = 0; i < positions.length; i += 3) {
                      const wp = BABYLON.Vector3.TransformCoordinates(
                        new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]), wm
                      );
                      minX = Math.min(minX, wp.x);
                      maxX = Math.max(maxX, wp.x);
                      minY = Math.min(minY, wp.y);
                      maxY = Math.max(maxY, wp.y);
                    }
                  }

                  const tw = maxX - minX || 1;
                  const th = maxY - minY || 1;

                  // Remap UVs using world-space coordinates and apply shared displayMat
                  for (const dm of displayMeshes) {
                    const positions = dm.getVerticesData(BABYLON.VertexBuffer.PositionKind);
                    if (!positions) continue;
                    dm.computeWorldMatrix(true);
                    const wm = dm.getWorldMatrix();
                    const newUVs = [];
                    for (let i = 0; i < positions.length; i += 3) {
                      const wp = BABYLON.Vector3.TransformCoordinates(
                        new BABYLON.Vector3(positions[i], positions[i + 1], positions[i + 2]), wm
                      );
                      newUVs.push((wp.x - minX) / tw, (wp.y - minY) / th);
                    }
                    dm.setVerticesData(BABYLON.VertexBuffer.UVKind, newUVs);
                    dm.material = displayMat; // shared material for seamless content
                  }
                }
              }
              // ─── END AUTO-MERGE ───
            }

            mesh.metadata = { id: obj.id, type: 'object', isLedScreen: true, ledConfig: { ...led } };
            break;
          }
          case 'mesh':
            // Load external 3D file
            if (obj.file_path || obj.file_id) {
              const meshParent = new BABYLON.TransformNode('mesh_' + obj.id, scene);
              // CRITICAL: Store transform data on metadata so async callback has correct values
              // This eliminates race conditions with ref updates
              meshParent.metadata = { 
                id: obj.id, 
                type: 'object',
                // Store transform data directly - used by async callback
                savedPosition: obj.position ? { ...obj.position } : { x: 0, y: 0, z: 0 },
                savedRotation: obj.rotation ? { ...obj.rotation } : { x: 0, y: 0, z: 0 },
                savedScale: obj.scale ? { ...obj.scale } : { x: 1, y: 1, z: 1 },
                dimensions: obj.dimensions ? { ...obj.dimensions } : null
              };
              
              // CRITICAL: Store meshParent in meshesRef immediately so scale updates work
              // Before this fix, imported models couldn't be scaled via dimensions panel
              meshesRef.current[obj.id] = meshParent;
              
              // Get the file extension to determine loader
              const ext = obj.file_ext || obj.file_path?.split('.').pop().toLowerCase() || 'obj';
              
              // Build file URL - MUST use serve endpoint (direct path goes to frontend)
              const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
              let fileUrl;
              
              if (obj.file_id) {
                // Use serve endpoint (correctly routes through API)
                fileUrl = `${backendUrl}/api/files/serve/${obj.file_id}`;
              } else if (obj.file_path) {
                // Handle blob URLs directly
                if (obj.file_path.startsWith('blob:') || obj.file_path.startsWith('http')) {
                  fileUrl = obj.file_path;
                } else if (obj.file_path.startsWith('/models/')) {
                  // Built-in static 3D models bundled under frontend/public/models.
                  // Use the frontend-relative path directly so the loader pulls
                  // them from the static asset path, not the backend serve API.
                  fileUrl = obj.file_path;
                } else {
                  // Extract file ID from path if possible
                  const pathParts = obj.file_path.split('/');
                  const filename = pathParts[pathParts.length - 1];
                  const fileId = filename.split('.')[0];
                  fileUrl = `${backendUrl}/api/files/serve/${fileId}`;
                }
              }
              
              // Determine the plugin extension for the loader
              let pluginExt = '.' + ext;
              let formatSupported = true;
              
              if (ext === 'glb' || ext === 'gltf') {
                pluginExt = '.glb';
              } else if (ext === 'obj') {
                pluginExt = '.obj';
              } else if (ext === 'fbx') {
                // FBX is supported via babylonjs-fbx-loader plugin
                // NOTE: ASCII FBX has known issues - GLB recommended
                pluginExt = '.fbx';
                logger.log('Loading FBX file using babylonjs-fbx-loader plugin');
                logger.log('NOTE: ASCII FBX files may have issues. Consider converting to GLB using Blender.');
              }
              
              // Store object ID for lookup in async callback
              const objId = obj.id;
              
              // Use SceneLoader to load the model asynchronously
              logger.log('Loading 3D file:', fileUrl, 'with plugin:', pluginExt);
              
              // Progress callback
              const progressCallback = (event) => {
                if (event.lengthComputable) {
                  const pct = Math.round(event.loaded / event.total * 100);
                  logger.log(`Loading: ${pct}%`);
                }
              };
              
              BABYLON.SceneLoader.ImportMeshAsync('', '', fileUrl, scene, progressCallback, pluginExt)
              .then((result) => {
                // ─── ASYNC LOAD GUARD (project-switch race fix) ───
                // If the user opened another project, hit New Project, or
                // simply deleted this object while the model was still
                // downloading, `meshParent` may already be disposed and
                // `objId` no longer present in `sceneObjectsRef`. Without
                // this guard the loaded meshes would parent to a disposed
                // node and become orphans visible in the new project's
                // scene that can't be selected or deleted.
                const stillExists = !!sceneObjectsRef.current?.find(o => o.id === objId);
                const parentDisposed = !meshParent || (meshParent.isDisposed && meshParent.isDisposed());
                if (!stillExists || parentDisposed) {
                  logger.log('Discarding stale async model load for', objId,
                    '— object gone or parent disposed');
                  try {
                    (result?.meshes || []).forEach(m => {
                      try { if (m && !m.isDisposed?.()) m.dispose(false, true); } catch (_e) {}
                    });
                    (result?.particleSystems || []).forEach(ps => {
                      try { ps.dispose(); } catch (_e) {}
                    });
                    (result?.skeletons || []).forEach(sk => {
                      try { sk.dispose(); } catch (_e) {}
                    });
                  } catch (_e) { /* best effort cleanup */ }
                  if (obj?._loadingToastId) {
                    try { toast.dismiss(obj._loadingToastId); } catch (_e) {}
                  }
                  return;
                }

                // CRITICAL FIX: Use multiple fallback sources for transform data
                // Priority: 1. meshParent.metadata (stored at creation time, most reliable)
                //           2. sceneObjectsRef.current (may be stale in race conditions)
                //           3. closure obj (definitely stale if state changed)
                const metadataTransforms = meshParent.metadata;
                const latestObj = sceneObjectsRef.current?.find(o => o.id === objId);
                const currentObjData = latestObj || obj;
                
                // Get transforms from the most reliable source (metadata stored at creation)
                const savedPosition = metadataTransforms?.savedPosition || currentObjData.position || { x: 0, y: 0, z: 0 };
                const savedRotation = metadataTransforms?.savedRotation || currentObjData.rotation || { x: 0, y: 0, z: 0 };
                const savedScale = metadataTransforms?.savedScale || currentObjData.scale || { x: 1, y: 1, z: 1 };
                
                logger.log('Loaded meshes:', result.meshes.length, 'for object:', objId, 'with transforms rot:', savedRotation);
                
                // Dismiss loading toast if present
                if (currentObjData._loadingToastId) {
                  toast.dismiss(currentObjData._loadingToastId);
                  toast.success(`Loaded ${currentObjData.name} (${result.meshes.length} meshes)`);
                }
                
                // Create a default SOLID material for all meshes
                // Use dark grey with good ambient lighting so models are always visible
                const defaultMat = new BABYLON.StandardMaterial('meshMat_' + objId, scene);
                defaultMat.diffuseColor = new BABYLON.Color3(0.4, 0.4, 0.4); // Dark grey
                defaultMat.ambientColor = new BABYLON.Color3(0.3, 0.3, 0.3); // Add ambient so visible without direct light
                defaultMat.emissiveColor = new BABYLON.Color3(0.1, 0.1, 0.1); // Slight self-illumination
                defaultMat.specularColor = new BABYLON.Color3(0.2, 0.2, 0.2);
                defaultMat.backFaceCulling = false;
                defaultMat.twoSidedLighting = true;
                defaultMat.wireframe = false; // Ensure solid rendering
                configureMaterialForProjectors(defaultMat); // Support multiple projectors
                
                // Count total vertices for performance warning
                let totalVertices = 0;
                
                result.meshes.forEach((loadedMesh, index) => {
                  const vertexCount = loadedMesh.getTotalVertices ? loadedMesh.getTotalVertices() : 0;
                  totalVertices += vertexCount;
                  logger.log(`  Mesh ${index}: ${loadedMesh.name}, vertices: ${vertexCount}, hasMat: ${!!loadedMesh.material}`);
                  
                  loadedMesh.parent = meshParent;
                  loadedMesh.metadata = { id: objId, type: 'object' };
                  loadedMesh.receiveShadows = true;
                  
                  // ALWAYS apply our default material to ensure visibility
                  // Many OBJ files come with black/missing materials
                  if (vertexCount > 0) {
                    // Check if mesh has a usable material
                    const hasBrightMaterial = loadedMesh.material && (
                      (loadedMesh.material.diffuseColor && 
                       (loadedMesh.material.diffuseColor.r > 0.1 || 
                        loadedMesh.material.diffuseColor.g > 0.1 || 
                        loadedMesh.material.diffuseColor.b > 0.1)) ||
                      (loadedMesh.material.albedoColor &&
                       (loadedMesh.material.albedoColor.r > 0.1 || 
                        loadedMesh.material.albedoColor.g > 0.1 || 
                        loadedMesh.material.albedoColor.b > 0.1))
                    );
                    
                    if (!hasBrightMaterial) {
                      // Material is too dark or missing - apply our default
                      const clonedMat = defaultMat.clone(loadedMesh.name + '_mat');
                      configureMaterialForProjectors(clonedMat); // Ensure multi-projector support
                      loadedMesh.material = clonedMat;
                    } else if (loadedMesh.material) {
                      // Has bright material - configure it for multi-projector and ensure solid
                      configureMaterialForProjectors(loadedMesh.material);
                      if ('wireframe' in loadedMesh.material) {
                        loadedMesh.material.wireframe = false;
                      }
                      if ('backFaceCulling' in loadedMesh.material) {
                        loadedMesh.material.backFaceCulling = false;
                      }
                    }
                    
                    // Enable silver edge rendering for loaded mesh visibility
                    // Using epsilon of 0.9 to show more edges (default is 0.95).
                    // Skip when the parent object explicitly has show_edges === false
                    // (e.g. built-in human models look cleaner without polygon outlines).
                    if (currentObjData?.show_edges !== false) {
                      loadedMesh.enableEdgesRendering(0.5);
                      loadedMesh.edgesWidth = 2.0;
                      loadedMesh.edgesColor = new BABYLON.Color4(0.75, 0.75, 0.75, 1.0); // Silver edges for loaded meshes
                    }
                  }
                    
                    // Add to shadow generators - but only meshes with geometry
                    // Shadow caster list is managed in the projectors useEffect, not here
                    // This prevents duplicate additions and improves performance
                  });
                  
                  // CRITICAL FIX: Reset parent transforms to identity BEFORE bounding box and baking.
                  // bakeCurrentTransformIntoVertices() uses computeWorldMatrix() which includes
                  // parent transforms. The useEffect sync loop may have already set rotation/position
                  // on meshParent — if those are active during baking, they get baked into child
                  // vertices AND re-applied via the parent transform = DOUBLE application.
                  // (e.g. 180° rotation baked + 180° parent = 360° = visually 0°)
                  meshParent.position = BABYLON.Vector3.Zero();
                  meshParent.rotationQuaternion = null;
                  meshParent.rotation = BABYLON.Vector3.Zero();
                  meshParent.scaling = new BABYLON.Vector3(1, 1, 1);
                  meshParent.computeWorldMatrix(true);
                  
                  // Calculate ACTUAL dimensions from the loaded mesh's bounding box
                  // This fixes the issue where imported 3D models show default 1m×1m×1m
                  // Only calculate dimensions on FIRST LOAD - skip if dimensions already exist
                  const hasExistingDimensions = currentObjData.dimensions && 
                    currentObjData.dimensions.width > 0 && 
                    currentObjData.dimensions.height > 0 && 
                    currentObjData.dimensions.depth > 0 &&
                    (currentObjData.dimensions.width !== 1 || currentObjData.dimensions.height !== 1 || currentObjData.dimensions.depth !== 1);
                  
                  // Always calculate bounding box for centering
                  try {
                    // Get overall bounding box for all loaded meshes
                    let minX = Infinity, minY = Infinity, minZ = Infinity;
                    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
                    
                    result.meshes.forEach(m => {
                      if (m.getBoundingInfo) {
                        const boundingInfo = m.getBoundingInfo();
                        const min = boundingInfo.boundingBox.minimumWorld;
                        const max = boundingInfo.boundingBox.maximumWorld;
                        minX = Math.min(minX, min.x);
                        minY = Math.min(minY, min.y);
                        minZ = Math.min(minZ, min.z);
                        maxX = Math.max(maxX, max.x);
                        maxY = Math.max(maxY, max.y);
                        maxZ = Math.max(maxZ, max.z);
                      }
                    });
                    
                    if (minX !== Infinity) {
                      const actualDimensions = {
                        width: Math.abs(maxX - minX),
                        height: Math.abs(maxY - minY),
                        depth: Math.abs(maxZ - minZ)
                      };
                      
                      // CENTER the model at origin (many models have offset coordinates)
                      const centerX = (minX + maxX) / 2;
                      const centerY = minY; // Keep bottom at ground level
                      const centerZ = (minZ + maxZ) / 2;
                      
                      // Calculate scale factor - only on first load
                      let scaleFactor = 1;
                      if (!hasExistingDimensions) {
                        // First load - calculate dimensions and scale
                        logger.log('Calculated actual dimensions for', obj.name, ':', actualDimensions);
                        
                        // AUTO-SCALE large models to fit within MAX_MODEL_SIZE
                        const MAX_MODEL_SIZE = 50; // meters
                        const maxDim = Math.max(actualDimensions.width, actualDimensions.height, actualDimensions.depth);
                        
                        if (maxDim > MAX_MODEL_SIZE) {
                          scaleFactor = MAX_MODEL_SIZE / maxDim;
                          logger.log(`Model too large (${maxDim.toFixed(1)}m), auto-scaling by ${scaleFactor.toFixed(6)} to fit ${MAX_MODEL_SIZE}m`);
                        }
                      } else {
                        // Existing dimensions - use scale from saved state
                        logger.log('Using existing dimensions for', currentObjData.name, ':', currentObjData.dimensions);
                        // Calculate scale factor based on saved vs actual dimensions
                        const savedWidth = currentObjData.dimensions.width;
                        const actualWidth = actualDimensions.width;
                        if (actualWidth > 0) {
                          scaleFactor = savedWidth / actualWidth;
                        }
                      }
                      
                      logger.log(`Model bounding box: min(${minX.toFixed(0)}, ${minY.toFixed(0)}, ${minZ.toFixed(0)}) max(${maxX.toFixed(0)}, ${maxY.toFixed(0)}, ${maxZ.toFixed(0)})`);
                      logger.log(`Centering model and applying scale factor: ${scaleFactor.toFixed(6)}`);
                      
                      // Apply centering and scaling to each child mesh, then bake into vertices
                      // This keeps meshParent.scaling at 1,1,1 so UI shows scale as 1
                      result.meshes.forEach(m => {
                        if (m.position) {
                          // Center the mesh
                          m.position.x -= centerX;
                          m.position.y -= centerY;
                          m.position.z -= centerZ;
                          
                          // Apply scale factor to position
                          m.position.x *= scaleFactor;
                          m.position.y *= scaleFactor;
                          m.position.z *= scaleFactor;
                        }
                        
                        // Apply scale to mesh
                        if (m.scaling) {
                          m.scaling = new BABYLON.Vector3(scaleFactor, scaleFactor, scaleFactor);
                        }
                        
                        // Bake all transforms into vertices
                        if (m.bakeCurrentTransformIntoVertices) {
                          try {
                            m.bakeCurrentTransformIntoVertices();
                          } catch (e) {
                            // Some meshes might not support this
                          }
                        }
                        
                        // Reset transforms after baking
                        if (m.position) {
                          m.position = new BABYLON.Vector3(0, 0, 0);
                        }
                        if (m.scaling) {
                          m.scaling = new BABYLON.Vector3(1, 1, 1);
                        }
                      });
                      
                      // Keep meshParent at scale 1 (transforms are baked into meshes)
                      meshParent.scaling = new BABYLON.Vector3(1, 1, 1);
                      
                      // CRITICAL FIX: Apply saved transforms from metadata (most reliable source)
                      // This avoids race conditions with sceneObjectsRef updates
                      
                      // Apply saved position from metadata
                      meshParent.position = new BABYLON.Vector3(
                        savedPosition.x || 0,
                        savedPosition.y || 0,
                        savedPosition.z || 0
                      );
                      
                      // Apply saved rotation from metadata
                      // Clear rotationQuaternion to use Euler angles
                      meshParent.rotationQuaternion = null;
                      meshParent.rotation = new BABYLON.Vector3(
                        BABYLON.Tools.ToRadians(savedRotation.x || 0),
                        BABYLON.Tools.ToRadians(savedRotation.y || 0),
                        BABYLON.Tools.ToRadians(savedRotation.z || 0)
                      );
                      
                      logger.log('Applied transforms to loaded mesh:', currentObjData.name, 
                        'pos:', savedPosition, 'rot:', savedRotation);
                      
                      // Only update dimensions on first load
                      if (!hasExistingDimensions) {
                        // Update dimensions to reflect scaled size
                        const scaledDimensions = {
                          width: actualDimensions.width * scaleFactor,
                          height: actualDimensions.height * scaleFactor,
                          depth: actualDimensions.depth * scaleFactor
                        };
                        
                        // Update the scene object
                        if (updateSceneObject) {
                          updateSceneObject(objId, { 
                            dimensions: scaledDimensions,
                            scale: { x: 1, y: 1, z: 1 },
                            originalDimensions: actualDimensions
                          });
                        }
                        
                        // Also update meshParent metadata with dimensions to prevent unnecessary recreation
                        meshParent.metadata.dimensions = scaledDimensions;
                        
                        const maxDim = Math.max(actualDimensions.width, actualDimensions.height, actualDimensions.depth);
                        if (maxDim > 50) {
                          logger.log('Scaled dimensions:', scaledDimensions);
                          toast.info(`Model scaled to fit (${scaledDimensions.width.toFixed(1)}m × ${scaledDimensions.height.toFixed(1)}m × ${scaledDimensions.depth.toFixed(1)}m)`, { duration: 4000 });
                        }
                      }
                    }
                  } catch (dimError) {
                    logger.warn('Could not calculate mesh dimensions:', dimError);
                  }
                  
                  // CRITICAL: ALWAYS apply transforms AFTER bounding box processing
                  // This ensures transforms are applied even if bounding box calculation fails
                  meshParent.position = new BABYLON.Vector3(
                    savedPosition.x || 0,
                    savedPosition.y || 0,
                    savedPosition.z || 0
                  );
                  
                  // Clear rotationQuaternion to use Euler angles
                  meshParent.rotationQuaternion = null;
                  meshParent.rotation = new BABYLON.Vector3(
                    BABYLON.Tools.ToRadians(savedRotation.x || 0),
                    BABYLON.Tools.ToRadians(savedRotation.y || 0),
                    BABYLON.Tools.ToRadians(savedRotation.z || 0)
                  );
                  
                  // Apply saved scale (user's gizmo scale, separate from dimension-baked scaling)
                  meshParent.scaling = new BABYLON.Vector3(
                    savedScale.x || 1,
                    savedScale.y || 1,
                    savedScale.z || 1
                  );
                  
                  // Warn about high vertex count (causes browser lag)
                  logger.log('Total vertices loaded:', totalVertices);
                  if (totalVertices > 500000) {
                    toast.warning(`High polygon model (${(totalVertices/1000).toFixed(0)}K vertices). Consider simplifying in Blender.`, { duration: 8000 });
                  } else if (totalVertices > 200000) {
                    toast.info(`Model has ${(totalVertices/1000).toFixed(0)}K vertices. May affect performance.`, { duration: 5000 });
                  }
                  
                  // Apply edited mesh data if available (from mesh editor)
                  if (currentObjData.editedMeshData && result.meshes.length > 0) {
                    // Find the first mesh with geometry to update
                    const targetMesh = result.meshes.find(m => m.getTotalVertices && m.getTotalVertices() > 0);
                    if (targetMesh && currentObjData.editedMeshData.positions && currentObjData.editedMeshData.indices) {
                      try {
                        // Use string constants for vertex buffer kinds
                        targetMesh.updateVerticesData(
                          'position',
                          new Float32Array(currentObjData.editedMeshData.positions)
                        );
                        if (currentObjData.editedMeshData.normals) {
                          targetMesh.updateVerticesData(
                            'normal',
                            new Float32Array(currentObjData.editedMeshData.normals)
                          );
                        }
                        targetMesh.setIndices(currentObjData.editedMeshData.indices);
                        targetMesh.refreshBoundingInfo();
                        logger.log('Applied edited mesh data for:', objId);
                      } catch (err) {
                        logger.error('Failed to apply edited mesh data:', err);
                      }
                    }
                  }
                  
                  // Apply deleted/hidden components if saved
                  if (currentObjData.deletedComponents && currentObjData.deletedComponents.length > 0) {
                    logger.log('Applying deleted components:', currentObjData.deletedComponents);
                    result.meshes.forEach(m => {
                      if (currentObjData.deletedComponents.includes(m.id) || currentObjData.deletedComponents.includes(m.name)) {
                        logger.log('Disposing deleted component:', m.name);
                        m.dispose();
                      }
                    });
                  }
                  
                  if (currentObjData.hiddenComponents && currentObjData.hiddenComponents.length > 0) {
                    logger.log('Applying hidden components:', currentObjData.hiddenComponents);
                    result.meshes.forEach(m => {
                      if (currentObjData.hiddenComponents.includes(m.id) || currentObjData.hiddenComponents.includes(m.name)) {
                        logger.log('Hiding component:', m.name);
                        m.isVisible = false;
                      }
                    });
                  }
                  
                  // Register loaded meshes with projection system
                  // This is needed because 3D models load asynchronously.
                  // Respect per-object Projection Mode — None mode and
                  // Reverse-without-UV must NOT be registered as receivers,
                  // otherwise the saved "None" setting silently breaks on
                  // reload (mesh starts receiving projection again as soon
                  // as the async loader resolves).
                  const _objMode = currentObjData.projectionMode
                    || (currentObjData.uvMappingEnabled === true ? 'reverse' : 'normal');
                  const _shouldReceive = !currentObjData.mtlData
                    && _objMode !== 'none'
                    && !(_objMode === 'reverse' && currentObjData.uvMappingEnabled !== true);
                  if (projectionSystemRef.current && _shouldReceive) {
                    result.meshes.forEach(loadedMesh => {
                      if (loadedMesh && loadedMesh.getTotalVertices && loadedMesh.getTotalVertices() > 0 && loadedMesh.isVisible) {
                        // Set mesh metadata BEFORE registration so texture URL is available
                        loadedMesh.metadata = {
                          ...(loadedMesh.metadata || {}),
                          id: currentObjData.id,
                          type: 'object',
                          textureUrl: currentObjData.textureUrl,
                          textureName: currentObjData.textureName,
                          textureBrightness: currentObjData.textureBrightness,
                          uvMappingEnabled: currentObjData.uvMappingEnabled,
                          objectData: {
                            textureUrl: currentObjData.textureUrl,
                            uvMappingEnabled: currentObjData.uvMappingEnabled,
                            textureBrightness: currentObjData.textureBrightness
                          }
                        };
                        
                        if (!projectionSystemRef.current.receiverMeshes.has(loadedMesh.uniqueId || loadedMesh.name)) {
                          projectionSystemRef.current.registerReceiver(loadedMesh, loadedMesh.material);
                          logger.log('Registered loaded 3D mesh for projections:', loadedMesh.name);
                        }
                        
                        // Re-apply edge rendering AFTER material registration to ensure it persists.
                        // This is needed because ProjectionSystem may modify the material.
                        // Honor show_edges === false (used by built-in human models).
                        if (currentObjData?.show_edges !== false) {
                          loadedMesh.enableEdgesRendering(0.5);
                          loadedMesh.edgesWidth = 2.0;
                          loadedMesh.edgesColor = new BABYLON.Color4(0.75, 0.75, 0.75, 1.0);
                        } else if (loadedMesh.edgesRenderer) {
                          loadedMesh.disableEdgesRendering();
                        }
                      }
                    });
                    // Trigger projection update
                    projectionSystemRef.current.updateProjectors(projectors);
                  }
                })
                .catch((error) => {
                  logger.error('Failed to load 3D file:', error);
                  logger.error('File URL:', fileUrl, 'Plugin:', pluginExt);

                  // ─── ASYNC LOAD GUARD (project-switch race fix) ───
                  // If the user switched projects / deleted the obj while
                  // the load was in flight we must NOT create the orange
                  // placeholder under a now-disposed parent.
                  const stillExists = !!sceneObjectsRef.current?.find(o => o.id === obj.id);
                  const parentDisposed = !meshParent || (meshParent.isDisposed && meshParent.isDisposed());
                  if (!stillExists || parentDisposed) {
                    if (obj._loadingToastId) {
                      try { toast.dismiss(obj._loadingToastId); } catch (_e) {}
                    }
                    return;
                  }

                  // Dismiss loading toast if present
                  if (obj._loadingToastId) {
                    toast.dismiss(obj._loadingToastId);
                  }
                  
                  // Show helpful error message based on file type
                  if (ext === 'fbx') {
                    toast.error(`FBX load failed. ASCII FBX files have known issues. Please convert to GLB using Blender.`, { duration: 10000 });
                  } else {
                    toast.error(`Failed to load ${obj.name}: ${error.message || 'Unknown error'}`);
                  }
                  
                  // Create a placeholder box if loading fails
                  const placeholder = BABYLON.MeshBuilder.CreateBox('placeholder_' + obj.id, { size: 1 }, scene);
                  placeholder.parent = meshParent;
                  const placeholderMat = new BABYLON.StandardMaterial('placeholderMat_' + obj.id, scene);
                  placeholderMat.diffuseColor = new BABYLON.Color3(1, 0.5, 0); // Orange for error
                  placeholderMat.wireframe = true;
                  placeholder.material = placeholderMat;
                });
              
              mesh = meshParent;
            } else {
              // No file path, create placeholder
              mesh = BABYLON.MeshBuilder.CreateBox('obj_' + obj.id, { size: 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
            }
            break;
          default:
            mesh = BABYLON.MeshBuilder.CreateBox('obj_' + obj.id, { size: 1, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
        }

        // Only apply material and shadow settings to actual meshes, not TransformNodes
        // Skip scrim_screen and led_screen as they have their own materials already set
        if (mesh instanceof BABYLON.Mesh && normalizedType !== 'scrim_screen' && normalizedType !== 'led_screen' && normalizedType !== 'mirror') {
          
          if (obj.mtlData) {
            // MTL material applied — parse and use it, skip projection registration
            const mtlMat = parseMTLToMaterial(obj.mtlData, 'mtl_' + obj.id, scene);
            mtlMat.backFaceCulling = false;
            mtlMat.twoSidedLighting = true;
            mesh.material = mtlMat;
            mesh.receiveShadows = true;

            mesh.enableEdgesRendering(0.5);
            mesh.edgesWidth = 2.0;
            mesh.edgesColor = new BABYLON.Color4(1.0, 1.0, 1.0, 1.0);
          } else {
          const mat = new BABYLON.StandardMaterial('mat_' + obj.id, scene);
          // Set diffuse color from object color or use white default for projection visibility
          const objColor = obj.color ? BABYLON.Color3.FromHexString(obj.color) : new BABYLON.Color3(1, 1, 1);
          mat.diffuseColor = objColor;
          mat.ambientColor = objColor.scale(0.3); // Some ambient for visibility without light
          mat.emissiveColor = objColor.scale(0.15); // Slight self-illumination so visible without projector light
          mat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1); // Low specular
          mat.backFaceCulling = false;
          mat.twoSidedLighting = true; // Light both sides of the mesh for projection visibility
          
          // Apply texture if textureUrl is set
          if (obj.textureUrl) {
            const texture = new BABYLON.Texture(resolveAssetUrl(obj.textureUrl), scene, false, true, BABYLON.Texture.TRILINEAR_SAMPLINGMODE);
            texture.hasAlpha = false;
            texture.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
            texture.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
            mat.diffuseTexture = texture;
            // When texture is applied, set diffuse to white so texture colors show correctly
            mat.diffuseColor = new BABYLON.Color3(1, 1, 1);
            
            // Apply texture brightness using emissive color
            const brightness = (obj.textureBrightness ?? 100) / 100;
            // Use emissive to brighten the texture without affecting scene lighting
            mat.emissiveColor = new BABYLON.Color3(brightness * 0.5, brightness * 0.5, brightness * 0.5);
            
            // Apply contrast and saturation via imageProcessingConfiguration
            const contrast = (obj.textureContrast ?? 100) / 100;
            const saturation = (obj.textureSaturation ?? 100) / 100;
            if (contrast !== 1.0 || saturation !== 1.0) {
              mat.imageProcessingConfiguration = new BABYLON.ImageProcessingConfiguration();
              mat.imageProcessingConfiguration.isEnabled = true;
              mat.imageProcessingConfiguration.contrast = contrast * 2;
              mat.imageProcessingConfiguration.exposure = 1.0;
              mat.cameraColorCurvesEnabled = true;
              const curves = new BABYLON.ColorCurves();
              curves.globalSaturation = (saturation - 1.0) * 100;
              mat.cameraColorCurves = curves;
            }
          }
          
          configureMaterialForProjectors(mat); // Support multiple projectors
          mesh.material = mat;
          mesh.receiveShadows = true;

          // Universal Texture Mapping — only activates when a texture is
          // actually applied; otherwise the plugin stays disabled and costs
          // nothing. Safe no-op for meshes without a diffuse texture.
          if (obj.textureUrl) {
            applyUniversalMapping(mesh, obj, projectionSystemRef.current);
          }
          
          // Enable white edge rendering for object visibility (thin white border like walls/floors)
          if (obj.show_edges !== false) {
            mesh.enableEdgesRendering(0.5);
            mesh.edgesWidth = 2.0;
            mesh.edgesColor = new BABYLON.Color4(1.0, 1.0, 1.0, 1.0); // White edges for primitives
          }
          }
          
          // Shadow casters are managed centrally in the projectors useEffect
          // This prevents duplicate additions and crashes with complex models
        }
        
        mesh.metadata = { 
          ...(mesh.metadata || {}), 
          id: obj.id, 
          type: 'object', 
          dimensions: obj.dimensions, 
          baseSize: { ...obj.dimensions },
          wallThickness: obj.wallThickness || 0,
          arcAngle: obj.arcAngle || 360,
          topCap: obj.topCap !== false,
          bottomCap: obj.bottomCap !== false,
          textureBrightness: obj.textureBrightness,
          textureUrl: obj.textureUrl,
          mtlData: obj.mtlData || null,
          show_edges: obj.show_edges,
          objectData: {
            textureUrl: obj.textureUrl,
            uvMappingEnabled: obj.uvMappingEnabled,
            textureBrightness: obj.textureBrightness
          }
        };
        // Store mesh reference
        meshesRef.current[obj.id] = mesh;

        // SELF-HEAL gizmo attachment.
        // If this object is the one currently selected (e.g. it was just
        // added via "Add LED" — which sets selectedObjectId in the same tick
        // as the sceneObjects mutation, before the mesh existed), the
        // selection effect already ran and bailed because meshesRef wasn't
        // populated yet. We attach the gizmo here so the user can move it
        // immediately, without needing a page refresh. LED-specific child
        // unfreeze happens here too so tiles follow the gizmo from the very
        // first drag tick.
        if (
          selectedObjectIdRef.current === obj.id &&
          gizmoManagerRef.current &&
          transformModeRef.current &&
          transformModeRef.current !== 'none' &&
          transformModeRef.current !== 'select' &&
          !obj.locked
        ) {
          const gm = gizmoManagerRef.current;
          gm.attachToMesh(mesh);
          if (mesh.metadata?.isLedScreen) unfreezeLEDChildren(mesh);
          if (gm._enforceGizmoConstantScreenSize) gm._enforceGizmoConstantScreenSize();
        }
        
        // Set pivot to center for proper center-scaling
        if (mesh instanceof BABYLON.Mesh) {
          mesh.computeWorldMatrix(true);
          mesh.refreshBoundingInfo();
          const pivotBBox = mesh.getBoundingInfo()?.boundingBox;
          if (pivotBBox) {
            mesh.setPivotPoint(pivotBBox.center, BABYLON.Space.LOCAL);
          }
        }
        
        // Apply edited mesh data if available (for primitives edited in mesh editor)
        if (obj.editedMeshData && mesh instanceof BABYLON.Mesh) {
          if (obj.editedMeshData.positions && obj.editedMeshData.indices) {
            try {
              // Use string constants for vertex buffer kinds
              mesh.updateVerticesData(
                'position',
                new Float32Array(obj.editedMeshData.positions)
              );
              if (obj.editedMeshData.normals) {
                mesh.updateVerticesData(
                  'normal',
                  new Float32Array(obj.editedMeshData.normals)
                );
              }
              mesh.setIndices(obj.editedMeshData.indices);
              mesh.refreshBoundingInfo();
              logger.log('Applied edited mesh data for primitive:', obj.id);
            } catch (err) {
              logger.error('Failed to apply edited mesh data to primitive:', err);
            }
          }
        }
      }

      // Update mesh properties (only if not dragging)
      if (!isDraggingRef.current) {
        mesh.position = new BABYLON.Vector3(
          obj.position?.x || 0,
          obj.position?.y || 0,
          obj.position?.z || 0
        );
        
        // Clear rotationQuaternion to use Euler angles
        // The gizmo may have set rotationQuaternion which overrides rotation
        mesh.rotationQuaternion = null;
        mesh.rotation = new BABYLON.Vector3(
          BABYLON.Tools.ToRadians(obj.rotation?.x || 0),
          BABYLON.Tools.ToRadians(obj.rotation?.y || 0),
          BABYLON.Tools.ToRadians(obj.rotation?.z || 0)
        );
        
        mesh.scaling = new BABYLON.Vector3(
          obj.scale?.x || 1,
          obj.scale?.y || 1,
          obj.scale?.z || 1
        );

        // ── LED scene-load position fix ────────────────────────────────────
        // LED cabinet/display children call freezeWorldMatrix() for perf
        // (10k+ tiles → 2 draw calls). When this effect first runs after a
        // scene load, the LED root was created at world (0,0,0), the children
        // were frozen there, and only NOW does mesh.position get the saved
        // value. Frozen children don't follow the parent → tiles render at
        // origin until the user clicks (which calls unfreezeLEDChildren).
        // Refresh frozen direct children here so tiles track the parent
        // transform on load and on Properties-panel slider edits.
        // Only touches the 2–5 direct merged children, NOT individual tiles,
        // so cost is O(1) regardless of cabinet count.
        if (mesh.metadata?.isLedScreen) {
          mesh.computeWorldMatrix(true);
          mesh.getChildMeshes(false).forEach(child => {
            if (child._isWorldMatrixFrozen) {
              try {
                child.unfreezeWorldMatrix();
                child.computeWorldMatrix(true);
                child.freezeWorldMatrix();
              } catch (e) {}
            }
          });
        }

        // placeOnFloor REMOVED — objects spawn at (0,0,0) center at origin
        // Half above, half below ground (as per user spec)

        // Real-time selection-outline sync: when the user edits position /
        // rotation / scale via the Properties-panel inputs, refresh the
        // yellow bounding box so it tracks the mesh instead of staying at
        // the old transform until another object is clicked.
        if (
          selectedObjectIdRef.current === obj.id &&
          refreshSelectionBoxRef.current
        ) {
          refreshSelectionBoxRef.current(mesh);
        }
      }
      
      // Update material color - for TransformNodes, update children's materials
      // Handle both standard materials and ProjectionSystem shader materials
      if (mesh.material && !(mesh.material instanceof BABYLON.ShaderMaterial)) {
        const newColor = BABYLON.Color3.FromHexString(obj.color || '#ffffff');
        
        // Handle texture update - check if textureUrl changed
        const currentTextureUrl = mesh.metadata?.textureUrl;
        const currentBrightness = mesh.metadata?.textureBrightness;
        const currentContrast = mesh.metadata?.textureContrast;
        const currentSaturation = mesh.metadata?.textureSaturation;
        
        // Helper to apply contrast/saturation to a material
        const applyContrastSaturation = (material, contrastVal, saturationVal) => {
          const contrast = (contrastVal ?? 100) / 100;
          const saturation = (saturationVal ?? 100) / 100;
          if (contrast !== 1.0 || saturation !== 1.0) {
            if (!material.imageProcessingConfiguration || !(material.imageProcessingConfiguration instanceof BABYLON.ImageProcessingConfiguration)) {
              material.imageProcessingConfiguration = new BABYLON.ImageProcessingConfiguration();
            }
            material.imageProcessingConfiguration.isEnabled = true;
            material.imageProcessingConfiguration.contrast = contrast * 2;
            material.imageProcessingConfiguration.exposure = 1.0;
            material.cameraColorCurvesEnabled = true;
            const curves = new BABYLON.ColorCurves();
            curves.globalSaturation = (saturation - 1.0) * 100;
            material.cameraColorCurves = curves;
          } else if (material.imageProcessingConfiguration) {
            material.imageProcessingConfiguration.isEnabled = false;
            material.cameraColorCurvesEnabled = false;
          }
        };
        
        if (obj.textureUrl && obj.textureUrl !== currentTextureUrl) {
          // ─── ANTI-FLICKER (upload race fix) ───
          // `new BABYLON.Texture(url, ...)` kicks off an *async* fetch.
          // Disposing the old `diffuseTexture` and assigning the new one
          // immediately leaves the mesh sampling a not-yet-decoded texture
          // for the duration of the network request — that's the ~0.1 s
          // blank/black flash the user reported.
          //
          // Fix without touching the material/rendering pipeline: load the
          // new texture in the background, keep the OLD diffuseTexture
          // wired up, and only do the atomic swap when `onLoad` fires.
          // Brightness/colour/metadata still update synchronously so React
          // state and 3D state stay in lockstep — only the GPU texture
          // hand-off waits for pixels to arrive.
          const oldDiffuse = mesh.material.diffuseTexture || null;
          const newUrl = resolveAssetUrl(obj.textureUrl);
          const onTexLoaded = () => {
            // Object may have been deleted / textureUrl changed while we
            // were loading — bail in that case.
            if (!mesh || mesh.isDisposed?.()) {
              try { texture.dispose(); } catch (_e) {}
              return;
            }
            if (mesh.metadata?.textureUrl !== obj.textureUrl) {
              try { texture.dispose(); } catch (_e) {}
              return;
            }
            // Atomic swap on the same frame the bytes arrive.
            mesh.material.diffuseTexture = texture;
            if (oldDiffuse && oldDiffuse !== texture) {
              try { oldDiffuse.dispose(); } catch (_e) {}
            }
          };
          const onTexError = () => {
            // Don't strand the mesh if the load failed — fall back to the
            // old texture (if any) and dispose the broken new one.
            try { texture.dispose(); } catch (_e) {}
          };
          const texture = new BABYLON.Texture(
            newUrl,
            scene,
            false,                                     // noMipmap
            true,                                      // invertY
            BABYLON.Texture.TRILINEAR_SAMPLINGMODE,
            onTexLoaded,                               // onLoad
            onTexError                                 // onError
          );
          texture.hasAlpha = false;
          texture.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
          texture.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
          // NOTE: we deliberately do NOT assign `diffuseTexture = texture`
          // here. The onLoad callback above performs the swap once decoded.
          mesh.material.diffuseColor = new BABYLON.Color3(1, 1, 1);
          const brightness = (obj.textureBrightness ?? 100) / 100;
          mesh.material.emissiveColor = new BABYLON.Color3(brightness * 0.5, brightness * 0.5, brightness * 0.5);
          applyContrastSaturation(mesh.material, obj.textureContrast, obj.textureSaturation);
          mesh.metadata = { ...(mesh.metadata || {}),
            textureUrl: obj.textureUrl,
            textureBrightness: obj.textureBrightness,
            textureContrast: obj.textureContrast,
            textureSaturation: obj.textureSaturation,
            texture_mapping_mode: obj.texture_mapping_mode || 'auto',
            texture_mapping_scale: obj.texture_mapping_scale ?? 1.0,
          };
          // Re-run the Universal Texture Mapping coordinator so Auto mode
          // re-evaluates (or the user-picked mode re-activates the plugin
          // AND the projection-receiver triplanar uniform).
          applyUniversalMapping(mesh, obj, projectionSystemRef.current);

          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverObjectTexture(mesh, resolveAssetUrl(obj.textureUrl));
          }
        } else if (obj.textureUrl && (obj.textureBrightness !== currentBrightness || obj.textureContrast !== currentContrast || obj.textureSaturation !== currentSaturation)) {
          // Same texture but brightness/contrast/saturation changed
          const brightness = (obj.textureBrightness ?? 100) / 100;
          mesh.material.emissiveColor = new BABYLON.Color3(brightness * 0.5, brightness * 0.5, brightness * 0.5);
          applyContrastSaturation(mesh.material, obj.textureContrast, obj.textureSaturation);
          mesh.metadata = { ...(mesh.metadata || {}), textureBrightness: obj.textureBrightness, textureContrast: obj.textureContrast, textureSaturation: obj.textureSaturation };
        } else if (obj.textureUrl && (
          (obj.texture_mapping_mode || 'auto') !== (mesh.metadata?.texture_mapping_mode || 'auto') ||
          (obj.texture_mapping_scale ?? 1.0) !== (mesh.metadata?.texture_mapping_scale ?? 1.0)
        )) {
          // Texture stays the same but mapping mode or scale changed —
          // re-run the bridge so both the StandardMaterial plugin and the
          // projection-shader uniform update in lockstep.
          applyUniversalMapping(mesh, obj, projectionSystemRef.current);
          mesh.metadata = { ...(mesh.metadata || {}),
            texture_mapping_mode: obj.texture_mapping_mode || 'auto',
            texture_mapping_scale: obj.texture_mapping_scale ?? 1.0,
          };
        } else if (!obj.textureUrl && currentTextureUrl) {
          // ─── ANTI-FLICKER (delete order fix) ───
          // `dispose()` immediately frees the GPU texture; if we did that
          // BEFORE clearing the material's `diffuseTexture` reference, the
          // material would briefly point at a disposed texture during the
          // same frame, which Babylon flags as an invalid bind and the
          // mesh renders blank for that frame.
          //
          // Safer order: clear the reference FIRST so the material rebinds
          // cleanly with no diffuse texture, then dispose the old GPU
          // resource on the next tick once rendering has settled.
          const oldDiffuse = mesh.material.diffuseTexture || null;
          mesh.material.diffuseTexture = null;
          mesh.material.diffuseColor = newColor;
          mesh.material.emissiveColor = newColor.scale(0.15);
          mesh.material.alpha = 1.0;
          // Clean up image processing (contrast/saturation) from texture mode
          if (mesh.material.imageProcessingConfiguration) {
            mesh.material.imageProcessingConfiguration.isEnabled = false;
          }
          mesh.material.cameraColorCurvesEnabled = false;
          mesh.material.cameraColorCurves = null;
          // Disable triplanar plugin (no-op if never enabled).
          disableTextureMapping(mesh);
          mesh.metadata = { ...(mesh.metadata || {}), textureUrl: null, textureBrightness: null, textureContrast: null, textureSaturation: null };

          if (oldDiffuse) {
            // Defer disposal to the next macrotask so the frame that
            // commits "no texture" can render without referencing the GPU
            // texture we're about to free.
            setTimeout(() => {
              try { oldDiffuse.dispose(); } catch (_e) {}
            }, 0);
          }

          // Update ProjectionSystem to clear object texture
          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverObjectTexture(mesh, null);
          }
        } else if (!obj.textureUrl) {
          // No texture - just update color
          mesh.material.diffuseColor = newColor;
          mesh.material.emissiveColor = newColor.scale(0.15);
        }
        
        // Update UV mapping state for reverse projection
        const currentUvMappingEnabled = mesh.metadata?.uvMappingEnabled;
        if (obj.uvMappingEnabled !== currentUvMappingEnabled) {
          mesh.metadata = { ...(mesh.metadata || {}), uvMappingEnabled: obj.uvMappingEnabled };
          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverUvMapping(mesh, obj.uvMappingEnabled);
          }
        }
        
        // Store object data in metadata for ProjectionSystem
        mesh.metadata = { 
          ...(mesh.metadata || {}), 
          objectData: {
            textureUrl: obj.textureUrl,
            uvMappingEnabled: obj.uvMappingEnabled,
            textureBrightness: obj.textureBrightness,
            textureContrast: obj.textureContrast,
            textureSaturation: obj.textureSaturation
          }
        };
        
        // If texture exists, keep diffuse white (don't override with color)
        
        // Update scrim opacity if this is a scrim screen
        if (obj.type === 'scrim_screen') {
          const scrimOpacity = obj.scrimOpacity || 0.15;
          // Update standard material alpha if present
          if (mesh.material.alpha !== undefined) {
            mesh.material.alpha = scrimOpacity;
          }
          // Also update ProjectionSystem shader material alpha
          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverAlpha(mesh, scrimOpacity);
          }
          // Also update metadata for shadow calculation
          if (mesh.metadata) {
            mesh.metadata.scrimOpacity = scrimOpacity;
          }
        }
      } else if (mesh.material instanceof BABYLON.ShaderMaterial) {
        // Handle ProjectionSystem shader material - update baseColor uniform
        const newColor = BABYLON.Color3.FromHexString(obj.color || '#ffffff');
        if (projectionSystemRef.current) {
          projectionSystemRef.current.updateReceiverColor(mesh, newColor);
        }
        
        // Handle texture update for ShaderMaterial (reverse projection)
        const currentTextureUrl = mesh.metadata?.textureUrl;
        const currentBrightness = mesh.metadata?.textureBrightness;
        const currentContrast = mesh.metadata?.textureContrast;
        const currentSaturation = mesh.metadata?.textureSaturation;
        
        if (obj.textureUrl && obj.textureUrl !== currentTextureUrl) {
          // New texture — update ProjectionSystem
          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverObjectTexture(mesh, resolveAssetUrl(obj.textureUrl));
            projectionSystemRef.current.updateReceiverTextureSettings(mesh, {
              brightness: obj.textureBrightness,
              contrast: obj.textureContrast,
              saturation: obj.textureSaturation
            });
          }
          // Apply Universal Texture Mapping (auto/uv/triplanar) on the
          // projection-receiver shader — imported meshes route texture
          // sampling through there, so the plugin path isn't enough.
          applyUniversalMapping(mesh, obj, projectionSystemRef.current);
          mesh.metadata = { ...(mesh.metadata || {}),
            textureUrl: obj.textureUrl,
            textureBrightness: obj.textureBrightness,
            textureContrast: obj.textureContrast,
            textureSaturation: obj.textureSaturation,
            texture_mapping_mode: obj.texture_mapping_mode || 'auto',
            texture_mapping_scale: obj.texture_mapping_scale ?? 1.0,
          };
        } else if (obj.textureUrl && (obj.textureBrightness !== currentBrightness || obj.textureContrast !== currentContrast || obj.textureSaturation !== currentSaturation)) {
          // Brightness/contrast/saturation changed — update shader uniforms live
          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverTextureSettings(mesh, {
              brightness: obj.textureBrightness,
              contrast: obj.textureContrast,
              saturation: obj.textureSaturation
            });
          }
          mesh.metadata = { ...(mesh.metadata || {}), textureBrightness: obj.textureBrightness, textureContrast: obj.textureContrast, textureSaturation: obj.textureSaturation };
        } else if (obj.textureUrl && (
          (obj.texture_mapping_mode || 'auto') !== (mesh.metadata?.texture_mapping_mode || 'auto') ||
          (obj.texture_mapping_scale ?? 1.0) !== (mesh.metadata?.texture_mapping_scale ?? 1.0)
        )) {
          // Mode-only change on an imported mesh — flip the projection
          // shader's triplanar uniform without touching the texture.
          applyUniversalMapping(mesh, obj, projectionSystemRef.current);
          mesh.metadata = { ...(mesh.metadata || {}),
            texture_mapping_mode: obj.texture_mapping_mode || 'auto',
            texture_mapping_scale: obj.texture_mapping_scale ?? 1.0,
          };
        } else if (!obj.textureUrl && currentTextureUrl) {
          // Texture removed — reset shader to use mesh color, not stale white
          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverObjectTexture(mesh, null);
            // Reset baseColor from white (texture mode) back to actual mesh color
            const meshColor = BABYLON.Color3.FromHexString(obj.color || '#ffffff');
            projectionSystemRef.current.updateReceiverColor(mesh, meshColor);
            // Ensure full opacity
            const meshId = mesh.uniqueId || mesh.name;
            const entry = projectionSystemRef.current.receiverMeshes?.get(meshId);
            if (entry && entry.material) {
              entry.material.setFloat('materialAlpha', 1.0);
              entry.material.setFloat('hasObjectTexture', 0.0);
              entry.material.setFloat('objectTextureBrightness', 1.0);
              entry.material.setFloat('objectTextureContrast', 1.0);
              entry.material.setFloat('objectTextureSaturation', 1.0);
            }
          }
          mesh.metadata = { ...(mesh.metadata || {}), textureUrl: null, textureBrightness: null, textureContrast: null, textureSaturation: null };
        }
        
        // Update objectData in metadata for shader uniforms
        mesh.metadata = { 
          ...(mesh.metadata || {}), 
          objectData: {
            textureUrl: obj.textureUrl,
            uvMappingEnabled: obj.uvMappingEnabled,
            textureBrightness: obj.textureBrightness,
            textureContrast: obj.textureContrast,
            textureSaturation: obj.textureSaturation
          }
        };
        
        // Handle scrim screen opacity
        if (obj.type === 'scrim_screen') {
          const scrimOpacity = obj.scrimOpacity || 0.15;
          if (projectionSystemRef.current) {
            projectionSystemRef.current.updateReceiverAlpha(mesh, scrimOpacity);
            // Update projection attenuation on back-wall receivers based on scrim opacity
            projectionSystemRef.current.updateScrimAttenuation(scrimOpacity);
          }
          if (mesh.metadata) {
            mesh.metadata.scrimOpacity = scrimOpacity;
          }
        }
      } else if (mesh instanceof BABYLON.TransformNode && obj.type === 'mesh') {
        // For loaded 3D meshes, update child mesh materials
        const children = mesh.getChildMeshes();
        const newColor = BABYLON.Color3.FromHexString(obj.color || '#888888');
        const currentTextureUrl = mesh.metadata?.textureUrl;
        
        const currentBrightness = mesh.metadata?.textureBrightness;
        
        children.forEach(child => {
          // Handle both standard materials and ProjectionSystem shader materials
          if (child.material && !(child.material instanceof BABYLON.ShaderMaterial)) {
            // Handle texture for imported meshes
            if (obj.textureUrl && obj.textureUrl !== currentTextureUrl) {
              const texture = new BABYLON.Texture(resolveAssetUrl(obj.textureUrl), scene, false, true, BABYLON.Texture.TRILINEAR_SAMPLINGMODE);
              texture.hasAlpha = false;
              texture.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
              texture.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
              if (child.material.diffuseTexture) {
                child.material.diffuseTexture.dispose();
              }
              child.material.diffuseTexture = texture;
              child.material.diffuseColor = new BABYLON.Color3(1, 1, 1);
              // Apply texture brightness
              const brightness = (obj.textureBrightness ?? 100) / 100;
              child.material.emissiveColor = new BABYLON.Color3(brightness * 0.5, brightness * 0.5, brightness * 0.5);
            } else if (obj.textureUrl && obj.textureBrightness !== currentBrightness) {
              // Same texture but brightness changed
              const brightness = (obj.textureBrightness ?? 100) / 100;
              child.material.emissiveColor = new BABYLON.Color3(brightness * 0.5, brightness * 0.5, brightness * 0.5);
            } else if (!obj.textureUrl && currentTextureUrl) {
              // Texture removed
              if (child.material.diffuseTexture) {
                child.material.diffuseTexture.dispose();
                child.material.diffuseTexture = null;
              }
              child.material.diffuseColor = newColor;
              child.material.emissiveColor = newColor.scale(0.15);
              child.material.alpha = 1.0;
              if (child.material.imageProcessingConfiguration) {
                child.material.imageProcessingConfiguration.isEnabled = false;
              }
              child.material.cameraColorCurvesEnabled = false;
              child.material.cameraColorCurves = null;
            } else if (!obj.textureUrl) {
              child.material.diffuseColor = newColor;
            }
          } else if (child.material instanceof BABYLON.ShaderMaterial && projectionSystemRef.current) {
            // Update ProjectionSystem shader material
            projectionSystemRef.current.updateReceiverColor(child, newColor);
            
            // Handle texture for reverse projection on imported meshes with ShaderMaterial
            if (obj.textureUrl && obj.textureUrl !== currentTextureUrl) {
              logger.log(`BabylonScene: TEXTURE CHANGED for child mesh ${child.name}, calling updateReceiverObjectTexture`);
              projectionSystemRef.current.updateReceiverObjectTexture(child, obj.textureUrl);
            } else if (!obj.textureUrl && currentTextureUrl) {
              // Texture removed
              logger.log(`BabylonScene: TEXTURE REMOVED for child mesh ${child.name}`);
              projectionSystemRef.current.updateReceiverObjectTexture(child, null);
            } else if (obj.textureUrl) {
              // Texture exists but didn't change - still update to be safe
              logger.log(`BabylonScene: TEXTURE EXISTS for child mesh ${child.name}, ensuring it's set`);
              projectionSystemRef.current.updateReceiverObjectTexture(child, obj.textureUrl);
            }
            
            // Update objectData in child mesh metadata
            child.metadata = { 
              ...(child.metadata || {}), 
              objectData: {
                textureUrl: obj.textureUrl,
                uvMappingEnabled: obj.uvMappingEnabled,
                textureBrightness: obj.textureBrightness
              }
            };
          }
        });
        
        // Update metadata for texture tracking (on parent TransformNode)
        if (obj.textureUrl !== currentTextureUrl || obj.textureBrightness !== currentBrightness) {
          mesh.metadata = { 
            ...(mesh.metadata || {}), 
            textureUrl: obj.textureUrl || null, 
            textureBrightness: obj.textureBrightness,
            objectData: {
              textureUrl: obj.textureUrl,
              uvMappingEnabled: obj.uvMappingEnabled,
              textureBrightness: obj.textureBrightness
            }
          };
        }
      }

      // Handle show_edges toggle — enable/disable white edge outline
      // Also store in metadata so projection effect can check it
      if (mesh.metadata) mesh.metadata.show_edges = obj.show_edges;
      if (mesh instanceof BABYLON.Mesh) {
        if (obj.show_edges === false) {
          if (mesh.edgesRenderer) {
            mesh.disableEdgesRendering();
          }
        } else if (!mesh.edgesRenderer) {
          mesh.enableEdgesRendering(0.5);
          mesh.edgesWidth = 2.0;
          mesh.edgesColor = new BABYLON.Color4(1.0, 1.0, 1.0, 1.0);
        }
      } else if (mesh instanceof BABYLON.TransformNode && obj.type === 'mesh') {
        const edgeChildren = mesh.getChildMeshes();
        edgeChildren.forEach(child => {
          if (obj.show_edges === false) {
            if (child.edgesRenderer) child.disableEdgesRendering();
          } else if (!child.edgesRenderer) {
            child.enableEdgesRendering(0.5);
            child.edgesWidth = 2.0;
            child.edgesColor = new BABYLON.Color4(0.75, 0.75, 0.75, 1.0);
          }
        });
      }

      // Scaffolding edges stay OFF always (UX decision — ringlock geometry
      // reads cleanly without outlines). The Show Edges toggle is hidden in
      // properties, and we proactively strip any leftover edge renderers from
      // older saved scenes whose poles / decks were built with edges on.
      if (obj.type === 'scaffolding') {
        const scaffRoot = scene.getTransformNodeByName('scaffolding_' + obj.id)
          || scene.getMeshByName('scaffolding_' + obj.id);
        if (scaffRoot && scaffRoot.getChildMeshes) {
          scaffRoot.getChildMeshes(false).forEach(child => {
            if (child?.edgesRenderer && typeof child.disableEdgesRendering === 'function') {
              child.disableEdgesRendering();
            }
          });
        }
      }

      // Handle visibility - Mesh uses isVisible, TransformNode uses setEnabled
      // BUT respect hiddenComponents list for child meshes
      // Skip LED screens - their root is always invisible, children handle visibility
      if (obj.type === 'led_screen') {
        // LED root stays invisible; toggle children's visibility instead
        const isVisible = obj.visible !== false;
        mesh.getChildMeshes().forEach(child => {
          if (child.isPickable) child.isVisible = isVisible;
        });
      } else if (obj.type === 'scaffolding') {
        // Scaffolding: toggle root enabled state and master visibility
        const isVisible = obj.visible !== false;
        mesh.setEnabled(isVisible);
        const children = mesh.getChildMeshes();
        children.forEach(child => {
          if (child.metadata?.scaffoldHelper) {
            // Bounding box helper: never visible, just toggle pickability
            child.isPickable = isVisible;
          } else if (child.metadata?.scaffoldMaster) {
            // Thin instance masters: must be visible for thin instances to render
            child.isVisible = isVisible;
          }
        });
      } else if (mesh instanceof BABYLON.Mesh) {
        mesh.isVisible = obj.visible !== false;
      } else if (mesh instanceof BABYLON.TransformNode) {
        mesh.setEnabled(obj.visible !== false);
        // Also update child mesh visibility, but respect hiddenComponents
        const children = mesh.getChildMeshes();
        const hiddenNames = obj.hiddenComponents || [];
        children.forEach(child => {
          // Check if this child is in the hidden list
          const isHidden = hiddenNames.includes(child.name) || hiddenNames.includes(child.id);
          if (isHidden) {
            child.isVisible = false;
          } else {
            child.isVisible = obj.visible !== false;
          }
        });
      }
    });

  }, [sceneObjects]);

  // ── Projector visibility (Hide/Show toggle in scene panel) ──────────────
  // Toggles every projector-owned visual: body, lens, frame, 90° lens parts,
  // beam wireframe, spotlight, and label. State (power_on, brightness, lens
  // selection) is preserved so toggling back to visible restores everything.
  useEffect(() => {
    projectors.forEach((projector) => {
      const isVisible = projector.visible !== false;
      const id = projector.id;

      // Body mesh + every sub-component stored in projectorMeshesRef under
      // keys that include the projector id (lens_, frame_, lens90*_)
      Object.entries(projectorMeshesRef.current).forEach(([key, value]) => {
        if (!value) return;
        const ownsThisProjector = key === id || key.endsWith('_' + id);
        if (!ownsThisProjector) return;
        // Lens entries are { housing, glass } objects
        if (value.housing && typeof value.housing.setEnabled === 'function') {
          value.housing.setEnabled(isVisible);
        }
        if (value.glass && typeof value.glass.setEnabled === 'function') {
          value.glass.setEnabled(isVisible);
        }
        if (typeof value.setEnabled === 'function') {
          value.setEnabled(isVisible);
        }
      });

      // Beam wireframe
      const beam = beamMeshesRef.current[id];
      if (beam && typeof beam.setEnabled === 'function') {
        beam.setEnabled(isVisible && projector.show_beam && projector.power_on);
      }

      // Spotlight — turn off when hidden so the scene actually goes dark
      const light = projectorLightsRef.current[id];
      if (light) {
        if (!isVisible) {
          light.setEnabled(false);
        } else {
          light.setEnabled(true);
        }
      }

      // Label sprite (Babylon GUI TextBlock — uses isVisible, not setEnabled)
      const label = projectorLabelsRef.current[id];
      if (label) {
        if (typeof label.isVisible !== 'undefined') {
          label.isVisible = isVisible;
        } else if (typeof label.setEnabled === 'function') {
          label.setEnabled(isVisible);
        }
      }
    });
  }, [projectors]);

  // ── Lock toggle: instantly detach gizmo when selected object becomes locked
  // Without this, locking only takes effect after the user changes transform
  // mode or selection because the main gizmo effect doesn't depend on lock
  // state. We also re-attach the gizmo if the user unlocks while still in a
  // transform mode so the experience stays seamless.
  useEffect(() => {
    if (!gizmoManagerRef.current || !selectedObjectId) return;
    // In multi-select mode the dedicated multi-select gizmo effect owns the
    // pivot/gizmo attachment. Bailing out here prevents this effect from
    // hijacking the gizmo back to a single mesh after every state update
    // (which broke repeat multi-drags).
    if (multiSelectIds && multiSelectIds.length > 1) return;
    const gm = gizmoManagerRef.current;
    const sceneObj = sceneObjects.find(o => o.id === selectedObjectId);
    const projector = projectors.find(p => p.id === selectedObjectId);
    const isLocked = sceneObj?.locked || projector?.locked;
    const isTransformMode = transformMode && transformMode !== 'none' && transformMode !== 'select';

    if (isLocked) {
      // Detach gizmo immediately — also clears the yellow selection box
      gm.attachToMesh(null);
      if (selectionBoxRef.current) {
        try { selectionBoxRef.current.dispose(); } catch (_) { /* noop */ }
        selectionBoxRef.current = null;
      }
    } else if (isTransformMode) {
      const mesh = meshesRef.current[selectedObjectId] || projectorMeshesRef.current[selectedObjectId];
      if (mesh) gm.attachToMesh(mesh);
    }
  }, [sceneObjects, projectors, selectedObjectId, transformMode, multiSelectIds]);

  useEffect(() => {
    if (!gizmoManagerRef.current) return;
    const gm = gizmoManagerRef.current;
    
    // transformMode can be 'none', 'position', 'rotation', 'scale', 'translate', 'rotate'
    const isTransformMode = transformMode && transformMode !== 'none' && transformMode !== 'select';
    
    gm.positionGizmoEnabled = transformMode === 'position' || transformMode === 'translate';
    gm.rotationGizmoEnabled = transformMode === 'rotation' || transformMode === 'rotate';
    gm.scaleGizmoEnabled = transformMode === 'scale';
    
    // Configure rotation gizmo to work with scaled objects
    if (gm.gizmos.rotationGizmo) {
      gm.gizmos.rotationGizmo.updateGizmoPositionToMatchAttachedMesh = true;
    }
    
    // Configure position gizmo — reduce sensitivity and add precision mode
    if (gm.gizmos.positionGizmo) {
      gm.gizmos.positionGizmo.updateGizmoRotationToMatchAttachedMesh = false; // Keep world aligned
      gm.gizmos.positionGizmo.updateGizmoPositionToMatchAttachedMesh = true;
      
      // dragDeltaRatio is a per-frame interpolation factor in Babylon, NOT a
      // magnitude multiplier. 1.0 = no smoothing (object tracks the pointer
      // exactly — smooth feel). Lower values cause lag / jumpiness on quick
      // mouse moves. Babylon's plane raycast already gives the right world
      // delta per pixel at any camera distance, so we don't need smoothing.
      const baseSensitivity = 1.0;       // no smoothing — direct response
      const precisionSensitivity = 0.25; // Shift = quarter-speed for fine work
      
      // Apply sensitivity to all axis and planar gizmos
      const axisGizmos = [
        gm.gizmos.positionGizmo.xGizmo,
        gm.gizmos.positionGizmo.yGizmo,
        gm.gizmos.positionGizmo.zGizmo,
        gm.gizmos.positionGizmo.xPlaneGizmo,
        gm.gizmos.positionGizmo.yPlaneGizmo,
        gm.gizmos.positionGizmo.zPlaneGizmo
      ];
      
      axisGizmos.forEach(ag => {
        if (ag && ag.dragBehavior) {
          ag.dragBehavior.dragDeltaRatio = baseSensitivity;
        }
      });
      
      // Precision mode: listen for Shift key to slow down movement
      const onKeyDown = (e) => {
        if (e.key === 'Shift' && isDraggingRef.current) {
          const sens = gm._currentPrecSens || precisionSensitivity;
          axisGizmos.forEach(ag => {
            if (ag && ag.dragBehavior) ag.dragBehavior.dragDeltaRatio = sens;
          });
        }
      };
      const onKeyUp = (e) => {
        if (e.key === 'Shift') {
          const sens = gm._currentBaseSens || baseSensitivity;
          axisGizmos.forEach(ag => {
            if (ag && ag.dragBehavior) ag.dragBehavior.dragDeltaRatio = sens;
          });
        }
      };
      document.addEventListener('keydown', onKeyDown);
      document.addEventListener('keyup', onKeyUp);
      // Store for cleanup
      gm._precisionKeyHandlers = { onKeyDown, onKeyUp };
    }
    
    // Configure scale gizmo with reduced sensitivity
    if (gm.gizmos.scaleGizmo) {
      gm.gizmos.scaleGizmo.updateGizmoRotationToMatchAttachedMesh = true;
      gm.gizmos.scaleGizmo.updateGizmoPositionToMatchAttachedMesh = true;
      gm.gizmos.scaleGizmo.sensitivity = 0.4;
    }
    
    // Update gizmo scale based on current camera distance
    if (gm._updateGizmoScale) {
      gm._updateGizmoScale();
    }
    
    // If in select mode (no transform), detach gizmo
    if (!isTransformMode) {
      gm.attachToMesh(null);
    } else if (selectedObjectId) {
      // Re-attach gizmo to currently selected mesh when switching transform modes
      const mesh = meshesRef.current[selectedObjectId] || projectorMeshesRef.current[selectedObjectId];
      if (mesh) {
        const sceneObj = sceneObjectsRef.current.find(o => o.id === selectedObjectId);
        const projector = projectorsRef.current.find(p => p.id === selectedObjectId);
        const isLocked = sceneObj?.locked || projector?.locked;
        if (!isLocked) {
          gm.attachToMesh(mesh);
        }
      }
    }
  }, [transformMode, selectedObjectId]);

  // ── LED gizmo-drag fix ──────────────────────────────────────────────────
  // LED tile children call freezeWorldMatrix() for performance (10k+ tiles =
  // 2 draw calls). When the gizmo drags the LED root, frozen children stop
  // following the parent → tiles stay put, root invisibly moves, the yellow
  // selection box (computed from getHierarchyBoundingVectors) "grows" to span
  // both old and new positions. Unfreezing while the LED is selected fixes
  // both: tiles follow the gizmo, selection box stays tight to the visible
  // mesh. Re-freezing on deselect restores the perf optimisation.
  const unfreezeLEDChildren = (rootMesh) => {
    if (!rootMesh) return;
    const meta = rootMesh.metadata || {};
    if (!meta.isLedScreen) return;
    const cleared = [];
    rootMesh.getChildMeshes(false).forEach(child => {
      if (child._isWorldMatrixFrozen) {
        try { child.unfreezeWorldMatrix(); cleared.push(child); } catch (e) {}
      }
    });
    unfrozenLEDRef.current = { root: rootMesh, children: cleared };
    // Force one immediate world-matrix recompute on the whole hierarchy so
    // the very first frame after attaching the gizmo already reflects the
    // correct tile positions (no flash).
    rootMesh.computeWorldMatrix(true);
    cleared.forEach(c => c.computeWorldMatrix(true));
  };

  const refreezeLEDChildren = () => {
    const state = unfrozenLEDRef.current;
    if (!state) return;
    state.children.forEach(child => {
      try {
        if (!child.isDisposed?.()) {
          child.computeWorldMatrix(true);
          child.freezeWorldMatrix();
        }
      } catch (e) {}
    });
    unfrozenLEDRef.current = null;
  };

  // Handle selection - only attach gizmo if transform mode is active AND object is not locked
  // NOTE: Uses refs (sceneObjectsRef, projectorsRef) instead of state for locked check
  // to avoid re-running this heavy effect whenever the projectors/sceneObjects arrays change.
  useEffect(() => {
    if (!gizmoManagerRef.current || !sceneRef.current) return;
    
    const gm = gizmoManagerRef.current;
    const hl = highlightLayerRef.current;
    const isTransformMode = transformMode && transformMode !== 'none' && transformMode !== 'select';
    
    // Clear any previous highlight layer meshes (removes glow from previous selection)
    if (hl) {
      hl.removeAllMeshes();
    }
    
    // Clear previous selection bounding box
    if (selectionBoxRef.current) {
      selectionBoxRef.current.dispose();
      selectionBoxRef.current = null;
    }
    
    // Helper function to create yellow selection bounding box
    const createSelectionBox = (targetMesh) => {
      if (!targetMesh || !sceneRef.current) return;
      // Always replace any existing box so live transform updates are visible
      if (selectionBoxRef.current) {
        try { selectionBoxRef.current.dispose(); } catch (e) {}
        selectionBoxRef.current = null;
      }
      
      try {
        targetMesh.computeWorldMatrix(true);
        const boundingInfo = targetMesh.getHierarchyBoundingVectors(true);
        const min = boundingInfo.min;
        const max = boundingInfo.max;
        
        const lines = [
          [new BABYLON.Vector3(min.x, min.y, min.z), new BABYLON.Vector3(max.x, min.y, min.z)],
          [new BABYLON.Vector3(max.x, min.y, min.z), new BABYLON.Vector3(max.x, min.y, max.z)],
          [new BABYLON.Vector3(max.x, min.y, max.z), new BABYLON.Vector3(min.x, min.y, max.z)],
          [new BABYLON.Vector3(min.x, min.y, max.z), new BABYLON.Vector3(min.x, min.y, min.z)],
          [new BABYLON.Vector3(min.x, max.y, min.z), new BABYLON.Vector3(max.x, max.y, min.z)],
          [new BABYLON.Vector3(max.x, max.y, min.z), new BABYLON.Vector3(max.x, max.y, max.z)],
          [new BABYLON.Vector3(max.x, max.y, max.z), new BABYLON.Vector3(min.x, max.y, max.z)],
          [new BABYLON.Vector3(min.x, max.y, max.z), new BABYLON.Vector3(min.x, max.y, min.z)],
          [new BABYLON.Vector3(min.x, min.y, min.z), new BABYLON.Vector3(min.x, max.y, min.z)],
          [new BABYLON.Vector3(max.x, min.y, min.z), new BABYLON.Vector3(max.x, max.y, min.z)],
          [new BABYLON.Vector3(max.x, min.y, max.z), new BABYLON.Vector3(max.x, max.y, max.z)],
          [new BABYLON.Vector3(min.x, min.y, max.z), new BABYLON.Vector3(min.x, max.y, max.z)]
        ];
        
        const selectionBox = BABYLON.MeshBuilder.CreateLineSystem('selectionBoundingBox', {
          lines: lines,
          updatable: false
        }, sceneRef.current);
        
        selectionBox.color = new BABYLON.Color3(1, 1, 0);
        selectionBox.isPickable = false;
        selectionBoxRef.current = selectionBox;
      } catch (e) {
        logger.log('Could not create selection box:', e);
      }
    };

    // Expose so the transform-sync effect (input fields) can refresh the box
    // in real-time without waiting for the user to click another object.
    refreshSelectionBoxRef.current = createSelectionBox;
    
    // SKIP during edit mode
    if (editMode) {
      gm.attachToMesh(null);
      // Clean up multi-select pivot
      if (multiPivotRef.current) {
        multiPivotRef.current.dispose();
        multiPivotRef.current = null;
      }
      return;
    }
    
    // Clean up previous multi-select pivot
    if (multiPivotRef.current) {
      multiPivotRef.current.dispose();
      multiPivotRef.current = null;
    }
    
    // MULTI-SELECT GIZMO: Create pivot at centroid of all selected meshes
    if (multiSelectIds.length > 1 && isTransformMode) {
      const allMeshes = [];
      for (const id of multiSelectIds) {
        const m = meshesRef.current[id] || projectorMeshesRef.current[id];
        if (m) allMeshes.push(m);
      }
      if (allMeshes.length > 1) {
        // Compute centroid
        const centroid = BABYLON.Vector3.Zero();
        allMeshes.forEach(m => { centroid.addInPlace(m.getAbsolutePosition()); });
        centroid.scaleInPlace(1 / allMeshes.length);
        
        // Create pivot TransformNode
        const pivot = new BABYLON.TransformNode('multiSelectPivot', sceneRef.current);
        pivot.position = centroid.clone();
        multiPivotRef.current = pivot;
        
        // Attach gizmo to pivot
        gm.attachToMesh(pivot);
        
        // Create selection box for primary if it exists
        if (selectedObjectId) {
          const primaryMesh = meshesRef.current[selectedObjectId] || projectorMeshesRef.current[selectedObjectId];
          if (primaryMesh) createSelectionBox(primaryMesh);
        }
        return;
      }
    }
    
    if (selectedObjectId) {
      // Use REFS for locked check to avoid depending on full arrays
      const sceneObj = sceneObjectsRef.current.find(o => o.id === selectedObjectId);
      const projector = projectorsRef.current.find(p => p.id === selectedObjectId);
      const isLocked = sceneObj?.locked || projector?.locked;
      
      const mesh = meshesRef.current[selectedObjectId];
      // If selection moved away from a previously-unfrozen LED, re-freeze it now
      if (unfrozenLEDRef.current && unfrozenLEDRef.current.root !== mesh) {
        refreezeLEDChildren();
      }
      if (mesh) {
        const hasNonUniformScale = mesh.scaling && (
          Math.abs(mesh.scaling.x - mesh.scaling.y) > 0.001 ||
          Math.abs(mesh.scaling.y - mesh.scaling.z) > 0.001 ||
          Math.abs(mesh.scaling.x - mesh.scaling.z) > 0.001
        );
        
        if (gm.gizmos.rotationGizmo) {
          gm.gizmos.rotationGizmo.updateGizmoRotationToMatchAttachedMesh = !hasNonUniformScale;
        }
        
        if (isTransformMode && !isLocked) {
          gm.attachToMesh(mesh);
          // LED-specific: unfreeze child tile matrices so they follow the
          // gizmo. (no-op for non-LED meshes — guarded by metadata check)
          unfreezeLEDChildren(mesh);
        } else {
          gm.attachToMesh(null);
        }
        
        createSelectionBox(mesh);
        return;
      }
      const projMesh = projectorMeshesRef.current[selectedObjectId];
      if (projMesh) {
        // Selection moved to a projector — re-freeze any LED we'd unfrozen
        if (unfrozenLEDRef.current) refreezeLEDChildren();
        if (isTransformMode && !isLocked) {
          gm.attachToMesh(projMesh);
        } else {
          gm.attachToMesh(null);
        }
        
        createSelectionBox(projMesh);
        return;
      }
    }
    
    // Nothing selected — re-freeze the LED if we'd unfrozen it
    if (unfrozenLEDRef.current) refreezeLEDChildren();
    gm.attachToMesh(null);
  }, [selectedObjectId, transformMode, editMode, multiSelectIds]);

  // Multi-select highlight boxes (cyan) for Ctrl+click selected items
  useEffect(() => {
    // Dispose previous multi-select boxes
    multiSelectBoxesRef.current.forEach(b => { try { b.dispose(); } catch(e) {} });
    multiSelectBoxesRef.current = [];
    
    if (!sceneRef.current || multiSelectIds.length === 0) return;
    
    multiSelectIds.forEach(id => {
      // Skip the primary selected object (it already has yellow box)
      if (id === selectedObjectId) return;
      const mesh = meshesRef.current[id] || projectorMeshesRef.current[id];
      if (!mesh) return;
      try {
        mesh.computeWorldMatrix(true);
        const b = mesh.getHierarchyBoundingVectors(true);
        const mn = b.min, mx = b.max;
        const lines = [
          [new BABYLON.Vector3(mn.x,mn.y,mn.z), new BABYLON.Vector3(mx.x,mn.y,mn.z)],
          [new BABYLON.Vector3(mx.x,mn.y,mn.z), new BABYLON.Vector3(mx.x,mn.y,mx.z)],
          [new BABYLON.Vector3(mx.x,mn.y,mx.z), new BABYLON.Vector3(mn.x,mn.y,mx.z)],
          [new BABYLON.Vector3(mn.x,mn.y,mx.z), new BABYLON.Vector3(mn.x,mn.y,mn.z)],
          [new BABYLON.Vector3(mn.x,mx.y,mn.z), new BABYLON.Vector3(mx.x,mx.y,mn.z)],
          [new BABYLON.Vector3(mx.x,mx.y,mn.z), new BABYLON.Vector3(mx.x,mx.y,mx.z)],
          [new BABYLON.Vector3(mx.x,mx.y,mx.z), new BABYLON.Vector3(mn.x,mx.y,mx.z)],
          [new BABYLON.Vector3(mn.x,mx.y,mx.z), new BABYLON.Vector3(mn.x,mx.y,mn.z)],
          [new BABYLON.Vector3(mn.x,mn.y,mn.z), new BABYLON.Vector3(mn.x,mx.y,mn.z)],
          [new BABYLON.Vector3(mx.x,mn.y,mn.z), new BABYLON.Vector3(mx.x,mx.y,mn.z)],
          [new BABYLON.Vector3(mx.x,mn.y,mx.z), new BABYLON.Vector3(mx.x,mx.y,mx.z)],
          [new BABYLON.Vector3(mn.x,mn.y,mx.z), new BABYLON.Vector3(mn.x,mx.y,mx.z)]
        ];
        const box = BABYLON.MeshBuilder.CreateLineSystem('multiSelectBox_' + id, { lines, updatable: false }, sceneRef.current);
        box.color = new BABYLON.Color3(0, 1, 1); // Cyan for multi-select
        box.isPickable = false;
        multiSelectBoxesRef.current.push(box);
      } catch(e) {}
    });
    
    return () => {
      multiSelectBoxesRef.current.forEach(b => { try { b.dispose(); } catch(e) {} });
      multiSelectBoxesRef.current = [];
    };
  }, [multiSelectIds, selectedObjectId, sceneObjects, projectors]);

  // Handle gizmo drag start and end
  useEffect(() => {
    if (!gizmoManagerRef.current) return;
    const gm = gizmoManagerRef.current;

    // --- Live Dimension Display helpers ---
    const disposeDimensionGizmos = () => {
      const dg = dimensionGizmoRef.current;
      dg.lines.forEach(l => { try { l.dispose(); } catch(e) {} });
      dg.labels.forEach(l => { try { if (l.host) guiTextureRef.current?.removeControl(l); l.dispose(); } catch(e) {} });
      dg.lines = [];
      dg.labels = [];
    };
    
    const showDimensionGizmos = (mesh) => {
      if (!mesh || !sceneRef.current || !guiTextureRef.current) return;
      disposeDimensionGizmos();
      
      const pos = mesh.position;
      const origin = BABYLON.Vector3.Zero();
      const dg = dimensionGizmoRef.current;
      
      // Axis configs: color, direction, value, label offset
      const axes = [
        { color: new BABYLON.Color3(1, 0.2, 0.2), from: new BABYLON.Vector3(0, pos.y, pos.z), to: new BABYLON.Vector3(pos.x, pos.y, pos.z), val: pos.x, axis: 'X' },
        { color: new BABYLON.Color3(0.2, 1, 0.2), from: new BABYLON.Vector3(pos.x, 0, pos.z), to: new BABYLON.Vector3(pos.x, pos.y, pos.z), val: pos.y, axis: 'Y' },
        { color: new BABYLON.Color3(0.3, 0.5, 1), from: new BABYLON.Vector3(pos.x, pos.y, 0), to: new BABYLON.Vector3(pos.x, pos.y, pos.z), val: pos.z, axis: 'Z' }
      ];
      
      axes.forEach(({ color, from, to, val, axis }) => {
        // Dimension line
        const line = BABYLON.MeshBuilder.CreateLines('dimLine_' + axis, {
          points: [from, to],
          updatable: false
        }, sceneRef.current);
        line.color = color;
        line.isPickable = false;
        dg.lines.push(line);
        
        // Dashed line from origin axis to start
        const dashFrom = axis === 'X' ? new BABYLON.Vector3(0, pos.y, pos.z) :
                         axis === 'Y' ? new BABYLON.Vector3(pos.x, 0, pos.z) :
                                        new BABYLON.Vector3(pos.x, pos.y, 0);
        const dashLine = BABYLON.MeshBuilder.CreateDashedLines('dimDash_' + axis, {
          points: [origin.clone(), dashFrom],
          dashSize: 3,
          gapSize: 3,
          dashNb: 40
        }, sceneRef.current);
        dashLine.color = color.scale(0.4);
        dashLine.isPickable = false;
        dg.lines.push(dashLine);
        
        // Text label at midpoint
        const mid = BABYLON.Vector3.Center(from, to);
        const label = new GUI.TextBlock('dimLabel_' + axis);
        const currentUnit = projectSettingsRef.current?.unit || 'm';
        const displayVal = formatWithUnit(Math.abs(val), currentUnit);
        label.text = `${axis}: ${displayVal}`;
        label.color = color.toHexString();
        label.fontSize = 14;
        label.fontFamily = 'monospace';
        label.fontWeight = 'bold';
        label.outlineWidth = 2;
        label.outlineColor = 'black';
        label.resizeToFit = true;
        label.paddingLeft = '4px';
        label.paddingRight = '4px';
        guiTextureRef.current.addControl(label);
        label.linkWithMesh(line);
        label.linkOffsetY = axis === 'X' ? -15 : axis === 'Y' ? 0 : 15;
        label.linkOffsetX = axis === 'Y' ? 15 : 0;
        dg.labels.push(label);
      });
    };

    const onDragStart = () => {
      isDraggingRef.current = true;
      // Capture the "before drag" state for undo
      saveToHistoryImmediate();
      
      // Dynamic sensitivity: scale based on camera distance
      // This ensures consistent feel regardless of zoom level
      if (gm.gizmos.positionGizmo) {
        const mesh = meshesRef.current[selectedObjectId] || projectorMeshesRef.current[selectedObjectId];
        const camera = cameraRef.current;
        if (mesh && camera) {
          mesh.computeWorldMatrix(true);

          // Babylon's `dragDeltaRatio` is a smoothing/interpolation factor
          // (NOT a magnitude multiplier). Per-frame it moves the object by:
          //     position += dragDeltaRatio * (pointerTarget - position)
          //   • 1.0  → no smoothing, object tracks pointer exactly (smooth feel)
          //   • 0.2  → Babylon's default (5-frame catch-up)
          //   • 0.01 → ~100-frame catch-up (laggy / jumpy on micro-shifts)
          //
          // Babylon's plane raycast already gives world-delta-per-pixel that's
          // naturally proportional to camera distance, so the drag feel is
          // already correct at any zoom — we just need to disable smoothing.
          const camRatio = 1.0;

          const axisGizmos = [
            gm.gizmos.positionGizmo.xGizmo, gm.gizmos.positionGizmo.yGizmo, gm.gizmos.positionGizmo.zGizmo,
            gm.gizmos.positionGizmo.xPlaneGizmo, gm.gizmos.positionGizmo.yPlaneGizmo, gm.gizmos.positionGizmo.zPlaneGizmo
          ];
          axisGizmos.forEach(ag => {
            if (ag && ag.dragBehavior) ag.dragBehavior.dragDeltaRatio = camRatio;
          });
          // Store for shift-key precision toggle
          gm._currentBaseSens = camRatio;
          gm._currentPrecSens = camRatio * 0.15;
        }
      }
      
      // Multi-select: capture initial state of all meshes and parent them to pivot
      if (multiPivotRef.current && multiSelectIds.length > 1) {
        const initials = [];
        for (const id of multiSelectIds) {
          const m = meshesRef.current[id] || projectorMeshesRef.current[id];
          if (!m) continue;
          m.computeWorldMatrix(true);
          initials.push({
            id,
            mesh: m,
            isProjector: !!projectorMeshesRef.current[id],
            origPos: m.position.clone(),
            origRot: m.rotationQuaternion ? m.rotationQuaternion.clone() : BABYLON.Quaternion.FromEulerVector(m.rotation),
            origScale: m.scaling.clone()
          });
        }
        multiInitialRef.current = initials;
        // Store pivot initial state
        multiPivotRef.current._initPos = multiPivotRef.current.position.clone();
        multiPivotRef.current._initRot = multiPivotRef.current.rotationQuaternion ? multiPivotRef.current.rotationQuaternion.clone() : BABYLON.Quaternion.Identity();
        multiPivotRef.current._initScale = multiPivotRef.current.scaling.clone();
        // Ensure pivot uses quaternion for rotation gizmo
        if (!multiPivotRef.current.rotationQuaternion) {
          multiPivotRef.current.rotationQuaternion = BABYLON.Quaternion.Identity();
        }
        return;
      }
      
      // Show dimension gizmos for selected object
      const mesh = meshesRef.current[selectedObjectId] || projectorMeshesRef.current[selectedObjectId];
      if (mesh) showDimensionGizmos(mesh);
    };

    // Real-time update during dragging - update spotlight position/direction
    const onDrag = () => {
      // Multi-select: propagate pivot delta to all selected meshes
      if (multiPivotRef.current && multiInitialRef.current.length > 0) {
        const pivot = multiPivotRef.current;
        const posDelta = pivot.position.subtract(pivot._initPos);
        const pivotRot = pivot.rotationQuaternion || BABYLON.Quaternion.Identity();
        const initRot = pivot._initRot || BABYLON.Quaternion.Identity();
        const rotDelta = initRot.conjugate().multiply(pivotRot);
        const scaleFactor = pivot.scaling.divide(pivot._initScale);
        
        for (const item of multiInitialRef.current) {
          const m = item.mesh;
          if (!m || m.isDisposed()) continue;
          
          // Position: rotate the offset around pivot center, apply scale, add delta
          const offset = item.origPos.subtract(pivot._initPos);
          const rotMat = new BABYLON.Matrix();
          rotDelta.toRotationMatrix(rotMat);
          const rotatedOffset = BABYLON.Vector3.TransformCoordinates(offset, rotMat);
          const scaledOffset = new BABYLON.Vector3(
            rotatedOffset.x * scaleFactor.x,
            rotatedOffset.y * scaleFactor.y,
            rotatedOffset.z * scaleFactor.z
          );
          m.position = pivot._initPos.add(posDelta).add(scaledOffset);
          
          // Rotation: combine original rotation with delta rotation
          if (!m.rotationQuaternion) {
            m.rotationQuaternion = item.origRot.clone();
          }
          m.rotationQuaternion = rotDelta.multiply(item.origRot);
          
          // Scale: multiply original scale by pivot scale factor
          m.scaling = new BABYLON.Vector3(
            item.origScale.x * scaleFactor.x,
            item.origScale.y * scaleFactor.y,
            item.origScale.z * scaleFactor.z
          );
        }
        
        // Update projectors in real-time
        if (projectionSystemRef.current) {
          projectionSystemRef.current.updateProjectors(projectors);
        }
        return;
      }
      
      if (!selectedObjectId) return;
      
      // Update dimension gizmos in real-time
      const activeMesh = meshesRef.current[selectedObjectId] || projectorMeshesRef.current[selectedObjectId];
      if (activeMesh) showDimensionGizmos(activeMesh);

      // Force LED tile world-matrix recompute mid-drag (children are
      // unfrozen on selection but their cached world matrix still needs a
      // tick to refresh when the parent moves).
      const ledMesh = meshesRef.current[selectedObjectId];
      if (ledMesh && ledMesh.metadata?.isLedScreen) {
        ledMesh.computeWorldMatrix(true);
        ledMesh.getChildMeshes(false).forEach(c => c.computeWorldMatrix(true));
      }
      
      const projMesh = projectorMeshesRef.current[selectedObjectId];
      if (projMesh) {
        const spotLight = projectorLightsRef.current[selectedObjectId];
        if (spotLight) {
          // Get the projector data for settings
          const projector = projectors.find(p => p.id === selectedObjectId);
          if (!projector) return;
          
          // Get current mesh rotation
          let rotX, rotY, rotZ;
          if (projMesh.rotationQuaternion) {
            const euler = projMesh.rotationQuaternion.toEulerAngles();
            rotX = euler.x;
            rotY = euler.y;
            rotZ = euler.z;
          } else {
            rotX = projMesh.rotation.x;
            rotY = projMesh.rotation.y;
            rotZ = projMesh.rotation.z;
          }
          
          // Apply lens shift to direction
          const lensShiftH = (projector.lens_shift_h || 0) / 100;
          const lensShiftV = (projector.lens_shift_v || 0) / 100;
          
          const direction = new BABYLON.Vector3(lensShiftH * 0.5, lensShiftV * 0.5, 1).normalize();
          const rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);
          const transformedDir = BABYLON.Vector3.TransformNormal(direction, rotationMatrix);

          // Lens position respects the lens type (standard / lens90 / topBottom / ust).
          // Build a temporary projector-like object so getProjectionOrigin sees the
          // current dragged position/rotation instead of the stale state in `projector`.
          const draggedProjectorState = {
            ...projector,
            position: { x: projMesh.position.x, y: projMesh.position.y, z: projMesh.position.z },
            rotation: { x: BABYLON.Tools.ToDegrees(rotX), y: BABYLON.Tools.ToDegrees(rotY), z: BABYLON.Tools.ToDegrees(rotZ) },
          };
          let lensPosition;
          if (projector.lensType === 'topBottom') {
            // Periscope apex baked into getProjectionOrigin.
            lensPosition = getProjectionOrigin(draggedProjectorState, projectorMeshesRef.current);
          } else if (projector.lensType === 'ust') {
            // UST: prefer actual mirror mesh world position + body-local nudge.
            const mirrorMesh = projectorMeshesRef.current['ustGlassv4_' + projector.id];
            if (mirrorMesh) {
              mirrorMesh.computeWorldMatrix(true);
              lensPosition = mirrorMesh.getAbsolutePosition().clone();
            } else {
              lensPosition = getProjectionOrigin(draggedProjectorState, projectorMeshesRef.current);
            }
            const ustNudgeLocal = new BABYLON.Vector3(0, 0.11, 0.36);
            const dragRotMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);
            const ustNudgeWorld = BABYLON.Vector3.TransformNormal(ustNudgeLocal, dragRotMatrix);
            lensPosition.addInPlace(ustNudgeWorld);
          } else if (projector.lensType === 'lens90') {
            // lens90 apex baked into getProjectionOrigin.
            lensPosition = getProjectionOrigin(draggedProjectorState, projectorMeshesRef.current);
          } else {
            lensPosition = new BABYLON.Vector3(
                projMesh.position.x + transformedDir.x * (((projector.body_depth || projector.spec?.depth || 600) / 1000) / 2 + 0.02),
                projMesh.position.y + transformedDir.y * (((projector.body_depth || projector.spec?.depth || 600) / 1000) / 2 + 0.02),
                projMesh.position.z + transformedDir.z * (((projector.body_depth || projector.spec?.depth || 600) / 1000) / 2 + 0.02)
              );
          }
          
          // Update spotlight in real-time
          spotLight.position = lensPosition;
          spotLight.direction = transformedDir;
          
          // Also update beam mesh if it exists
          const beamMesh = beamMeshesRef.current[selectedObjectId];
          if (beamMesh) {
            beamMesh.position = lensPosition.clone();
            // Beam orientation must match the lens-type-specific frustum direction
            if (projector.lensType === 'lens90') {
              const rot90 = projector.lens90Rotation || 'right';
              const projQuat = BABYLON.Quaternion.RotationYawPitchRoll(rotY, rotX, rotZ);
              let lensOffsetQuat;
              switch (rot90) {
                case 'left':   lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(-Math.PI / 2, 0, 0); break;
                case 'right':  lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI / 2, 0, 0); break;
                case 'top':    lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(0, -Math.PI / 2, 0); break;
                case 'bottom': lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(0, Math.PI / 2, 0); break;
                default:       lensOffsetQuat = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI / 2, 0, 0); break;
              }
              beamMesh.rotationQuaternion = projQuat.multiply(lensOffsetQuat);
            } else if (projector.lensType === 'topBottom') {
              const projQuat = BABYLON.Quaternion.RotationYawPitchRoll(rotY, rotX, rotZ);
              const flipQuat = BABYLON.Quaternion.RotationYawPitchRoll(Math.PI, 0, 0);
              beamMesh.rotationQuaternion = projQuat.multiply(flipQuat);
            } else if (projector.lensType === 'ust') {
              // UST: horizontal -Z beam, just yaw 180°.
              const projQuat = BABYLON.Quaternion.RotationYawPitchRoll(rotY, rotX, rotZ);
              const yawQuat   = BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Y, Math.PI);
              beamMesh.rotationQuaternion = projQuat.multiply(yawQuat);
            } else {
              beamMesh.rotationQuaternion = null;
              beamMesh.rotation = new BABYLON.Vector3(rotX, rotY, rotZ);
            }
          }
          
          // Update projector label position
          const label = projectorLabelsRef.current[selectedObjectId];
          if (label) {
            label.linkOffsetY = -50;
          }
          
          // REAL-TIME PROJECTION UPDATE: Update projection system during drag
          // Create a temporary projector array with updated position/rotation
          if (projectionSystemRef.current) {
            const rotXDeg = BABYLON.Tools.ToDegrees(rotX);
            const rotYDeg = BABYLON.Tools.ToDegrees(rotY);
            const rotZDeg = BABYLON.Tools.ToDegrees(rotZ);
            
            // Create updated projectors array with real-time position/rotation
            const updatedProjectors = projectors.map(p => {
              if (p.id === selectedObjectId) {
                return {
                  ...p,
                  position: { 
                    x: projMesh.position.x, 
                    y: projMesh.position.y, 
                    z: projMesh.position.z 
                  },
                  rotation: { 
                    x: rotXDeg, 
                    y: rotYDeg, 
                    z: rotZDeg 
                  }
                };
              }
              return p;
            });
            
            // Update projection system immediately (no debounce during drag)
            projectionSystemRef.current.updateProjectors(updatedProjectors);
          }
        }
      }
      
      // Also handle regular scene objects - update shadows in real-time
      const regularMesh = meshesRef.current[selectedObjectId];
      if (regularMesh && projectionSystemRef.current) {
        // Trigger shadow map re-render for real-time shadow updates
        projectionSystemRef.current.updateProjectors(projectors);
      }
    };

    const onDragEnd = () => {
      isDraggingRef.current = false;
      // Hide dimension gizmos
      disposeDimensionGizmos();
      
      // Multi-select: save all mesh transforms to state
      if (multiPivotRef.current && multiInitialRef.current.length > 0) {
        for (const item of multiInitialRef.current) {
          const m = item.mesh;
          if (!m || m.isDisposed()) continue;
          
          let rotX, rotY, rotZ;
          if (m.rotationQuaternion) {
            const euler = m.rotationQuaternion.toEulerAngles();
            rotX = BABYLON.Tools.ToDegrees(euler.x);
            rotY = BABYLON.Tools.ToDegrees(euler.y);
            rotZ = BABYLON.Tools.ToDegrees(euler.z);
          } else {
            rotX = BABYLON.Tools.ToDegrees(m.rotation.x);
            rotY = BABYLON.Tools.ToDegrees(m.rotation.y);
            rotZ = BABYLON.Tools.ToDegrees(m.rotation.z);
          }
          
          const transforms = {
            position: { x: m.position.x, y: m.position.y, z: m.position.z },
            rotation: { x: rotX, y: rotY, z: rotZ },
            scale: { x: m.scaling.x, y: m.scaling.y, z: m.scaling.z }
          };
          
          if (item.isProjector) {
            updateProjector(item.id, transforms);
          } else {
            updateSceneObject(item.id, transforms);
          }
        }
        
        multiInitialRef.current = [];
        
        // Recompute pivot position at new centroid
        const pivot = multiPivotRef.current;
        const allMeshes = [];
        for (const id of multiSelectIds) {
          const m = meshesRef.current[id] || projectorMeshesRef.current[id];
          if (m) allMeshes.push(m);
        }
        if (allMeshes.length > 1 && pivot) {
          const centroid = BABYLON.Vector3.Zero();
          allMeshes.forEach(m => { centroid.addInPlace(m.getAbsolutePosition()); });
          centroid.scaleInPlace(1 / allMeshes.length);
          pivot.position = centroid;
          if (pivot.rotationQuaternion) {
            pivot.rotationQuaternion = BABYLON.Quaternion.Identity();
          }
          pivot.scaling = new BABYLON.Vector3(1, 1, 1);

          // Re-attach the gizmo so its internal drag-state caches (initial
          // node matrix, grab offset, etc.) are rebuilt with the freshly
          // reset pivot. Without this, the second drag computes deltas from
          // stale references and objects don't move.
          try {
            gm.attachToMesh(null);
            gm.attachToMesh(pivot);
          } catch (_) { /* gizmo may not exist yet during early init */ }
        }
        
        return;
      }
      
      if (!selectedObjectId) return;

      // Refresh the selection bounding box to match new position
      if (selectionBoxRef.current) {
        selectionBoxRef.current.dispose();
        selectionBoxRef.current = null;
      }
      const refreshSelectionBox = (targetMesh) => {
        if (!targetMesh || !sceneRef.current) return;
        try {
          targetMesh.computeWorldMatrix(true);
          const b = targetMesh.getHierarchyBoundingVectors(true);
          const mn = b.min, mx = b.max;
          const lines = [
            [new BABYLON.Vector3(mn.x,mn.y,mn.z), new BABYLON.Vector3(mx.x,mn.y,mn.z)],
            [new BABYLON.Vector3(mx.x,mn.y,mn.z), new BABYLON.Vector3(mx.x,mn.y,mx.z)],
            [new BABYLON.Vector3(mx.x,mn.y,mx.z), new BABYLON.Vector3(mn.x,mn.y,mx.z)],
            [new BABYLON.Vector3(mn.x,mn.y,mx.z), new BABYLON.Vector3(mn.x,mn.y,mn.z)],
            [new BABYLON.Vector3(mn.x,mx.y,mn.z), new BABYLON.Vector3(mx.x,mx.y,mn.z)],
            [new BABYLON.Vector3(mx.x,mx.y,mn.z), new BABYLON.Vector3(mx.x,mx.y,mx.z)],
            [new BABYLON.Vector3(mx.x,mx.y,mx.z), new BABYLON.Vector3(mn.x,mx.y,mx.z)],
            [new BABYLON.Vector3(mn.x,mx.y,mx.z), new BABYLON.Vector3(mn.x,mx.y,mn.z)],
            [new BABYLON.Vector3(mn.x,mn.y,mn.z), new BABYLON.Vector3(mn.x,mx.y,mn.z)],
            [new BABYLON.Vector3(mx.x,mn.y,mn.z), new BABYLON.Vector3(mx.x,mx.y,mn.z)],
            [new BABYLON.Vector3(mx.x,mn.y,mx.z), new BABYLON.Vector3(mx.x,mx.y,mx.z)],
            [new BABYLON.Vector3(mn.x,mn.y,mx.z), new BABYLON.Vector3(mn.x,mx.y,mx.z)]
          ];
          const box = BABYLON.MeshBuilder.CreateLineSystem('selectionBoundingBox', { lines, updatable: false }, sceneRef.current);
          box.color = new BABYLON.Color3(1, 1, 0);
          box.isPickable = false;
          selectionBoxRef.current = box;
        } catch(e) {}
      };

      const mesh = meshesRef.current[selectedObjectId];
      if (mesh) {
        // Get rotation values - check rotationQuaternion first (gizmo may use it)
        let rotX, rotY, rotZ;
        if (mesh.rotationQuaternion) {
          // Convert quaternion to Euler angles
          const euler = mesh.rotationQuaternion.toEulerAngles();
          rotX = BABYLON.Tools.ToDegrees(euler.x);
          rotY = BABYLON.Tools.ToDegrees(euler.y);
          rotZ = BABYLON.Tools.ToDegrees(euler.z);
        } else {
          rotX = BABYLON.Tools.ToDegrees(mesh.rotation.x);
          rotY = BABYLON.Tools.ToDegrees(mesh.rotation.y);
          rotZ = BABYLON.Tools.ToDegrees(mesh.rotation.z);
        }
        
        updateSceneObject(selectedObjectId, {
          position: { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z },
          rotation: { x: rotX, y: rotY, z: rotZ },
          scale: { x: mesh.scaling.x, y: mesh.scaling.y, z: mesh.scaling.z }
        });

        // ── Bake scale → dimensions ─────────────────────────────────────
        // When the user drags the SCALE gizmo, Babylon writes the result into
        // `mesh.scaling`. Without this step, the Properties panel would show
        // `Scale = 1.5` but `Dimensions = 1.00 m` — visually inconsistent and
        // useless for AV planning (the user needs real metres).
        //
        // We "bake" the non-unit scaling into the object's stored dimensions
        // (e.g. scale.x = 1.5 + width = 1.00 m  →  width = 1.50 m, scale.x = 1).
        // The mesh-rebuild effect then regenerates the geometry at the new
        // dimensions so the rendered size is identical to before the bake.
        //
        // Cylinder / pipe keep their own special-case below (they only have a
        // single `width` field for diameter; bake doesn't fit their model).
        const currentObj = sceneObjectsRef.current.find(o => o.id === selectedObjectId);
        if (currentObj && currentObj.dimensions
            && currentObj.type !== 'cylinder' && currentObj.type !== 'pipe') {
          const sx = mesh.scaling.x;
          const sy = mesh.scaling.y;
          const sz = mesh.scaling.z;
          const isUnit = Math.abs(sx - 1) < 1e-3 && Math.abs(sy - 1) < 1e-3 && Math.abs(sz - 1) < 1e-3;
          if (!isUnit) {
            const d = currentObj.dimensions;
            updateSceneObject(selectedObjectId, {
              dimensions: {
                ...d,
                width:  Math.round(((d.width  || 1) * sx) * 1000) / 1000,
                height: Math.round(((d.height || 1) * sy) * 1000) / 1000,
                depth:  Math.round(((d.depth  || 1) * sz) * 1000) / 1000,
              },
              scale: { x: 1, y: 1, z: 1 },
            });
          }
        }

        // Sync dimensions with scale for cylinder/pipe types (bidirectional sync)
        if (currentObj && (currentObj.type === 'cylinder' || currentObj.type === 'pipe') && currentObj.dimensions) {
          const dims = currentObj.dimensions;
          const newDepth = (dims.width || 1) * mesh.scaling.z;
          if (Math.abs(newDepth - (dims.depth || 1)) > 0.001) {
            updateSceneObject(selectedObjectId, {
              dimensions: { ...dims, depth: newDepth }
            });
          }
        }
        refreshSelectionBox(mesh);
        return;
      }

      const projMesh = projectorMeshesRef.current[selectedObjectId];
      if (projMesh) {
        // Get rotation values - check rotationQuaternion first
        let rotX, rotY, rotZ;
        if (projMesh.rotationQuaternion) {
          const euler = projMesh.rotationQuaternion.toEulerAngles();
          rotX = BABYLON.Tools.ToDegrees(euler.x);
          rotY = BABYLON.Tools.ToDegrees(euler.y);
          rotZ = BABYLON.Tools.ToDegrees(euler.z);
        } else {
          rotX = BABYLON.Tools.ToDegrees(projMesh.rotation.x);
          rotY = BABYLON.Tools.ToDegrees(projMesh.rotation.y);
          rotZ = BABYLON.Tools.ToDegrees(projMesh.rotation.z);
        }
        
        updateProjector(selectedObjectId, {
          position: { x: projMesh.position.x, y: projMesh.position.y, z: projMesh.position.z },
          rotation: { x: rotX, y: rotY, z: rotZ }
        });
        refreshSelectionBox(projMesh);
      }
    };

    // Add drag start, drag (real-time), and end observers
    if (gm.gizmos.positionGizmo) {
      gm.gizmos.positionGizmo.onDragStartObservable.add(onDragStart);
      gm.gizmos.positionGizmo.onDragObservable.add(onDrag);
      gm.gizmos.positionGizmo.onDragEndObservable.add(onDragEnd);
    }
    if (gm.gizmos.rotationGizmo) {
      gm.gizmos.rotationGizmo.onDragStartObservable.add(onDragStart);
      gm.gizmos.rotationGizmo.onDragObservable.add(onDrag);
      gm.gizmos.rotationGizmo.onDragEndObservable.add(onDragEnd);
    }
    if (gm.gizmos.scaleGizmo) {
      gm.gizmos.scaleGizmo.onDragStartObservable.add(onDragStart);
      gm.gizmos.scaleGizmo.onDragObservable.add(onDrag);
      gm.gizmos.scaleGizmo.onDragEndObservable.add(onDragEnd);
    }

    return () => {
      disposeDimensionGizmos();
      // Cleanup precision mode key handlers
      if (gm._precisionKeyHandlers) {
        document.removeEventListener('keydown', gm._precisionKeyHandlers.onKeyDown);
        document.removeEventListener('keyup', gm._precisionKeyHandlers.onKeyUp);
      }
      if (gm.gizmos.positionGizmo) {
        gm.gizmos.positionGizmo.onDragStartObservable.clear();
        gm.gizmos.positionGizmo.onDragObservable.clear();
        gm.gizmos.positionGizmo.onDragEndObservable.clear();
      }
      if (gm.gizmos.rotationGizmo) {
        gm.gizmos.rotationGizmo.onDragStartObservable.clear();
        gm.gizmos.rotationGizmo.onDragObservable.clear();
        gm.gizmos.rotationGizmo.onDragEndObservable.clear();
      }
      if (gm.gizmos.scaleGizmo) {
        gm.gizmos.scaleGizmo.onDragStartObservable.clear();
        gm.gizmos.scaleGizmo.onDragObservable.clear();
        gm.gizmos.scaleGizmo.onDragEndObservable.clear();
      }
    };
  }, [selectedObjectId, transformMode, updateSceneObject, updateProjector, projectors, multiSelectIds]);

  // Handle view preset changes
  useEffect(() => {
    if (!cameraRef.current || !viewPreset) return;
    const camera = cameraRef.current;
    const distance = 15;

    // Handle saved camera views (object with type='camera')
    if (viewPreset && typeof viewPreset === 'object' && viewPreset.type === 'camera') {
      const cam = viewPreset.data;
      if (cam) {
        camera.mode = BABYLON.Camera.PERSPECTIVE_CAMERA;
        if (cam.alpha !== undefined) camera.alpha = cam.alpha;
        if (cam.beta !== undefined) camera.beta = cam.beta;
        if (cam.radius !== undefined) camera.radius = cam.radius;
        if (cam.target) {
          camera.target = new BABYLON.Vector3(cam.target.x || 0, cam.target.y || 0, cam.target.z || 0);
        }
        if (cam.fov !== undefined) camera.fov = cam.fov;
      }
      if (onViewPresetReset) onViewPresetReset();
      return;
    }

    // Smoothly tween alpha/beta/radius so view-preset clicks (menu + ViewCube) feel
    // like professional 3D software instead of jump-cuts.
    const tweenCamera = (targetAlpha, targetBeta, targetRadius, mode) => {
      const scene = sceneRef.current;
      if (!scene) return;
      const fps = 60;
      const frames = 24; // ~0.4s at 60fps
      const makeAnim = (prop, toVal) => {
        const anim = new BABYLON.Animation(
          `vcAnim_${prop}_${Date.now()}`, prop, fps,
          BABYLON.Animation.ANIMATIONTYPE_FLOAT,
          BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
        );
        anim.setKeys([
          { frame: 0, value: camera[prop] },
          { frame: frames, value: toVal },
        ]);
        const ease = new BABYLON.CubicEase();
        ease.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
        anim.setEasingFunction(ease);
        return anim;
      };
      // Normalise alpha delta to shortest path so orbit doesn't spin the long way round
      const TAU = Math.PI * 2;
      let delta = targetAlpha - camera.alpha;
      while (delta > Math.PI) delta -= TAU;
      while (delta < -Math.PI) delta += TAU;
      const shortestAlpha = camera.alpha + delta;

      camera.animations = [
        makeAnim('alpha', shortestAlpha),
        makeAnim('beta', targetBeta),
        makeAnim('radius', targetRadius),
      ];
      scene.beginAnimation(camera, 0, frames, false, 1.0, () => {
        // Snap final camera mode AFTER the tween so ortho math kicks in cleanly.
        camera.alpha = targetAlpha; // normalise
        if (mode === 'ortho') {
          camera.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;
          const aspect = sceneRef.current?.getEngine()?.getAspectRatio(camera) || 1.78;
          const orthoSize = camera.radius || distance;
          camera.orthoTop = orthoSize / 2;
          camera.orthoBottom = -orthoSize / 2;
          camera.orthoLeft = -orthoSize * aspect / 2;
          camera.orthoRight = orthoSize * aspect / 2;
        } else {
          camera.mode = BABYLON.Camera.PERSPECTIVE_CAMERA;
        }
      });
    };

    if (viewPreset === 'reset') {
      tweenCamera(-Math.PI / 4, Math.PI / 3, distance, 'persp');
      if (onViewPresetReset) onViewPresetReset();
      return;
    }

    // Isometric / corner views (object form: { type:'iso', alpha, beta })
    if (viewPreset && typeof viewPreset === 'object' && viewPreset.type === 'iso') {
      const a = typeof viewPreset.alpha === 'number' ? viewPreset.alpha : -Math.PI / 4;
      const b = typeof viewPreset.beta === 'number' ? viewPreset.beta : Math.PI / 3;
      tweenCamera(a, b, distance, 'persp');
      if (onViewPresetReset) onViewPresetReset();
      return;
    }

    // Axis-aligned ortho views
    let targetAlpha = camera.alpha, targetBeta = camera.beta;
    switch (viewPreset) {
      case 'front':  targetAlpha = -Math.PI / 2;  targetBeta = Math.PI / 2;   break;
      case 'back':   targetAlpha = Math.PI / 2;   targetBeta = Math.PI / 2;   break;
      case 'left':   targetAlpha = 0;             targetBeta = Math.PI / 2;   break;
      case 'right':  targetAlpha = Math.PI;       targetBeta = Math.PI / 2;   break;
      case 'top':    targetAlpha = -Math.PI / 2;  targetBeta = 0.01;          break;
      case 'bottom': targetAlpha = -Math.PI / 2;  targetBeta = Math.PI - 0.01; break;
      default: break;
    }
    tweenCamera(targetAlpha, targetBeta, distance, 'ortho');
    if (onViewPresetReset) onViewPresetReset();
  }, [viewPreset, onViewPresetReset]);

  // Handle control mode changes (orbit vs pan)
  useEffect(() => {
    if (!cameraRef.current) return;
    const camera = cameraRef.current;
    
    if (controlMode === 'pan') {
      // In pan mode, use panning buttons (shift + drag or middle mouse)
      camera.panningSensibility = 50; // Lower = faster panning
      // Disable default rotation behavior by holding right click for pan
      camera._panningMouseButton = 0; // Left mouse button for pan
    } else {
      // In orbit mode, restore default behavior
      camera.panningSensibility = 100;
      camera._panningMouseButton = 2; // Right mouse button for pan (default)
    }
  }, [controlMode]);

  // Render measurements as lines and labels
  useEffect(() => {
    if (!sceneRef.current || !guiTextureRef.current) return;
    const scene = sceneRef.current;
    const guiTexture = guiTextureRef.current;
    
    // Remove old measurement meshes that no longer exist
    Object.keys(measurementMeshesRef.current).forEach(id => {
      if (!measurements.find(m => m.id === id)) {
        const meshData = measurementMeshesRef.current[id];
        if (meshData.line) meshData.line.dispose();
        if (meshData.label) meshData.label.dispose();
        if (meshData.startSphere) meshData.startSphere.dispose();
        if (meshData.endSphere) meshData.endSphere.dispose();
        delete measurementMeshesRef.current[id];
      }
    });
    
    // Create/update measurement visualizations
    measurements.forEach(measurement => {
      if (!measurement.startPoint || !measurement.endPoint) return;
      
      const existing = measurementMeshesRef.current[measurement.id];
      
      // Parse color for line
      const lineColor = measurement.lineColor || '#00ff00';
      const r = parseInt(lineColor.slice(1, 3), 16) / 255;
      const g = parseInt(lineColor.slice(3, 5), 16) / 255;
      const b = parseInt(lineColor.slice(5, 7), 16) / 255;
      const color = new BABYLON.Color3(r, g, b);
      
      if (!existing) {
        // Create new measurement visualization
        let startPt = new BABYLON.Vector3(
          measurement.startPoint.x,
          measurement.startPoint.y,
          measurement.startPoint.z
        );
        let endPt = new BABYLON.Vector3(
          measurement.endPoint.x,
          measurement.endPoint.y,
          measurement.endPoint.z
        );
        
        // Compute and store normalized bounding box offsets for object-linked measurements
        if (measurement.startObjectId || measurement.endObjectId) {
          const offsets = { startNormalized: null, endNormalized: null, startObjectId: measurement.startObjectId, endObjectId: measurement.endObjectId };
          if (measurement.startObjectId) {
            const startMesh = meshesRef.current[measurement.startObjectId];
            if (startMesh && typeof startMesh.refreshBoundingInfo === 'function') {
              startMesh.computeWorldMatrix(true);
              startMesh.refreshBoundingInfo();
              const bbox = startMesh.getBoundingInfo().boundingBox;
              const min = bbox.minimumWorld;
              const max = bbox.maximumWorld;
              const rangeX = (max.x - min.x) || 1;
              const rangeY = (max.y - min.y) || 1;
              const rangeZ = (max.z - min.z) || 1;
              offsets.startNormalized = {
                x: (startPt.x - min.x) / rangeX,
                y: (startPt.y - min.y) / rangeY,
                z: (startPt.z - min.z) / rangeZ
              };
            }
          }
          if (measurement.endObjectId) {
            const endMesh = meshesRef.current[measurement.endObjectId];
            if (endMesh && typeof endMesh.refreshBoundingInfo === 'function') {
              endMesh.computeWorldMatrix(true);
              endMesh.refreshBoundingInfo();
              const bbox = endMesh.getBoundingInfo().boundingBox;
              const min = bbox.minimumWorld;
              const max = bbox.maximumWorld;
              const rangeX = (max.x - min.x) || 1;
              const rangeY = (max.y - min.y) || 1;
              const rangeZ = (max.z - min.z) || 1;
              offsets.endNormalized = {
                x: (endPt.x - min.x) / rangeX,
                y: (endPt.y - min.y) / rangeY,
                z: (endPt.z - min.z) / rangeZ
              };
            }
          }
          measurementLocalOffsetsRef.current[measurement.id] = offsets;
        }
        
        // Create line
        const line = BABYLON.MeshBuilder.CreateLines('measureLine_' + measurement.id, {
          points: [startPt, endPt],
          updatable: true
        }, scene);
        line.color = color;
        line.isPickable = false;
        
        // Create endpoint spheres
        const sphereSize = 0.05;
        const startSphere = BABYLON.MeshBuilder.CreateSphere('measureStart_' + measurement.id, { diameter: sphereSize }, scene);
        startSphere.position = startPt;
        const sphereMat = new BABYLON.StandardMaterial('measureSphereMat_' + measurement.id, scene);
        sphereMat.diffuseColor = color;
        sphereMat.emissiveColor = color.scale(0.5);
        startSphere.material = sphereMat;
        startSphere.isPickable = false;
        
        const endSphere = BABYLON.MeshBuilder.CreateSphere('measureEnd_' + measurement.id, { diameter: sphereSize }, scene);
        endSphere.position = endPt;
        endSphere.material = sphereMat;
        endSphere.isPickable = false;
        
        // Create label with distance using project unit
        const currentUnit = projectSettingsRef.current?.unit || 'm';
        const midPoint = startPt.add(endPt).scale(0.5);
        const label = new GUI.TextBlock('measureText_' + measurement.id);
        label.text = formatWithUnit(measurement.distance, currentUnit);
        label.color = measurement.lineColor || '#00ff00';
        label.fontSize = measurement.textSize || 16;
        label.fontWeight = 'bold';
        label.outlineWidth = 2;
        label.outlineColor = 'black';
        
        guiTexture.addControl(label);
        
        // Position label at midpoint
        const labelAnchor = new BABYLON.TransformNode('measureAnchor_' + measurement.id, scene);
        labelAnchor.position = midPoint;
        label.linkWithMesh(labelAnchor);
        
        // Set visibility
        line.isVisible = measurement.visible !== false;
        startSphere.isVisible = measurement.visible !== false;
        endSphere.isVisible = measurement.visible !== false;
        label.isVisible = measurement.visible !== false;
        
        measurementMeshesRef.current[measurement.id] = {
          line, startSphere, endSphere, label, labelAnchor, sphereMat
        };
      } else {
        // Update existing measurement - recalculate for linked objects using bounding box
        let startPt = new BABYLON.Vector3(measurement.startPoint.x, measurement.startPoint.y, measurement.startPoint.z);
        let endPt = new BABYLON.Vector3(measurement.endPoint.x, measurement.endPoint.y, measurement.endPoint.z);
        let distance = measurement.distance;
        let needsPositionUpdate = false;
        
        const offsets = measurementLocalOffsetsRef.current[measurement.id];
        if (offsets) {
          if (offsets.startNormalized && offsets.startObjectId) {
            const startMesh = meshesRef.current[offsets.startObjectId];
            if (startMesh && typeof startMesh.refreshBoundingInfo === 'function') {
              startMesh.computeWorldMatrix(true);
              startMesh.refreshBoundingInfo();
              const bbox = startMesh.getBoundingInfo().boundingBox;
              const min = bbox.minimumWorld;
              const max = bbox.maximumWorld;
              startPt = new BABYLON.Vector3(
                min.x + offsets.startNormalized.x * (max.x - min.x),
                min.y + offsets.startNormalized.y * (max.y - min.y),
                min.z + offsets.startNormalized.z * (max.z - min.z)
              );
              needsPositionUpdate = true;
            }
          }
          if (offsets.endNormalized && offsets.endObjectId) {
            const endMesh = meshesRef.current[offsets.endObjectId];
            if (endMesh && typeof endMesh.refreshBoundingInfo === 'function') {
              endMesh.computeWorldMatrix(true);
              endMesh.refreshBoundingInfo();
              const bbox = endMesh.getBoundingInfo().boundingBox;
              const min = bbox.minimumWorld;
              const max = bbox.maximumWorld;
              endPt = new BABYLON.Vector3(
                min.x + offsets.endNormalized.x * (max.x - min.x),
                min.y + offsets.endNormalized.y * (max.y - min.y),
                min.z + offsets.endNormalized.z * (max.z - min.z)
              );
              needsPositionUpdate = true;
            }
          }
          if (needsPositionUpdate) {
            distance = BABYLON.Vector3.Distance(startPt, endPt);
            const oldStart = measurement.startPoint;
            const oldEnd = measurement.endPoint;
            const threshold = 0.001;
            const startChanged = Math.abs(startPt.x - oldStart.x) > threshold || 
                                 Math.abs(startPt.y - oldStart.y) > threshold || 
                                 Math.abs(startPt.z - oldStart.z) > threshold;
            const endChanged = Math.abs(endPt.x - oldEnd.x) > threshold || 
                               Math.abs(endPt.y - oldEnd.y) > threshold || 
                               Math.abs(endPt.z - oldEnd.z) > threshold;
            if (startChanged || endChanged) {
              updateMeasurement(measurement.id, {
                startPoint: { x: startPt.x, y: startPt.y, z: startPt.z },
                endPoint: { x: endPt.x, y: endPt.y, z: endPt.z },
                distance
              });
            } else {
              needsPositionUpdate = false;
            }
          }
        }
        
        const { startSphere, endSphere, label, labelAnchor, sphereMat } = existing;
        
        // Recreate line if positions changed
        if (needsPositionUpdate) {
          existing.line.dispose();
          const newLine = BABYLON.MeshBuilder.CreateLines('measureLine_' + measurement.id, {
            points: [startPt, endPt], updatable: true
          }, scene);
          newLine.color = color;
          newLine.isPickable = false;
          existing.line = newLine;
          startSphere.position = startPt;
          endSphere.position = endPt;
          if (labelAnchor) labelAnchor.position = startPt.add(endPt).scale(0.5);
        }
        
        // Update visibility
        existing.line.isVisible = measurement.visible !== false;
        startSphere.isVisible = measurement.visible !== false;
        endSphere.isVisible = measurement.visible !== false;
        label.isVisible = measurement.visible !== false;
        
        // Update color
        existing.line.color = color;
        if (sphereMat) {
          sphereMat.diffuseColor = color;
          sphereMat.emissiveColor = color.scale(0.5);
        }
        label.color = measurement.lineColor || '#00ff00';
        
        // Update label text with current unit
        const currentUnit = projectSettingsRef.current?.unit || 'm';
        label.text = formatWithUnit(needsPositionUpdate ? distance : measurement.distance, currentUnit);
      }
    });
    
    return () => {
      // Cleanup handled by object removal loop above
    };
  }, [measurements, sceneObjects, projectSettings]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        outline: 'none'
      }}
      data-testid="babylon-canvas"
    />
  );
};

export default BabylonScene;
